import React, { useState, useEffect, useMemo, useRef, useCallback, Suspense, lazy } from 'react';
import './App.css';
import { durationLabelShort } from './marketplaceDurations';
import { transactionBadgeClass, transactionTypeLabel, isPositiveTransaction, isGoldTransaction, displayBalanceBefore } from './transactionTypes';
import Icon from './icons';
import ResellerLogin from './ResellerLogin';
import ResellerDashboard from './ResellerDashboard';
import AdminLogin from './AdminLogin';
import CreditsPoolLogin from './CreditsPoolLogin';
import { CpSecurityGate } from './CpAccountSecurity';
import CreditsPoolDashboard from './CreditsPoolDashboard';
import UserStatusPortal from './UserStatusPortal';
import BulkStatusPortal from './BulkStatusPortal';
import RedeemKey from './RedeemKey';
import KeyRedemptionAdmin from './KeyRedemptionAdmin';
import ManualOrgAdmin from './ManualOrgAdmin';
import MarketplaceSupplierProducts from './MarketplaceSupplierProducts';
import SupplierSourceEditor from './SupplierSourceEditor';
import TelegramShopAdmin from './TelegramShopAdmin';
import IpMonitorAdmin from './IpMonitorAdmin';
import AdminDashboard from './AdminDashboard';
import { ThemeToggle, useTheme } from './theme';

// The AI Creative Hub entry point was removed 2026-08-23: the module was
// deleted from the production VPS at the owner's request. Its source still
// lives under client/src/ai-hub/ and in git, so restoring means re-adding the
// lazy import, the /ai-hub early return, the path branch and the nav item.

import useAppVersion from './useAppVersion';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// CCMTeamDelegates is Adobe's "Team Delegates" admin group, not a licence — it
// grants no applications. Hidden from every product picker; the server refuses
// to assign it regardless, so this is convenience rather than the real guard.
const isBlockedProduct = (name) =>
    String(name || '').toLowerCase().replace(/[\s._-]/g, '').includes('teamdelegate');
const assignableProducts = (products) =>
    (Array.isArray(products) ? products : []).filter(p => !isBlockedProduct(p));

const ADMIN_PATH = process.env.REACT_APP_ADMIN_PATH || '/adminsecure';
// Negative = day plan (-7 = 7 days, -14 = 14 days), 1–12 = months, 0 = Lifetime.
// The built-in durations. The live list comes from the server (which may hold
// extras the owner added) and falls back to this before it arrives - the page
// must render prices even if that request is slow.
const BASE_MARKETPLACE_MONTHS = [-7, -14, ...Array.from({ length: 12 }, (_, i) => i + 1), 0];
let MARKETPLACE_MONTHS = BASE_MARKETPLACE_MONTHS;
const DEFAULT_MARKETPLACE_PRICES = MARKETPLACE_MONTHS.reduce((acc, month) => {
  acc[month] = month <= 0 ? 0 : month;
  return acc;
}, {});
const DEFAULT_MARKETPLACE_ENABLED_MONTHS = [1, 2];
const sortMarketplaceMonths = (a, b) => {
  if (a === 0) return 1;
  if (b === 0) return -1;
  if (a < 0 && b < 0) return b - a;
  if (a < 0) return -1;
  if (b < 0) return 1;
  return a - b;
};
// Shared with the Telegram admin, so a duration cannot read one way here and
// another way there.
const formatMarketplaceMonth = durationLabelShort;

// Round to 2 decimals so float drift like `0 + 0.6` rendering as
// `0.6000000000000001` never reaches the admin UI.
function roundCredits(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : 0;
}

function formatCredits(value) {
  return roundCredits(value).toFixed(2);
}

function buildCPResellerSearchText(reseller = {}) {
  const orgText = (reseller.organizationAccess || [])
    .map(access => [
      access.organizationName,
      access.organizationId,
      ...(access.products || [])
    ].filter(Boolean).join(' '))
    .join(' ');

  return [
    reseller.id,
    reseller.username,
    reseller.userGroup,
    orgText,
    reseller.creditsBalance,
    reseller.goldCoinsBalance,
    reseller.refundDaysBalance,
    reseller.activeUsers,
    reseller.creditsUsed,
    reseller.totalCreditsIssued,
    reseller.creditsAddedByAdmin,
    reseller.poolDaysLeft,
    reseller.stats?.activeUsers,
    reseller.stats?.netCreditsUsed,
    reseller.stats?.totalCreditsUsed,
    reseller.stats?.totalCreditsRefunded,
    reseller.apiAccessEnabled ? 'api' : '',
    reseller.showExpirationInfo ? 'show expiration on' : 'show expiration off',
    reseller.allowTransfer === false ? 'transfer off disabled' : 'transfer on enabled',
    reseller.allowReinvite === false ? 'reinvite off disabled' : 'reinvite on enabled',
    reseller.transferSourceOrgAction === 'keep' ? 'keep org a' : 'remove org a'
  ].filter(value => value !== undefined && value !== null).join(' ').toLowerCase();
}

function filterCPResellersByQuery(resellers, query) {
  const terms = String(query || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return resellers;
  return resellers.filter(reseller => {
    const searchText = buildCPResellerSearchText(reseller);
    return terms.every(term => searchText.includes(term));
  });
}

function parseDecimalAmount(value, { allowNegative = true, allowZero = false } = {}) {
  const text = String(value ?? '').trim();
  if (!text) return null;

  const pattern = allowNegative ? /^-?(?:\d+\.?\d*|\.\d+)$/ : /^(?:\d+\.?\d*|\.\d+)$/;
  if (!pattern.test(text)) return null;

  const amount = Number(text);
  if (!Number.isFinite(amount)) return null;
  if (!allowNegative && amount < 0) return null;
  if (!allowZero && amount === 0) return null;
  return amount;
}

function normalizeCreditsPerMonthConfig(config = {}) {
  const normalized = {};
  for (let month = 1; month <= 12; month++) {
    const amount = parseDecimalAmount(config[month] ?? config[String(month)], { allowNegative: false });
    normalized[month] = amount && amount > 0 ? amount : month;
  }
  for (const [key, defaultAmount] of Object.entries({ '7d': 0.4, '14d': 0.6 })) {
    const amount = parseDecimalAmount(config[key], { allowNegative: false });
    normalized[key] = amount && amount > 0 ? amount : defaultAmount;
  }
  return normalized;
}

function normalizeMarketplaceEnabledMonths(months) {
  const source = Array.isArray(months) && months.length > 0 ? months : DEFAULT_MARKETPLACE_ENABLED_MONTHS;
  return [...new Set(source
    .map(month => parseInt(month, 10))
    .filter(month => MARKETPLACE_MONTHS.includes(month)))]
    .sort((a, b) => (a === 0 ? 1 : b === 0 ? -1 : a - b));
}

function normalizeMarketplacePrices(product = {}) {
  const source = product.priceByMonth && typeof product.priceByMonth === 'object' ? product.priceByMonth : {};
  const prices = {};
  MARKETPLACE_MONTHS.forEach(month => {
    const raw = source[month] ?? source[String(month)] ??
      (month === 1 ? product.price1Month : month === 2 ? product.price2Month : DEFAULT_MARKETPLACE_PRICES[month]);
    const parsed = Number(raw);
    prices[month] = Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_MARKETPLACE_PRICES[month];
  });
  return prices;
}

function createMarketplaceProductForm(product = {}) {
  return {
    name: product.name || '',
    imageUrl: product.imageUrl || '',
    description: product.description || '',
    priceByMonth: normalizeMarketplacePrices(product),
    enabledMonths: normalizeMarketplaceEnabledMonths(product.enabledMonths),
    active: product.active !== false,
    category: product.category || '',
    requiresEmail: !!product.requiresEmail,
    manualFulfill: !!product.manualFulfill,
    pinned: !!product.pinned,
    warrantyType: product.warrantyType || '',
    warrantyDays: product.warrantyDays || '',
    visibility: product.visibility === 'restricted' ? 'restricted' : 'all',
    allowedResellerIds: Array.isArray(product.allowedResellerIds)
      ? product.allowedResellerIds.map(Number).filter(Number.isFinite)
      : []
  };
}

// Warranty shown to resellers on the product page. Days are optional
// ("7-day warranty"); "No warranty" has no days.
function WarrantyFields({ form, onChange }) {
  const type = form.warrantyType || '';
  return (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
      <div style={{ flex: '1 1 220px' }}>
        <label className="form-label">Warranty</label>
        <select className="form-input" value={type} onChange={e => onChange({ ...form, warrantyType: e.target.value, warrantyDays: e.target.value === 'none' ? '' : form.warrantyDays })}>
          <option value="">— not stated —</option>
          <option value="full">Full Warranty</option>
          <option value="first_login">Covers initial login or activation only</option>
          <option value="none">No warranty</option>
        </select>
      </div>
      <div style={{ flex: '0 1 160px' }}>
        <label className="form-label">Warranty period (days)</label>
        <input type="number" min="1" max="3650" className="form-input" disabled={!type || type === 'none'}
          value={form.warrantyDays || ''} placeholder={type === 'first_login' ? 'e.g. 1' : 'e.g. 7, 30'}
          onChange={e => onChange({ ...form, warrantyDays: e.target.value })} />
      </div>
    </div>
  );
}

function getMarketplaceMonthStock(product, month) {
  return product.availableByMonth?.[month] || product.availableByMonth?.[String(month)] || 0;
}

// Per-product visibility: everyone, or a restricted allow-list of CP resellers.
function MarketplaceVisibilityControl({ form, onChange, resellers }) {
  const [query, setQuery] = React.useState('');
  const restricted = form.visibility === 'restricted';
  const allowed = new Set((form.allowedResellerIds || []).map(Number));
  const q = query.trim().toLowerCase();
  const list = (resellers || []).filter(r => !q || String(r.username || '').toLowerCase().includes(q));
  const toggle = (id) => {
    const next = new Set(allowed);
    if (next.has(Number(id))) next.delete(Number(id)); else next.add(Number(id));
    onChange({ ...form, allowedResellerIds: [...next] });
  };
  return (
    <div style={{ marginTop: '12px' }}>
      <label className="form-label" style={{ display: 'block', marginBottom: '6px' }}>Visibility</label>
      <div style={{ display: 'flex', gap: '16px', marginBottom: restricted ? '8px' : 0 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input type="radio" checked={!restricted} onChange={() => onChange({ ...form, visibility: 'all' })} />
          All resellers
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input type="radio" checked={restricted} onChange={() => onChange({ ...form, visibility: 'restricted' })} />
          Only selected resellers
        </label>
      </div>
      {restricted && (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px' }}>
          <input className="form-input" placeholder="Search resellers…" value={query}
            onChange={e => setQuery(e.target.value)} style={{ marginBottom: '8px' }} />
          <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>
            {allowed.size} reseller{allowed.size === 1 ? '' : 's'} selected{allowed.size === 0 ? ' — product will be hidden from everyone until you pick some' : ''}
          </div>
          <div style={{ maxHeight: '180px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {list.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#94a3b8', padding: '6px' }}>No resellers found.</div>
            ) : list.map(r => (
              <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                <input type="checkbox" checked={allowed.has(Number(r.id))} onChange={() => toggle(r.id)} />
                <span>{r.username}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Marketplace Promotions (admin sale campaigns) helpers ----

const PROMO_TYPE_PRESETS = [
  { type: 'clearance', label: 'Stock Clearance', badge: 'Stock Clearance', color: '#dc2626' },
  { type: 'holiday', label: 'Holiday Sale', badge: 'Holiday Sale', color: '#16a34a' },
  { type: 'weekly', label: 'Weekly Deal', badge: 'Weekly Deal', color: '#2563eb' },
  { type: 'custom', label: 'Custom', badge: '', color: '#f59e0b' }
];

// How long the sale lasts — presets compute endsAt = now + N at save time.
const PROMO_DURATION_PRESETS = [
  { key: '24h', label: '24 hours', hours: 24 },
  { key: '3d', label: '3 days', hours: 72 },
  { key: '7d', label: '7 days', hours: 168 },
  { key: '14d', label: '14 days', hours: 336 },
  { key: '30d', label: '30 days', hours: 720 },
  { key: 'custom', label: 'Custom range', hours: null },
  { key: 'noend', label: 'No end (until turned off)', hours: null }
];

function createPromotionForm(promo = {}) {
  return {
    name: promo.name || '',
    promoType: promo.promoType || 'custom',
    discountPercent: promo.discountPercent !== undefined ? promo.discountPercent : 20,
    productIds: Array.isArray(promo.productIds) ? promo.productIds.map(Number).filter(Number.isFinite) : [],
    badgeText: promo.badgeText || '',
    badgeColor: promo.badgeColor || '#dc2626',
    durationPreset: promo.endsAt || promo.startsAt ? 'custom' : 'noend',
    startsAt: isoToLocalInput(promo.startsAt),
    endsAt: isoToLocalInput(promo.endsAt),
    active: promo.active !== false
  };
}

// ISO string -> value usable in <input type="datetime-local"> (local time).
function isoToLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatPromoTimeRemaining(endsAt) {
  if (!endsAt) return '';
  const diffMs = new Date(endsAt).getTime() - Date.now();
  if (Number.isNaN(diffMs)) return '';
  if (diffMs <= 0) return 'Ended';
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `Ends in ${days}d ${hours}h`;
  if (hours > 0) return `Ends in ${hours}h ${minutes}m`;
  return `Ends in ${minutes}m`;
}

// Map Adobe technical/group names to friendly display names in the panel
function getProductDisplayName(technicalName) {
  if (!technicalName || typeof technicalName !== 'string') return technicalName;
  const map = {
    'CCMTeamDelegates': 'Creative Cloud Pro',
    'CCM Team Delegates': 'Creative Cloud Pro',
  };
  const trimmed = technicalName.trim();
  return map[trimmed] || map[technicalName] || technicalName;
}

function formatDurationFromTimestamp(timestamp) {
  if (!timestamp) return '-';

  const startedAt = new Date(timestamp).getTime();
  if (Number.isNaN(startedAt)) return '-';

  const diffMs = Math.max(0, Date.now() - startedAt);
  const totalMinutes = Math.floor(diffMs / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

const ORGANIZATION_STATUS_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'updating', label: 'Updating' }
];

const CREDIT_DURATION_OPTIONS = [
  { key: '7d', label: '7 days', defaultCredits: 0.4 },
  { key: '14d', label: '14 days', defaultCredits: 0.6 },
  ...Array.from({ length: 12 }, (_, index) => {
    const month = index + 1;
    return { key: month, label: `${month} month${month === 1 ? '' : 's'}`, defaultCredits: month };
  })
];

const DEFAULT_CP_CREDITS = CREDIT_DURATION_OPTIONS.reduce((acc, option) => {
  acc[option.key] = option.defaultCredits;
  return acc;
}, {});

function getOrganizationStatusStyle(status) {
  switch (status) {
    case 'inactive':
      return { background: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' };
    case 'updating':
      return { background: '#fef3c7', color: '#92400e', borderColor: '#fde68a' };
    default:
      return { background: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' };
  }
}

function sortOrganizationsForDisplay(orgs) {
  const nameSet = new Set((orgs || []).map(org => String(org.name || '').trim()).filter(Boolean));

  const getSortInfo = (org) => {
    const name = String(org.name || '').trim();
    const legacyGroupMatch = name.match(/^(.*?)\s*\/\s*Group\s+(\d+)$/i);
    if (legacyGroupMatch) {
      return {
        baseName: legacyGroupMatch[1].trim(),
        groupNumber: Number.parseInt(legacyGroupMatch[2], 10) || 1
      };
    }

    const numberedCopyMatch = name.match(/^(.*\S)\s+(\d+)$/);
    if (numberedCopyMatch && nameSet.has(numberedCopyMatch[1].trim())) {
      return {
        baseName: numberedCopyMatch[1].trim(),
        groupNumber: Number.parseInt(numberedCopyMatch[2], 10) || 1
      };
    }

    return { baseName: name, groupNumber: 1 };
  };

  return [...(orgs || [])].sort((a, b) => {
    const aInfo = getSortInfo(a);
    const bInfo = getSortInfo(b);
    const baseCompare = aInfo.baseName.localeCompare(bInfo.baseName, undefined, { sensitivity: 'base', numeric: true });
    if (baseCompare !== 0) return baseCompare;
    if (aInfo.groupNumber !== bInfo.groupNumber) return aInfo.groupNumber - bInfo.groupNumber;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  });
}

const DEFAULT_ADMIN_MIGRATION_SETTINGS = {
  batchSize: 10,
  parallelBatches: 1,
  baseDelayMs: 6200,
  progressFlushMs: 5000,
  maxRetries: 3
};
const DEFAULT_BULK_SETTINGS = {
  maxConcurrent: 10,
  maxUsersPerOp: 0,
  cpBulkTransferEnabled: false,
  bulkOperationsPaused: false,
  selfSwitchPaused: false,
  creditToGcEnabled: true,
  emailChangeWithoutApi: false
};

function OrgSearchSelect({ organizations, value, onChange, placeholder = 'Type to search organizations...', excludeId = null, disabled = false }) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = organizations.find(o => String(o.id) === String(value));
  const filtered = React.useMemo(() => {
    const list = excludeId != null ? organizations.filter(o => String(o.id) !== String(excludeId)) : organizations;
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(o => o.name.toLowerCase().includes(q) || String(o.id).includes(q));
  }, [organizations, query, excludeId]);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="form-input"
        type="text"
        disabled={disabled}
        value={open ? query : (selected ? `${selected.name} (ID: ${selected.id})` : '')}
        placeholder={placeholder}
        onFocus={() => { setQuery(''); setOpen(true); }}
        onChange={e => setQuery(e.target.value)}
      />
      {open && (
        <div className="org-search-dropdown" style={{ position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0, maxHeight: '260px', overflowY: 'auto', borderRadius: '6px', boxShadow: '0 6px 16px rgba(0,0,0,0.18)', marginTop: '2px' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '8px 10px', fontSize: '13px', opacity: 0.6 }}>No matching organizations</div>
          ) : (
            filtered.slice(0, 200).map(o => (
              <div
                key={o.id}
                className="org-search-item"
                onMouseDown={e => e.preventDefault()}
                onClick={() => { onChange(String(o.id)); setQuery(''); setOpen(false); }}
                style={{ padding: '7px 10px', fontSize: '13px', cursor: 'pointer', fontWeight: String(o.id) === String(value) ? 600 : 400 }}
              >
                {o.name} <span style={{ opacity: 0.6 }}>(ID: {o.id})</span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function LocalOrgReassign({ organizations, onDone, onError }) {
  const [srcId, setSrcId] = React.useState('');
  const [tgtId, setTgtId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [report, setReport] = React.useState(null);

  const srcOrg = organizations.find(o => o.id === parseInt(srcId));
  const tgtOrg = organizations.find(o => o.id === parseInt(tgtId));
  const canPreview = srcId && tgtId && srcId !== tgtId;

  const handlePreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/all-users?limit=1&orgIdFilter=${srcId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setPreview({ count: data.pagination?.totalFiltered || data.users?.length || 0 });
    } catch (_) { setPreview({ count: '?' }); }
    setLoading(false);
  };

  const handleExecute = async () => {
    if (!window.confirm(`Move ALL active users from "${srcOrg?.name}" → "${tgtOrg?.name}"?\n\nThis only updates the local database. No Adobe API calls are made.`)) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/organizations/local-reassign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ sourceOrgId: parseInt(srcId), targetOrgId: parseInt(tgtId) })
      });
      const data = await res.json();
      if (data.success) {
        setReport(data);
        onDone(data.message);
        setSrcId(''); setTgtId(''); setPreview(null);
      } else {
        onError(data.error || 'Reassignment failed');
      }
    } catch (err) { onError('Request failed: ' + err.message); }
    setLoading(false);
  };

  const fmtSnap = (s) => s ? `${(s.tradActive || 0) + (s.cpActive || 0)} active (${(s.tradTotal || 0) + (s.cpTotal || 0)} total)` : '-';

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '12px', alignItems: 'end', marginBottom: '16px' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Source Organization (from)</label>
          <OrgSearchSelect organizations={organizations} value={srcId} onChange={v => { setSrcId(v); setPreview(null); }} placeholder="Select source..." />
        </div>
        <div style={{ textAlign: 'center', paddingBottom: '8px', fontSize: '22px', color: '#0b7da6' }}>→</div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label className="form-label">Target Organization (to)</label>
          <OrgSearchSelect organizations={organizations} value={tgtId} onChange={v => { setTgtId(v); setPreview(null); }} placeholder="Select target..." excludeId={srcId ? parseInt(srcId) : null} />
        </div>
      </div>

      {canPreview && (
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {!preview ? (
            <button onClick={handlePreview} disabled={loading} className="btn btn-secondary">
              {loading ? 'Counting...' : 'Preview'}
            </button>
          ) : (
            <>
              <div style={{ padding: '10px 16px', background: '#f0fafe', borderRadius: '8px', border: '1px solid #bfe7f4', fontSize: '14px', color: '#0b7da6' }}>
                <strong>{preview.count}</strong> user(s) will be moved from <strong>{srcOrg?.name}</strong> → <strong>{tgtOrg?.name}</strong>
              </div>
              <button onClick={handleExecute} disabled={loading} className="btn btn-primary" style={{ background: '#0b7da6', borderColor: '#0b7da6' }}>
                {loading ? 'Moving...' : 'Move Users (Local Only)'}
              </button>
              <button onClick={() => setPreview(null)} className="btn btn-secondary">Cancel</button>
            </>
          )}
        </div>
      )}

      {/* Detailed before/after report — proves exactly what changed */}
      {report && (
        <div style={{ marginTop: '16px', padding: '16px', background: '#faf5ff', border: '1px solid #e9d5ff', borderRadius: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <strong style={{ color: '#0b7da6' }}>Move Report</strong>
            <button onClick={() => setReport(null)} className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '11px' }}>Dismiss</button>
          </div>
          <div style={{ fontSize: '13px', color: '#581c87', marginBottom: '10px' }}>
            <strong>{report.moved?.total ?? 0}</strong> row(s) moved
            (<strong>{report.moved?.traditional ?? 0}</strong> traditional + <strong>{report.moved?.cp ?? 0}</strong> credits-pool)
          </div>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3e8ff' }}>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Organization</th>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Before</th>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>After</th>
                <th style={{ padding: '6px 8px', textAlign: 'left' }}>Change</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '6px 8px' }}><strong>{report.sourceOrg}</strong> (source)</td>
                <td style={{ padding: '6px 8px' }}>{fmtSnap(report.before?.source)}</td>
                <td style={{ padding: '6px 8px' }}>{fmtSnap(report.after?.source)}</td>
                <td style={{ padding: '6px 8px', color: '#dc2626' }}>
                  {(((report.after?.source?.tradActive || 0) + (report.after?.source?.cpActive || 0)) - ((report.before?.source?.tradActive || 0) + (report.before?.source?.cpActive || 0)))}
                </td>
              </tr>
              <tr style={{ background: '#fff' }}>
                <td style={{ padding: '6px 8px' }}><strong>{report.targetOrg}</strong> (target)</td>
                <td style={{ padding: '6px 8px' }}>{fmtSnap(report.before?.target)}</td>
                <td style={{ padding: '6px 8px' }}>{fmtSnap(report.after?.target)}</td>
                <td style={{ padding: '6px 8px', color: '#16a34a' }}>
                  +{(((report.after?.target?.tradActive || 0) + (report.after?.target?.cpActive || 0)) - ((report.before?.target?.tradActive || 0) + (report.before?.target?.cpActive || 0)))}
                </td>
              </tr>
            </tbody>
          </table>
          <div style={{ fontSize: '11px', color: '#0b7da6', marginTop: '8px' }}>
            "Active" = what the org list shows. "Total" = all rows including deleted/removed-from-Adobe.
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  useTheme();

  const [email, setEmail] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const hash = window.location.hash.slice(1);
    const valid = ['dashboard','organizations','resellers','resellerDetails','assign','bulk','customers','cpResellers','cpMonitoring','apiMonitoring','migrationLogs','adminLogs','whitelist','embedDomains','supplierApi','redeem','announcements','feedback','bulkOrg','backupKeys','marketplace','telegramShop','ipMonitor','manualOrg','settings'];
    return valid.includes(hash) ? hash : 'organizations';
  });
  const [, setUptimeTick] = useState(0);

  // States for bulk CSV upload
  const [csvContent, setCsvContent] = useState('');

  // Organization management state
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  // Flat credit cost of a K12 activation (admin-set; default 1).
  const [k12Cost, setK12Cost] = useState(1);
  const [k12CostDraft, setK12CostDraft] = useState('');
  const [newOrg, setNewOrg] = useState({ name: '', clientId: '', clientSecret: '', orgId: '', apiKey: '', totalLicenses: '', availableAsTarget: true, autoDisableUserLimit: '' });
  const [testingOrgId, setTestingOrgId] = useState(null);
  const [refreshingOrgId, setRefreshingOrgId] = useState(null);
  const [autoCheckingOrgId, setAutoCheckingOrgId] = useState(null);
  const [updatingOrgStatusId, setUpdatingOrgStatusId] = useState(null);
  // Self-switch targets list per row — plain useState toggle (not a native <details>)
  // so collapsed rows render nothing into the DOM. A <details> element gets force-opened
  // by the browser's own Ctrl+F find-in-page whenever a match is found inside it, which
  // flooded the page with every other org's target list whenever one org name appeared
  // as a candidate in many rows (very common, since a handful of healthy orgs end up
  // eligible everywhere).
  const [expandedSelfSwitchOrgIds, setExpandedSelfSwitchOrgIds] = useState(() => new Set());
  const [editingOrg, setEditingOrg] = useState(null);
  const [editOrgForm, setEditOrgForm] = useState({ clientId: '', clientSecret: '', orgId: '', apiKey: '', skipRemoveFromOld: false, forceReassign: false });
  const [orgUpdateProgress, setOrgUpdateProgress] = useState(null); // { status: 'validating'|'migrating', elapsed: 0, message: '' }
  const [migrationResult, setMigrationResult] = useState(null);
  const [renamingOrg, setRenamingOrg] = useState(null); // { id, currentName }
  const [newOrgName, setNewOrgName] = useState('');
  const [orgApiSyncSelection, setOrgApiSyncSelection] = useState(new Set()); // IDs selected for bulk API sync action
  const [orgUserLimitDrafts, setOrgUserLimitDrafts] = useState({});
  const [bulkApiSyncLoading, setBulkApiSyncLoading] = useState(false);
  // Organizations table: filters + pagination (the list can run into the hundreds).
  const [orgFilters, setOrgFilters] = useState({ search: '', status: 'all', apiSync: 'all', target: 'all', tier: 'all' });
  const [orgPage, setOrgPage] = useState(1);
  // Orgs whose full product list is expanded (collapsed to 2 by default so a
  // team with many products doesn't blow up the row height).
  const [expandedProductOrgIds, setExpandedProductOrgIds] = useState(() => new Set());

  // Console Migration Product Mapping
  const [showConsoleMigrationMapping, setShowConsoleMigrationMapping] = useState(false);
  const [consoleMigrationData, setConsoleMigrationData] = useState(null); // { oldProducts, newProducts, orgId, orgName }
  const [consoleMigrationMapping, setConsoleMigrationMapping] = useState({}); // { "Old Product": "New Product" }

  // Reseller management state
  const [resellers, setResellers] = useState([]);
  const [loadingResellers, setLoadingResellers] = useState(false);
  const [newReseller, setNewReseller] = useState({ username: '', password: '', userGroup: '', maxLicenses: '', workingDays: '', organizationAccess: [] });
  const [editingReseller, setEditingReseller] = useState(null);
  const [editForm, setEditForm] = useState({ password: '', maxLicenses: '', workingDays: '', organizationAccess: [] });
  const [editOrgModalOpen, setEditOrgModalOpen] = useState(false);

  // Reseller details view state
  const [selectedReseller, setSelectedReseller] = useState(null);
  const [resellerUsers, setResellerUsers] = useState([]);
  const [loadingResellerUsers, setLoadingResellerUsers] = useState(false);
  const [licenseBreakdown, setLicenseBreakdown] = useState({ monthly: 0, quarterly: 0, yearly: 0 });
  const [resellerActivityLogs, setResellerActivityLogs] = useState([]);
  const [loadingActivityLogs, setLoadingActivityLogs] = useState(false);

  // Credits Pool Resellers state
  const [cpResellers, setCpResellers] = useState([]);
  const [orphanedCPResellers, setOrphanedCPResellers] = useState([]);
  const [loadingCPResellers, setLoadingCPResellers] = useState(false);
  const [cpResellerSearchQuery, setCpResellerSearchQuery] = useState('');
  const [newCPReseller, setNewCPReseller] = useState({ username: '', password: '', userGroup: '', initialCredits: '', initialGoldCoins: '', poolValidityMonths: '12', organizationAccess: [], creditsPerMonth: { ...DEFAULT_CP_CREDITS }, hiddenMonths: [], apiAccessEnabled: false, allowBackupKeys: false });
  const [editingCPReseller, setEditingCPReseller] = useState(null);
  const [addCreditsForm, setAddCreditsForm] = useState({ credits: '', reason: '' });
  const [editingCPGoldCoins, setEditingCPGoldCoins] = useState(null);
  const [addGoldCoinsForm, setAddGoldCoinsForm] = useState({ goldCoins: '', reason: '' });
  const [editingCPOrgAccess, setEditingCPOrgAccess] = useState(null); // CP reseller ID being edited
  const [editCPOrgForm, setEditCPOrgForm] = useState({ organizationAccess: [] });
  const [editCPSettingsId, setEditCPSettingsId] = useState(null); // For password/expiry edit modal
  const [editCPSettingsForm, setEditCPSettingsForm] = useState({ password: '', extendPoolMonths: '', creditsPerMonth: { ...DEFAULT_CP_CREDITS }, hiddenMonths: [], apiAccessEnabled: false, apiDefaultOrganizationId: null });
  // Bulk pricing: apply one credits-per-duration config to ALL CP resellers at once.
  const [showBulkPricing, setShowBulkPricing] = useState(false);
  const [bulkPricingForm, setBulkPricingForm] = useState({ creditsPerMonth: { ...DEFAULT_CP_CREDITS }, hiddenMonths: [], applyHidden: true });
  // Pro-rata refund settings (global cutoff date + old-price table + enable).
  const [showRefundSettings, setShowRefundSettings] = useState(false);
  // Version of the server answering this page (not the bundle's build-time value).
  const appVersion = useAppVersion();
  const [refundSettings, setRefundSettings] = useState({ enabled: false, cutoffDate: '', oldPrices: {}, graceHours: 2, graceOverrides: {}, selfRedeemThreshold: 0 });
  // Reseller-raised redemption requests awaiting admin approval (above the
  // self-redeem threshold). Loaded on the dashboard; surfaced with a badge.
  const [redemptionRequests, setRedemptionRequests] = useState([]);
  const [showRedemptionModal, setShowRedemptionModal] = useState(false);
  // The live self-redeem limit (from the requests endpoint, so the modal shows
  // the real value without opening Refund Settings), plus its edit buffer.
  const [redeemThreshold, setRedeemThreshold] = useState(0);
  const [redeemThresholdInput, setRedeemThresholdInput] = useState('0');
  // Overrides present when the modal was opened. Needed on save to tell the
  // server which ones were REMOVED — it merges patches, so a dropped key would
  // otherwise survive untouched.
  const [refundLoadedOverrideIds, setRefundLoadedOverrideIds] = useState([]);
  const [refundOverrideDraft, setRefundOverrideDraft] = useState({ resellerId: '', hours: '0' });

  // Product Mapping for Migration
  const [showProductMappingModal, setShowProductMappingModal] = useState(false);
  const [migrationData, setMigrationData] = useState(null); // { removedOrgIds, newOrgId, oldProducts, newProducts }
  const [productMapping, setProductMapping] = useState({}); // { "Old Product": "New Product" }

  // API Monitoring state (for admin)
  const [apiMonitoringData, setApiMonitoringData] = useState({ stats: {}, keys: [] });
  const [loadingApiMonitoring, setLoadingApiMonitoring] = useState(false);

  // Credits Pool Monitoring state (for admin)
  const [cpMonitoringResellers, setCpMonitoringResellers] = useState([]);
  const [loadingCPMonitoring, setLoadingCPMonitoring] = useState(false);
  const [cpMonitoringSearchQuery, setCpMonitoringSearchQuery] = useState('');
  const [selectedCPResellerForMonitor, setSelectedCPResellerForMonitor] = useState(null);
  const [cpResellerTransactions, setCpResellerTransactions] = useState([]);
  const [cpResellerUsers, setCpResellerUsers] = useState([]);
  const [loadingCPResellerDetails, setLoadingCPResellerDetails] = useState(false);
  const [cpDetailSearch, setCpDetailSearch] = useState('');
  const [cpTransactionPage, setCpTransactionPage] = useState(1);
  const [cpDetailFullscreen, setCpDetailFullscreen] = useState(false);
  // Bulk rows whose email list is open, keyed by transaction id.
  const [cpExpandedTx, setCpExpandedTx] = useState(() => new Set());

  // Undo a reseller's bulk delete: preview → confirm → run in the background.
  // Clicking again while it runs shows progress.
  const restoreBulkDelete = async (opId) => {
    const headers = { Authorization: `Bearer ${localStorage.getItem('adminToken')}`, 'Content-Type': 'application/json' };
    try {
      const res = await fetch(`${API_URL}/admin/credits-pool/bulk-delete/${opId}/restore`, { headers });
      const p = await res.json();
      if (!res.ok) { alert(p.error || 'Could not load the restore preview'); return; }
      if (p.job && p.job.status === 'running') {
        alert(`Restore of bulk delete #${opId} is running: ${p.job.restored} restored, ${p.job.failed} failed of ${p.job.total}.`);
        return;
      }
      const last = p.job && p.job.status !== 'running'
        ? `\n\nLast restore: ${p.job.status} — ${p.job.restored} restored, ${p.job.failed} failed.` : '';
      if (!p.toRestore) { alert(`Nothing left to restore for bulk delete #${opId}.${last}`); return; }
      const teams = Object.entries(p.byTeam || {}).map(([n, c]) => `  • ${n}: ${c}`).join('\n');
      const skipped = [p.skipped.expired && `${p.skipped.expired} expired`, p.skipped.alreadyActive && `${p.skipped.alreadyActive} already re-added`, p.skipped.teamGone && `${p.skipped.teamGone} team removed`].filter(Boolean).join(', ');
      const takeBack = [p.takeBack.pendingCredits > 0 && `${p.takeBack.pendingCredits} pending refund credits (pending now: ${p.takeBack.pendingBalanceNow})`, p.takeBack.credits > 0 && `${p.takeBack.credits} credits from the balance`, p.takeBack.refundDays > 0 && `${p.takeBack.refundDays} refund days`].filter(Boolean).join(', ') || 'nothing';
      const ok = window.confirm(
        `Restore bulk delete #${opId} for ${p.reseller} (#${p.resellerId})?\n\n` +
        `Re-add ${p.toRestore} of ${p.deletedCount} deleted users to Adobe, keeping their original expiry dates:\n${teams}\n` +
        (skipped ? `\nSkipped: ${skipped}.` : '') +
        `\nTake back what the delete paid for them: ${takeBack}.\n\nIt runs in the background (Adobe allows ~10 requests a minute per team). Continue?${last}`);
      if (!ok) return;
      const start = await fetch(`${API_URL}/admin/credits-pool/bulk-delete/${opId}/restore`, { method: 'POST', headers });
      const s = await start.json();
      if (!start.ok) { alert(s.error || 'Could not start the restore'); return; }
      alert(`Restore started: ${s.total} users. Press Restore on the same row again to see progress; the reseller sees it under Operations.`);
    } catch (e) {
      alert(`Restore failed: ${e.message}`);
    }
  };
  const [marketplaceDurations, setMarketplaceDurations] = useState(
    BASE_MARKETPLACE_MONTHS.map(value => ({ value, builtIn: true })));
  const CP_TRANSACTIONS_PER_PAGE = 50;
  // Classification lives in transactionTypes.js so this page and the reseller's
  // own dashboard cannot disagree about the same row - which they did.
  const cpTransactionLabel = transactionTypeLabel;

  const getCPTransactionBadgeClass = (tx) => transactionBadgeClass(tx.type);

  const getCPTransactionAmountDisplay = (tx) => {
    const isPositive = isPositiveTransaction(tx.type);
    const refundDays = Math.max(0, parseInt(tx.refundDays || 0, 10) || 0);
    if (refundDays > 0) {
      return {
        text: `${isPositive ? '+' : '-'}${refundDays} day(s)`,
        color: isPositive ? '#059669' : '#dc2626'
      };
    }

    const rawCredits = Number(tx.credits || 0);
    const credits = Math.abs(Number.isFinite(rawCredits) ? rawCredits : 0);
    if (credits === 0) {
      return { text: '0', color: '#64748b' };
    }

    return {
      text: `${isPositive ? '+' : '-'}${Number(credits.toFixed(4))}`,
      color: isPositive ? '#059669' : '#dc2626'
    };
  };

  // Add user to reseller form
  const [addUserToResellerForm, setAddUserToResellerForm] = useState({ email: '', durationMonths: 1, organizationId: '', products: [] });
  const [addingUserToReseller, setAddingUserToReseller] = useState(false);

  // All customers state (admin view of all reseller-assigned users)
  const [allCustomers, setAllCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customersSummary, setCustomersSummary] = useState({ total: 0, active: 0, expiring: 0, expired: 0 });
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editCustomerForm, setEditCustomerForm] = useState({ products: [], durationMonths: 1 });
  const [deleteOverrideCandidate, setDeleteOverrideCandidate] = useState(null);
  // Advanced filters for customers
  const [customerOrgFilter, setCustomerOrgFilter] = useState('');
  const [customerDurationFilter, setCustomerDurationFilter] = useState('');
  const [customerDaysFilter, setCustomerDaysFilter] = useState('');
  // Pagination state for All Customers
  const [customersPage, setCustomersPage] = useState(1);
  const [customersPagination, setCustomersPagination] = useState({ page: 1, limit: 50, totalPages: 1, totalFiltered: 0, hasMore: false });
  const [customerStatusFilter, setCustomerStatusFilter] = useState('');
  // Bulk delete by email
  const [bulkDeleteEmails, setBulkDeleteEmails] = useState('');
  // Bulk extend by email
  const [bulkExtendEmails, setBulkExtendEmails] = useState('');
  const [bulkExtendMonths, setBulkExtendMonths] = useState(1);

  // Migration logs state
  const [migrationLogs, setMigrationLogs] = useState({ active: [], history: [] });
  const [loadingMigrationLogs, setLoadingMigrationLogs] = useState(false);

  // Pending-resume prompt state (migrations interrupted by server restart)
  const [pendingResumeMigrations, setPendingResumeMigrations] = useState([]);
  const [showPendingResumeModal, setShowPendingResumeModal] = useState(false);

  // Admin Logs state
  const [adminLogs, setAdminLogs] = useState([]);
  const [loadingAdminLogs, setLoadingAdminLogs] = useState(false);
  const [adminLogsPagination, setAdminLogsPagination] = useState({ page: 1, limit: 20, totalPages: 1, totalFiltered: 0, hasMore: false });
  const [adminLogsPage, setAdminLogsPage] = useState(1);

  // Admin user assignment state
  const [selectedOrgForAssign, setSelectedOrgForAssign] = useState(null);
  const [selectedProductsForAssign, setSelectedProductsForAssign] = useState([]);
  const [selectedResellerForAssign, setSelectedResellerForAssign] = useState(null);
  const [assignDurationMonths, setAssignDurationMonths] = useState(1);
  const [assignCustomExpiry, setAssignCustomExpiry] = useState(''); // optional YYYY-MM-DD custom expiry for new users
  // Org Note Editing
  const [editingNoteOrg, setEditingNoteOrg] = useState(null);
  const [noteInput, setNoteInput] = useState('');
  const [smartCopyOrg, setSmartCopyOrg] = useState(null);
  const [smartCopyLimit, setSmartCopyLimit] = useState(5000);
  const [smartCopyingOrgId, setSmartCopyingOrgId] = useState(null);

  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '', type: 'info', target: 'all', specificResellerIds: [] });

  // Reseller Feedback state
  const [feedbackList, setFeedbackList] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [feedbackNewCount, setFeedbackNewCount] = useState(0);
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState('all');
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState('all');
  const [feedbackReplyDrafts, setFeedbackReplyDrafts] = useState({});
  const [submittingReplyId, setSubmittingReplyId] = useState(null);

  // Email Whitelist state
  const [emailWhitelist, setEmailWhitelist] = useState({ enabled: false, mode: 'global', domains: [], organizationRules: [] });
  const [loadingWhitelist, setLoadingWhitelist] = useState(false);
  const [whitelistInput, setWhitelistInput] = useState('');

  // Supplier API (connection test only — no product sync yet)
  const [supplierSettings, setSupplierSettings] = useState({ configured: false, source: 'none', maskedKey: '', baseUrl: '' });
  const [loadingSupplierSettings, setLoadingSupplierSettings] = useState(false);
  const [supplierApiKeyInput, setSupplierApiKeyInput] = useState('');
  const [supplierBaseUrlInput, setSupplierBaseUrlInput] = useState('');
  const [savingSupplierKey, setSavingSupplierKey] = useState(false);
  const [testingSupplierConnection, setTestingSupplierConnection] = useState(false);
  const [supplierTestResult, setSupplierTestResult] = useState(null); // { success, balance, currency, error }
  const [supplierMarkup, setSupplierMarkup] = useState('0');
  const [supplierProductsPreview, setSupplierProductsPreview] = useState(null);
  const [loadingSupplierProducts, setLoadingSupplierProducts] = useState(false);
  const [importingSupplierProducts, setImportingSupplierProducts] = useState(false);

  // Embed Allowed Domains state (CSP frame-ancestors allowlist)
  const [embedDomains, setEmbedDomains] = useState([]);
  const [loadingEmbedDomains, setLoadingEmbedDomains] = useState(false);
  const [embedDomainInput, setEmbedDomainInput] = useState('');
  const [embedDomainNote, setEmbedDomainNote] = useState('');
  const [addingEmbedDomain, setAddingEmbedDomain] = useState(false);

  // Reseller Announcement View (Admin)
  const [viewingResellerAnnouncements, setViewingResellerAnnouncements] = useState(null); // { resellerId, resellerName, announcements }
  const [loadingResellerAnnouncements, setLoadingResellerAnnouncements] = useState(false);

  // Reseller User Migration state
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [migrateSourceType, setMigrateSourceType] = useState('traditional'); // 'traditional' or 'credits-pool'
  const [migrateTargetType, setMigrateTargetType] = useState('traditional');
  const [migrateSourceId, setMigrateSourceId] = useState('');
  const [migrateTargetId, setMigrateTargetId] = useState('');
  const [migratePreview, setMigratePreview] = useState(null);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);
  const [migrateSelectedEmails, setMigrateSelectedEmails] = useState([]);
  const [migrateSelectAll, setMigrateSelectAll] = useState(true);
  const [migrateMode, setMigrateMode] = useState('all'); // 'all' or 'specific'
  const [migrateEmailInput, setMigrateEmailInput] = useState(''); // textarea for pasting emails

  // Bulk Organization Management state
  const [bulkOrgSelectedOrg, setBulkOrgSelectedOrg] = useState('');
  const [bulkOrgAction, setBulkOrgAction] = useState('add');
  const [bulkOrgTargetTypes, setBulkOrgTargetTypes] = useState({ traditional: true, cp: true });
  const [bulkOrgSelectedProducts, setBulkOrgSelectedProducts] = useState([]);
  const [bulkOrgPreview, setBulkOrgPreview] = useState(null);
  const [bulkOrgLoadingPreview, setBulkOrgLoadingPreview] = useState(false);
  const [bulkOrgSelectedResellers, setBulkOrgSelectedResellers] = useState(new Set());
  const [bulkOrgExecuting, setBulkOrgExecuting] = useState(false);
  const [orgTransferSourceOrg, setOrgTransferSourceOrg] = useState('');
  const [orgTransferTargetOrg, setOrgTransferTargetOrg] = useState('');
  const [orgTransferSelectionMode, setOrgTransferSelectionMode] = useState('all');
  const [orgTransferStartNumber, setOrgTransferStartNumber] = useState(1);
  const [orgTransferCount, setOrgTransferCount] = useState(10);
  const [orgTransferKeepSource, setOrgTransferKeepSource] = useState(false);
  const [orgTransferTargetProducts, setOrgTransferTargetProducts] = useState([]);
  const [orgTransferPreview, setOrgTransferPreview] = useState(null);
  const [orgTransferLoadingPreview, setOrgTransferLoadingPreview] = useState(false);
  const [orgTransferExecuting, setOrgTransferExecuting] = useState(false);
  const [orgTransferOperation, setOrgTransferOperation] = useState(null);
  const [orgTransferStatus, setOrgTransferStatus] = useState(null);
  const [orgTransferRefreshingStatus, setOrgTransferRefreshingStatus] = useState(false);

  // Backup Keys Management state
  const [backupKeysData, setBackupKeysData] = useState(null);
  const [loadingBackupKeys, setLoadingBackupKeys] = useState(false);
  const [backupKeyImportText, setBackupKeyImportText] = useState('');
  const [backupKeyImportDuration, setBackupKeyImportDuration] = useState(1);

  // Marketplace Management state
  const [marketplaceData, setMarketplaceData] = useState(null);
  const [loadingMarketplace, setLoadingMarketplace] = useState(false);
  const [newProduct, setNewProduct] = useState(createMarketplaceProductForm());
  const [editingProductId, setEditingProductId] = useState(null);
  const [editProductForm, setEditProductForm] = useState(createMarketplaceProductForm());
  const [importCodesForProductId, setImportCodesForProductId] = useState(null);
  const [importCodesText, setImportCodesText] = useState('');
  const [importCodesDuration, setImportCodesDuration] = useState(1);
  // Off by default: keeping duplicates is right for placeholders and wrong for
  // real keys, so it is a decision taken per import, never a remembered setting.
  const [importAllowDuplicates, setImportAllowDuplicates] = useState(false);
  const [marketplaceOrders, setMarketplaceOrders] = useState([]);
  const [showMarketplaceOrders, setShowMarketplaceOrders] = useState(false);
  const [marketplaceOrdersPage, setMarketplaceOrdersPage] = useState(1);
  const [marketplaceOrdersTotalPages, setMarketplaceOrdersTotalPages] = useState(1);
  const [marketplaceOrdersTotal, setMarketplaceOrdersTotal] = useState(0);
  const [marketplaceOrdersSearch, setMarketplaceOrdersSearch] = useState('');
  // 'all' | 'active' | 'refunded'. Refunded orders are kept in the list now, so
  // this only chooses which slice is shown, never what exists.
  const [marketplaceOrdersStatus, setMarketplaceOrdersStatus] = useState('all');
  const [marketplaceOrdersCounts, setMarketplaceOrdersCounts] = useState({ all: 0, active: 0, refunded: 0 });
  const [loadingMarketplaceOrders, setLoadingMarketplaceOrders] = useState(false);
  const [refundingOrderId, setRefundingOrderId] = useState(null);
  // Manual-fulfilment shipping: the order being shipped and the details to send.
  const [shippingOrder, setShippingOrder] = useState(null);
  const [shipInfo, setShipInfo] = useState('');
  const [shipBusy, setShipBusy] = useState(false);
  // Sub-tabs within the Marketplace admin tab — keeps Promotions/Banners
  // reachable without scrolling past however many products are listed.
  const [marketplaceSubTab, setMarketplaceSubTab] = useState('products');
  // The products list can run to hundreds of items, so it is searched,
  // filtered, sorted and paged in the browser (they are all already loaded).
  const [productSearch, setProductSearch] = useState('');
  const [productStatusFilter, setProductStatusFilter] = useState('all'); // all|active|disabled|out|restricted
  const [productCategoryFilter, setProductCategoryFilter] = useState('all');
  const [productSort, setProductSort] = useState('default'); // default|newest|name|sold|stock|lowstock
  const [productsPage, setProductsPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(20);
  // The "Codes" button opens an inline viewer of the codes in stock for one
  // product (was a blind delete-all).
  const [viewCodesProductId, setViewCodesProductId] = useState(null);
  // Supplier stock: own Marketplace product (per duration) restocked from a linked supplier product.
  const [supplierSourceFor, setSupplierSourceFor] = useState(null);
  const [marketplaceLinks, setMarketplaceLinks] = useState([]);
  const loadMarketplaceLinks = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/admin/marketplace/supplier-links`, { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
      const d = await res.json();
      if (res.ok) setMarketplaceLinks(d.links || []);
    } catch (e) { /* the badges are only a hint */ }
  }, []);
  useEffect(() => { if (activeTab === 'marketplace') loadMarketplaceLinks(); }, [activeTab, loadMarketplaceLinks]);
  const [viewCodesData, setViewCodesData] = useState(null);
  const [viewCodesLoading, setViewCodesLoading] = useState(false);
  // Sub-tabs within the CP Resellers admin tab — keeps "Create New Reseller"
  // reachable without scrolling past the full resellers table.
  const [cpResellersSubTab, setCpResellersSubTab] = useState('list');
  // New-order notification state
  const [newOrderNotice, setNewOrderNotice] = useState(null); // { count, manual, orders: [...] }

  // Marketplace Promotions state
  const [marketplacePromotions, setMarketplacePromotions] = useState([]);
  const [loadingPromotions, setLoadingPromotions] = useState(false);
  const [promoForm, setPromoForm] = useState(createPromotionForm());
  const [editingPromoId, setEditingPromoId] = useState(null);
  const [showPromoForm, setShowPromoForm] = useState(false);

  // CP Dashboard config (modern dashboard + banners) state
  const [cpDashboardConfig, setCpDashboardConfig] = useState(null);
  const [bannerForm, setBannerForm] = useState({ file: null, title: '', subtitle: '', linkTab: '', linkProductId: '' });
  const [uploadingBanner, setUploadingBanner] = useState(false);

  // Migration settings state
  const [migrationSettings, setMigrationSettings] = useState(DEFAULT_ADMIN_MIGRATION_SETTINGS);
  const [migrationSettingsDefaults, setMigrationSettingsDefaults] = useState(DEFAULT_ADMIN_MIGRATION_SETTINGS);
  const [migrationPresets, setMigrationPresets] = useState([]);
  const [loadingMigrationSettings, setLoadingMigrationSettings] = useState(false);
  const [savingMigrationSettings, setSavingMigrationSettings] = useState(false);
  const [activationSettings, setActivationSettings] = useState({ pausedMonths: [], monthOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] });
  const [loadingActivationSettings, setLoadingActivationSettings] = useState(false);
  const [savingActivationSettings, setSavingActivationSettings] = useState(false);
  const [bulkSettings, setBulkSettings] = useState(DEFAULT_BULK_SETTINGS);
  const [bulkRunning, setBulkRunning] = useState(0);
  const [loadingBulkSettings, setLoadingBulkSettings] = useState(false);
  const [savingBulkSettings, setSavingBulkSettings] = useState(false);
  // True once the REAL settings have come back from the server. Until then the
  // form shows defaults, and saving would overwrite the real values with them.
  const [bulkSettingsLoaded, setBulkSettingsLoaded] = useState(false);
  const [bulkJustSaved, setBulkJustSaved] = useState(false);

  // Debounce timer ref for instant search
  const customerSearchDebounceRef = useRef(null);

  // Track if initial mount has completed for auto-search
  const isFirstRenderRef = useRef(true);

  // Detect mode from URL path immediately (synchronous)
  const currentPath = window.location.pathname;

  // CRITICAL: Force credits-pool detection FIRST
  const isCreditsPoolPath = currentPath.toLowerCase().includes('/credits-pool');
  const isResellerPath = currentPath.toLowerCase().includes('/reseller');
  const isAdminPath = currentPath.includes('/adminsecure') || currentPath.includes(ADMIN_PATH);
  const isStatusPath = currentPath.toLowerCase().includes('/status');

  // Debug logging (remove in production)
  console.log('Path detection:', { currentPath, isCreditsPoolPath, isResellerPath, isAdminPath, isStatusPath });

  // Determine initial modes - CREDITS POOL HAS HIGHEST PRIORITY, then STATUS (public)
  const initialIsCreditsPoolMode = isCreditsPoolPath;
  const initialIsStatusMode = !isCreditsPoolPath && isStatusPath;
  const initialIsResellerMode = !isCreditsPoolPath && !isStatusPath && isResellerPath;
  const initialIsAdminMode = !isCreditsPoolPath && !isStatusPath && !isResellerPath && isAdminPath;

  // Mode state (initialized from URL)
  const [isResellerMode, setIsResellerMode] = useState(initialIsResellerMode);
  const [isAdminMode, setIsAdminMode] = useState(initialIsAdminMode);
  const [isCreditsPoolMode, setIsCreditsPoolMode] = useState(initialIsCreditsPoolMode);
  const [resellerAuth, setResellerAuth] = useState({ isLoggedIn: false, reseller: null, token: null });
  const [cpAuth, setCpAuth] = useState({ isLoggedIn: false, reseller: null, token: null });

  // Admin authentication state
  const [adminAuth, setAdminAuth] = useState({ isLoggedIn: false, admin: null, token: null });
  // CRITICAL: Only check admin auth if actually in admin mode (not status or other public pages)
  const [checkingAdminAuth, setCheckingAdminAuth] = useState(initialIsAdminMode && !isStatusPath);

  // Manual organizations (no Adobe API): master switch + queue counts for the nav badge.
  const [manualOrgInfo, setManualOrgInfo] = useState({ loaded: false, enabled: false, pending: 0, toRemove: 0 });
  const [savingManualOrg, setSavingManualOrg] = useState(false);
  const fetchManualOrgCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/manual-org/counts`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const d = await res.json();
      setManualOrgInfo({ loaded: true, enabled: d.enabled === true, pending: d.pending || 0, toRemove: d.toRemove || 0 });
    } catch (e) { /* badge only; retried on the next tick */ }
  }, []);
  const onManualOrgCounts = useCallback((c) => setManualOrgInfo(prev => ({ ...prev, ...c })), []);
  const saveManualOrgEnabled = async (enabled) => {
    if (!window.confirm(enabled
      ? 'Turn ON manual organizations?\n\nOrganizations marked as manual will accept new customers without the Adobe API, as pending activations for your team.'
      : 'Turn OFF manual organizations?\n\nManual organizations will refuse new customers. Anything already in the queue stays there.')) return;
    setSavingManualOrg(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/manual-org/settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ enabled })
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) setError(d.error || 'Could not save the manual organizations switch.');
      else { setResult(`Manual organizations turned ${d.enabled ? 'ON' : 'OFF'}.`); setManualOrgInfo(prev => ({ ...prev, enabled: d.enabled === true })); }
    } catch (e) { setError('Could not save the manual organizations switch.'); }
    setSavingManualOrg(false);
  };
  useEffect(() => {
    if (!adminAuth.isLoggedIn) return undefined;
    fetchManualOrgCounts();
    const t = setInterval(fetchManualOrgCounts, 60000);
    return () => clearInterval(t);
  }, [adminAuth.isLoggedIn, fetchManualOrgCounts]);

  const filteredCPResellers = useMemo(
    () => filterCPResellersByQuery(cpResellers, cpResellerSearchQuery),
    [cpResellers, cpResellerSearchQuery]
  );
  const filteredCPMonitoringResellers = useMemo(
    () => filterCPResellersByQuery(cpMonitoringResellers, cpMonitoringSearchQuery),
    [cpMonitoringResellers, cpMonitoringSearchQuery]
  );

  useEffect(() => {
    const path = window.location.pathname;

    // IMPORTANT: Check credits-pool FIRST (before admin check)
    if (path.includes('/credits-pool')) {
      // Credits Pool Reseller mode
      setIsCreditsPoolMode(true);
      setIsResellerMode(false);
      setIsAdminMode(false);
      const token = localStorage.getItem('cpResellerToken');
      const info = localStorage.getItem('cpResellerInfo');
      if (token && info) {
        setCpAuth({ isLoggedIn: true, reseller: JSON.parse(info), token });
      }
      setCheckingAdminAuth(false);
    } else if (path.includes(ADMIN_PATH) || path.includes('/adminsecure')) {
      // Admin mode
      setIsAdminMode(true);
      setIsResellerMode(false);
      setIsCreditsPoolMode(false);
      const token = localStorage.getItem('adminToken');
      if (token) {
        const _verifyAc = new AbortController();
        const _verifyTimeout = setTimeout(() => _verifyAc.abort(), 5000);
        fetch(`${API_URL}/admin/verify`, {
          headers: { 'Authorization': `Bearer ${token}` },
          signal: _verifyAc.signal
        })
          .then(res => res.json())
          .then(data => {
            clearTimeout(_verifyTimeout);
            if (data.success) {
              const info = localStorage.getItem('adminInfo');
              setAdminAuth({ isLoggedIn: true, admin: info ? JSON.parse(info) : data.admin, token });
            } else {
              localStorage.removeItem('adminToken');
              localStorage.removeItem('adminInfo');
            }
            setCheckingAdminAuth(false);
          })
          .catch(() => {
            clearTimeout(_verifyTimeout);
            setCheckingAdminAuth(false);
          });
      } else {
        setCheckingAdminAuth(false);
      }
    } else if (path.includes('/reseller')) {
      // Reseller mode
      setIsResellerMode(true);
      setIsAdminMode(false);
      setIsCreditsPoolMode(false);
      const token = localStorage.getItem('resellerToken');
      const info = localStorage.getItem('resellerInfo');
      if (token && info) {
        setResellerAuth({ isLoggedIn: true, reseller: JSON.parse(info), token });
      }
      setCheckingAdminAuth(false);
    } else if (path.includes('/status')) {
      // User Status Portal - public access, no redirect needed
      setIsResellerMode(false);
      setIsAdminMode(false);
      setIsCreditsPoolMode(false);
      setCheckingAdminAuth(false);
    } else if (path.includes('/bulk-status')) {
      // Bulk User Status Portal - public access, no redirect needed
      setIsResellerMode(false);
      setIsAdminMode(false);
      setIsCreditsPoolMode(false);
      setCheckingAdminAuth(false);
    } else if (path.includes('/redeem-key')) {
      // Key redemption page - public access, no redirect needed
      setIsResellerMode(false);
      setIsAdminMode(false);
      setIsCreditsPoolMode(false);
      setCheckingAdminAuth(false);
    } else {
      // Root path - redirect to /reseller
      window.location.href = '/reseller';
    }
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setUptimeTick((tick) => tick + 1);
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  // Keep the sidebar refund-request badge live from any tab while signed in.
  useEffect(() => {
    if (!adminAuth.isLoggedIn) return;
    fetchRedemptionRequests();
    const t = setInterval(fetchRedemptionRequests, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminAuth.isLoggedIn]);

  // Filter customers based on all filter criteria
  useEffect(() => {
    let filtered = allCustomers;

    // Text search filter
    // Same cleanup as the server: pasted emails often carry a trailing space or
    // an invisible character, which made a correct server result vanish here.
    const query = customerSearchQuery.replace(/[​-‏⁠﻿]/g, '').trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(user =>
        user.email?.toLowerCase().includes(query) ||
        (user.resellerName && user.resellerName.toLowerCase().includes(query)) ||
        (user.resellerGroup && user.resellerGroup.toLowerCase().includes(query)) ||
        (user.organizationName && user.organizationName.toLowerCase().includes(query))
      );
    }

    // Organization filter
    if (customerOrgFilter) {
      filtered = filtered.filter(user => user.organizationName === customerOrgFilter);
    }

    // Duration filter
    if (customerDurationFilter) {
      const duration = parseInt(customerDurationFilter);
      filtered = filtered.filter(user => (user.durationMonths || 1) === duration);
    }

    // Days remaining filter
    if (customerDaysFilter !== '') {
      const days = parseInt(customerDaysFilter);
      if (!isNaN(days)) {
        filtered = filtered.filter(user => {
          if (user.daysLeft === null || user.daysLeft === undefined) return false;
          return user.daysLeft <= days;
        });
      }
    }

    setFilteredCustomers(filtered);
  }, [allCustomers, customerSearchQuery, customerOrgFilter, customerDurationFilter, customerDaysFilter]);

  // Auto-search: Fetch from backend when search/filters change (debounced)
  useEffect(() => {
    // Skip on very first mount to allow initial data load
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    // Only run when logged in as admin and on customers tab
    if (!adminAuth.isLoggedIn || activeTab !== 'customers') return;

    // Debounce the API call - wait 500ms after user stops typing
    const timeoutId = setTimeout(() => {
      fetchAllCustomers(1);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerSearchQuery, customerOrgFilter, customerDurationFilter, customerStatusFilter, customerDaysFilter]);

  // Keep URL hash in sync with active admin tab so refresh restores position
  useEffect(() => {
    if (adminAuth.isLoggedIn) {
      window.history.replaceState(null, '', window.location.pathname + '#' + activeTab);
    }
  }, [activeTab, adminAuth.isLoggedIn]);

  // Respond to manual hash edits too. Typing a new #tab into an already-open
  // tab fires hashchange, NOT a page load — without this listener the initial
  // hash read never re-runs, the edit silently does nothing, and the sync
  // effect above writes the old tab straight back into the URL.
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash.slice(1);
      const valid = ['dashboard','organizations','resellers','resellerDetails','assign','bulk','customers','cpResellers','cpMonitoring','apiMonitoring','migrationLogs','adminLogs','whitelist','embedDomains','supplierApi','redeem','announcements','feedback','bulkOrg','backupKeys','marketplace','telegramShop','ipMonitor','manualOrg','settings'];
      if (valid.includes(hash)) setActiveTab(hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Marketplace data used to be fetched only by the nav item's click handler,
  // so arriving any other way — a #marketplace deep link, a page reload (the
  // hash is restored above), or a manual hash edit — left the tab sitting on
  // "Click Refresh to load marketplace data".
  //
  // Deps are deliberately just the tab and login state. Including
  // marketplaceData/loadingMarketplace would re-run this every time a fetch
  // finished, and a failed fetch (data still null, loading back to false)
  // would then retry forever.
  useEffect(() => {
    if (!adminAuth.isLoggedIn || activeTab !== 'marketplace') return;
    if (marketplaceData || loadingMarketplace) return;
    fetchMarketplace();
    fetchMarketplaceDurations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, adminAuth.isLoggedIn]);

  // Initial admin data load once authenticated.
  useEffect(() => {
    if (!adminAuth.isLoggedIn) return;

    fetchOrganizations();
    fetchResellers();
    fetchEmailWhitelist();
    fetchAnnouncements();
    fetchFeedback();

    // Check for migrations that were interrupted by a server restart
    (async () => {
      try {
        const token = localStorage.getItem('adminToken');
        const res = await fetch('/api/admin/migration/pending-resume', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.success && data.pending && data.pending.length > 0) {
          setPendingResumeMigrations(data.pending);
          setShowPendingResumeModal(true);
        }
      } catch (e) {
        console.error('Failed to check pending migrations:', e);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminAuth.isLoggedIn]);

  // Large datasets are loaded lazily — only when the user actually navigates to
  // the relevant tab — so the initial admin login stays fast.
  useEffect(() => {
    if (!adminAuth.isLoggedIn) return;

    if (activeTab === 'customers') {
      fetchAllCustomers(1);
    } else if (activeTab === 'cpResellers') {
      fetchCPResellers();
      if (organizations.length === 0) fetchOrganizations();
    } else if (activeTab === 'cpMonitoring') {
      fetchCPMonitoring();
    } else if (activeTab === 'settings') {
      // These were fetched only by the Settings menu item's click, so reaching
      // Settings any other way (a page reload restores the tab from the address)
      // left every form on its defaults - and Save then overwrote the real
      // settings with them.
      fetchMigrationSettings();
      fetchActivationSettings();
      fetchBulkSettings();
    } else if (activeTab === 'marketplace') {
      // Needed to populate the per-product visibility allow-list picker.
      if (cpResellers.length === 0) fetchCPResellers();
      fetchMarketplacePromotions();
      fetchCpDashboardConfig();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminAuth.isLoggedIn, activeTab]);

  // Poll the server for new marketplace orders and alert the admin (sound + popup).
  useEffect(() => {
    if (!adminAuth.isLoggedIn) return undefined;
    let cancelled = false;

    // Short two-tone beep via the Web Audio API (no asset file needed).
    const playOrderBeep = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const beep = (freq, startAt, duration) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = freq;
          osc.connect(gain);
          gain.connect(ctx.destination);
          gain.gain.setValueAtTime(0.001, ctx.currentTime + startAt);
          gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + startAt + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startAt + duration);
          osc.start(ctx.currentTime + startAt);
          osc.stop(ctx.currentTime + startAt + duration);
        };
        beep(880, 0, 0.18);
        beep(1175, 0.2, 0.22);
        setTimeout(() => { try { ctx.close(); } catch (e) {} }, 800);
      } catch (e) { /* audio not available */ }
    };

    const poll = async () => {
      try {
        const token = localStorage.getItem('adminToken');
        if (!token) return;
        const res = await fetch(`${API_URL}/admin/marketplace/orders/summary`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data.success) return;

        const maxOrderId = Number(data.maxOrderId) || 0;
        const stored = localStorage.getItem('mpLastSeenOrderId');

        // First run after login: set baseline silently so we don't alarm for
        // every pre-existing order.
        if (stored == null) {
          localStorage.setItem('mpLastSeenOrderId', String(maxOrderId));
          return;
        }

        const since = parseInt(stored, 10) || 0;
        if (maxOrderId > since) {
          // Fetch details of just the new orders to describe them in the alert.
          let newOrders = [];
          try {
            const r2 = await fetch(`${API_URL}/admin/marketplace/orders?sinceId=${since}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            const d2 = await r2.json();
            if (d2.success) newOrders = d2.orders || [];
          } catch (e) { /* ignore */ }

          if (!cancelled && newOrders.length > 0) {
            const manual = newOrders.filter(o => o.buyerEmail).length;
            playOrderBeep();
            setNewOrderNotice({ count: newOrders.length, manual, orders: newOrders.slice(0, 5) });
            if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
              try {
                new Notification(`${newOrders.length} new marketplace order(s)`, {
                  body: manual > 0 ? `${manual} need manual processing` : 'Tap to view orders'
                });
              } catch (e) { /* ignore */ }
            }
          }
          localStorage.setItem('mpLastSeenOrderId', String(maxOrderId));
        }
      } catch (e) { /* network hiccup — retry next tick */ }
    };

    // Ask for desktop notification permission once (in-app popup works regardless).
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      try { Notification.requestPermission(); } catch (e) {}
    }

    poll();
    const interval = setInterval(poll, 30000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [adminAuth.isLoggedIn]);

  // CRITICAL: Check credits-pool path FIRST before any other mode checks
  const earlyPath = window.location.pathname.toLowerCase();
  if (earlyPath.includes('/credits-pool')) {
    console.log('[EARLY] Credits Pool path detected, rendering CP mode');
    if (cpAuth.isLoggedIn) {
      return (
        <CpSecurityGate token={cpAuth.token} username={cpAuth.reseller?.username}
          onToken={t => setCpAuth(a => ({ ...a, token: t }))}
          onLogout={() => setCpAuth({ isLoggedIn: false, reseller: null, token: null })}>
          <CreditsPoolDashboard reseller={cpAuth.reseller} token={cpAuth.token} onLogout={() => setCpAuth({ isLoggedIn: false, reseller: null, token: null })} />
        </CpSecurityGate>
      );
    }
    return <CreditsPoolLogin onLogin={(reseller, token) => setCpAuth({ isLoggedIn: true, reseller, token })} />;
  }

  // CRITICAL: Check /bulk-status path EARLY
  if (earlyPath.includes('/bulk-status')) {
    console.log('[EARLY] Bulk Status portal path detected, rendering BulkStatusPortal');
    return <BulkStatusPortal />;
  }

  // CRITICAL: Check /redeem-key path EARLY - public account distribution page
  if (earlyPath.includes('/redeem-key')) {
    return <RedeemKey />;
  }

  // CRITICAL: Check /status path EARLY - public access, no mode checks needed
  if (earlyPath.includes('/status')) {
    console.log('[EARLY] Status portal path detected, rendering UserStatusPortal');
    return <UserStatusPortal />;
  }

  // Reseller mode handling
  if (isResellerMode) {
    if (!resellerAuth.isLoggedIn) {
      return (
        <ResellerLogin
          onLoginSuccess={(reseller, token) => {
            setResellerAuth({ isLoggedIn: true, reseller, token });
          }}
        />
      );
    }
    return (
      <ResellerDashboard
        reseller={resellerAuth.reseller}
        token={resellerAuth.token}
        onLogout={() => {
          setResellerAuth({ isLoggedIn: false, reseller: null, token: null });
        }}
      />
    );
  }

  // Admin login check (show loading while checking)
  if (checkingAdminAuth) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#1e1e2e',
        color: '#fff',
        fontSize: '18px'
      }}>
        Loading...
      </div>
    );
  }

  // Show admin login if not authenticated
  if (!adminAuth.isLoggedIn) {
    return (
      <AdminLogin
        onLoginSuccess={(admin, token) => {
          setAdminAuth({ isLoggedIn: true, admin, token });
        }}
      />
    );
  }

  // Admin logout function
  const handleAdminLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminInfo');
    setAdminAuth({ isLoggedIn: false, admin: null, token: null });
  };



  // Organization Functions
  const fetchOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/organizations', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setOrganizations(data.organizations || []);
        if (data.k12Cost !== undefined) setK12Cost(data.k12Cost);
      }
    } catch (err) {
      setError('Failed to fetch organizations: ' + err.message);
    } finally {
      setLoadingOrgs(false);
    }
  };

  // Mark/unmark an organization as a K12 team (flat-priced activations).
  const toggleOrgK12 = async (id, makeK12) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${id}/k12`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ k12: makeK12 }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || 'Failed to update K12'); return; }
      setOrganizations(prev => prev.map(o => o.id === id ? { ...o, k12: data.k12 } : o));
    } catch (err) {
      setError('Failed to update K12: ' + err.message);
    }
  };

  // Mark many orgs K12/Pro at once — either the current selection, or every org
  // whose products indicate a K-12 configuration.
  const bulkSetK12 = async ({ ids, matchK12Products, k12 }) => {
    const label = matchK12Products
      ? 'every organization whose products are K-12'
      : `${ids.length} selected organization(s)`;
    if (!window.confirm(`Mark ${label} as ${k12 ? 'K12' : 'Pro'}?\n\nThis changes what resellers are charged to activate users on ${k12 ? 'these' : 'those'} teams.`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/organizations/bulk-k12', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(matchK12Products ? { matchK12Products: true, k12 } : { ids, k12 }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || 'Bulk K12 update failed'); return; }
      setResult(`${data.updated} organization(s) marked as ${k12 ? 'K12' : 'Pro'}.`);
      setOrgApiSyncSelection(new Set());
      fetchOrganizations();
    } catch (err) {
      setError('Bulk K12 update failed: ' + err.message);
    }
  };

  const saveK12Cost = async () => {
    const val = Number(k12CostDraft);
    if (!Number.isFinite(val) || val < 0) { setError('Enter a valid K12 cost (0 or more).'); return; }
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/k12-cost', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ k12Cost: val }),
      });
      const data = await res.json();
      if (!res.ok || data.error) { setError(data.error || 'Failed to save K12 cost'); return; }
      setK12Cost(data.k12Cost); setK12CostDraft(''); setResult(`K12 activation cost set to ${data.k12Cost} credit(s) per month.`);
    } catch (err) {
      setError('Failed to save K12 cost: ' + err.message);
    }
  };

  // Migration Logs Functions
  const fetchMigrationLogs = async () => {
    setLoadingMigrationLogs(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/migration-history', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMigrationLogs({ active: data.active || [], history: data.history || [] });
      }
    } catch (err) {
      console.error('Failed to fetch migration logs:', err);
    }
    setLoadingMigrationLogs(false);
  };

  // Admin Logs Functions
  const fetchAdminLogs = async (page = 1) => {
    setLoadingAdminLogs(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/activity-logs?page=${page}&limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setAdminLogs(data.logs || []);
        setAdminLogsPagination(data.pagination || { page: 1, limit: 20, totalPages: 1, totalFiltered: 0, hasMore: false });
        setAdminLogsPage(page);
      }
    } catch (err) {
      console.error('Failed to fetch admin logs:', err);
    }
    setLoadingAdminLogs(false);
  };

  // Pause an active migration
  const handlePauseMigration = async (migrationId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/migration/${migrationId}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
        fetchMigrationLogs();
      } else {
        setError(data.error || 'Failed to pause migration');
      }
    } catch (err) {
      setError(`Pause error: ${err.message}`);
    }
  };

  // Resume a paused migration
  const handleResumeMigration = async (migrationId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/migration/${migrationId}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
        fetchMigrationLogs();
      } else {
        setError(data.error || 'Failed to resume migration');
      }
    } catch (err) {
      setError(`Resume error: ${err.message}`);
    }
  };

  // Cancel a migration and download CSV report
  const handleCancelMigration = async (migrationId, orgName) => {
    if (!window.confirm(`Are you sure you want to cancel the migration for "${orgName}"?\n\nA CSV report will be downloaded showing added and cancelled users.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/migration/${migrationId}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
        fetchMigrationLogs();

        // Download CSV report
        if (data.csvContent) {
          const blob = new Blob([data.csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', `migration_${orgName}_cancelled_${new Date().toISOString().slice(0, 10)}.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        }
      } else {
        setError(data.error || 'Failed to cancel migration');
      }
    } catch (err) {
      setError(`Cancel error: ${err.message}`);
    }
  };

  const handleCreateOrg = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/organizations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newOrg),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to create organization');
      } else {
        setResult(`Organization "${newOrg.name}" created successfully!`);
        setNewOrg({ name: '', clientId: '', clientSecret: '', orgId: '', apiKey: '', totalLicenses: '', availableAsTarget: true, autoDisableUserLimit: '' });
        fetchOrganizations();
      }
    } catch (err) {
      setError('Failed to create organization: ' + err.message);
    }
    setLoading(false);
  };

  const handleDeleteOrg = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete organization "${name}"?`)) return;

    setError(null);
    setResult(null);
    const token = localStorage.getItem('adminToken');

    const sendDelete = async (force) => fetch(`/api/admin/organizations/${id}${force ? '?force=true' : ''}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    try {
      let res = await sendDelete(false);
      let data = await res.json();

      // Server blocks deletion when users still belong to the org. Require a
      // second, explicit confirmation before forcing — these users will be
      // detached and shown as "Unknown".
      if (res.status === 409 && data.requiresForce) {
        const proceed = window.confirm(
          `${data.error}\n\nIf you continue, these users will be detached from the organization ` +
          `and will display as "Unknown". This cannot be undone.\n\nForce delete "${name}"?`
        );
        if (!proceed) return;
        res = await sendDelete(true);
        data = await res.json();
      }

      if (!res.ok || data.error) {
        setError(data.error || 'Failed to delete organization');
      } else {
        setResult(data.message);
        fetchOrganizations();
      }
    } catch (err) {
      setError('Failed to delete organization: ' + err.message);
    }
  };

  const handleTestOrgConnection = async (id) => {
    setTestingOrgId(id);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${id}/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
      } else {
        setError(data.details || data.error || 'Connection test failed');
      }
    } catch (err) {
      setError('Connection test failed: ' + err.message);
    }
    setTestingOrgId(null);
    fetchOrganizations();
  };

  const handleRefreshProducts = async (id, name) => {
    setRefreshingOrgId(id);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${id}/refresh-products`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
        fetchOrganizations();
      } else {
        const details = data.details;
        const msg = typeof details === 'string' ? details : (details ? JSON.stringify(details) : null);
        setError(msg || data.error || 'Failed to refresh products');
      }
    } catch (err) {
      setError('Failed to refresh products: ' + err.message);
    }
    setRefreshingOrgId(null);
  };

  const handleUpdateOrgStatus = async (id, updates) => {
    setUpdatingOrgStatusId(id);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
        fetchOrganizations();
      } else {
        setError(data.error || 'Failed to update organization status');
      }
    } catch (err) {
      setError('Failed to update organization status: ' + err.message);
    }
    setUpdatingOrgStatusId(null);
  };

  const handleEditOrgStatusMessage = async (org) => {
    const nextMessage = window.prompt('Status page message for customers:', org.statusMessage || '');
    if (nextMessage === null) return;
    await handleUpdateOrgStatus(org.id, { message: nextMessage });
  };

  // Manually (re-)run the self-switch evacuation for an org's current users —
  // for an org that's full but wasn't marked Inactive, or to retry users that
  // failed the automatic pass (e.g. a target rate-limited at the time).
  const handleEvacuateSelfSwitch = async (org) => {
    if (!window.confirm(`Move every user currently in "${org.name}" to a live backup organization now? This runs in the background and may take a while for a large org.`)) return;
    setUpdatingOrgStatusId(org.id);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${org.id}/evacuate-self-switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
      } else {
        setError(data.error || 'Failed to start evacuation');
      }
    } catch (err) {
      setError('Failed to start evacuation: ' + err.message);
    }
    setUpdatingOrgStatusId(null);
  };

  const handleBulkApiSync = async (enable) => {
    if (orgApiSyncSelection.size === 0) return;
    setBulkApiSyncLoading(true);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/organizations/bulk-api-sync', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ orgIds: [...orgApiSyncSelection], apiSyncEnabled: enable })
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
        setOrgApiSyncSelection(new Set());
        fetchOrganizations();
      } else {
        setError(data.error || 'Bulk API sync update failed');
      }
    } catch (err) {
      setError('Bulk API sync update failed: ' + err.message);
    }
    setBulkApiSyncLoading(false);
  };

  const handleToggleOrgApiSync = (org) => {
    handleUpdateOrgStatus(org.id, { apiSyncEnabled: !org.apiSyncEnabled });
  };

  const handleSaveOrgUserLimit = async (org) => {
    const draftValue = orgUserLimitDrafts[org.id] !== undefined
      ? orgUserLimitDrafts[org.id]
      : (org.autoDisableUserLimit || '');
    await handleUpdateOrgStatus(org.id, { autoDisableUserLimit: draftValue === '' ? null : draftValue });
    setOrgUserLimitDrafts(prev => {
      const next = { ...prev };
      delete next[org.id];
      return next;
    });
  };

  const handleAutoStatusCheck = async (id) => {
    setAutoCheckingOrgId(id);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${id}/auto-status`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.details ? `${data.message} Details: ${data.details}` : data.message);
        fetchOrganizations();
      } else {
        setError(data.error || 'Failed to run automated status check');
      }
    } catch (err) {
      setError('Failed to run automated status check: ' + err.message);
    }
    setAutoCheckingOrgId(null);
  };

  const handleRenameOrg = async () => {
    if (!renamingOrg || !newOrgName.trim()) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${renamingOrg.id}/rename`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name: newOrgName.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
        setRenamingOrg(null);
        setNewOrgName('');
        fetchOrganizations();
      } else {
        setError(data.error || 'Failed to rename organization');
      }
    } catch (err) {
      setError('Failed to rename organization: ' + err.message);
    }
    setLoading(false);
  };

  const handleSmartCopyOrg = async () => {
    if (!smartCopyOrg) return;

    const maxUsersPerGroup = Math.min(5000, Math.max(1, parseInt(smartCopyLimit, 10) || 5000));
    const totalUsers = smartCopyOrg.totalUsers || smartCopyOrg.userCounts?.totalUsers || 0;
    const groupCount = Math.max(1, Math.ceil(totalUsers / maxUsersPerGroup));
    const copyCount = Math.max(0, groupCount - 1);

    if (copyCount === 0) {
      setResult(`Organization "${smartCopyOrg.name}" is already within the ${maxUsersPerGroup} user limit.`);
      setSmartCopyOrg(null);
      return;
    }

    if (!window.confirm(`Smart copy "${smartCopyOrg.name}"?\n\nTotal users: ${totalUsers.toLocaleString()}\nLimit per organization: ${maxUsersPerGroup.toLocaleString()}\nNew local organization copies: ${copyCount}\n\nUsers beyond the first group will be reassigned to the copied organizations.`)) {
      return;
    }

    setSmartCopyingOrgId(smartCopyOrg.id);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${smartCopyOrg.id}/smart-copy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ maxUsersPerGroup }),
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        setError(data.error || 'Failed to smart copy organization');
      } else {
        setResult(data.message || `Smart copy completed for "${smartCopyOrg.name}".`);
        setSmartCopyOrg(null);
        fetchOrganizations();
      }
    } catch (err) {
      setError('Failed to smart copy organization: ' + err.message);
    }

    setSmartCopyingOrgId(null);
  };

  // Update organization credentials (for migrating to a new console)
  const handleUpdateOrg = async (e, confirmedMapping = null) => {
    if (e) e.preventDefault();
    if (!editingOrg) return;

    setLoading(true);
    setError(null);
    setResult(null);
    setMigrationResult(null);

    // Start progress timer
    const startTime = Date.now();
    setOrgUpdateProgress({ status: 'validating', elapsed: 0, message: 'Validating credentials with Adobe...' });

    const timerInterval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setOrgUpdateProgress(prev => prev ? { ...prev, elapsed } : null);
    }, 1000);

    try {
      // ALWAYS check for product differences before migration (regardless of skip checkbox)
      if (!confirmedMapping) {
        setOrgUpdateProgress(prev => ({ ...prev, message: 'Checking new console products...' }));
        console.log('[Migration] Calling preview-migration endpoint...');

        const token = localStorage.getItem('adminToken');
        const previewRes = await fetch(`/api/admin/organizations/${editingOrg.id}/preview-migration`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(editOrgForm),
        });
        const previewData = await previewRes.json();
        console.log('[Migration] Preview response:', previewData);

        if (!previewRes.ok || previewData.error) {
          clearInterval(timerInterval);
          setError(previewData.error || 'Failed to preview migration');
          setLoading(false);
          setOrgUpdateProgress(null);
          return;
        }

        // Check if products differ and there are users to migrate
        const { usedOldProducts, newProducts, newOrgName, userCount } = previewData.preview;
        console.log('[Migration] Old products:', usedOldProducts);
        console.log('[Migration] New products:', newProducts);
        console.log('[Migration] User count:', userCount);

        const newProductsSet = new Set(newProducts);
        const unmappedProducts = usedOldProducts.filter(p => !newProductsSet.has(p));
        console.log('[Migration] Unmapped products:', unmappedProducts);

        // Show mapping modal if ANY products are different OR if old products exist
        if (usedOldProducts.length > 0 && userCount > 0) {
          // Show product mapping modal
          clearInterval(timerInterval);
          setConsoleMigrationData({
            oldProducts: usedOldProducts,
            newProducts: newProducts,
            orgName: newOrgName,
            userCount: userCount
          });
          // Initialize mapping with auto-match where possible
          const initialMapping = {};
          usedOldProducts.forEach(oldP => {
            if (newProductsSet.has(oldP)) {
              initialMapping[oldP] = oldP;
            } else {
              // Try to find similar product
              const similarNew = newProducts.find(newP =>
                newP.toLowerCase().includes('all apps') && oldP.toLowerCase().includes('all apps')
              );
              if (similarNew) initialMapping[oldP] = similarNew;
            }
          });
          console.log('[Migration] Initial mapping:', initialMapping);
          setConsoleMigrationMapping(initialMapping);
          setShowConsoleMigrationMapping(true);
          setLoading(false);
          setOrgUpdateProgress(null);
          return; // Wait for user to confirm mapping
        }
      }

      // Proceed with actual migration
      setOrgUpdateProgress(prev => ({ ...prev, message: 'Connecting to Adobe API and migrating users...' }));

      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${editingOrg.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...editOrgForm,
          productMapping: confirmedMapping || consoleMigrationMapping || {}
        }),
      });
      const data = await res.json();

      clearInterval(timerInterval);

      if (!res.ok || data.error) {
        setError(data.error || 'Failed to update organization');
      } else {
        setResult(data.message || 'Organization updated successfully! Products have been refreshed.');

        if (data.migrationResult) {
          setMigrationResult(data.migrationResult);
        }

        setEditingOrg(null);
        setEditOrgForm({ clientId: '', clientSecret: '', orgId: '', apiKey: '', skipRemoveFromOld: false, forceReassign: false });
        setShowConsoleMigrationMapping(false);
        setConsoleMigrationData(null);
        setConsoleMigrationMapping({});
        fetchOrganizations();
      }
    } catch (err) {
      clearInterval(timerInterval);
      setError('Failed to update organization: ' + err.message);
    }
    setLoading(false);
    setOrgUpdateProgress(null);
  };

  // Cancel a running migration for an organization
  const cancelMigration = async (orgId) => {
    if (!window.confirm('Are you sure you want to cancel the migration? This will stop after the current batch completes.')) {
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${orgId}/cancel-migration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (data.success) {
        setResult('Migration cancellation requested. It will stop after the current batch.');
      } else {
        setError(data.error || 'Failed to cancel migration');
      }
    } catch (err) {
      setError('Failed to cancel migration: ' + err.message);
    }
  };

  // Admin Panel Functions
  const fetchUsers = async () => {
    setLoadingUsers(true);
    setError(null);
    try {
      const res = await fetch('/api/users');
      const data = await res.json();
      if (data.error) {
        setError(JSON.stringify(data, null, 2));
      } else {
        const usersList = data.users.users || [];
        setUsers(usersList);
        setFilteredUsers(usersList);
      }
    } catch (err) {
      setError('Failed to fetch users: ' + err.message);
    }
    setLoadingUsers(false);
  };

  const fetchProducts = async () => {
    setLoadingProducts(true);
    setError(null);
    try {
      const res = await fetch('/api/products');
      const data = await res.json();
      if (data.error) {
        setError(JSON.stringify(data, null, 2));
      } else {
        setProducts(data.groups || []);
      }
    } catch (err) {
      setError('Failed to fetch products: ' + err.message);
    }
    setLoadingProducts(false);
  };

  const fetchResellers = async () => {
    setLoadingResellers(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/resellers', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.error && !data.resellers) {
        setError(data.error);
      } else {
        setResellers(data.resellers || []);
      }
    } catch (err) {
      setError('Failed to fetch resellers: ' + err.message);
    }
    setLoadingResellers(false);
  };

  const fetchResellerUsers = async (resellerId) => {
    setLoadingResellerUsers(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/resellers/${resellerId}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSelectedReseller(data.reseller);
        setResellerUsers(data.users || []);
        setLicenseBreakdown(data.licenseBreakdown || { monthly: 0, quarterly: 0, yearly: 0 });
      }
    } catch (err) {
      setError('Failed to fetch reseller users: ' + err.message);
    }
    setLoadingResellerUsers(false);
  };

  const fetchActivityLogs = async (resellerId) => {
    setLoadingActivityLogs(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/resellers/${resellerId}/activity`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResellerActivityLogs(data.logs || []);
      }
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
      setResellerActivityLogs([]);
    }
    setLoadingActivityLogs(false);
  };

  // Add user to reseller (admin action)
  const handleAddUserToReseller = async (e) => {
    e.preventDefault();
    if (!selectedReseller) return;

    setAddingUserToReseller(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/resellers/${selectedReseller.id}/assign-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: addUserToResellerForm.email,
          durationMonths: addUserToResellerForm.durationMonths,
          organizationId: addUserToResellerForm.organizationId || undefined,
          products: addUserToResellerForm.products.length > 0 ? addUserToResellerForm.products : undefined
        })
      });
      const data = await res.json();

      if (!res.ok || data.error) {
        setError(data.error || 'Failed to add user');
      } else {
        setResult(data.message || 'User added successfully');
        setAddUserToResellerForm({ email: '', durationMonths: 1, organizationId: '', products: [] });
        fetchResellerUsers(selectedReseller.id); // Refresh user list
      }
    } catch (err) {
      setError('Failed to add user: ' + err.message);
    }
    setAddingUserToReseller(false);
  };

  const fetchAllCustomers = async (page = 1) => {
    setLoadingCustomers(true);
    setError(null);
    try {
      // Search is sent to backend for Apply button, client-side filtering also works on loaded data
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
        search: customerSearchQuery || '',
        orgFilter: customerOrgFilter || '',
        durationFilter: customerDurationFilter || '',
        statusFilter: customerStatusFilter || '',
        daysFilter: customerDaysFilter || ''
      });
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/all-users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setAllCustomers(data.users || []);
        setCustomersSummary(data.summary || { total: 0, active: 0, expiring: 0, expired: 0 });
        setCustomersPagination(data.pagination || { page: 1, limit: 50, totalPages: 1, totalFiltered: 0, hasMore: false });
        setCustomersPage(page);
      }
    } catch (err) {
      setError('Failed to fetch customers: ' + err.message);
    }
    setLoadingCustomers(false);
  };


  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        user.email.toLowerCase().includes(query.toLowerCase()) ||
        (user.firstname && user.firstname.toLowerCase().includes(query.toLowerCase())) ||
        (user.lastname && user.lastname.toLowerCase().includes(query.toLowerCase()))
      );
      setFilteredUsers(filtered);
    }
  };

  const handleRemoveUser = async (userEmail) => {
    if (!window.confirm(`Are you sure you want to remove ${userEmail} from the organization?`)) return;

    setError(null);
    setResult(null);
    try {
      const res = await fetch('/api/remove-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail }),
      });
      const data = await res.json();
      if (data.error) {
        setError(JSON.stringify(data, null, 2));
      } else {
        setResult(`User ${userEmail} removed successfully!`);
        fetchUsers();
      }
    } catch (err) {
      setError('Failed to remove user: ' + err.message);
    }
  };

  // Reseller Management Functions
  const handleCreateReseller = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/resellers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newReseller),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to create reseller');
      } else {
        setResult(`Reseller "${newReseller.username}" created successfully!`);
        setNewReseller({ username: '', password: '', userGroup: '', maxLicenses: '', workingDays: '', organizationAccess: [] });
        fetchResellers();
      }
    } catch (err) {
      setError('Failed to create reseller: ' + err.message);
    }
    setLoading(false);
  };

  const handleUpdateReseller = async (id) => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const updateData = {};
      if (editForm.password) updateData.password = editForm.password;
      if (editForm.maxLicenses) updateData.maxLicenses = parseInt(editForm.maxLicenses);
      if (editForm.workingDays) updateData.workingDays = parseInt(editForm.workingDays);

      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/resellers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to update reseller');
      } else {
        setResult('Reseller updated successfully!');
        setEditingReseller(null);
        setEditForm({ password: '', maxLicenses: '', workingDays: '', organizationAccess: [] });
        fetchResellers();
      }
    } catch (err) {
      setError('Failed to update reseller: ' + err.message);
    }
    setLoading(false);
  };

  const handleUpdateOrgAccess = async () => {
    if (!editingReseller) return;
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/resellers/${editingReseller}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ organizationAccess: editForm.organizationAccess }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to update organization access');
      } else {
        setResult('Organization access updated successfully!');
        setEditOrgModalOpen(false);
        setEditingReseller(null);
        setEditForm({ password: '', maxLicenses: '', organizationAccess: [] });
        fetchResellers();
      }
    } catch (err) {
      setError('Failed to update: ' + err.message);
    }
    setLoading(false);
  };


  const handleDeleteReseller = async (id, username) => {
    if (!window.confirm(`Are you sure you want to delete reseller "${username}"?`)) return;

    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/resellers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to delete reseller');
      } else {
        setResult(`Reseller "${username}" deleted successfully!`);
        fetchResellers();
      }
    } catch (err) {
      setError('Failed to delete reseller: ' + err.message);
    }
  };

  // Parse email input textarea into array
  const parseMigrateEmails = () => {
    if (migrateMode === 'all') return [];
    return migrateEmailInput
      .split(/[\n,;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
  };

  // Migrate users between resellers - Preview
  const handleMigratePreview = async () => {
    if (!migrateSourceId || !migrateTargetId) return;
    const specificEmails = parseMigrateEmails();
    if (migrateMode === 'specific' && specificEmails.length === 0) {
      setError('Please enter at least one valid email address');
      return;
    }
    setMigrateLoading(true);
    setMigratePreview(null);
    setMigrateResult(null);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/migrate-reseller-users/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          sourceResellerId: parseInt(migrateSourceId),
          targetResellerId: parseInt(migrateTargetId),
          sourceType: migrateSourceType,
          targetType: migrateTargetType,
          emails: specificEmails
        })
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to preview migration');
      } else {
        setMigratePreview(data);
        setMigrateSelectedEmails(data.usersToMigrate.map(u => u.email));
        setMigrateSelectAll(true);
      }
    } catch (err) {
      setError('Failed to preview migration: ' + err.message);
    }
    setMigrateLoading(false);
  };

  // Migrate users between resellers - Execute
  const handleMigrateExecute = async () => {
    if (!migratePreview) return;
    const count = migrateSelectAll ? migratePreview.usersToMigrate.length : migrateSelectedEmails.length;
    const srcLabel = migrateSourceType === 'credits-pool' ? '[CP] ' : '';
    const tgtLabel = migrateTargetType === 'credits-pool' ? '[CP] ' : '';
    if (!window.confirm(`Are you sure you want to migrate ${count} user(s) from ${srcLabel}"${migratePreview.source.username}" to ${tgtLabel}"${migratePreview.target.username}"?`)) return;

    setMigrateLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/migrate-reseller-users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          sourceResellerId: parseInt(migrateSourceId),
          targetResellerId: parseInt(migrateTargetId),
          sourceType: migrateSourceType,
          targetType: migrateTargetType,
          // Always send the explicit email list. Sending [] tells the server
          // "no filter → migrate everyone from source" which is NOT what we want
          // here — even "Select All" means "all rows shown in the preview".
          emails: migrateSelectAll
            ? migratePreview.usersToMigrate.map(u => u.email)
            : migrateSelectedEmails
        })
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Migration failed');
      } else {
        setMigrateResult(data);
        fetchResellers();
        fetchCPResellers();
      }
    } catch (err) {
      setError('Migration failed: ' + err.message);
    }
    setMigrateLoading(false);
  };

  // Reset migration modal
  const resetMigrateModal = () => {
    setShowMigrateModal(false);
    setMigrateSourceType('traditional');
    setMigrateTargetType('traditional');
    setMigrateSourceId('');
    setMigrateTargetId('');
    setMigratePreview(null);
    setMigrateResult(null);
    setMigrateMode('all');
    setMigrateEmailInput('');
    setMigrateSelectedEmails([]);
    setMigrateSelectAll(true);
  };

  // Toggle show expiration info for a reseller
  const handleToggleShowExpiration = async (type, id, currentValue) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/reseller/${type}/${id}/show-expiration`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ showExpirationInfo: !currentValue })
      });
      const data = await res.json();
      if (data.success) {
        // Refresh the appropriate resellers list
        if (type === 'traditional') {
          fetchResellers();
        } else {
          fetchCPMonitoring();
        }
      } else {
        setError(data.error || 'Failed to update');
      }
    } catch (err) {
      setError('Failed to toggle show expiration: ' + err.message);
    }
  };

  // ==================== CREDITS POOL RESELLER FUNCTIONS ====================

  const fetchCPResellers = async () => {
    setLoadingCPResellers(true);
    fetchRedemptionRequests(); // keep the Refund Settings badge live on this tab
    try {
      const token = localStorage.getItem('adminToken');
      const [res, orphanRes] = await Promise.all([
        fetch('/api/admin/credits-pool-resellers', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/admin/credits-pool-resellers/orphaned', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      const data = await res.json();
      if (data.success) {
        setCpResellers(data.resellers || []);
      }
      const orphanData = await orphanRes.json();
      if (orphanData.success) {
        setOrphanedCPResellers(orphanData.orphaned || []);
      }
    } catch (err) {
      setError('Failed to fetch CP resellers');
    } finally {
      setLoadingCPResellers(false);
    }
  };

  const handleCreateCPReseller = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResult(null);

    const initialCreditsAmount = newCPReseller.initialCredits === ''
      ? 0
      : parseDecimalAmount(newCPReseller.initialCredits, { allowNegative: false, allowZero: true });

    if (initialCreditsAmount === null) {
      setError('Please enter a valid initial credits amount, for example 0.5');
      setLoading(false);
      return;
    }

    const initialGoldCoinsAmount = newCPReseller.initialGoldCoins === ''
      ? 0
      : parseDecimalAmount(newCPReseller.initialGoldCoins, { allowNegative: false, allowZero: true });

    if (initialGoldCoinsAmount === null) {
      setError('Please enter a valid initial Gold Coins amount, for example 5.5');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/credits-pool-resellers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newCPReseller,
          initialCredits: initialCreditsAmount,
          initialGoldCoins: initialGoldCoinsAmount,
          creditsPerMonth: normalizeCreditsPerMonthConfig(newCPReseller.creditsPerMonth)
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to create CP reseller');
      } else {
        setResult(`Credits Pool Reseller "${newCPReseller.username}" created!`);
        setNewCPReseller({ username: '', password: '', userGroup: '', initialCredits: '', initialGoldCoins: '', poolValidityMonths: '12', organizationAccess: [], creditsPerMonth: { ...DEFAULT_CP_CREDITS }, hiddenMonths: [], apiAccessEnabled: false, allowBackupKeys: false });
        fetchCPResellers();
      }
    } catch (err) {
      setError('Failed to create CP reseller: ' + err.message);
    }
    setLoading(false);
  };

  const handleAddCredits = async (id) => {
    const creditsAmount = parseDecimalAmount(addCreditsForm.credits, { allowNegative: true });
    if (creditsAmount === null) {
      setError('Please enter a valid credits amount, for example 0.5 or -0.5');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${id}/add-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...addCreditsForm, credits: creditsAmount }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to add credits');
      } else {
        setResult(data.message);
        setAddCreditsForm({ credits: '', reason: '' });
        setEditingCPReseller(null);
        fetchCPResellers();
      }
    } catch (err) {
      setError('Failed to add credits: ' + err.message);
    }
    setLoading(false);
  };

  /**
   * Rename a reseller.
   *
   * Panels hold names that cannot be retyped - Japanese, combining marks,
   * zero-width characters - which makes the account unusable anywhere a name
   * has to be typed. This changes their LOGIN, so it confirms first.
   */
  const handleRenameCPReseller = async (cpr) => {
    const next = window.prompt(
      `Rename reseller #${cpr.id}.

This is the name they sign in with, so tell them before you change it.
` +
      'Past records keep the old name.', cpr.username);
    if (next === null) return;
    const username = next.trim();
    if (!username || username === cpr.username) return;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${cpr.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ username })
      });
      const data = await res.json();
      if (!res.ok || data.error) setError(data.error || 'Failed to rename');
      else { setResult(`Renamed to "${username}". They must sign in with the new name.`); fetchCPResellers(); }
    } catch (err) {
      setError('Failed to rename: ' + err.message);
    }
    setLoading(false);
  };

  /**
   * This agent's own cap on users per bulk operation.
   *
   * Three states, and the prompt says so: blank follows the server default,
   * 0 removes the cap for them, a number caps them there.
   */
  const handleBulkLimit = async (cpr) => {
    const current = cpr.maxUsersPerBulk === null || cpr.maxUsersPerBulk === undefined ? '' : String(cpr.maxUsersPerBulk);
    const next = window.prompt(
      `Users per bulk operation for "${cpr.username}".

` +
      'Leave blank to follow the server default, 0 for no limit, or a number to cap them.',
      current);
    if (next === null) return;

    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${cpr.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ maxUsersPerBulk: next.trim() === '' ? null : next.trim() })
      });
      const data = await res.json();
      if (!res.ok || data.error) setError(data.error || 'Failed to set the limit');
      else {
        setResult(next.trim() === '' ? `${cpr.username} now follows the server default.`
          : Number(next) === 0 ? `${cpr.username} has no bulk limit.`
          : `${cpr.username} is limited to ${Number(next)} users per bulk operation.`);
        fetchCPResellers();
      }
    } catch (err) {
      setError('Failed to set the limit: ' + err.message);
    }
    setLoading(false);
  };

  const handleAddGoldCoins = async (id) => {
    const goldCoinsAmount = parseDecimalAmount(addGoldCoinsForm.goldCoins, { allowNegative: true });
    if (goldCoinsAmount === null) {
      setError('Please enter a valid Gold Coins amount, for example 5 or -2.5');
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${id}/add-gold-coins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ...addGoldCoinsForm, goldCoins: goldCoinsAmount }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to update Gold Coins');
      } else {
        setResult(data.message);
        setAddGoldCoinsForm({ goldCoins: '', reason: '' });
        setEditingCPGoldCoins(null);
        fetchCPResellers();
      }
    } catch (err) {
      setError('Failed to update Gold Coins: ' + err.message);
    }
    setLoading(false);
  };

  const handleClearRefundDays = async (id, username, refundDaysBalance) => {
    if (!refundDaysBalance || refundDaysBalance <= 0) {
      setResult(`${username} has no refund days to clear.`);
      return;
    }

    if (!window.confirm(`Clear ${refundDaysBalance} refund day(s) for "${username}" after customer-service redemption?`)) {
      return;
    }

    const reason = window.prompt('Optional reason for clearing refund days:', 'Redeemed with customer service') || '';
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${id}/clear-refund-days`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reason }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to clear refund days');
      } else {
        setResult(data.message);
        fetchCPResellers();
        if (activeTab === 'cpMonitoring') {
          fetchCPMonitoring();
        }
      }
    } catch (err) {
      setError('Failed to clear refund days: ' + err.message);
    }
    setLoading(false);
  };

  const handleDeleteCPReseller = async (id, username) => {
    if (!window.confirm(`Delete CP reseller "${username}"? This cannot be undone.`)) return;

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error);
      } else {
        setResult(`Deleted "${username}"`);
        fetchCPResellers();
      }
    } catch (err) {
      setError('Failed to delete');
    }
  };

  const handleUpdateCPOrgAccess = async (arg = null) => {
    console.log('[DEBUG] handleUpdateCPOrgAccess called', { arg, editingCPOrgAccess, editCPOrgForm });

    // If called from onClick, arg is an Event object. Treat it as null.
    const confirmedMapping = (arg && arg.nativeEvent) ? null : arg;

    if (!editingCPOrgAccess) {
      console.log('[DEBUG] Early return: editingCPOrgAccess is falsy');
      return;
    }
    setLoading(true);
    setError(null);

    // Check for product mismatch if not already confirmed
    // Note: Product mismatch modal was removed - backend handles user migration/removal
    // When orgs are removed, the backend will handle removing users from those orgs

    try {
      const body = {
        organizationAccess: editCPOrgForm.organizationAccess,
        productMapping: confirmedMapping
      };

      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${editingCPOrgAccess}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to update organization access');
      } else {
        setResult('Organization access updated!');
        setEditingCPOrgAccess(null);
        setEditCPOrgForm({ organizationAccess: [] });
        setShowProductMappingModal(false);
        setProductMapping({});
        fetchCPResellers();
      }
    } catch (err) {
      setError('Failed to update: ' + err.message);
    }
    setLoading(false);
  };

  const handleUpdateCPSettings = async () => {
    if (!editCPSettingsId) return;
    setLoading(true);
    setError(null);

    try {
      const updateData = {};
      if (editCPSettingsForm.password) updateData.password = editCPSettingsForm.password;
      if (editCPSettingsForm.extendPoolMonths) updateData.extendPoolMonths = parseInt(editCPSettingsForm.extendPoolMonths);
      // Always send credits config
      if (editCPSettingsForm.creditsPerMonth) updateData.creditsPerMonth = normalizeCreditsPerMonthConfig(editCPSettingsForm.creditsPerMonth);
      if (editCPSettingsForm.hiddenMonths) updateData.hiddenMonths = editCPSettingsForm.hiddenMonths;
      updateData.apiAccessEnabled = editCPSettingsForm.apiAccessEnabled === true;
      if (editCPSettingsForm.apiDefaultOrganizationId) {
        updateData.apiDefaultOrganizationId = parseInt(editCPSettingsForm.apiDefaultOrganizationId);
      } else {
        updateData.apiDefaultOrganizationId = null;
      }

      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${editCPSettingsId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updateData),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to update');
      } else {
        setResult('Settings updated successfully!');
        setEditCPSettingsId(null);
        setEditCPSettingsForm({ password: '', extendPoolMonths: '', creditsPerMonth: { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8, 9: 9, 10: 10, 11: 11, 12: 12 }, hiddenMonths: [], apiAccessEnabled: false, apiDefaultOrganizationId: null });
        fetchCPResellers();
      }
    } catch (err) {
      setError('Failed to update: ' + err.message);
    }
    setLoading(false);
  };

  // Apply the bulk pricing config to every CP reseller in one request.
  const handleBulkPricing = async () => {
    const count = cpResellers.length;
    if (!window.confirm(`Apply this pricing to ALL ${count} Credits Pool reseller${count === 1 ? '' : 's'}? This overwrites their current per-duration credit prices${bulkPricingForm.applyHidden ? ' and visibility' : ''}.`)) return;
    setLoading(true);
    setError(null);
    try {
      const payload = { creditsPerMonth: normalizeCreditsPerMonthConfig(bulkPricingForm.creditsPerMonth) };
      if (bulkPricingForm.applyHidden) payload.hiddenMonths = bulkPricingForm.hiddenMonths || [];
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/credits-pool-resellers/bulk-pricing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to apply bulk pricing');
      } else {
        setResult(data.message || `Pricing applied to ${data.updated} reseller(s)!`);
        setShowBulkPricing(false);
        fetchCPResellers();
      }
    } catch (err) {
      setError('Failed to apply bulk pricing: ' + err.message);
    }
    setLoading(false);
  };

  // Duration keys the old-price table covers (7-day, 14-day, 1–12 months).
  const REFUND_DURATION_KEYS = ['7d', '14d', ...Array.from({ length: 12 }, (_, i) => String(i + 1))];
  const refundDurationLabel = (k) => (k === '7d' ? '7 days' : k === '14d' ? '14 days' : `${k} mo`);

  const openRefundSettings = async () => {
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/refund-settings', { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok && data.success) {
        const s = data.settings || {};
        const overrides = s.graceHoursByReseller || {};
        setRefundSettings({
          enabled: !!s.enabled,
          cutoffDate: s.cutoffDate ? String(s.cutoffDate).slice(0, 10) : '',
          oldPrices: s.oldPrices || {},
          graceHours: (s.graceHours === undefined || s.graceHours === null) ? (data.defaultGraceHours ?? 2) : s.graceHours,
          graceOverrides: { ...overrides },
          selfRedeemThreshold: s.selfRedeemThreshold ?? 0,
        });
        setRefundLoadedOverrideIds(Object.keys(overrides));
        setRefundOverrideDraft({ resellerId: '', hours: '0' });
        fetchRedemptionRequests();
        setShowRefundSettings(true);
      } else {
        setError(data.error || 'Failed to load refund settings');
      }
    } catch (err) {
      setError('Failed to load refund settings: ' + err.message);
    }
  };

  const fetchRedemptionRequests = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/credits-pool/redemption-requests', { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok && data.success) {
        setRedemptionRequests(data.requests || []);
        if (typeof data.selfRedeemThreshold === 'number') setRedeemThreshold(data.selfRedeemThreshold);
      }
    } catch (err) {
      /* non-fatal: the badge just won't show */
    }
  };

  const saveSelfRedeemThreshold = async () => {
    const val = Number(redeemThresholdInput);
    if (!Number.isFinite(val) || val < 0) { setError('Enter a number of credits (0 or more).'); return; }
    try {
      const token = localStorage.getItem('adminToken');
      // saveSettings merges, so sending only this field leaves everything else.
      const res = await fetch('/api/admin/refund-settings', {
        method: 'PUT', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ selfRedeemThreshold: val })
      });
      const data = await res.json();
      if (res.ok && !data.error) {
        const saved = data.settings?.selfRedeemThreshold ?? val;
        setRedeemThreshold(saved);
        setRedeemThresholdInput(String(saved));
        setRefundSettings(s => ({ ...s, selfRedeemThreshold: saved }));
        setResult(`Self-redeem limit set to ${saved} credit(s). Below this, resellers redeem instantly; above it needs your approval.`);
      } else {
        setError(data.error || 'Could not save the limit');
      }
    } catch (e) {
      setError('Could not save the limit: ' + e.message);
    }
  };

  const resolveRedemptionRequest = async (resellerId, action) => {
    const verb = action === 'approve' ? 'Approve' : 'Reject';
    if (!window.confirm(`${verb} this redemption request?`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool/redemption-requests/${resellerId}/${action}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) { setResult(data.message); fetchRedemptionRequests(); }
      else setError(data.error || `Could not ${action} the request`);
    } catch (err) {
      setError(`Could not ${action} the request: ` + err.message);
    }
  };

  const saveRefundSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      // Send removed overrides explicitly as null; the server merges, so simply
      // omitting a key leaves the old override in place.
      const graceHoursByReseller = { ...(refundSettings.graceOverrides || {}) };
      for (const id of refundLoadedOverrideIds) {
        if (!(id in graceHoursByReseller)) graceHoursByReseller[id] = null;
      }
      const payload = {
        enabled: refundSettings.enabled === true,
        cutoffDate: refundSettings.cutoffDate ? new Date(refundSettings.cutoffDate + 'T00:00:00Z').toISOString() : null,
        oldPrices: refundSettings.oldPrices || {},
        graceHours: refundSettings.graceHours,
        graceHoursByReseller,
        selfRedeemThreshold: refundSettings.selfRedeemThreshold,
      };
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/refund-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || data.error) setError(data.error || 'Failed to save refund settings');
      else { setResult('Refund settings saved.'); setShowRefundSettings(false); }
    } catch (err) {
      setError('Failed to save refund settings: ' + err.message);
    }
    setLoading(false);
  };

  const settleRefundCredits = async (id, username, amount, mode = 'credit') => {
    const verb = mode === 'credit' ? `add ${amount} credit(s) to ${username}'s balance` : `discard ${amount} pending credit(s) for ${username}`;
    if (!window.confirm(`Settle pending refund: ${verb}?`)) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${id}/settle-refund-credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ mode }),
      });
      const data = await res.json();
      if (!res.ok || data.error) setError(data.error || 'Failed to settle');
      else { setResult(data.message || 'Settled.'); fetchCPResellers(); }
    } catch (err) {
      setError('Failed to settle: ' + err.message);
    }
    setLoading(false);
  };

  // Fetch API monitoring data for admin
  const fetchApiMonitoring = async () => {
    setLoadingApiMonitoring(true);
    try {
      const token = adminAuth?.token || localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/api-monitoring`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setApiMonitoringData({ stats: data.stats, keys: data.keys });
      } else {
        setError(data.error || 'Failed to fetch API monitoring data');
      }
    } catch (err) {
      setError('Failed to fetch API monitoring data');
    }
    setLoadingApiMonitoring(false);
  };

  const handleRevokeApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/api-keys/${encodeURIComponent(keyId)}/revoke`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
        fetchApiMonitoring();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to revoke API key');
    }
  };

  const handleKillSwitchAll = async () => {
    if (!window.confirm('KILL SWITCH: This will revoke ALL active API keys across ALL resellers. Are you sure?')) return;
    if (!window.confirm('This action cannot be undone. Resellers will need to generate new keys. Confirm?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/api-keys/revoke-all`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Admin triggered global kill switch' })
      });
      const data = await res.json();
      if (data.success) {
        setResult(`Kill switch activated: ${data.revokedCount} key(s) revoked`);
        fetchApiMonitoring();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to execute kill switch');
    }
  };

  const handleToggleApiAccess = async (resellerId, enabled) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/api-access/${resellerId}/toggle`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled })
      });
      const data = await res.json();
      if (data.success) {
        setResult(data.message);
        fetchApiMonitoring();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to toggle API access');
    }
  };

  // Fetch all CP resellers with usage stats for monitoring
  const fetchCPMonitoring = async () => {
    setLoadingCPMonitoring(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/credits-pool-resellers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        const now = new Date();
        setCpMonitoringResellers((data.resellers || []).map(r => {
          const expiry = r.creditsPoolValidity ? new Date(r.creditsPoolValidity) : null;
          const poolDaysLeft = expiry ? Math.ceil((expiry - now) / (1000 * 60 * 60 * 24)) : 0;
          return {
            ...r,
            poolDaysLeft,
            isPoolExpired: expiry ? expiry < now : false,
            stats: {
              activeUsers: r.activeUsers || 0,
              netCreditsUsed: r.creditsUsed || 0,
              totalCreditsUsed: r.creditsUsed || 0,
              totalCreditsRefunded: 0,
              lastTransaction: null
            }
          };
        }));
      }
    } catch (err) {
      console.error('Failed to fetch CP monitoring data:', err);
    } finally {
      setLoadingCPMonitoring(false);
    }
  };

  const handleBulkToggleTransfers = async (allow) => {
    if (!window.confirm(`Are you sure you want to ${allow ? 'ENABLE' : 'DISABLE'} Bulk Transfers for ALL Credits Pool resellers?`)) return;

    setLoadingCPMonitoring(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/credits-pool-resellers/bulk/allow-transfer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ allowTransfer: allow })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data.message);
        fetchCPMonitoring(); // Refresh the table
      } else {
        setError(data.error || 'Failed to bulk update transfers');
      }
    } catch (err) {
      setError('Failed to bulk update transfers: ' + err.message);
    }
    setLoadingCPMonitoring(false);
  };

  const handleBulkToggleReinvite = async (allow) => {
    if (!window.confirm(`Are you sure you want to ${allow ? 'ENABLE' : 'DISABLE'} Reinvite for ALL Credits Pool resellers?`)) return;

    setLoadingCPMonitoring(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/credits-pool-resellers/bulk/allow-reinvite`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ allowReinvite: allow })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data.message);
        fetchCPMonitoring();
      } else {
        setError(data.error || 'Failed to bulk update reinvite');
      }
    } catch (err) {
      setError('Failed to bulk update reinvite: ' + err.message);
    }
    setLoadingCPMonitoring(false);
  };

  const handleUpdateCPTransferSourceOrgAction = async (resellerId, transferSourceOrgAction) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/credits-pool-resellers/${resellerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ transferSourceOrgAction })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(`Transfer behavior updated for ${data.reseller?.username || 'the reseller'}`);
        fetchCPMonitoring();
      } else {
        setError(data.error || 'Failed to update transfer behavior');
      }
    } catch (err) {
      setError('Failed to update transfer behavior: ' + err.message);
    }
  };

  const handleUpdateTraditionalTransferSourceOrgAction = async (resellerId, transferSourceOrgAction) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/resellers/${resellerId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ transferSourceOrgAction })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(`Transfer behavior updated for ${data.reseller?.username || 'the reseller'}`);
        fetchResellers();
      } else {
        setError(data.error || 'Failed to update transfer behavior');
      }
    } catch (err) {
      setError('Failed to update transfer behavior: ' + err.message);
    }
  };

  // Announcement Management
  const fetchAnnouncements = async () => {
    setLoadingAnnouncements(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/announcements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setAnnouncements(data.announcements || []);
      }
    } catch (err) {
      console.error('Failed to fetch announcements:', err);
    }
    setLoadingAnnouncements(false);
  };

  const handleCreateAnnouncement = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/announcements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newAnnouncement)
      });
      const data = await res.json();
      if (res.ok) {
        setResult('Announcement created successfully');
        setNewAnnouncement({ title: '', message: '', type: 'info', target: 'all', specificResellerIds: [] });
        fetchAnnouncements();
      } else {
        setError(data.error || 'Failed to create announcement');
      }
    } catch (err) {
      setError('Failed to create announcement: ' + err.message);
    }
    setLoading(false);
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setResult('Announcement deleted');
        fetchAnnouncements();
      } else {
        setError('Failed to delete announcement');
      }
    } catch (err) {
      setError('Delete error: ' + err.message);
    }
    setLoading(false);
  };

  const fetchFeedback = async (statusFilter = feedbackStatusFilter, typeFilter = feedbackTypeFilter) => {
    setLoadingFeedback(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (typeFilter && typeFilter !== 'all') params.set('type', typeFilter);
      const res = await fetch(`${API_URL}/admin/feedback?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        setFeedbackList(data.feedback || []);
        setFeedbackNewCount(data.newCount || 0);
      }
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    }
    setLoadingFeedback(false);
  };

  const handleUpdateFeedbackStatus = async (id, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/feedback/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ status })
      });
      if (res.ok) {
        fetchFeedback();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to update feedback');
      }
    } catch (err) {
      setError('Update error: ' + err.message);
    }
  };

  const handleDeleteFeedback = async (id) => {
    if (!window.confirm('Delete this feedback entry?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/feedback/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        fetchFeedback();
      } else {
        setError('Failed to delete feedback');
      }
    } catch (err) {
      setError('Delete error: ' + err.message);
    }
  };

  const handleSubmitFeedbackReply = async (id) => {
    const reply = (feedbackReplyDrafts[id] || '').trim();
    if (!reply) return;
    setSubmittingReplyId(id);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/feedback/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ adminNote: reply })
      });
      if (res.ok) {
        setFeedbackReplyDrafts(prev => ({ ...prev, [id]: undefined }));
        fetchFeedback();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to send reply');
      }
    } catch (err) {
      setError('Reply error: ' + err.message);
    }
    setSubmittingReplyId(null);
  };

  // Helper to export users for an organization
  const handleExportUsers = async (orgId, orgName) => {
    if (!window.confirm(`Export all users for ${orgName}?`)) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      // Use limit=all so large organizations are not capped at 100,000 rows.
      const res = await fetch(`/api/admin/all-users?limit=all&orgIdFilter=${orgId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.success && data.users) {
        if (data.users.length === 0) {
          setResult(`No users found for ${orgName}`);
        } else {
          // Generate CSV
          const headers = ['Email', 'First Name', 'Last Name', 'Organization', 'Products', 'Reseller', 'Status', 'Created At'];
          const rows = data.users.map(u => [
            u.email,
            u.firstname || '',
            u.lastname || '',
            u.organizationName || orgName,
            (u.products || []).join('; '),
            u.resellerName || '',
            u.status,
            u.assignedAt || u.activatedAt || ''
          ]);

          const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${(String(cell) || '').replace(/"/g, '""')}"`).join(','))
          ].join('\n');

          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.setAttribute('href', url);
          link.setAttribute('download', `${orgName.replace(/[^a-z0-9]/gi, '_')}_users_export.csv`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setResult(`Exported ${data.users.length} users for ${orgName}`);
        }
      } else {
        setError('Failed to fetch users for export');
      }
    } catch (err) {
      setError('Export failed: ' + err.message);
    }
    setLoading(false);
  };

  // Handle saving organization note
  const handleSaveNote = async () => {
    if (!editingNoteOrg) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/organizations/${editingNoteOrg.id}/note`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ note: noteInput })
      });

      const data = await res.json();

      if (res.ok) {
        setResult('Organization note updated');
        setEditingNoteOrg(null);
        fetchOrganizations(); // Refresh to show new note if we display it
      } else {
        setError(data.error || 'Failed to update note');
      }
    } catch (err) {
      setError('Update failed: ' + err.message);
    }
    setLoading(false);
  };

  // Backup Keys Management
  const fetchBackupKeys = async () => {
    setLoadingBackupKeys(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/backup-keys`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setBackupKeysData(data);
      }
    } catch (err) {
      console.error('Failed to fetch backup keys:', err);
    }
    setLoadingBackupKeys(false);
  };

  const handleToggleBackupKeys = async (enabled) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/backup-keys/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ enabled })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(`Backup keys feature ${data.enabled ? 'enabled' : 'disabled'}`);
        fetchBackupKeys();
      } else {
        setError(data.error || 'Failed to toggle');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleImportBackupKeys = async (e) => {
    e.preventDefault();
    if (!backupKeyImportText.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/backup-keys/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ keys: backupKeyImportText, durationMonths: backupKeyImportDuration })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data.message);
        setBackupKeyImportText('');
        fetchBackupKeys();
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Network error');
    }
    setLoading(false);
  };

  const handleDeleteBackupKeys = async (durationMonths) => {
    const msg = durationMonths
      ? `Delete ALL available ${durationMonths}-month keys?`
      : 'Delete ALL available backup keys?';
    if (!window.confirm(msg)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/backup-keys`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ durationMonths })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(`Deleted ${data.removed} key(s). ${data.remaining} remaining.`);
        fetchBackupKeys();
      } else {
        setError(data.error || 'Delete failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  // Marketplace Management
  /**
   * The durations this shop sells, from the server.
   *
   * Rendering the grid from a constant in this file is what made "2 years"
   * impossible: the value existed nowhere the admin could add it. The module
   * level MARKETPLACE_MONTHS is updated so the handful of helpers defined
   * above (which run outside React) see the same list.
   */
  const fetchMarketplaceDurations = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/marketplace/durations', { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data?.success && Array.isArray(data.durations)) {
        setMarketplaceDurations(data.durations);
        MARKETPLACE_MONTHS = data.durations.map(d => d.value);
      }
    } catch (e) {
      /* the built-in list still renders */
    }
  };

  const addMarketplaceDuration = async () => {
    const raw = window.prompt(
      'Add a duration.\n\n' +
      'A number of months (24 = 2 years), 0 for lifetime, or a negative number of days (-30 = 30 days).',
      '24');
    if (raw === null) return;
    const token = localStorage.getItem('adminToken');
    const res = await fetch('/api/admin/marketplace/durations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ value: raw.trim() })
    });
    const data = await res.json();
    if (!res.ok || data.error) return setError(data.error || 'Could not add that duration');
    setResult(data.message);
    fetchMarketplaceDurations();
  };

  const removeMarketplaceDuration = async (d) => {
    if (!window.confirm(`Remove "${d.label}" from the pricing grid?\n\nProducts already priced against it keep their price.`)) return;
    const token = localStorage.getItem('adminToken');
    const res = await fetch(`/api/admin/marketplace/durations/${d.value}`, {
      method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (!res.ok || data.error) return setError(data.error || 'Could not remove that duration');
    setResult(data.message);
    fetchMarketplaceDurations();
  };

  const fetchMarketplace = async () => {
    setLoadingMarketplace(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMarketplaceData(data);
      }
    } catch (err) {
      console.error('Failed to fetch marketplace:', err);
    }
    setLoadingMarketplace(false);
  };

  const handleToggleMarketplace = async (enabled) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ enabled })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(`Marketplace ${data.enabled ? 'enabled' : 'disabled'}`);
        fetchMarketplace();
      } else {
        setError(data.error || 'Failed to toggle');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.name.trim()) { setError('Product name is required'); return; }
    setLoading(true); setError(null); setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: newProduct.name,
          imageUrl: newProduct.imageUrl,
          description: newProduct.description,
          priceByMonth: newProduct.priceByMonth,
          enabledMonths: newProduct.enabledMonths,
          category: newProduct.category,
          requiresEmail: newProduct.requiresEmail,
          pinned: newProduct.pinned,
          warrantyType: newProduct.warrantyType || '',
          warrantyDays: newProduct.warrantyDays ? Number(newProduct.warrantyDays) : null,
          visibility: newProduct.visibility,
          allowedResellerIds: newProduct.allowedResellerIds
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(`Product "${data.product.name}" created`);
        setNewProduct(createMarketplaceProductForm());
        fetchMarketplace();
      } else {
        setError(data.error || 'Failed to create product');
      }
    } catch (err) {
      setError('Network error');
    }
    setLoading(false);
  };

  const handleUpdateProduct = async (productId) => {
    setLoading(true); setError(null); setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/products/${productId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          name: editProductForm.name,
          imageUrl: editProductForm.imageUrl,
          description: editProductForm.description,
          priceByMonth: editProductForm.priceByMonth,
          enabledMonths: editProductForm.enabledMonths,
          active: editProductForm.active,
          category: editProductForm.category,
          requiresEmail: editProductForm.requiresEmail,
          pinned: editProductForm.pinned,
          warrantyType: editProductForm.warrantyType || '',
          warrantyDays: editProductForm.warrantyDays ? Number(editProductForm.warrantyDays) : null,
          visibility: editProductForm.visibility,
          allowedResellerIds: editProductForm.allowedResellerIds
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult('Product updated');
        setEditingProductId(null);
        fetchMarketplace();
      } else {
        setError(data.error || 'Failed to update product');
      }
    } catch (err) {
      setError('Network error');
    }
    setLoading(false);
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!window.confirm(`Delete product "${productName}"? All its codes and order history will be removed.`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/products/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data.message);
        fetchMarketplace();
      } else {
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const handleImportProductCodes = async (e) => {
    e.preventDefault();
    if (!importCodesForProductId || !importCodesText.trim()) return;
    setLoading(true); setError(null); setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/products/${importCodesForProductId}/import-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ codes: importCodesText, durationMonths: importCodesDuration, allowDuplicates: importAllowDuplicates })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data.message);
        setImportCodesText('');
        setImportCodesForProductId(null);
        setImportCodesDuration(1);
        setImportAllowDuplicates(false);
        fetchMarketplace();
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Network error');
    }
    setLoading(false);
  };

  // Open (or toggle shut) the inline codes viewer for a product and load its
  // codes. Replaces the old behaviour where "Codes" silently deleted them all.
  const openProductCodes = async (product) => {
    if (viewCodesProductId === product.id) { setViewCodesProductId(null); return; }
    setViewCodesProductId(product.id);
    setViewCodesData(null);
    setViewCodesLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/products/${product.id}/codes`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok && data.success) setViewCodesData(data);
      else setError(data.error || 'Could not load codes');
    } catch (e) {
      setError('Network error');
    } finally {
      setViewCodesLoading(false);
    }
  };

  const copyProductCodes = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setResult(label);
    } catch (e) {
      setError('Could not copy to clipboard');
    }
  };

  const deleteOneProductCode = async (productId, codeId) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/products/${productId}/codes/${codeId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setViewCodesData(prev => prev ? { ...prev, codes: prev.codes.filter(c => c.id !== codeId), total: Math.max(0, (prev.total || 0) - 1) } : prev);
        fetchMarketplace(); // keep the card's stock count in step
      } else {
        setError(data.error || 'Delete failed');
      }
    } catch (e) {
      setError('Network error');
    }
  };

  const handleDeleteProductCodes = async (productId, productName, includeOrders) => {
    const msg = includeOrders
      ? `Delete ALL available codes AND order history for "${productName}"?`
      : `Delete ALL available codes for "${productName}"? Order history is kept.`;
    if (!window.confirm(msg)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/products/${productId}/codes`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ includeOrders: !!includeOrders })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(`Removed ${data.removedAvailable} available code(s)${data.removedOrders ? ` and ${data.removedOrders} order(s)` : ''}.`);
        // If the viewer is open on this product, empty it too.
        setViewCodesData(prev => (prev && viewCodesProductId === productId) ? { ...prev, codes: [], total: 0 } : prev);
        fetchMarketplace();
      } else {
        setError(data.error || 'Delete failed');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  const fetchMarketplaceOrders = async (page = 1, search = marketplaceOrdersSearch, status = marketplaceOrdersStatus) => {
    setLoadingMarketplaceOrders(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({ page: String(page), pageSize: '100' });
      if (search && search.trim()) params.set('search', search.trim());
      if (status && status !== 'all') params.set('status', status);
      const res = await fetch(`${API_URL}/admin/marketplace/orders?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMarketplaceOrders(data.orders || []);
        setMarketplaceOrdersPage(data.pagination?.page || 1);
        setMarketplaceOrdersTotalPages(data.pagination?.totalPages || 1);
        setMarketplaceOrdersTotal(data.pagination?.total ?? (data.orders || []).length);
        if (data.counts) setMarketplaceOrdersCounts(data.counts);
        setShowMarketplaceOrders(true);
        // Viewing orders marks them as seen — advance the notification baseline
        if (data.maxOrderId != null) {
          localStorage.setItem('mpLastSeenOrderId', String(data.maxOrderId));
        }
      } else {
        setError(data.error || 'Failed to fetch orders');
      }
    } catch (err) {
      setError('Network error');
    } finally {
      setLoadingMarketplaceOrders(false);
    }
  };

  // Manual orders never took a code out of stock, so don't promise one back.
  const isManualOrder = (order) => order?.fulfillmentStatus === 'pending' || order?.fulfillmentStatus === 'completed';

  const openShipOrder = (order) => {
    setShippingOrder(order);
    setShipInfo(order.deliveredInfo || '');
  };

  const handleShipMarketplaceOrder = async () => {
    if (!shippingOrder) return;
    const info = shipInfo.trim();
    if (!info) { setError('Enter the information to send to the reseller.'); return; }
    setShipBusy(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/orders/${shippingOrder.id}/fulfil`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ deliveredInfo: info })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(`Order #${shippingOrder.id} marked completed. ${shippingOrder.resellerUsername || 'The reseller'} can now see the details.`);
        setShippingOrder(null);
        setShipInfo('');
        fetchMarketplaceOrders(marketplaceOrdersPage, marketplaceOrdersSearch);
      } else {
        setError(data.error || 'Failed to ship order');
      }
    } catch (err) {
      setError('Network error');
    }
    setShipBusy(false);
  };

  const handleRefundMarketplaceOrder = async (order) => {
    const gc = order.priceGoldCoins ?? order.priceCredits;
    // Say plainly what happens to the code: it is retired so it can never be
    // sold again, which is the whole point of refunding a faulty one.
    const stockNote = (isManualOrder(order) || !order.code)
      ? ''
      : ', and the code will be retired so it cannot be sold again';
    if (!window.confirm(`Refund order #${order.id} (${order.productName})? ${gc} GC will be credited back to ${order.resellerUsername || `reseller #${order.resellerId}`}${stockNote}.`)) return;
    setRefundingOrderId(order.id);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/orders/${order.id}/refund`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(data.message);
        // Reload in place: the order stays in the list, now marked Refunded.
        fetchMarketplaceOrders(marketplaceOrdersPage, marketplaceOrdersSearch, marketplaceOrdersStatus);
      } else {
        setError(data.error || 'Failed to refund order');
      }
    } catch (err) {
      setError('Network error');
    }
    setRefundingOrderId(null);
  };

  // ---- Marketplace Promotions handlers ----
  const fetchMarketplacePromotions = async () => {
    setLoadingPromotions(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/promotions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) setMarketplacePromotions(data.promotions || []);
    } catch (err) {
      console.error('Failed to fetch promotions:', err);
    }
    setLoadingPromotions(false);
  };

  const handleSavePromotion = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null); setResult(null);
    try {
      // Duration preset -> concrete start/end timestamps.
      let startsAt = null;
      let endsAt = null;
      const preset = PROMO_DURATION_PRESETS.find(p => p.key === promoForm.durationPreset);
      if (preset && preset.hours) {
        endsAt = new Date(Date.now() + preset.hours * 3600 * 1000).toISOString();
      } else if (promoForm.durationPreset === 'custom') {
        startsAt = promoForm.startsAt ? new Date(promoForm.startsAt).toISOString() : null;
        endsAt = promoForm.endsAt ? new Date(promoForm.endsAt).toISOString() : null;
      }
      const payload = {
        name: promoForm.name,
        promoType: promoForm.promoType,
        discountPercent: Number(promoForm.discountPercent),
        productIds: promoForm.productIds,
        badgeText: promoForm.badgeText,
        badgeColor: promoForm.badgeColor,
        startsAt,
        endsAt,
        active: promoForm.active
      };
      const token = localStorage.getItem('adminToken');
      const url = editingPromoId
        ? `${API_URL}/admin/marketplace/promotions/${editingPromoId}`
        : `${API_URL}/admin/marketplace/promotions`;
      const res = await fetch(url, {
        method: editingPromoId ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult(editingPromoId ? 'Promotion updated' : `Promotion "${data.promotion.name}" created`);
        setPromoForm(createPromotionForm());
        setEditingPromoId(null);
        setShowPromoForm(false);
        fetchMarketplacePromotions();
      } else {
        setError(data.error || 'Failed to save promotion');
      }
    } catch (err) {
      setError('Network error');
    }
    setLoading(false);
  };

  const handleTogglePromotionActive = async (promo) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/promotions/${promo.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ active: !promo.active })
      });
      const data = await res.json();
      if (res.ok && data.success) fetchMarketplacePromotions();
      else setError(data.error || 'Failed to update promotion');
    } catch (err) {
      setError('Network error');
    }
  };

  const handleDeletePromotion = async (promo) => {
    if (!window.confirm(`Delete promotion "${promo.name}"? Products go back to full price immediately.`)) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/marketplace/promotions/${promo.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult('Promotion deleted');
        fetchMarketplacePromotions();
      } else {
        setError(data.error || 'Failed to delete promotion');
      }
    } catch (err) {
      setError('Network error');
    }
  };

  // ---- CP Dashboard config (modern dashboard + banners) handlers ----
  const fetchCpDashboardConfig = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/cp-dashboard/config`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) setCpDashboardConfig(data.config);
    } catch (err) {
      console.error('Failed to fetch CP dashboard config:', err);
    }
  };

  const handleSaveCpDashboardToggles = async (patch) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/cp-dashboard/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(patch)
      });
      const data = await res.json();
      if (res.ok && data.success) setCpDashboardConfig(data.config);
      else setError(data.error || 'Failed to save dashboard settings');
    } catch (err) {
      setError('Network error');
    }
  };

  const handleUploadBanner = async (e) => {
    e.preventDefault();
    if (!bannerForm.file) { setError('Choose a banner image first'); return; }
    setUploadingBanner(true); setError(null); setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const fd = new FormData();
      fd.append('banner', bannerForm.file);
      fd.append('title', bannerForm.title);
      fd.append('subtitle', bannerForm.subtitle);
      if (bannerForm.linkTab) fd.append('linkTab', bannerForm.linkTab);
      if (bannerForm.linkProductId) fd.append('linkProductId', bannerForm.linkProductId);
      const res = await fetch(`${API_URL}/admin/cp-dashboard/banners`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult('Banner uploaded');
        setCpDashboardConfig(data.config);
        setBannerForm({ file: null, title: '', subtitle: '', linkTab: '', linkProductId: '' });
      } else {
        setError(data.error || 'Failed to upload banner');
      }
    } catch (err) {
      setError('Network error');
    }
    setUploadingBanner(false);
  };

  const handleBannerPatch = async (bannerId, patch) => {
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/cp-dashboard/banners/${bannerId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(patch)
      });
      const data = await res.json();
      if (res.ok && data.success) setCpDashboardConfig(data.config);
      else setError(data.error || 'Failed to update banner');
    } catch (err) {
      setError('Network error');
    }
  };

  const handleDeleteBanner = async (bannerId) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/cp-dashboard/banners/${bannerId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) setCpDashboardConfig(data.config);
      else setError(data.error || 'Failed to delete banner');
    } catch (err) {
      setError('Network error');
    }
  };

  const fetchMigrationSettings = async () => {
    setLoadingMigrationSettings(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/migration-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMigrationSettings(data.settings || DEFAULT_ADMIN_MIGRATION_SETTINGS);
        setMigrationSettingsDefaults(data.defaults || DEFAULT_ADMIN_MIGRATION_SETTINGS);
        setMigrationPresets(data.presets || []);
      } else {
        setError(data.error || 'Failed to load migration settings');
      }
    } catch (err) {
      setError('Failed to load migration settings: ' + err.message);
    }
    setLoadingMigrationSettings(false);
  };

  const handleSaveMigrationSettings = async (e) => {
    if (e) e.preventDefault();

    setSavingMigrationSettings(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/migration-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(migrationSettings)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMigrationSettings(data.settings || DEFAULT_ADMIN_MIGRATION_SETTINGS);
        setResult(data.message || 'Migration settings saved');
      } else {
        setError(data.error || 'Failed to save migration settings');
      }
    } catch (err) {
      setError('Failed to save migration settings: ' + err.message);
    }

    setSavingMigrationSettings(false);
  };

  const handleLoadMigrationPreset = (preset) => {
    if (!preset || !preset.settings) return;
    setError(null);
    setMigrationSettings({ ...preset.settings });
    setResult(`${preset.name} preset loaded. Save settings to apply it.`);
  };

  const handleResetMigrationSettings = () => {
    setError(null);
    setMigrationSettings({ ...migrationSettingsDefaults });
    setResult('Default migration settings loaded. Save settings to apply them.');
  };

  const fetchActivationSettings = async () => {
    setLoadingActivationSettings(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/activation-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActivationSettings(data.settings || { pausedMonths: [], monthOptions: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] });
      } else {
        setError(data.error || 'Failed to load activation settings');
      }
    } catch (err) {
      setError('Failed to load activation settings: ' + err.message);
    }
    setLoadingActivationSettings(false);
  };

  const handleTogglePausedActivationMonth = (month) => {
    const paused = activationSettings.pausedMonths || [];
    const nextPaused = paused.includes(month)
      ? paused.filter(m => m !== month)
      : [...paused, month].sort((a, b) => a - b);
    setActivationSettings({ ...activationSettings, pausedMonths: nextPaused });
    handleSaveActivationSettings(nextPaused);
  };

  const handleSaveActivationSettings = async (pausedMonthsOverride = null) => {
    setSavingActivationSettings(true);
    setError(null);
    setResult(null);
    const pausedMonths = Array.isArray(pausedMonthsOverride)
      ? pausedMonthsOverride
      : (activationSettings.pausedMonths || []);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/activation-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ pausedMonths })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setActivationSettings(data.settings || activationSettings);
        setResult(data.message || 'Activation settings saved');
      } else {
        setError(data.error || 'Failed to save activation settings');
      }
    } catch (err) {
      setError('Failed to save activation settings: ' + err.message);
    }

    setSavingActivationSettings(false);
  };

  const fetchBulkSettings = async () => {
    setLoadingBulkSettings(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/bulk-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setBulkSettings({ ...DEFAULT_BULK_SETTINGS, ...(data.settings || {}) });
        setBulkRunning(data.running || 0);
        setBulkSettingsLoaded(true);
      } else {
        setError(data.error || 'Failed to load bulk settings');
      }
    } catch (err) {
      setError('Failed to load bulk settings: ' + err.message);
    }
    setLoadingBulkSettings(false);
  };

  const handleSaveBulkSettings = async (e) => {
    if (e) e.preventDefault();
    // Never save the placeholder defaults: they would overwrite the real settings.
    if (!bulkSettingsLoaded) {
      setError('The current settings have not loaded yet. Click Refresh, then change and save.');
      return;
    }
    setSavingBulkSettings(true);
    setError(null);
    setResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/bulk-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(bulkSettings)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setBulkSettings({ ...DEFAULT_BULK_SETTINGS, ...(data.settings || {}) });
        setResult(data.message || 'Bulk settings saved');
        // The message above appears at the top of the page, out of sight from the
        // Save button - confirm right beside it too.
        setBulkJustSaved(true);
        setTimeout(() => setBulkJustSaved(false), 4000);
      } else {
        setError(data.error || 'Failed to save bulk settings');
      }
    } catch (err) {
      setError('Failed to save bulk settings: ' + err.message);
    }
    setSavingBulkSettings(false);
  };

  // Email Whitelist Management
  const fetchEmailWhitelist = async () => {
    setLoadingWhitelist(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/email-whitelist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setEmailWhitelist(data.settings);
        if (data.settings.domains && data.settings.domains.length > 0) {
          setWhitelistInput(data.settings.domains.join('\n'));
        }
      } else {
        setError(data.error || 'Failed to fetch whitelist');
      }
    } catch (err) {
      setError('Failed to fetch whitelist');
    }
    setLoadingWhitelist(false);
  };

  const handleUpdateEmailWhitelist = async (enabled, domains, mode, organizationRules) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/email-whitelist`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ enabled, domains, mode: mode || emailWhitelist.mode || 'global', organizationRules: organizationRules || emailWhitelist.organizationRules || [] })
      });
      const data = await res.json();
      if (data.success) {
        setEmailWhitelist(data.settings);
        setResult('Whitelist updated successfully');
      } else {
        setError(data.error || 'Failed to update whitelist');
      }
    } catch (err) {
      setError('Failed to update whitelist: ' + err.message);
    }
    setLoading(false);
  };

  // Supplier API (connection test only — no product sync yet)
  const fetchSupplierSettings = async () => {
    setLoadingSupplierSettings(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/supplier/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSupplierSettings(data);
        setSupplierBaseUrlInput(data.baseUrl || '');
      } else {
        setError(data.error || 'Failed to fetch supplier API settings');
      }
    } catch (err) {
      setError('Failed to fetch supplier API settings: ' + err.message);
    }
    setLoadingSupplierSettings(false);
  };

  const handleSaveSupplierApiKey = async (e) => {
    e.preventDefault();
    setSavingSupplierKey(true);
    setError(null);
    setSupplierTestResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/supplier/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ apiKey: supplierApiKeyInput || undefined, baseUrl: supplierBaseUrlInput || undefined })
      });
      const data = await res.json();
      if (data.success) {
        setSupplierSettings(data);
        setSupplierApiKeyInput('');
        setSupplierBaseUrlInput(data.baseUrl || supplierBaseUrlInput);
        setResult('Supplier API key saved');
      } else {
        setError(data.error || 'Failed to save supplier API key');
      }
    } catch (err) {
      setError('Failed to save supplier API key: ' + err.message);
    }
    setSavingSupplierKey(false);
  };

  const handleTestSupplierConnection = async () => {
    setTestingSupplierConnection(true);
    setSupplierTestResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/supplier/test-connection`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSupplierTestResult(data);
    } catch (err) {
      setSupplierTestResult({ success: false, error: 'Failed to reach the server: ' + err.message });
    }
    setTestingSupplierConnection(false);
  };

  const handlePreviewSupplierProducts = async () => {
    setLoadingSupplierProducts(true);
    setSupplierProductsPreview(null);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({ markup: supplierMarkup || '0', limit: '25' });
      const res = await fetch(`${API_URL}/admin/supplier/products?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setSupplierProductsPreview(data);
    } catch (err) {
      setSupplierProductsPreview({ success: false, error: 'Failed to fetch supplier products: ' + err.message });
    }
    setLoadingSupplierProducts(false);
  };

  const handleImportSupplierProducts = async () => {
    if (!window.confirm(`Import/update supplier products with $${supplierMarkup || 0} markup?`)) return;
    setImportingSupplierProducts(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/supplier/import-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ markup: supplierMarkup || 0, limit: 5000, active: true })
      });
      const data = await res.json();
      setSupplierProductsPreview(data);
      if (data.success) {
        setResult(`Imported supplier products: ${data.created} created, ${data.updated} updated`);
      } else {
        setError(data.error || 'Failed to import supplier products');
      }
    } catch (err) {
      setError('Failed to import supplier products: ' + err.message);
    }
    setImportingSupplierProducts(false);
  };

  // Embed Allowed Domains (CSP frame-ancestors allowlist for the public status page)
  const fetchEmbedDomains = async () => {
    setLoadingEmbedDomains(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/embed-domains`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) setEmbedDomains(data.domains || []);
      else setError(data.error || 'Failed to fetch embed domains');
    } catch (err) {
      setError('Failed to fetch embed domains: ' + err.message);
    }
    setLoadingEmbedDomains(false);
  };

  const handleAddEmbedDomain = async (e) => {
    e.preventDefault();
    if (!embedDomainInput.trim()) return;
    setAddingEmbedDomain(true);
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/embed-domains`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ origin: embedDomainInput.trim(), note: embedDomainNote.trim() })
      });
      const data = await res.json();
      if (data.success) {
        setEmbedDomainInput('');
        setEmbedDomainNote('');
        setResult('Domain approved for embedding');
        fetchEmbedDomains();
      } else {
        setError(data.error || 'Failed to add domain');
      }
    } catch (err) {
      setError('Failed to add domain: ' + err.message);
    }
    setAddingEmbedDomain(false);
  };

  const handleRemoveEmbedDomain = async (id, origin) => {
    if (!window.confirm(`Remove embed permission for ${origin}? They will no longer be able to embed the status page.`)) return;
    setError(null);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`${API_URL}/admin/embed-domains/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setResult('Domain removed');
        fetchEmbedDomains();
      } else {
        setError(data.error || 'Failed to remove domain');
      }
    } catch (err) {
      setError('Failed to remove domain: ' + err.message);
    }
  };

  // Fetch transactions and users for a specific CP reseller
  const fetchCPResellerDetails = async (resellerId) => {
    setLoadingCPResellerDetails(true);
    setCpDetailSearch('');
    try {
      const token = localStorage.getItem('adminToken');
      // Use the main admin endpoints (authenticated with the same adminToken as
      // the rest of this page). The /credits-pool/* router mounts its own admin
      // auth keyed to a different secret and rejected this token, which is why
      // the modal showed 0 transactions / 0 users even for busy resellers.
      const [transRes, usersRes] = await Promise.all([
        fetch(`${API_URL}/admin/credits-pool-resellers/${resellerId}/transactions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/admin/credits-pool-resellers/${resellerId}/users`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      const transData = await transRes.json();
      const usersData = await usersRes.json();
      if (transData.success) setCpResellerTransactions(transData.transactions || []);
      if (usersData.success) {
        // This endpoint returns raw user rows; derive the display fields the
        // table expects (the previous endpoint computed these server-side).
        const now = Date.now();
        setCpResellerUsers((usersData.users || []).map(u => {
          const exp = u.expiresAt ? new Date(u.expiresAt) : null;
          const daysLeft = exp ? Math.ceil((exp.getTime() - now) / 86400000) : 0;
          return {
            ...u,
            isDeleted: !!u.deletedAt,
            isExpired: exp ? exp.getTime() < now : false,
            daysLeft: daysLeft > 0 ? daysLeft : 0
          };
        }));
      }
    } catch (err) {
      console.error('Failed to fetch CP reseller details:', err);
    }
    setLoadingCPResellerDetails(false);
  };



  const handleViewResellerAnnouncements = async (reseller) => {
    setLoadingResellerAnnouncements(true);
    setViewingResellerAnnouncements({ resellerId: reseller.id, resellerName: reseller.username, announcements: [] });

    try {
      const token = localStorage.getItem('adminToken');
      // We'll fetch all active announcements and filter client-side or use a specific endpoint if one exists
      // For now, let's just fetch all admin announcements and filter by target logic
      const res = await fetch('/api/admin/announcements', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();

      if (res.ok && data.announcements) {
        const specific = data.announcements.filter(a => {
          if (a.target === 'all') return true;
          if (a.target === 'specific' && a.specificResellerIds && a.specificResellerIds.includes(reseller.id)) return true;
          return false;
        });
        setViewingResellerAnnouncements(prev => ({ ...prev, announcements: specific }));
      }
    } catch (err) {
      console.error('Failed to fetch reseller announcements:', err);
    }
    setLoadingResellerAnnouncements(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOrgForAssign) {
      setError('Please select an organization first');
      return;
    }
    if (selectedProductsForAssign.length === 0) {
      setError('Please select at least one product');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      let res, data;

      // If a reseller is selected, use the reseller endpoint
      if (selectedResellerForAssign) {
        res = await fetch(`/api/admin/resellers/${selectedResellerForAssign.id}/assign-user`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email,
            organizationId: selectedOrgForAssign.id,
            products: selectedProductsForAssign,
            durationMonths: assignDurationMonths,
            customExpiryDate: assignCustomExpiry || undefined
          }),
        });
      } else {
        // Direct assignment without reseller
        res = await fetch('/api/admin/assign-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            email,
            organizationId: selectedOrgForAssign.id,
            products: selectedProductsForAssign
          }),
        });
      }

      data = await res.json();

      if (!res.ok || data.error) {
        setError(data.error || data.message || 'Failed to assign user');
      } else {
        setResult(data.message || 'User assigned successfully!');
        setEmail('');
        setAssignCustomExpiry('');
        // Refresh reseller data if assigned to reseller
        if (selectedResellerForAssign) {
          fetchResellers();
        }
      }
    } catch (err) {
      setError('Failed to connect to server: ' + err.message);
    }
    setLoading(false);
  };

  const handleBulkAssign = async (e) => {
    e.preventDefault();
    if (!selectedOrgForAssign) {
      setError('Please select an organization first');
      return;
    }
    if (selectedProductsForAssign.length === 0) {
      setError('Please select at least one product');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);

    // Parse emails from CSV content
    const allEmails = csvContent
      .split(/[,\n\r]+/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));

    if (allEmails.length === 0) {
      setError('No valid email addresses found');
      setLoading(false);
      return;
    }

    // CHUNKED PROCESSING: Send 100 users per request to avoid timeout
    const CHUNK_SIZE = 100;
    let totalSuccess = 0;
    let totalSkipped = 0;
    let totalFailed = 0;
    const allFailures = [];

    const totalChunks = Math.ceil(allEmails.length / CHUNK_SIZE);

    for (let i = 0; i < allEmails.length; i += CHUNK_SIZE) {
      const chunk = allEmails.slice(i, i + CHUNK_SIZE);
      const chunkNum = Math.floor(i / CHUNK_SIZE) + 1;

      // Update progress message
      setResult(`Processing chunk ${chunkNum}/${totalChunks} (${i + chunk.length}/${allEmails.length} users)...`);

      try {
        const token = localStorage.getItem('adminToken');
        const res = await fetch('/api/admin/bulk-assign-users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            emails: chunk,
            organizationId: selectedOrgForAssign.id,
            products: selectedProductsForAssign,
            resellerId: selectedResellerForAssign?.id || null,
            durationMonths: assignDurationMonths
          }),
        });

        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch (parseError) {
          // If JSON parse fails, count chunk as failed
          totalFailed += chunk.length;
          allFailures.push(`Chunk ${chunkNum}: Invalid response`);
          continue;
        }

        if (data.error) {
          totalFailed += chunk.length;
          allFailures.push(`Chunk ${chunkNum}: ${data.error}`);
        } else {
          totalSuccess += data.newUsers || 0;
          totalSkipped += data.skippedUsers || 0;
          totalFailed += data.failCount || 0;
          if (data.failures) {
            allFailures.push(...data.failures);
          }
        }
      } catch (err) {
        totalFailed += chunk.length;
        allFailures.push(`Chunk ${chunkNum}: ${err.message}`);
      }
    }

    // Final result
    let resultMessage = `Completed! ${totalSuccess} new users added`;
    if (totalSkipped > 0) {
      resultMessage += `, ${totalSkipped} already existed`;
    }
    if (totalFailed > 0) {
      resultMessage += `, ${totalFailed} failed`;
    }

    setResult(resultMessage);
    setCsvContent('');

    // Refresh reseller data if assigned to reseller
    if (selectedResellerForAssign) {
      if (selectedResellerForAssign.type === 'credits-pool') {
        fetchCPResellers();
      } else {
        fetchResellers();
      }
    }

    // Show failures if any
    if (allFailures.length > 0) {
      setError('Some failures:\n' + allFailures.slice(0, 5).join('\n') +
        (allFailures.length > 5 ? `\n...and ${allFailures.length - 5} more` : ''));
    }

    setLoading(false);
  };

  // Export all customers to CSV
  const exportAllCustomers = async () => {
    setLoadingCustomers(true);
    setError(null);

    try {
      // Fetch ALL users without pagination for export
      const params = new URLSearchParams({
        page: '1',
        limit: 'all',
        search: customerSearchQuery || '',
        orgFilter: customerOrgFilter || '',
        durationFilter: customerDurationFilter || '',
        statusFilter: customerStatusFilter || '',
        daysFilter: customerDaysFilter || ''
      });
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/all-users?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();

      if (!data.success || !data.users || data.users.length === 0) {
        setError('No customers to export');
        setLoadingCustomers(false);
        return;
      }

      const allUsersForExport = data.users;

      // Create CSV content with headers
      const headers = ['Email', 'Reseller', 'Organization', 'Products', 'Duration (months)', 'Status', 'Days Left', 'Assigned At', 'Expires At'];
      const rows = allUsersForExport.map(user => [
        user.email,
        user.resellerName || 'Unknown',
        user.organizationName || 'Unknown',
        (user.products || []).join('; '),
        user.durationMonths || 1,
        user.status || 'active',
        user.daysLeft !== null ? user.daysLeft : '',
        user.assignedAt ? new Date(user.assignedAt).toLocaleDateString() : '',
        user.expiresAt ? new Date(user.expiresAt).toLocaleDateString() : ''
      ]);

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      ].join('\n');

      // Create and download the file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `all_customers_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();

      setResult(`Exported ${allUsersForExport.length} customers to CSV`);
    } catch (err) {
      setError('Failed to export customers: ' + err.message);
    }
    setLoadingCustomers(false);
  };

  // Delete a customer from reseller's records
  const handleDeleteCustomer = async (customer, options = {}) => {
    const forceLocal = options.forceLocal === true;
    const confirmMessage = forceLocal
      ? `Override Adobe removal failure and delete ${customer.email} from local records only?\n\nThis will NOT remove the user from Adobe.`
      : `Are you sure you want to delete ${customer.email}?\n\nThis will remove them from the reseller's records after Adobe removal succeeds.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    if (!forceLocal) {
      setDeleteOverrideCandidate(null);
    }

    try {
      const token = localStorage.getItem('adminToken');
      const url = `/api/admin/reseller-users/${customer.resellerId}/${encodeURIComponent(customer.email)}${forceLocal ? '?forceLocal=true' : ''}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          ...(forceLocal ? { 'X-Force-Local-Delete': 'true' } : {})
        }
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        if (data.allowLocalOverride) {
          setDeleteOverrideCandidate({ customer, message: data.error || 'Adobe removal failed' });
        }
        setError(data.error || 'Failed to delete customer');
      } else {
        setDeleteOverrideCandidate(null);
        setResult(data.localOnly
          ? `Override complete: removed ${customer.email} from local records only.`
          : `Successfully deleted ${customer.email}`);
        fetchAllCustomers();
      }
    } catch (err) {
      setError('Failed to delete customer: ' + err.message);
    }
    setLoading(false);
  };

  // Update a customer's details
  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    if (!editingCustomer) return;

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch(`/api/admin/reseller-users/${editingCustomer.resellerId}/${encodeURIComponent(editingCustomer.email)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          products: editCustomerForm.products,
          // Only when changed: the server recalculates expiry from the start date
          // whenever a duration arrives, which would undo earlier extensions.
          durationMonths: (editCustomerForm.customExpiryDate || editCustomerForm.durationMonths !== (editingCustomer.durationMonths || 1))
            ? editCustomerForm.durationMonths : undefined,
          customExpiryDate: editCustomerForm.customExpiryDate || undefined,
          organizationId: editCustomerForm.organizationId || undefined
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || 'Failed to update customer');
      } else {
        setResult(data.orgMove ? data.message : `Successfully updated ${editingCustomer.email}`);
        setEditingCustomer(null);
        fetchAllCustomers();
      }
    } catch (err) {
      setError('Failed to update customer: ' + err.message);
    }
    setLoading(false);
  };

  // Bulk delete selected customers
  const handleBulkDeleteCustomers = async () => {
    if (selectedCustomers.length === 0) {
      setError('No customers selected');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedCustomers.length} selected customers?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    let successCount = 0;
    let failCount = 0;

    const token = localStorage.getItem('adminToken');
    for (const customer of selectedCustomers) {
      try {
        const res = await fetch(`/api/admin/reseller-users/${customer.resellerId}/${encodeURIComponent(customer.email)}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }
    }

    setLoading(false);
    setSelectedCustomers([]);
    fetchAllCustomers();

    if (failCount === 0) {
      setResult(`Successfully deleted ${successCount} customers`);
    } else {
      setResult(`Deleted ${successCount} customers. ${failCount} failed.`);
    }
  };

  // Bulk delete customers by pasting email addresses
  const handleBulkDeleteByEmail = async (e) => {
    e.preventDefault();

    // Parse emails from textarea
    const rawEmails = bulkDeleteEmails
      .split(/[\n,;]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email && email.includes('@'));

    if (rawEmails.length === 0) {
      setError('No valid email addresses found. Please enter at least one email address.');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${rawEmails.length} user(s)? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      // Send raw emails to server - server searches FULL database (not limited by pagination)
      const send = async (includeActive) => {
        const res = await fetch('/api/admin/bulk-delete-users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ emails: rawEmails, includeActive })
        });
        let data;
        try {
          data = await res.json();
        } catch {
          data = {};
        }
        return { res, data };
      };

      let { res, data } = await send(false);
      // The server skips subscriptions that are still active (paid, not expired)
      // unless asked explicitly; it reports them only as the remainder.
      const r1 = data.results || {};
      let skipped = Math.max(0, (r1.total || 0) - (r1.deleted || 0) - (r1.notFound || 0));
      let deleted = r1.deleted || 0;
      let alsoDeletedActive = 0;
      if (res.ok && skipped > 0 && window.confirm(
        `${deleted} deleted. ${skipped} were NOT deleted because they are still ACTIVE subscriptions (paid, not expired yet).\n\n` +
        `Delete these ${skipped} active customers too? They lose access and are removed from Adobe. This cannot be undone.`
      )) {
        ({ res, data } = await send(true));
        alsoDeletedActive = data.results?.deleted || 0;
        if (res.ok) skipped = Math.max(0, skipped - alsoDeletedActive);
      }
      setLoading(false);
      fetchAllCustomers();

      if (res.ok) {
        let resultMessage = `Deleted ${deleted + alsoDeletedActive} customer(s)` +
          (alsoDeletedActive ? ` (${alsoDeletedActive} of them still active).` : '.');
        if (r1.notFound > 0) {
          resultMessage += ` ${r1.notFound} not found in database.`;
        }
        if (skipped > 0) {
          resultMessage += ` ${skipped} NOT deleted: still active subscriptions.`;
        }
        if (deleted + alsoDeletedActive > 0) resultMessage += ' Adobe removal is running in the background.';
        setResult(resultMessage);
        if (skipped === 0) setBulkDeleteEmails('');
      } else {
        setError(data.error || `Server error (${res.status})`);
      }
    } catch (err) {
      setLoading(false);
      setError(`Bulk delete failed: ${err.message}`);
    }
  };

  // Bulk extend customers by pasting email addresses
  const handleBulkExtendByEmail = async (e) => {
    e.preventDefault();

    const rawEmails = bulkExtendEmails
      .split(/[\n,;]+/)
      .map(email => email.trim().toLowerCase())
      .filter(email => email && email.includes('@'));

    if (rawEmails.length === 0) {
      setError('No valid email addresses found.');
      return;
    }

    if (!window.confirm(`Extend ${rawEmails.length} user(s) by ${bulkExtendMonths} month(s)?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const res = await fetch('/api/admin/bulk-extend-users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ emails: rawEmails, extensionMonths: bulkExtendMonths })
      });

      const data = await res.json();
      setLoading(false);
      fetchAllCustomers();

      if (res.ok) {
        setResult(`Extended ${data.results?.extended || 0} user(s) by ${bulkExtendMonths} month(s). ${data.results?.notFound || 0} not found.`);
        setBulkExtendEmails('');
      } else {
        setError(data.error || 'Bulk extend failed');
      }
    } catch (err) {
      setLoading(false);
      setError(`Bulk extend failed: ${err.message}`);
    }
  };

  // Toggle customer selection
  const toggleCustomerSelection = (customer) => {
    const key = `${customer.resellerId}-${customer.email}`;
    if (selectedCustomers.some(c => `${c.resellerId}-${c.email}` === key)) {
      setSelectedCustomers(selectedCustomers.filter(c => `${c.resellerId}-${c.email}` !== key));
    } else {
      setSelectedCustomers([...selectedCustomers, customer]);
    }
  };

  // Toggle select all customers
  const toggleSelectAllCustomers = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers([...filteredCustomers]);
    }
  };

  const getMigrationRemainingCount = (migration) => {
    if (!migration) return 0;
    if (typeof migration.remainingCount === 'number') return migration.remainingCount;
    if (Array.isArray(migration.remainingUsers)) return migration.remainingUsers.length;
    return Math.max(0, (Number(migration.totalUsers) || 0) - (Number(migration.migratedCount) || 0));
  };

  const migrationSettingsPresetKey = migrationPresets.find((preset) =>
    ['batchSize', 'parallelBatches', 'baseDelayMs', 'progressFlushMs', 'maxRetries']
      .every((key) => Number(migrationSettings[key]) === Number(preset.settings?.[key]))
  )?.key || 'custom';
  const migrationUsersPerRound = (Number(migrationSettings.batchSize) || 0) * (Number(migrationSettings.parallelBatches) || 0);
  const migrationEstimatedUsersPerMinute = migrationUsersPerRound > 0 && (Number(migrationSettings.baseDelayMs) || 0) > 0
    ? Math.round((migrationUsersPerRound * 60000) / Number(migrationSettings.baseDelayMs))
    : 0;

  const getPageTitle = () => {
    switch (activeTab) {
      case 'dashboard': return 'Dashboard';
      case 'organizations': return 'Organizations';
      case 'resellers': return 'Manage Resellers';
      case 'resellerDetails': return `Reseller: ${selectedReseller?.username || ''}`;
      case 'assign': return 'Assign User';
      case 'customers': return 'All Customers';
      case 'cpResellers': return 'Credits Pool Resellers';
      case 'whitelist': return 'Email Whitelist';
      case 'embedDomains': return 'Embed Domains';
      case 'supplierApi': return 'Supplier API';
      case 'redeem': return 'Key Redemption';
      case 'manualOrg': return 'Manual Activations';
      case 'telegramShop': return 'Telegram Shop';
      case 'ipMonitor': return 'IP Monitor';
      case 'backupKeys': return 'Pre-Made Keys';
      case 'announcements': return 'Manage Announcements';
      case 'bulk': return 'Bulk Import (CSV)';
      case 'bulkOrg': return 'Bulk Organization Access';
      case 'adminLogs': return 'Admin Activity Logs';
      case 'apiMonitoring': return 'API Monitoring';
      case 'marketplace': return 'Marketplace';
      case 'settings': return 'Settings';
      default: return 'Dashboard';
    }
  };

  // Debug: Log render state
  const renderPath = window.location.pathname;
  const renderIsCreditsPool = renderPath.toLowerCase().includes('/credits-pool');
  const renderIsReseller = !renderIsCreditsPool && renderPath.toLowerCase().includes('/reseller');
  const renderIsBulkStatus = renderPath.toLowerCase().includes('/bulk-status');
  const renderIsStatus = !renderIsBulkStatus && renderPath.toLowerCase().includes('/status');
  console.log('Render state:', { renderPath, renderIsCreditsPool, renderIsReseller, renderIsStatus, renderIsBulkStatus, isCreditsPoolMode, isAdminMode, checkingAdminAuth });

  // PUBLIC BULK USER STATUS PORTAL (no auth required)
  if (renderIsBulkStatus) {
    return <BulkStatusPortal />;
  }

  // PUBLIC USER STATUS PORTAL - CHECK FIRST (no auth required)
  if (renderIsStatus) {
    return <UserStatusPortal />;
  }

  // CRITICAL: Check path DIRECTLY to avoid state issues
  // Credits Pool Reseller Mode - CHECK FIRST using path directly
  if (renderIsCreditsPool) {
    console.log('Rendering Credits Pool mode (path check) - BUILD v3');
    if (cpAuth.isLoggedIn) {
      return (
        <CpSecurityGate token={cpAuth.token} username={cpAuth.reseller?.username}
          onToken={t => setCpAuth(a => ({ ...a, token: t }))}
          onLogout={() => setCpAuth({ isLoggedIn: false, reseller: null, token: null })}>
          <CreditsPoolDashboard reseller={cpAuth.reseller} token={cpAuth.token} onLogout={() => setCpAuth({ isLoggedIn: false, reseller: null, token: null })} />
        </CpSecurityGate>
      );
    }
    return <CreditsPoolLogin onLogin={(reseller, token) => setCpAuth({ isLoggedIn: true, reseller, token })} />;
  }

  // Show loading while checking admin auth state  
  if (checkingAdminAuth) {
    console.log('Showing loading because checkingAdminAuth is true');
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Regular Reseller Mode  
  if (isResellerMode) {
    if (resellerAuth.isLoggedIn) {
      return <ResellerDashboard reseller={resellerAuth.reseller} token={resellerAuth.token} onLogout={() => setResellerAuth({ isLoggedIn: false, reseller: null, token: null })} />;
    }
    return <ResellerLogin onLogin={(reseller, token) => setResellerAuth({ isLoggedIn: true, reseller, token })} />;
  }

  // Admin Mode - Show login or admin panel
  if (isAdminMode && !adminAuth.isLoggedIn) {
    return <AdminLogin onLogin={(admin, token) => setAdminAuth({ isLoggedIn: true, admin, token })} />;
  }

  return (
    <div className="App">
      {/* Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <h1>Admin Console</h1>
          <p>Adobe User Management</p>
          {appVersion.version && (
            <span className="app-version-badge"
              title={appVersion.startedAt ? `Server started ${new Date(appVersion.startedAt).toLocaleString()}` : undefined}>
              v{appVersion.version}
            </span>
          )}
        </div>
        <nav className="nav-menu">
          <div
            className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => { setActiveTab('dashboard'); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="chart" /></span>
            <span>Dashboard</span>
          </div>
          <div
            className={`nav-item ${activeTab === 'organizations' ? 'active' : ''}`}
            onClick={() => { setActiveTab('organizations'); fetchOrganizations(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="building" /></span>
            <span>Organizations</span>
          </div>
          <div
            className={`nav-item ${activeTab === 'resellers' || activeTab === 'resellerDetails' ? 'active' : ''}`}
            onClick={() => { setActiveTab('resellers'); fetchResellers(); fetchOrganizations(); setSelectedReseller(null); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="store" /></span>
            <span>Resellers</span>
          </div>

          <div className="nav-divider"></div>

          <div
            className={`nav-item ${activeTab === 'assign' ? 'active' : ''}`}
            onClick={() => { setActiveTab('assign'); fetchOrganizations(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="plus" /></span>
            <span>Assign User</span>
          </div>
          <div
            className={`nav-item ${activeTab === 'bulk' ? 'active' : ''}`}
            onClick={() => { setActiveTab('bulk'); fetchOrganizations(); fetchResellers(); fetchCPResellers(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="file" /></span>
            <span>Bulk Import</span>
          </div>
          <div
            className={`nav-item ${activeTab === 'customers' ? 'active' : ''}`}
            onClick={() => { setActiveTab('customers'); fetchAllCustomers(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="users" /></span>
            <span>All Customers</span>
          </div>
          <div
            className={`nav-item ${activeTab === 'cpResellers' ? 'active' : ''}`}
            onClick={() => { setActiveTab('cpResellers'); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="card" /></span>
            <span>Credits Pool Resellers</span>
            {redemptionRequests.length > 0 && (
              <span
                onClick={(e) => { e.stopPropagation(); setRedeemThresholdInput(String(redeemThreshold)); fetchRedemptionRequests(); setShowRedemptionModal(true); }}
                title={`${redemptionRequests.length} refund redemption request(s) awaiting approval — click to review`}
                style={{ marginLeft: 'auto', background: '#ef4444', color: 'white', borderRadius: '999px', minWidth: '18px', height: '18px', padding: '0 6px', fontSize: '11px', fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                {redemptionRequests.length}
              </span>
            )}
          </div>
          <div
            className={`nav-item ${activeTab === 'cpMonitoring' ? 'active' : ''}`}
            onClick={() => { setActiveTab('cpMonitoring'); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="chart" /></span>
            <span>CP Monitoring</span>
          </div>
          <div
            className={`nav-item ${activeTab === 'apiMonitoring' ? 'active' : ''}`}
            onClick={() => { setActiveTab('apiMonitoring'); fetchApiMonitoring(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="plug" /></span>
            <span>API Monitor</span>
          </div>
          <div
            className={`nav-item ${activeTab === 'migrationLogs' ? 'active' : ''}`}
            onClick={() => { setActiveTab('migrationLogs'); fetchMigrationLogs(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="clipboard" /></span>
            <span>Migration Logs</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'adminLogs' ? 'active' : ''}`}
            onClick={() => { setActiveTab('adminLogs'); fetchAdminLogs(1); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="shield" /></span>
            <span>Admin Logs</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'whitelist' ? 'active' : ''}`}
            onClick={() => { setActiveTab('whitelist'); fetchEmailWhitelist(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="shield" /></span>
            <span>Email Whitelist</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'embedDomains' ? 'active' : ''}`}
            onClick={() => { setActiveTab('embedDomains'); fetchEmbedDomains(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="image" /></span>
            <span>Embed Domains</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'supplierApi' ? 'active' : ''}`}
            onClick={() => { setActiveTab('supplierApi'); fetchSupplierSettings(); setSupplierTestResult(null); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="key" /></span>
            <span>Supplier API</span>
          </div>

          {(manualOrgInfo.enabled || manualOrgInfo.pending > 0 || manualOrgInfo.toRemove > 0) && (
            <div
              className={`nav-item ${activeTab === 'manualOrg' ? 'active' : ''}`}
              onClick={() => { setActiveTab('manualOrg'); setError(null); setResult(null); }}
            >
              <span className="nav-icon"><Icon name="users" /></span>
              <span>Manual Activations</span>
              {(manualOrgInfo.pending + manualOrgInfo.toRemove) > 0 && (
                <span style={{ marginLeft: 'auto', background: '#f59e0b', color: '#fff', borderRadius: '999px', padding: '0 7px', fontSize: '11px', fontWeight: 700 }}>
                  {manualOrgInfo.pending + manualOrgInfo.toRemove}
                </span>
              )}
            </div>
          )}

          <div
            className={`nav-item ${activeTab === 'redeem' ? 'active' : ''}`}
            onClick={() => { setActiveTab('redeem'); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="key" /></span>
            <span>Key Redemption</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'ipMonitor' ? 'active' : ''}`}
            onClick={() => { setActiveTab('ipMonitor'); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="shield" /></span>
            <span>IP Monitor</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'telegramShop' ? 'active' : ''}`}
            onClick={() => { setActiveTab('telegramShop'); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="send" /></span>
            <span>Telegram Shop</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'announcements' ? 'active' : ''}`}
            onClick={() => { setActiveTab('announcements'); fetchAnnouncements(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="megaphone" /></span>
            <span>Announcements</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'feedback' ? 'active' : ''}`}
            onClick={() => { setActiveTab('feedback'); fetchFeedback(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="message" /></span>
            <span>Reseller Feedback</span>
            {feedbackNewCount > 0 && (
              <span style={{ marginLeft: 'auto', background: '#dc2626', color: 'white', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                {feedbackNewCount}
              </span>
            )}
          </div>

          <div
            className={`nav-item ${activeTab === 'bulkOrg' ? 'active' : ''}`}
            onClick={() => { setActiveTab('bulkOrg'); fetchOrganizations(); fetchResellers(); fetchCPResellers(); setError(null); setResult(null); setBulkOrgPreview(null); setOrgTransferPreview(null); }}
          >
            <span className="nav-icon"><Icon name="refresh" /></span>
            <span>Bulk Org Access</span>
          </div>
          {/* Pre-Made Keys retired — unified into the Key Redemption tab.
              Its backend/data remain; global pricing now lives under
              Key Redemption → Pricing. */}

          <div
            className={`nav-item ${activeTab === 'backupKeys' ? 'active' : ''}`}
            onClick={() => { setActiveTab('backupKeys'); fetchBackupKeys(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="key" /></span>
            <span>Pre-Made Keys</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'marketplace' ? 'active' : ''}`}
            onClick={() => { setActiveTab('marketplace'); fetchMarketplace(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="bag" /></span>
            <span>Marketplace</span>
          </div>

          <div
            className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => { setActiveTab('settings'); fetchMigrationSettings(); fetchActivationSettings(); fetchBulkSettings(); setError(null); setResult(null); }}
          >
            <span className="nav-icon"><Icon name="settings" /></span>
            <span>Settings</span>
          </div>

          <div className="nav-divider"></div>

          {/* Admin Info & Logout - visible in mobile nav */}
          <div className="nav-item logout-item" onClick={handleAdminLogout}>
            <span className="nav-icon"><Icon name="logout" /></span>
            <span>Logout</span>
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Top Bar */}
        <div className="top-bar">
          <h2 className="page-title">{getPageTitle()}</h2>
          <div className="user-info">
            <span style={{ marginRight: '16px', color: '#10b981' }}>Logged in as {adminAuth.admin?.username || 'Admin'}</span>
            <a href="/reseller" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
              Open Reseller Portal →
            </a>
            <ThemeToggle compact />
            <button
              onClick={handleAdminLogout}
              style={{
                marginLeft: '8px',
                padding: '6px 14px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="content-wrapper">
          {/* Alerts */}
          {error && (
            <div className="alert alert-error">
              <span style={{ fontSize: '18px' }}><Icon name="alert" size={16} /></span>
              <div>
                <strong>Error occurred</strong>
                <pre style={{ marginTop: '8px', fontSize: '13px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{error}</pre>
                {deleteOverrideCandidate?.message === error && (
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '12px' }}>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => handleDeleteCustomer(deleteOverrideCandidate.customer, { forceLocal: true })}
                    >
                      Override: delete local record only
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setDeleteOverrideCandidate(null);
                        setError(null);
                      }}
                    >
                      Cancel override
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          {result && (
            <div className="alert alert-success">
              <span style={{ display: 'inline-flex' }}><Icon name="check" size={17} /></span>
              <div><strong>{result}</strong></div>
            </div>
          )}

          {/* Organizations Tab */}
          {activeTab === 'organizations' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Total Organizations</div>
                  <div className="stat-value">{organizations.length}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                  <div className="stat-label">Total Licenses</div>
                  <div className="stat-value">{organizations.reduce((sum, o) => sum + (o.totalLicenses || 0), 0)}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                  <div className="stat-label" title="Sum of 'Active users' across organizations whose status is Normal or Updating">Active Users</div>
                  <div className="stat-value">
                    {organizations.filter(o => o.organizationStatus !== 'inactive').reduce((sum, o) => sum + (o.totalUsers || 0), 0).toLocaleString()}
                  </div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#ef4444' }}>
                  <div className="stat-label" title="Users on teams whose status is Inactive. The users may still be active in Adobe; it is their team that is disabled.">
                    Users on Inactive Teams
                  </div>
                  {(() => {
                    const dead = organizations.filter(o => o.organizationStatus === 'inactive');
                    const sum = set => set.reduce((total, o) => total + (o.totalUsers || 0), 0);
                    const k12 = dead.filter(o => o.k12);
                    const pro = dead.filter(o => !o.k12);
                    return (
                      <>
                        <div className="stat-value">{sum(dead).toLocaleString()}</div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          Pro <b>{sum(pro).toLocaleString()}</b> ({pro.length} team{pro.length === 1 ? '' : 's'})
                          {' · '}
                          K‑12 <b>{sum(k12).toLocaleString()}</b> ({k12.length} team{k12.length === 1 ? '' : 's'})
                        </div>
                      </>
                    );
                  })()}
                </div>
                {(() => {
                  const live = organizations.filter(o => o.organizationStatus !== 'inactive');
                  const tally = tier => {
                    const orgs = live.filter(o => (tier === 'k12' ? o.k12 : !o.k12));
                    return {
                      orgs: orgs.length,
                      users: orgs.reduce((sum, o) => sum + (o.totalUsers || 0), 0),
                      seats: orgs.reduce((sum, o) => sum + ((o.officialLicenseSummary && o.officialLicenseSummary.total) || 0), 0)
                    };
                  };
                  const k12 = tally('k12');
                  const pro = tally('pro');
                  const both = k12.users + pro.users;
                  const share = n => both > 0 ? `${Math.round((n / both) * 100)}%` : '—';

                  return (
                    <>
                      <div className="stat-card" style={{ borderLeftColor: '#0ea5e9' }}>
                        <div className="stat-label" title="Active users on teams marked K12, charged the flat K12 rate per activation. Adobe does not quota-track EDU profiles, so no seat total is shown for them.">
                          K‑12 Users
                        </div>
                        <div className="stat-value">{k12.users.toLocaleString()}</div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          {share(k12.users)} · {k12.orgs} team{k12.orgs === 1 ? '' : 's'}
                          {k12.seats > 0 && <> · {k12.seats.toLocaleString()} seats</>}
                        </div>
                      </div>
                      <div className="stat-card" style={{ borderLeftColor: '#8b5cf6' }}>
                        <div className="stat-label" title="Active users on teams that are not marked K12. These use the reseller's own per-duration pricing.">
                          Pro Users
                        </div>
                        <div className="stat-value">{pro.users.toLocaleString()}</div>
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>
                          {share(pro.users)} · {pro.orgs} team{pro.orgs === 1 ? '' : 's'}
                          {pro.seats > 0 && <> · {pro.seats.toLocaleString()} seats</>}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Create New Organization */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Add New Organization (Adobe Console)</h3>
                </div>
                <div style={{ marginBottom: '16px', padding: '12px', background: '#dbeafe', borderRadius: '6px', borderLeft: '4px solid #3b82f6' }}>
                  <span style={{ color: '#1e40af', fontSize: '13px' }}>
                    Organization name will be automatically fetched from Adobe when you add the credentials.
                  </span>
                </div>
                <form onSubmit={handleCreateOrg}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Client ID</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Adobe Client ID"
                        value={newOrg.clientId}
                        onChange={e => setNewOrg({ ...newOrg, clientId: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Client Secret</label>
                      <input
                        type="password"
                        className="form-input"
                        placeholder="Adobe Client Secret"
                        value={newOrg.clientSecret}
                        onChange={e => setNewOrg({ ...newOrg, clientSecret: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Organization ID</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Adobe Organization ID"
                        value={newOrg.orgId}
                        onChange={e => setNewOrg({ ...newOrg, orgId: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">API Key</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Adobe API Key"
                        value={newOrg.apiKey}
                        onChange={e => setNewOrg({ ...newOrg, apiKey: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Auto-disable at users</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        className="form-input"
                        placeholder="Blank, 3000, 4000..."
                        value={newOrg.autoDisableUserLimit}
                        onChange={e => setNewOrg({ ...newOrg, autoDisableUserLimit: e.target.value })}
                      />
                      <div style={{ color: '#64748b', fontSize: '11px', marginTop: '4px' }}>
                        When active users reach this number, API sync is disabled and this org is removed from reseller access.
                      </div>
                    </div>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '4px 0 16px', cursor: 'pointer', maxWidth: '640px' }}>
                    <input
                      type="checkbox"
                      checked={newOrg.availableAsTarget !== false}
                      onChange={e => setNewOrg({ ...newOrg, availableAsTarget: e.target.checked })}
                      style={{ marginTop: '3px' }}
                    />
                    <span style={{ fontSize: '13px', color: '#475569' }}>
                      <strong>Allow as a transfer / switch target</strong> — users moving from a disabled organization may be sent here.
                      It is removed automatically (and permanently) the first time this org runs out of licenses.
                    </span>
                  </label>
                  <button type="submit" disabled={loading} className="btn btn-primary">
                    {loading ? 'Creating...' : 'Add Organization'}
                  </button>
                </form>
              </div>

              {/* Organizations List */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Organizations</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px', background: 'var(--app-surface-alt)', borderRadius: '8px', border: '1px solid var(--app-border)' }} title="Credits PER MONTH charged when a reseller activates a user on a K12 team (2 months = 2x this)">
                      <span style={{ fontSize: '12px', color: 'var(--app-muted)' }}>K12 cost/mo:</span>
                      <input type="number" min="0" step="any"
                        value={k12CostDraft !== '' ? k12CostDraft : k12Cost}
                        onChange={e => setK12CostDraft(e.target.value)}
                        style={{ width: '54px', padding: '3px 6px', borderRadius: '5px', border: '1px solid var(--app-border)', fontSize: '12px' }} />
                      <span style={{ fontSize: '12px', color: 'var(--app-muted)' }}>cr/mo</span>
                      <button onClick={saveK12Cost} className="btn btn-secondary" style={{ padding: '3px 8px', fontSize: '12px' }}>Save</button>
                    </div>
                    <button
                      onClick={() => bulkSetK12({ matchK12Products: true, k12: true })}
                      className="btn btn-secondary"
                      style={{ background: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc' }}
                      title="Mark every organization whose products are K-12 configurations as a K12 team"
                    >
                      Auto-flag K-12 teams
                    </button>
                    <button onClick={() => fetchOrganizations()} disabled={loadingOrgs} className="btn btn-secondary">
                      Refresh
                    </button>
                  </div>
                </div>

                {loadingOrgs && <div className="loading">Loading organizations...</div>}

                {!loadingOrgs && organizations.length > 0 && (() => {
                  const q = orgFilters.search.trim().toLowerCase();
                  const filteredOrgs = sortOrganizationsForDisplay(organizations).filter(org => {
                    const targetState = org.targetDisqualified ? 'disqualified' : (org.availableAsTarget === false ? 'off' : 'usable');
                    if (orgFilters.status !== 'all' && (org.organizationStatus || 'normal') !== orgFilters.status) return false;
                    if (orgFilters.apiSync === 'on' && org.apiSyncEnabled === false) return false;
                    if (orgFilters.apiSync === 'off' && org.apiSyncEnabled !== false) return false;
                    if (orgFilters.target !== 'all' && targetState !== orgFilters.target) return false;
                    if (orgFilters.tier === 'k12' && org.k12 !== true) return false;
                    if (orgFilters.tier === 'pro' && org.k12 === true) return false;
                    if (q) {
                      const hay = `${org.name || ''} ${org.id} ${org.orgId || ''} ${org.clientIdPreview || ''}`.toLowerCase();
                      if (!hay.includes(q)) return false;
                    }
                    return true;
                  });
                  const ORG_PAGE_SIZE = 25;
                  const totalPages = Math.max(1, Math.ceil(filteredOrgs.length / ORG_PAGE_SIZE));
                  const page = Math.min(Math.max(1, orgPage), totalPages);
                  const pagedOrgs = filteredOrgs.slice((page - 1) * ORG_PAGE_SIZE, page * ORG_PAGE_SIZE);
                  const filtersActive = orgFilters.search || orgFilters.status !== 'all' || orgFilters.apiSync !== 'all' || orgFilters.target !== 'all' || orgFilters.tier !== 'all';
                  return (
                  <>
                  {/* Filter / search bar */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                    <input
                      type="search"
                      className="form-input"
                      value={orgFilters.search}
                      onChange={e => { setOrgFilters(f => ({ ...f, search: e.target.value })); setOrgPage(1); }}
                      placeholder="Search name, org ID, client ID…"
                      style={{ flex: '1 1 240px', minWidth: '200px', maxWidth: '360px' }}
                    />
                    <select className="form-input" style={{ width: 'auto' }} value={orgFilters.status}
                      onChange={e => { setOrgFilters(f => ({ ...f, status: e.target.value })); setOrgPage(1); }}>
                      <option value="all">All statuses</option>
                      {ORGANIZATION_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <select className="form-input" style={{ width: 'auto' }} value={orgFilters.apiSync}
                      onChange={e => { setOrgFilters(f => ({ ...f, apiSync: e.target.value })); setOrgPage(1); }}>
                      <option value="all">API sync: all</option>
                      <option value="on">API sync: on</option>
                      <option value="off">API sync: off</option>
                    </select>
                    <select className="form-input" style={{ width: 'auto' }} value={orgFilters.target}
                      onChange={e => { setOrgFilters(f => ({ ...f, target: e.target.value })); setOrgPage(1); }}>
                      <option value="all">Target: all</option>
                      <option value="usable">Usable as target</option>
                      <option value="off">Target off</option>
                      <option value="disqualified">Target used up</option>
                    </select>
                    <select className="form-input" style={{ width: 'auto' }} value={orgFilters.tier}
                      onChange={e => { setOrgFilters(f => ({ ...f, tier: e.target.value })); setOrgPage(1); }}>
                      <option value="all">Tier: all</option>
                      <option value="k12">K12 teams</option>
                      <option value="pro">Pro teams</option>
                    </select>
                    {filtersActive && (
                      <button type="button" className="btn btn-secondary" style={{ padding: '6px 12px' }}
                        onClick={() => { setOrgFilters({ search: '', status: 'all', apiSync: 'all', target: 'all', tier: 'all' }); setOrgPage(1); }}>
                        Clear filters
                      </button>
                    )}
                    <span style={{ fontSize: '13px', color: 'var(--app-muted)', marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                      {filteredOrgs.length === organizations.length
                        ? `${organizations.length} organizations`
                        : `${filteredOrgs.length} of ${organizations.length}`}
                    </span>
                  </div>
                  {/* Bulk action toolbar — appears when any org is selected */}
                  {orgApiSyncSelection.size > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', marginBottom: '10px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#1d4ed8' }}>
                        {orgApiSyncSelection.size} org{orgApiSyncSelection.size !== 1 ? 's' : ''} selected
                      </span>
                      <button
                        className="btn btn-secondary"
                        disabled={bulkApiSyncLoading}
                        onClick={() => handleBulkApiSync(true)}
                        style={{ fontSize: '12px', padding: '4px 10px', background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }}
                        title="Enable Adobe API sync for selected organizations"
                      >
                        Enable API
                      </button>
                      <button
                        className="btn btn-secondary"
                        disabled={bulkApiSyncLoading}
                        onClick={() => handleBulkApiSync(false)}
                        style={{ fontSize: '12px', padding: '4px 10px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}
                        title="Disable Adobe API sync for selected organizations"
                      >
                        Disable API
                      </button>
                      <button
                        className="btn btn-secondary"
                        disabled={bulkApiSyncLoading}
                        onClick={() => bulkSetK12({ ids: [...orgApiSyncSelection], k12: true })}
                        style={{ fontSize: '12px', padding: '4px 10px', background: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc' }}
                        title="Mark the selected organizations as K12 teams (flat activation cost)"
                      >
                        Mark K12
                      </button>
                      <button
                        className="btn btn-secondary"
                        disabled={bulkApiSyncLoading}
                        onClick={() => bulkSetK12({ ids: [...orgApiSyncSelection], k12: false })}
                        style={{ fontSize: '12px', padding: '4px 10px' }}
                        title="Mark the selected organizations as Pro teams (per-duration pricing)"
                      >
                        Mark Pro
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setOrgApiSyncSelection(new Set())}
                        style={{ fontSize: '12px', padding: '4px 10px' }}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  {filteredOrgs.length === 0 ? (
                    <div className="empty-state">
                      <div className="empty-icon"><Icon name="search" size={16} /></div>
                      <div className="empty-text">No organizations match your filters.</div>
                    </div>
                  ) : (
                  <>
                  <div className="data-table-scroll" style={{ overflowX: 'auto', width: '100%' }}>
                  <table className="data-table org-data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '36px' }}>
                          <input
                            type="checkbox"
                            title="Select / deselect all shown by the current filter"
                            checked={filteredOrgs.length > 0 && filteredOrgs.every(o => orgApiSyncSelection.has(o.id))}
                            onChange={e => setOrgApiSyncSelection(e.target.checked ? new Set(filteredOrgs.map(o => o.id)) : new Set())}
                          />
                        </th>
                        <th>Name</th>
                        <th>Organization ID</th>
                        <th>Products</th>
                        <th>Total Licenses</th>
                        <th>Status</th>
                        <th>Client ID</th>
                        <th>API Uptime</th>
                        <th style={{ minWidth: '160px' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedOrgs.map((org) => (
                        <tr key={org.id} className={org.apiSyncEnabled === false ? 'org-api-sync-disabled-row' : ''}>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={orgApiSyncSelection.has(org.id)}
                              onChange={e => {
                                const next = new Set(orgApiSyncSelection);
                                if (e.target.checked) next.add(org.id); else next.delete(org.id);
                                setOrgApiSyncSelection(next);
                              }}
                            />
                          </td>
                          <td>
                            {renamingOrg && renamingOrg.id === org.id ? (
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  value={newOrgName}
                                  onChange={(e) => setNewOrgName(e.target.value)}
                                  autoFocus
                                  style={{ padding: '4px 8px', fontSize: '14px', border: '1px solid #3b82f6', borderRadius: '4px', width: '180px' }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleRenameOrg();
                                    if (e.key === 'Escape') { setRenamingOrg(null); setNewOrgName(''); }
                                  }}
                                />
                                <button onClick={handleRenameOrg} className="btn btn-primary" style={{ padding: '4px 8px', fontSize: '12px' }} aria-label="Save"><Icon name="check" size={14} /></button>
                                <button onClick={() => { setRenamingOrg(null); setNewOrgName(''); }} className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} aria-label="Cancel"><Icon name="x" size={14} /></button>
                              </div>
                            ) : (
                              <div>
                                <div
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}
                                  onClick={() => { setRenamingOrg({ id: org.id, currentName: org.name }); setNewOrgName(org.name); }}
                                  title="Click to rename"
                                >
                                  <strong>{org.name}</strong>
                                  <span style={{ fontSize: '12px', opacity: 0.5 }}><Icon name="edit" size={16} /></span>
                                </div>
                                <div style={{ color: 'var(--app-muted)', fontSize: '11px', marginTop: '2px' }}>
                                  ID: {org.id}
                                </div>
                                <div style={{ color: (org.totalUsers || 0) > 5000 ? '#b45309' : '#64748b', fontSize: '12px', marginTop: '4px' }}>
                                  Active users: {(org.totalUsers || 0).toLocaleString()}
                                </div>
                                <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                  <span style={{ color: '#64748b', fontSize: '11px' }}>Auto-disable at</span>
                                  <input
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={orgUserLimitDrafts[org.id] !== undefined ? orgUserLimitDrafts[org.id] : (org.autoDisableUserLimit || '')}
                                    onChange={e => setOrgUserLimitDrafts(prev => ({ ...prev, [org.id]: e.target.value }))}
                                    placeholder="Off"
                                    style={{ width: '82px', padding: '3px 6px', border: '1px solid #cbd5e1', borderRadius: '5px', fontSize: '11px' }}
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => handleSaveOrgUserLimit(org)}
                                    disabled={updatingOrgStatusId === org.id}
                                    style={{ padding: '3px 7px', fontSize: '11px' }}
                                    title="Save auto-disable user limit"
                                  >
                                    Save
                                  </button>
                                  {org.autoDisabledByUserLimitAt && (
                                    <span style={{ fontSize: '10px', color: '#991b1b', fontWeight: 700 }}>
                                      Triggered
                                    </span>
                                  )}
                                </div>
                                {org.officialLicenseSummary && (
                                  <div style={{
                                    color: org.officialLicenseSummary.available <= 0 ? '#b91c1c' : '#64748b',
                                    fontSize: '12px',
                                    marginTop: '2px',
                                    fontWeight: org.officialLicenseSummary.available <= 0 ? 600 : 400
                                  }}>
                                    Adobe seats: {org.officialLicenseSummary.used.toLocaleString()} / {org.officialLicenseSummary.total.toLocaleString()}
                                    {' '}({org.officialLicenseSummary.available.toLocaleString()} left)
                                  </div>
                                )}
                                {/* Transfer/switch target eligibility */}
                                <div style={{ marginTop: '4px' }}>
                                  {org.targetDisqualified ? (
                                    <span title={org.targetDisqualifiedAt ? `Disqualified ${new Date(org.targetDisqualifiedAt).toLocaleString()} — licenses ran out` : 'Licenses ran out — permanently unavailable as a target'}
                                      style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#fee2e2', color: '#991b1b' }}>
                                      Target disabled (used up)
                                    </span>
                                  ) : org.availableAsTarget === false ? (
                                    <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#f1f5f9', color: '#64748b' }}>
                                      Target: off
                                    </span>
                                  ) : (
                                    <span title="Users from disabled orgs may be switched here" style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#dcfce7', color: '#166534' }}>
                                      Usable as target
                                    </span>
                                  )}
                                </div>
                                {/* Adobe API sync status badge */}
                                {org.apiSyncEnabled === false && (
                                  <div style={{ marginTop: '4px' }}>
                                    <span
                                      title={org.apiSyncDisabledAt ? `API sync disabled on ${new Date(org.apiSyncDisabledAt).toLocaleString()}` : 'Adobe API calls are disabled for this org'}
                                      style={{ fontSize: '10px', fontWeight: 700, padding: '2px 7px', borderRadius: '10px', background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}
                                    >
                                      API sync off
                                    </span>
                                  </div>
                                )}
                                {/* K12 team toggle — click to switch this team between Pro and K12 pricing */}
                                <div style={{ marginTop: '4px' }}>
                                  <span
                                    role="button" tabIndex={0}
                                    onClick={() => toggleOrgK12(org.id, !org.k12)}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleOrgK12(org.id, !org.k12); } }}
                                    title={org.k12 ? `K12 team — activations cost ${k12Cost} credit(s) per month (2 months = 2x). Click to make it a Pro team.` : 'Pro team — reseller per-duration pricing. Click to mark as a K12 team.'}
                                    style={{ cursor: 'pointer', fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px',
                                      background: org.k12 ? '#e0f2fe' : '#f1f5f9', color: org.k12 ? '#0369a1' : '#64748b',
                                      border: `1px solid ${org.k12 ? '#7dd3fc' : '#cbd5e1'}` }}
                                  >
                                    {org.k12 ? `K12 · ${k12Cost}cr/mo` : 'Pro'}
                                  </span>
                                </div>
                              </div>
                            )}
                          </td>
                          <td>
                            {/* Truncated: the full "…@AdobeOrg" id is 33 chars and
                                cannot wrap, which was forcing the whole table wider
                                than the card. Click to copy, hover for the full value. */}
                            <code
                              className="org-id-cell"
                              title={`${org.orgId}\n(click to copy)`}
                              onClick={() => { navigator.clipboard?.writeText(org.orgId || ''); setResult('Organization ID copied'); }}
                            >
                              {org.orgId}
                            </code>
                          </td>
                          <td>
                            {(() => {
                              // Collapse long product lists: show 2, then "+N more".
                              // A team can carry a dozen products, which otherwise
                              // makes one row taller than the whole viewport.
                              const PRODUCT_PREVIEW = 2;
                              const expanded = expandedProductOrgIds.has(org.id);
                              const toggle = () => setExpandedProductOrgIds(prev => {
                                const next = new Set(prev);
                                if (next.has(org.id)) next.delete(org.id); else next.add(org.id);
                                return next;
                              });
                              const withLic = org.productsWithLicenses || [];
                              const plain = org.products || [];
                              const total = withLic.length || plain.length;
                              if (total === 0) return <span style={{ color: 'var(--app-muted)', fontSize: '12px' }}>No products</span>;
                              const hidden = total - PRODUCT_PREVIEW;
                              const moreBtn = hidden > 0 ? (
                                <button type="button" onClick={toggle}
                                  style={{ background: 'none', border: 'none', padding: 0, marginTop: 2, fontSize: '11px', color: 'var(--app-link)', cursor: 'pointer', fontWeight: 600, textAlign: 'left' }}>
                                  {expanded ? '▾ show less' : `▸ +${hidden} more`}
                                </button>
                              ) : null;
                              const shown = (list) => (expanded ? list : list.slice(0, PRODUCT_PREVIEW));
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', maxWidth: '190px' }}>
                                  {withLic.length > 0
                                    ? shown(withLic).map((p, i) => (
                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
                                          <span className="badge badge-info org-product-badge" title={p.name}>{getProductDisplayName(p.name)}</span>
                                          <span style={{ fontSize: '10px', color: '#64748b', whiteSpace: 'nowrap' }}>
                                            ({p.memberCount || 0}/{p.licenseQuota === 'UNLIMITED' || p.licenseQuota === null ? '∞' : (p.licenseQuota || 0)})
                                          </span>
                                        </div>
                                      ))
                                    : shown(plain).map((p, i) => (
                                        <span key={i} className="badge badge-info org-product-badge" title={p}>{getProductDisplayName(p)}</span>
                                      ))}
                                  {moreBtn}
                                </div>
                              );
                            })()}
                          </td>
                          <td>{typeof org.totalLicenses === 'number' ? org.totalLicenses : '∞'}</td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start', minWidth: '130px' }}>
                              <select
                                className="form-input"
                                value={org.organizationStatus || 'normal'}
                                disabled={updatingOrgStatusId === org.id}
                                onChange={(e) => handleUpdateOrgStatus(org.id, { status: e.target.value })}
                                style={{
                                  fontSize: '12px',
                                  padding: '4px 8px',
                                  height: '30px',
                                  width: '118px',
                                  borderColor: getOrganizationStatusStyle(org.organizationStatus).borderColor,
                                  ...getOrganizationStatusStyle(org.organizationStatus)
                                }}
                              >
                                {ORGANIZATION_STATUS_OPTIONS.map(option => (
                                  <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                              </select>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() => handleUpdateOrgStatus(org.id, { published: !org.statusPublished })}
                                  disabled={updatingOrgStatusId === org.id}
                                  className="btn btn-secondary"
                                  style={{
                                    fontSize: '11px',
                                    padding: '3px 7px',
                                    background: org.statusPublished ? '#dcfce7' : undefined,
                                    color: org.statusPublished ? '#166534' : undefined
                                  }}
                                  title={org.statusPublished ? 'Hide status from customer status page' : 'Publish status to customer status page'}
                                >
                                  {org.statusPublished ? 'Published' : 'Publish'}
                                </button>
                                <button
                                  onClick={() => handleEditOrgStatusMessage(org)}
                                  disabled={updatingOrgStatusId === org.id}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '11px', padding: '3px 7px' }}
                                  title="Edit status page message"
                                >
                                  Message
                                </button>
                                <button
                                  onClick={() => handleEvacuateSelfSwitch(org)}
                                  disabled={updatingOrgStatusId === org.id}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '11px', padding: '3px 7px' }}
                                  title="Move every user currently in this org to a live backup organization now (works even if this org isn't marked Inactive)"
                                >
                                  Evacuate users
                                </button>
                              </div>
                              {org.statusLastCheckedAt && (
                                <div style={{ fontSize: '10px', color: '#64748b' }}>
                                  Checked {formatDurationFromTimestamp(org.statusLastCheckedAt)} ago
                                </div>
                              )}
                              {org.statusLastError && (
                                <div title={org.statusLastError} style={{ fontSize: '10px', color: '#b91c1c', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {org.statusLastError}
                                </div>
                              )}
                              {org.organizationStatus === 'inactive' && (
                                <div style={{ width: '100%', marginTop: '2px' }}>
                                  <button
                                    type="button"
                                    onClick={() => setExpandedSelfSwitchOrgIds(prev => {
                                      const next = new Set(prev);
                                      if (next.has(org.id)) next.delete(org.id); else next.add(org.id);
                                      return next;
                                    })}
                                    style={{ background: 'none', border: 'none', padding: 0, fontSize: '11px', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}
                                  >
                                    {expandedSelfSwitchOrgIds.has(org.id) ? '▾' : '▸'} Self-switch targets ({(org.selfSwitchTargetOrgIds || []).length})
                                  </button>
                                  {expandedSelfSwitchOrgIds.has(org.id) && (
                                    <div style={{ marginTop: '4px', padding: '6px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', maxHeight: '160px', overflowY: 'auto', minWidth: '180px' }}>
                                      <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '6px' }}>
                                        Live — automatically any org the affected users' reseller(s) already have access to, with a matching product. Updates instantly as that access changes; not manually editable here.
                                      </div>
                                      {(org.selfSwitchTargetOrgIds || []).length === 0 ? (
                                        <div style={{ fontSize: '11px', color: 'var(--app-muted)' }}>No eligible organizations right now</div>
                                      ) : (
                                        (org.selfSwitchTargetOrgIds || []).map(targetId => {
                                          const t = organizations.find(o => o.id === targetId);
                                          return (
                                            <div key={targetId} style={{ fontSize: '11px', padding: '2px 0' }}>
                                              {t ? t.name : `Org #${targetId}`}
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td><code>{org.clientIdPreview}</code></td>
                          <td
                            title={(org.credentialsReplacedAt || org.createdAt)
                              ? `Since ${new Date(org.credentialsReplacedAt || org.createdAt).toLocaleString()}`
                              : 'No replacement date recorded'}
                            style={{ fontSize: '12px' }}
                          >
                            <div style={{ fontWeight: 600, color: '#0f172a' }}>
                              {formatDurationFromTimestamp(org.credentialsReplacedAt || org.createdAt)}
                            </div>
                            <div style={{ color: '#64748b', marginTop: '2px' }}>
                              since {(org.credentialsReplacedAt || org.createdAt)
                                ? new Date(org.credentialsReplacedAt || org.createdAt).toLocaleDateString()
                                : '-'}
                            </div>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'flex-start' }}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                <button
                                  onClick={() => handleTestOrgConnection(org.id)}
                                  disabled={testingOrgId === org.id}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '12px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                                >
                                  {testingOrgId === org.id ? 'Testing…' : 'Test'}
                                </button>
                                <button
                                  onClick={() => handleRefreshProducts(org.id, org.name)}
                                  disabled={refreshingOrgId === org.id}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '12px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                                  title="Refresh products from Adobe"
                                >
                                  {refreshingOrgId === org.id ? 'Loading…' : 'Products'}
                                </button>
                                <button
                                  onClick={() => handleAutoStatusCheck(org.id)}
                                  disabled={autoCheckingOrgId === org.id}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '12px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                                  title="Check Adobe API and product availability, then set Normal or Inactive"
                                >
                                  {autoCheckingOrgId === org.id ? 'Checking...' : 'Auto Status'}
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingOrg(org);
                                    setEditOrgForm({ clientId: '', clientSecret: '', orgId: '', apiKey: '', skipRemoveFromOld: false, forceReassign: false });
                                  }}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '12px', padding: '4px 8px', whiteSpace: 'nowrap' }}
                                  title="Edit credentials (migrate to new console)"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteOrg(org.id, org.name)}
                                  className="btn btn-danger"
                                  style={{ fontSize: '12px', padding: '4px 8px' }}
                                ><Icon name="trash" size={16} /></button>
                              </div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                <button
                                  onClick={() => {
                                    setEditingNoteOrg(org);
                                    setNoteInput(org.notice || '');
                                  }}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '11px', padding: '3px 7px', whiteSpace: 'nowrap' }}
                                  title="Edit Public Note"
                                >
                                  Note
                                </button>
                                <button
                                  onClick={() => handleExportUsers(org.id, org.name)}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '11px', padding: '3px 7px', whiteSpace: 'nowrap' }}
                                  title="Export Users to CSV"
                                >
                                  Export
                                </button>
                                <button
                                  onClick={() => {
                                    setSmartCopyOrg(org);
                                    setSmartCopyLimit(5000);
                                  }}
                                  disabled={smartCopyingOrgId === org.id}
                                  className="btn btn-secondary"
                                  style={{ fontSize: '11px', padding: '3px 7px', whiteSpace: 'nowrap' }}
                                  title="Split users across copied local organizations"
                                >
                                  {smartCopyingOrgId === org.id ? 'Copying…' : 'Smart Copy'}
                                </button>
                                {/* Adobe API sync toggle */}
                                <button
                                  onClick={() => handleToggleOrgApiSync(org)}
                                  disabled={updatingOrgStatusId === org.id}
                                  className="btn btn-secondary"
                                  style={{
                                    fontSize: '11px', padding: '3px 7px', whiteSpace: 'nowrap',
                                    background: org.apiSyncEnabled === false ? '#d1fae5' : '#fee2e2',
                                    color: org.apiSyncEnabled === false ? '#065f46' : '#991b1b',
                                    border: `1px solid ${org.apiSyncEnabled === false ? '#6ee7b7' : '#fca5a5'}`
                                  }}
                                  title={org.apiSyncEnabled === false
                                    ? 'Adobe API sync is OFF — click to re-enable'
                                    : 'Click to disable Adobe API calls for this org'}
                                >
                                  {org.apiSyncEnabled === false ? 'Enable API' : 'Disable API'}
                                </button>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {totalPages > 1 && (
                    <div className="pagination" style={{ marginTop: '16px' }}>
                      <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setOrgPage(1)}>« First</button>
                      <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setOrgPage(page - 1)}>‹ Prev</button>
                      <span style={{ padding: '0 12px', alignSelf: 'center' }}>Page {page} / {totalPages}</span>
                      <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setOrgPage(page + 1)}>Next ›</button>
                      <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setOrgPage(totalPages)}>Last »</button>
                    </div>
                  )}
                  </>
                  )}
                  </>
                  );
                })()}

                {!loadingOrgs && organizations.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="building" size={16} /></div>
                    <div className="empty-text">No organizations yet. Add one above to get started!</div>
                  </div>
                )}
              </div>

              {/* Edit Organization Modal */}
              {editingOrg && (
                <div className="modal-overlay">
                  <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
                    <div className="modal-header">
                      <h3>Edit Organization: {editingOrg.name}</h3>
                      <button className="modal-close" onClick={() => setEditingOrg(null)}>×</button>
                    </div>

                    <div style={{
                      background: '#fdf6d8',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      border: '1px solid #fbbf24'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '20px' }}><Icon name="alert" size={16} /></span>
                        <div>
                          <strong style={{ color: '#b45309' }}>Console Migration</strong>
                          <div style={{ color: '#92400e', fontSize: '14px' }}>
                            Enter new Adobe credentials to migrate to a different console.
                            Resellers will maintain access to their existing user records.
                            <br /><strong>Note:</strong> All users will be automatically added to the new Adobe Console.
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Progress Indicator */}
                    {orgUpdateProgress && (
                      <div style={{
                        background: '#dff4fd',
                        padding: '20px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        border: '1px solid #3b82f6',
                        textAlign: 'center'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '10px' }}>
                          <div style={{
                            width: '24px',
                            height: '24px',
                            border: '3px solid #3b82f6',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          <span style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e40af' }}>
                            {Math.floor(orgUpdateProgress.elapsed / 60)}:{String(orgUpdateProgress.elapsed % 60).padStart(2, '0')}
                          </span>
                        </div>
                        <div style={{ color: '#1e40af', fontSize: '14px' }}>
                          {orgUpdateProgress.message}
                        </div>
                        <div style={{ color: '#3b82f6', fontSize: '12px', marginTop: '8px' }}>
                          Please wait... This may take a while if migrating many users.
                        </div>
                        <button
                          type="button"
                          onClick={() => cancelMigration(editingOrg.id)}
                          style={{
                            marginTop: '12px',
                            padding: '8px 16px',
                            background: '#e03e3e',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '13px'
                          }}
                        >
                          Cancel Migration
                        </button>
                      </div>
                    )}

                    <form onSubmit={handleUpdateOrg}>
                      <div className="form-group">
                        <label className="form-label">Client ID</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Enter new Client ID (leave blank to keep current)"
                          value={editOrgForm.clientId}
                          onChange={(e) => setEditOrgForm({ ...editOrgForm, clientId: e.target.value })}
                          disabled={loading}
                        />
                        {editingOrg.clientIdPreview && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                            Current: {editingOrg.clientIdPreview}
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">Client Secret</label>
                        <input
                          type="password"
                          className="form-input"
                          placeholder="Enter new Client Secret (leave blank to keep current)"
                          value={editOrgForm.clientSecret}
                          onChange={(e) => setEditOrgForm({ ...editOrgForm, clientSecret: e.target.value })}
                          disabled={loading}
                        />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Organization ID</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Enter new Org ID (leave blank to keep current)"
                          value={editOrgForm.orgId}
                          onChange={(e) => setEditOrgForm({ ...editOrgForm, orgId: e.target.value })}
                          disabled={loading}
                        />
                        {editingOrg.orgId && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                            Current: {editingOrg.orgId}
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">API Key</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Enter new API Key (leave blank to keep current)"
                          value={editOrgForm.apiKey}
                          onChange={(e) => setEditOrgForm({ ...editOrgForm, apiKey: e.target.value })}
                          disabled={loading}
                        />
                        {editingOrg.apiKeyPreview && (
                          <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                            Current: {editingOrg.apiKeyPreview}
                          </div>
                        )}
                      </div>

                      {/* Skip removal checkbox */}
                      <div style={{
                        background: '#fdf6d8',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginTop: '16px',
                        border: '1px solid #fbbf24'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={editOrgForm.skipRemoveFromOld}
                            onChange={(e) => setEditOrgForm({ ...editOrgForm, skipRemoveFromOld: e.target.checked })}
                            disabled={loading}
                            style={{ marginTop: '3px', width: '18px', height: '18px' }}
                          />
                          <div>
                            <strong style={{ color: '#b45309' }}>Skip removal from old console</strong>
                            <div style={{ color: '#92400e', fontSize: '12px', marginTop: '2px' }}>
                              Check this if you no longer have admin access to the old console.
                              Users will only be added to the new console without attempting removal from the old one.
                            </div>
                          </div>
                        </label>
                      </div>

                      {/* Force re-assign checkbox */}
                      <div style={{
                        background: '#dff4fd',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        marginTop: '12px',
                        border: '1px solid #3b82f6'
                      }}>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={editOrgForm.forceReassign}
                            onChange={(e) => setEditOrgForm({ ...editOrgForm, forceReassign: e.target.checked })}
                            disabled={loading}
                            style={{ marginTop: '3px', width: '18px', height: '18px' }}
                          />
                          <div>
                            <strong style={{ color: '#1e40af' }}>Force re-assign all users</strong>
                            <div style={{ color: '#1e3a8a', fontSize: '12px', marginTop: '2px' }}>
                              Check this to re-add all users even if using the same organization.
                              Useful after a cancelled migration or to fix user assignments.
                            </div>
                          </div>
                        </label>
                      </div>

                      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <button
                          type="submit"
                          disabled={loading || (!editOrgForm.clientId && !editOrgForm.clientSecret && !editOrgForm.orgId && !editOrgForm.apiKey)}
                          className="btn btn-primary"
                          style={{ flex: 1 }}
                        >
                          {loading ? 'Updating & Migrating Users...' : 'Update Organization'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingOrg(null)}
                          className="btn btn-secondary"
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}

          {smartCopyOrg && (
            <div className="modal-overlay">
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
                <div className="modal-header">
                  <h3>Smart Copy: {smartCopyOrg.name}</h3>
                  <button className="modal-close" onClick={() => setSmartCopyOrg(null)}>×</button>
                </div>
                <div style={{ padding: '16px' }}>
                  <div style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '12px',
                    marginBottom: '16px',
                    color: '#334155',
                    fontSize: '13px'
                  }}>
                    <div><strong>Total users:</strong> {(smartCopyOrg.totalUsers || 0).toLocaleString()}</div>
                    <div style={{ marginTop: '4px' }}>
                      <strong>Planned groups:</strong> {Math.max(1, Math.ceil((smartCopyOrg.totalUsers || 0) / (parseInt(smartCopyLimit, 10) || 5000))).toLocaleString()}
                    </div>
                    <div style={{ marginTop: '4px', color: '#64748b' }}>
                      The original organization keeps the first group. Extra users move into copied local organizations with the same credentials and products.
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Max users per copied organization</label>
                    <input
                      type="number"
                      className="form-input"
                      min="1"
                      max="5000"
                      value={smartCopyLimit}
                      onChange={(e) => setSmartCopyLimit(e.target.value)}
                      disabled={smartCopyingOrgId === smartCopyOrg.id}
                    />
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                      Adobe limit is 5000, so larger values will be capped automatically.
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      onClick={() => setSmartCopyOrg(null)}
                      className="btn btn-secondary"
                      disabled={smartCopyingOrgId === smartCopyOrg.id}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSmartCopyOrg}
                      className="btn btn-primary"
                      disabled={smartCopyingOrgId === smartCopyOrg.id}
                    >
                      {smartCopyingOrgId === smartCopyOrg.id ? 'Copying...' : 'Start Smart Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Edit Note Modal */}
          {editingNoteOrg && (
            <div className="modal-overlay">
              <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                  <h3>Edit Note for {editingNoteOrg.name}</h3>
                  <button className="modal-close" onClick={() => setEditingNoteOrg(null)}>×</button>
                </div>
                <div style={{ padding: '16px' }}>
                  <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>
                    This note will be displayed to users on their Status Page. Use this to communicate migration status, downtime, or other important information.
                  </p>
                  <textarea
                    className="form-input"
                    rows={5}
                    placeholder="Enter a message for users..."
                    value={noteInput}
                    onChange={(e) => setNoteInput(e.target.value)}
                    style={{ width: '100%', marginBottom: '16px' }}
                  />
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button onClick={() => setEditingNoteOrg(null)} className="btn btn-secondary">Cancel</button>
                    <button onClick={handleSaveNote} disabled={loading} className="btn btn-primary">
                      {loading ? 'Saving...' : 'Save Note'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Resellers Management Tab */}
          {activeTab === 'resellers' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Total Resellers</div>
                  <div className="stat-value">{resellers.length}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                  <div className="stat-label">Total Licenses Allocated</div>
                  <div className="stat-value">{resellers.reduce((sum, r) => sum + (r.maxLicenses || 0), 0)}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#0b7da6' }}>
                  <div className="stat-label">Total Licenses Used</div>
                  <div className="stat-value">{resellers.reduce((sum, r) => sum + (typeof r.usedLicenses === 'number' ? r.usedLicenses : 0), 0)}</div>
                </div>
              </div>

              {/* Create New Reseller */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Create New Reseller</h3>
                </div>
                <form onSubmit={handleCreateReseller}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label className="form-label">Username</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="reseller_username"
                        value={newReseller.username}
                        onChange={e => setNewReseller({ ...newReseller, username: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Password</label>
                      <input
                        type="password"
                        className="form-input"
                        placeholder="••••••••"
                        value={newReseller.password}
                        onChange={e => setNewReseller({ ...newReseller, password: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Assign Organizations & Products</label>
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: '6px', padding: '12px', maxHeight: '300px', overflowY: 'auto' }}>
                        {organizations.map(org => {
                          const isOrgSelected = newReseller.organizationAccess.some(a => a.organizationId === org.id);
                          const orgAccess = newReseller.organizationAccess.find(a => a.organizationId === org.id);
                          const products = org.products || [];

                          return (
                            <div key={org.id} style={{
                              marginBottom: '12px',
                              padding: '12px',
                              background: isOrgSelected ? '#f0f9ff' : '#f8fafc',
                              borderRadius: '6px',
                              border: isOrgSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0'
                            }}>
                              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={isOrgSelected}
                                  onChange={e => {
                                    if (e.target.checked) {
                                      setNewReseller({
                                        ...newReseller,
                                        organizationAccess: [...newReseller.organizationAccess, { organizationId: org.id, products: [] }]
                                      });
                                    } else {
                                      setNewReseller({
                                        ...newReseller,
                                        organizationAccess: newReseller.organizationAccess.filter(a => a.organizationId !== org.id)
                                      });
                                    }
                                  }}
                                  style={{ marginRight: '10px' }}
                                />
                                <span style={{ fontWeight: 600, fontSize: '14px' }}>{org.name}</span>
                              </label>

                              {isOrgSelected && products.length > 0 && (
                                <div style={{ marginLeft: '24px', marginTop: '8px' }}>
                                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Select products for this org:</div>
                                  {products.map((p, idx) => (
                                    <label key={idx} style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '4px 8px',
                                      cursor: 'pointer',
                                      borderRadius: '4px',
                                      background: orgAccess?.products?.includes(p) ? '#dbeafe' : 'transparent'
                                    }}>
                                      <input
                                        type="checkbox"
                                        checked={orgAccess?.products?.includes(p) || false}
                                        onChange={e => {
                                          const updatedAccess = newReseller.organizationAccess.map(a => {
                                            if (a.organizationId !== org.id) return a;
                                            return {
                                              ...a,
                                              products: e.target.checked
                                                ? [...(a.products || []), p]
                                                : (a.products || []).filter(prod => prod !== p)
                                            };
                                          });
                                          setNewReseller({ ...newReseller, organizationAccess: updatedAccess });
                                        }}
                                        style={{ marginRight: '8px' }}
                                      />
                                      <span style={{ fontSize: '12px' }}>{p}</span>
                                    </label>
                                  ))}
                                  {orgAccess?.products?.length === 0 && (
                                    <div style={{ color: '#dc2626', fontSize: '11px', marginTop: '4px' }}>Select at least one product</div>
                                  )}
                                </div>
                              )}
                              {isOrgSelected && products.length === 0 && (
                                <div style={{ marginLeft: '24px', fontSize: '12px', color: '#92400e' }}>No products in this org</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {newReseller.organizationAccess.length === 0 && (
                        <div style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>Select at least one organization</div>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">User Group Name</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="ResellerGroupName"
                        value={newReseller.userGroup}
                        onChange={e => setNewReseller({ ...newReseller, userGroup: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Max Licenses</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="10"
                        min="1"
                        value={newReseller.maxLicenses}
                        onChange={e => setNewReseller({ ...newReseller, maxLicenses: e.target.value })}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Working Days</label>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="30"
                        min="0"
                        value={newReseller.workingDays}
                        onChange={e => setNewReseller({ ...newReseller, workingDays: e.target.value })}
                      />
                    </div>
                  </div>
                  <button type="submit" disabled={loading} className="btn btn-primary">
                    {loading ? 'Creating...' : 'Create Reseller'}
                  </button>
                </form>
              </div>

              {/* Resellers Overview */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Resellers Overview</h3>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button onClick={() => { setShowMigrateModal(true); fetchCPResellers(); }} className="btn btn-primary" title="Migrate users between resellers or credits pool">
                      Migrate Users
                    </button>
                    <button onClick={fetchResellers} disabled={loadingResellers} className="btn btn-secondary">
                      Refresh
                    </button>
                  </div>
                </div>

                {loadingResellers && <div className="loading">Loading resellers...</div>}

                {!loadingResellers && resellers.length > 0 && (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Username</th>
                        <th>Organization</th>
                        <th>Product</th>
                        <th>User Group</th>
                        <th>Licenses</th>
                        <th>Remaining Days</th>
                        <th style={{ textAlign: 'center' }} title="Show expiration info to customers">Show Exp.</th>
                        <th style={{ textAlign: 'center' }} title="Allow this reseller to delete users">Delete</th>
                        <th style={{ textAlign: 'center' }} title="Allow organization transfers">Transfer</th>
                        <th style={{ textAlign: 'center' }} title="Choose whether transfers keep the source organization or remove it">Org A</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resellers.map((reseller) => (
                        <tr key={reseller.id}>
                          <td style={{ color: '#64748b', fontSize: '12px' }}>#{reseller.id}</td>
                          <td><strong>{reseller.username}</strong></td>
                          <td>
                            {reseller.organizationAccess && reseller.organizationAccess.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {reseller.organizationAccess.map((access, i) => (
                                  <span key={i} className="badge badge-info" style={{ fontSize: '11px' }}>
                                    {access.organizationName || 'Unknown'}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="badge badge-info">{reseller.organizationName || 'Not Assigned'}</span>
                            )}
                          </td>
                          <td>
                            {reseller.organizationAccess && reseller.organizationAccess.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {reseller.organizationAccess.map((access, i) => (
                                  <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: '2px' }}>
                                    {access.products && access.products.map((p, j) => (
                                      <span key={j} className="badge badge-success" style={{ fontSize: '10px' }}>{p}</span>
                                    ))}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span style={{ color: 'var(--app-muted)', fontSize: '12px' }}>—</span>
                            )}
                          </td>
                          <td>{reseller.userGroup}</td>
                          <td>
                            <span className={`badge ${reseller.usedLicenses >= reseller.maxLicenses ? 'badge-danger' : 'badge-success'}`}>
                              {reseller.usedLicenses}/{reseller.maxLicenses}
                            </span>
                          </td>
                          <td>
                            {(() => {
                              if (!reseller.createdAt || !reseller.workingDays) return `${reseller.workingDays || 0} days`;
                              const expiryDate = new Date(reseller.createdAt);
                              expiryDate.setDate(expiryDate.getDate() + reseller.workingDays);
                              const remaining = Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24));
                              if (remaining < 0) return <span style={{ color: '#ef4444' }}>Expired</span>;
                              if (remaining <= 7) return <span style={{ color: '#f59e0b' }}>{remaining} days</span>;
                              return <span style={{ color: '#10b981' }}>{remaining} days</span>;
                            })()}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              onClick={() => handleToggleShowExpiration('traditional', reseller.id, reseller.showExpirationInfo)}
                              className={`btn ${reseller.showExpirationInfo ? 'btn-success' : 'btn-secondary'}`}
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              title={reseller.showExpirationInfo ? 'Enabled (click to disable)' : 'Disabled (click to enable)'}
                            >
                              {reseller.showExpirationInfo ? 'ON' : 'OFF'}
                            </button>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              onClick={async () => {
                                const newVal = !(reseller.allowDeleteUsers === true);
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const res = await fetch(`/api/admin/resellers/${reseller.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ allowDeleteUsers: newVal })
                                  });
                                  if (res.ok) fetchResellers();
                                } catch (e) { console.error(e); }
                              }}
                              className={`btn ${reseller.allowDeleteUsers === true ? 'btn-success' : 'btn-secondary'}`}
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              title={reseller.allowDeleteUsers === true ? 'Delete enabled (click to disable)' : 'Delete hidden (click to enable)'}
                            >
                              {reseller.allowDeleteUsers === true ? 'ON' : 'OFF'}
                            </button>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <button
                              onClick={async () => {
                                const newVal = !(reseller.allowTransfer !== false);
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const res = await fetch(`/api/admin/resellers/${reseller.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ allowTransfer: newVal })
                                  });
                                  if (res.ok) fetchResellers();
                                } catch (e) { console.error(e); }
                              }}
                              className={`btn ${reseller.allowTransfer !== false ? 'btn-success' : 'btn-danger'}`}
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              title={reseller.allowTransfer !== false ? 'Transfers allowed (click to disable)' : 'Transfers disabled (click to enable)'}
                            >
                              {reseller.allowTransfer !== false ? 'ON' : 'OFF'}
                            </button>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <select
                              value={reseller.transferSourceOrgAction === 'keep' ? 'keep' : 'remove'}
                              onChange={(e) => handleUpdateTraditionalTransferSourceOrgAction(reseller.id, e.target.value)}
                              className="form-input"
                              style={{ minWidth: '120px', padding: '4px 8px', fontSize: '11px' }}
                              title="Choose whether transfers keep the source organization or remove it"
                            >
                              <option value="remove">Remove Org A</option>
                              <option value="keep">Keep Org A</option>
                            </select>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            {editingReseller === reseller.id ? (
                              <div className="edit-inline">
                                <input
                                  type="password"
                                  className="form-input"
                                  placeholder="New password"
                                  value={editForm.password}
                                  onChange={e => setEditForm({ ...editForm, password: e.target.value })}
                                  style={{ width: '100px', marginRight: '4px' }}
                                />
                                <input
                                  type="number"
                                  className="form-input"
                                  placeholder="Max"
                                  value={editForm.maxLicenses}
                                  onChange={e => setEditForm({ ...editForm, maxLicenses: e.target.value })}
                                  style={{ width: '60px', marginRight: '4px' }}
                                />
                                <input
                                  type="number"
                                  className="form-input"
                                  placeholder="Days"
                                  value={editForm.workingDays}
                                  onChange={e => setEditForm({ ...editForm, workingDays: e.target.value })}
                                  style={{ width: '60px', marginRight: '4px' }}
                                  title="Working Days (Validity Period)"
                                />
                                <button onClick={() => handleUpdateReseller(reseller.id)} className="btn btn-primary" style={{ marginRight: '4px', padding: '6px 10px' }} aria-label="Save"><Icon name="check" size={14} /></button>
                                <button onClick={() => { setEditingReseller(null); setEditForm({ password: '', maxLicenses: '', workingDays: '' }); }} className="btn btn-secondary" style={{ padding: '6px 10px' }} aria-label="Cancel"><Icon name="x" size={14} /></button>
                              </div>
                            ) : (
                              <>
                                <button onClick={() => { fetchResellerUsers(reseller.id); fetchActivityLogs(reseller.id); setActiveTab('resellerDetails'); }} className="btn btn-primary" style={{ marginRight: '4px' }}>
                                  View
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingReseller(reseller.id);
                                    setEditForm({ ...editForm, organizationAccess: reseller.organizationAccess || [] });
                                    setEditOrgModalOpen(true);
                                  }}
                                  className="btn btn-secondary"
                                  style={{ marginRight: '4px' }}
                                  title="Edit Organizations & Products"
                                ><Icon name="building" size={16} /></button>
                                <button onClick={() => { setEditingReseller(reseller.id); setEditForm({ password: '', maxLicenses: reseller.maxLicenses.toString(), workingDays: (reseller.workingDays || '').toString(), organizationAccess: [] }); }} className="btn btn-secondary" style={{ marginRight: '4px' }}><Icon name="edit" size={16} /></button>
                                <button onClick={() => handleViewResellerAnnouncements(reseller)} className="btn btn-secondary" style={{ marginRight: '4px' }} title="View Announcements"><Icon name="megaphone" size={16} /></button>
                                <button onClick={() => handleDeleteReseller(reseller.id, reseller.username)} className="btn btn-danger"><Icon name="trash" size={16} /></button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {!loadingResellers && resellers.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="store" size={16} /></div>
                    <div className="empty-text">No resellers yet. Create one above!</div>
                  </div>
                )}
              </div>

              {/* Reseller Announcements Modal */}
              {viewingResellerAnnouncements && (
                <div className="modal-overlay" onClick={() => setViewingResellerAnnouncements(null)}>
                  <div className="modal-content" onClick={e => e.stopPropagation()}>
                    <div className="modal-header">
                      <h3 className="modal-title">Announcements for {viewingResellerAnnouncements.resellerName}</h3>
                      <button className="modal-close" onClick={() => setViewingResellerAnnouncements(null)}>×</button>
                    </div>

                    <div className="modal-body">
                      {loadingResellerAnnouncements ? (
                        <div className="loading">Loading announcements...</div>
                      ) : viewingResellerAnnouncements.announcements.length === 0 ? (
                        <div className="empty-state">
                          <div className="empty-text">No active announcements for this reseller.</div>
                        </div>
                      ) : (
                        <div className="announcements-list">
                          {viewingResellerAnnouncements.announcements.map(ann => (
                            <div key={ann.id} className="announcement-card" style={{
                              padding: '12px',
                              marginBottom: '10px',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px',
                              background: ann.type === 'alert' ? '#fee2e2' : '#f0f9ff',
                              borderColor: ann.type === 'alert' ? '#fecaca' : '#bae6fd'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                <strong style={{ color: ann.type === 'alert' ? '#b91c1c' : '#0369a1' }}>{ann.title}</strong>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                  {new Date(ann.createdAt).toLocaleDateString()}
                                </span>
                              </div>
                              <p style={{ margin: 0, fontSize: '14px', whiteSpace: 'pre-wrap', color: '#334155' }}>
                                {ann.message}
                              </p>
                              <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
                                Target: {ann.target === 'all' ? 'All Resellers' : 'Direct Message'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Organization Access Modal */}
              {/* Migrate Users Modal */}
              {showMigrateModal && (
                <div className="modal-overlay" onClick={resetMigrateModal}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '80vh', overflow: 'auto' }}>
                    <div className="modal-header">
                      <h3 className="modal-title">Migrate Users Between Resellers</h3>
                      <button className="modal-close" onClick={resetMigrateModal}>×</button>
                    </div>

                    {migrateResult ? (
                      <div style={{ padding: '20px' }}>
                        <div style={{ background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#065f46', marginBottom: '8px' }}>Migration Complete!</div>
                          <div style={{ color: '#047857' }}>{migrateResult.message}</div>
                          <div style={{ marginTop: '8px', fontSize: '13px', color: '#065f46' }}>
                            <div>Migrated: {migrateResult.migratedCount} users</div>
                            {migrateResult.skippedDuplicates > 0 && <div>Skipped (duplicates): {migrateResult.skippedDuplicates}</div>}
                          </div>
                        </div>
                        <button onClick={resetMigrateModal} className="btn btn-primary" style={{ width: '100%' }}>Close</button>
                      </div>
                    ) : (
                      <div style={{ padding: '20px' }}>
                        {/* Source Section */}
                        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                          <div style={{ flex: 1 }}>
                            <label className="form-label" style={{ fontWeight: 'bold', color: '#dc2626' }}>Source (From)</label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                              <button
                                onClick={() => { setMigrateSourceType('traditional'); setMigrateSourceId(''); setMigratePreview(null); }}
                                className={`btn ${migrateSourceType === 'traditional' ? 'btn-primary' : 'btn-secondary'}`}
                                style={{ flex: 1, padding: '6px 8px', fontSize: '12px' }}
                              >Reseller</button>
                              <button
                                onClick={() => { setMigrateSourceType('credits-pool'); setMigrateSourceId(''); setMigratePreview(null); }}
                                className={`btn ${migrateSourceType === 'credits-pool' ? 'btn-primary' : 'btn-secondary'}`}
                                style={{ flex: 1, padding: '6px 8px', fontSize: '12px' }}
                              >Credits Pool</button>
                            </div>
                            <select
                              className="form-input"
                              value={migrateSourceId}
                              onChange={e => { setMigrateSourceId(e.target.value); setMigratePreview(null); setMigrateResult(null); }}
                            >
                              <option value="">-- Select Source --</option>
                              {migrateSourceType === 'traditional'
                                ? resellers.map(r => (
                                  <option key={r.id} value={r.id} disabled={migrateTargetType === 'traditional' && r.id === parseInt(migrateTargetId)}>
                                    {r.username} ({r.usedLicenses || 0} users)
                                  </option>
                                ))
                                : <>
                                    {cpResellers.map(r => (
                                      <option key={r.id} value={r.id} disabled={migrateTargetType === 'credits-pool' && r.id === parseInt(migrateTargetId)}>
                                        {r.username} ({r.activeUsers || 0} users)
                                      </option>
                                    ))}
                                    {orphanedCPResellers.length > 0 && (
                                      <optgroup label="── Deleted Resellers (Orphaned Users) ──">
                                        {orphanedCPResellers.map(r => (
                                          <option key={`orphan-${r.id}`} value={r.id} disabled={migrateTargetType === 'credits-pool' && r.id === parseInt(migrateTargetId)}>
                                            {r.username} ({r.activeUsers} users)
                                          </option>
                                        ))}
                                      </optgroup>
                                    )}
                                  </>
                              }
                            </select>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', paddingTop: '40px', fontSize: '24px' }}>→</div>
                          <div style={{ flex: 1 }}>
                            <label className="form-label" style={{ fontWeight: 'bold', color: '#059669' }}>Target (To)</label>
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                              <button
                                onClick={() => { setMigrateTargetType('traditional'); setMigrateTargetId(''); setMigratePreview(null); }}
                                className={`btn ${migrateTargetType === 'traditional' ? 'btn-primary' : 'btn-secondary'}`}
                                style={{ flex: 1, padding: '6px 8px', fontSize: '12px' }}
                              >Reseller</button>
                              <button
                                onClick={() => { setMigrateTargetType('credits-pool'); setMigrateTargetId(''); setMigratePreview(null); }}
                                className={`btn ${migrateTargetType === 'credits-pool' ? 'btn-primary' : 'btn-secondary'}`}
                                style={{ flex: 1, padding: '6px 8px', fontSize: '12px' }}
                              >Credits Pool</button>
                            </div>
                            <select
                              className="form-input"
                              value={migrateTargetId}
                              onChange={e => { setMigrateTargetId(e.target.value); setMigratePreview(null); setMigrateResult(null); }}
                            >
                              <option value="">-- Select Target --</option>
                              {migrateTargetType === 'traditional'
                                ? resellers.map(r => (
                                  <option key={r.id} value={r.id} disabled={migrateSourceType === 'traditional' && r.id === parseInt(migrateSourceId)}>
                                    {r.username} ({r.maxLicenses - (r.usedLicenses || 0)} slots free)
                                  </option>
                                ))
                                : cpResellers.map(r => (
                                  <option key={r.id} value={r.id} disabled={migrateSourceType === 'credits-pool' && r.id === parseInt(migrateSourceId)}>
                                    {r.username} ({formatCredits(r.creditsBalance)} credits)
                                  </option>
                                ))
                              }
                            </select>
                          </div>
                        </div>

                        {/* Cross-type info banner */}
                        {migrateSourceType !== migrateTargetType && (
                          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px', color: '#1e40af', fontSize: '12px' }}>
                            Cross-type migration: Users will be converted from {migrateSourceType === 'traditional' ? 'Traditional' : 'Credits Pool'} format to {migrateTargetType === 'traditional' ? 'Traditional' : 'Credits Pool'} format.
                          </div>
                        )}

                        {/* Migration Mode: All or Specific */}
                        <div style={{ marginBottom: '16px' }}>
                          <label className="form-label" style={{ fontWeight: 'bold' }}>Which users to migrate?</label>
                          <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                            <button
                              onClick={() => { setMigrateMode('all'); setMigratePreview(null); }}
                              className={`btn ${migrateMode === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                              style={{ flex: 1, padding: '8px', fontSize: '13px' }}
                            >All Users</button>
                            <button
                              onClick={() => { setMigrateMode('specific'); setMigratePreview(null); }}
                              className={`btn ${migrateMode === 'specific' ? 'btn-primary' : 'btn-secondary'}`}
                              style={{ flex: 1, padding: '8px', fontSize: '13px' }}
                            >Specific Users</button>
                          </div>
                          {migrateMode === 'specific' && (
                            <div>
                              <textarea
                                className="form-input"
                                value={migrateEmailInput}
                                onChange={e => { setMigrateEmailInput(e.target.value); setMigratePreview(null); }}
                                placeholder={"Enter emails to migrate (one per line, or comma/semicolon separated):\nuser1@example.com\nuser2@example.com\nuser3@example.com"}
                                rows={5}
                                style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px', resize: 'vertical', boxSizing: 'border-box' }}
                              />
                              <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                                {parseMigrateEmails().length} valid email(s) entered
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Preview Button */}
                        <button
                          onClick={handleMigratePreview}
                          disabled={!migrateSourceId || !migrateTargetId || migrateLoading || (migrateMode === 'specific' && parseMigrateEmails().length === 0)}
                          className="btn btn-primary"
                          style={{ width: '100%', marginBottom: '16px' }}
                        >
                          {migrateLoading ? 'Loading preview...' : 'Preview Migration'}
                        </button>

                        {/* Preview Results */}
                        {migratePreview && (
                          <div>
                            {/* Summary */}
                            <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                              <div style={{ flex: 1, background: '#fef2f2', padding: '12px', borderRadius: '8px', border: '1px solid #fecaca' }}>
                                <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: 'bold' }}>
                                  FROM{migratePreview.sourceType === 'credits-pool' ? ' [CP]' : ''}: {migratePreview.source.username}
                                </div>
                                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc2626' }}>{migratePreview.source.totalUsers} users</div>
                                <div style={{ fontSize: '11px', color: '#6b7280' }}>Group: {migratePreview.source.userGroup}</div>
                              </div>
                              <div style={{ flex: 1, background: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                <div style={{ fontSize: '11px', color: '#166534', fontWeight: 'bold' }}>
                                  TO{migratePreview.targetType === 'credits-pool' ? ' [CP]' : ''}: {migratePreview.target.username}
                                </div>
                                {migratePreview.targetType === 'credits-pool' ? (
                                  <>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#059669' }}>{migratePreview.target.totalUsers} users</div>
                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{formatCredits(migratePreview.target.creditsBalance)} credits available</div>
                                  </>
                                ) : (
                                  <>
                                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#059669' }}>{migratePreview.target.totalUsers} / {migratePreview.target.maxLicenses}</div>
                                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{migratePreview.target.availableSlots} slots available</div>
                                  </>
                                )}
                              </div>
                            </div>

                            {/* Warnings */}
                            {migratePreview.willExceedCapacity && (
                              <div style={{ background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px', marginBottom: '12px', color: '#92400e', fontSize: '13px' }}>
                                Warning: Migration will exceed target reseller's license capacity ({migratePreview.target.maxLicenses}). Migration will still proceed but target will be over-allocated.
                              </div>
                            )}

                            {migratePreview.totalDuplicates > 0 && (
                              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '12px', marginBottom: '12px', color: '#9a3412', fontSize: '13px' }}>
                                {migratePreview.totalDuplicates} user(s) already exist in the target reseller and will be skipped: {migratePreview.duplicates.map(d => d.email).join(', ')}
                              </div>
                            )}

                            {/* User Selection */}
                            {migratePreview.usersToMigrate.length > 0 && (
                              <div style={{ marginBottom: '16px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                  <strong style={{ fontSize: '14px' }}>Users to Migrate ({migrateSelectAll ? migratePreview.usersToMigrate.length : migrateSelectedEmails.length} selected)</strong>
                                  <label style={{ fontSize: '12px', cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={migrateSelectAll}
                                      onChange={e => {
                                        setMigrateSelectAll(e.target.checked);
                                        if (e.target.checked) {
                                          setMigrateSelectedEmails(migratePreview.usersToMigrate.map(u => u.email));
                                        } else {
                                          setMigrateSelectedEmails([]);
                                        }
                                      }}
                                      style={{ marginRight: '4px' }}
                                    />
                                    Select All
                                  </label>
                                </div>
                                <div style={{ maxHeight: '250px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                                  <table className="data-table" style={{ margin: 0 }}>
                                    <thead>
                                      <tr>
                                        <th style={{ width: '30px' }}></th>
                                        <th>Email</th>
                                        <th>Duration</th>
                                        <th>Expires</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {migratePreview.usersToMigrate.map(user => (
                                        <tr key={user.email}>
                                          <td>
                                            <input
                                              type="checkbox"
                                              checked={migrateSelectAll || migrateSelectedEmails.includes(user.email)}
                                              disabled={migrateSelectAll}
                                              onChange={e => {
                                                if (e.target.checked) {
                                                  setMigrateSelectedEmails([...migrateSelectedEmails, user.email]);
                                                } else {
                                                  setMigrateSelectedEmails(migrateSelectedEmails.filter(em => em !== user.email));
                                                }
                                              }}
                                            />
                                          </td>
                                          <td style={{ fontSize: '12px' }}>{user.email}</td>
                                          <td style={{ fontSize: '12px' }}>{user.durationMonths}mo</td>
                                          <td style={{ fontSize: '12px' }}>{user.expiresAt ? new Date(user.expiresAt).toLocaleDateString() : '-'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {migratePreview.usersToMigrate.length === 0 && (
                              <div style={{ background: '#fef2f2', padding: '16px', borderRadius: '8px', textAlign: 'center', color: '#991b1b', marginBottom: '16px' }}>
                                No users available to migrate (all may be duplicates in the target).
                              </div>
                            )}

                            {/* Execute Button */}
                            <button
                              onClick={handleMigrateExecute}
                              disabled={migrateLoading || migratePreview.usersToMigrate.length === 0 || (!migrateSelectAll && migrateSelectedEmails.length === 0)}
                              className="btn btn-danger"
                              style={{ width: '100%', padding: '12px', fontSize: '15px' }}
                            >
                              {migrateLoading ? 'Migrating...' : `Migrate ${migrateSelectAll ? migratePreview.usersToMigrate.length : migrateSelectedEmails.length} User(s)`}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {editOrgModalOpen && (
                <div className="modal-overlay" style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                  <div className="modal-content" style={{
                    background: '#fff', borderRadius: '12px', padding: '24px', width: '90%', maxWidth: '600px', maxHeight: '80vh', overflow: 'auto'
                  }}>
                    <h3 style={{ marginBottom: '16px' }}>Edit Organization & Product Access</h3>
                    <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
                      Select which organizations and products this reseller can access.
                    </p>

                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px', maxHeight: '400px', overflowY: 'auto' }}>
                      {organizations.map(org => {
                        const isOrgSelected = editForm.organizationAccess.some(a => a.organizationId === org.id);
                        const orgAccess = editForm.organizationAccess.find(a => a.organizationId === org.id);
                        const products = org.products || [];

                        return (
                          <div key={org.id} style={{
                            marginBottom: '12px', padding: '12px',
                            background: isOrgSelected ? '#f0f9ff' : '#f8fafc',
                            borderRadius: '6px', border: isOrgSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0'
                          }}>
                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '8px' }}>
                              <input
                                type="checkbox"
                                checked={isOrgSelected}
                                onChange={e => {
                                  if (e.target.checked) {
                                    setEditForm({
                                      ...editForm,
                                      organizationAccess: [...editForm.organizationAccess, { organizationId: org.id, products: [] }]
                                    });
                                  } else {
                                    setEditForm({
                                      ...editForm,
                                      organizationAccess: editForm.organizationAccess.filter(a => a.organizationId !== org.id)
                                    });
                                  }
                                }}
                                style={{ marginRight: '10px' }}
                              />
                              <span style={{ fontWeight: 600, fontSize: '14px' }}>{org.name}</span>
                            </label>

                            {isOrgSelected && products.length > 0 && (
                              <div style={{ marginLeft: '24px', marginTop: '8px' }}>
                                {products.map((p, i) => (
                                  <label key={i} style={{ display: 'flex', alignItems: 'center', padding: '4px 0', cursor: 'pointer' }}>
                                    <input
                                      type="checkbox"
                                      checked={orgAccess?.products?.includes(p) || false}
                                      onChange={e => {
                                        const newAccess = editForm.organizationAccess.map(a => {
                                          if (a.organizationId === org.id) {
                                            if (e.target.checked) {
                                              return { ...a, products: [...(a.products || []), p] };
                                            } else {
                                              return { ...a, products: (a.products || []).filter(prod => prod !== p) };
                                            }
                                          }
                                          return a;
                                        });
                                        setEditForm({ ...editForm, organizationAccess: newAccess });
                                      }}
                                      style={{ marginRight: '8px' }}
                                    />
                                    <span style={{ fontSize: '13px' }}>{p}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginTop: '20px', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => { setEditOrgModalOpen(false); setEditingReseller(null); setEditForm({ ...editForm, organizationAccess: [] }); }}
                        className="btn btn-secondary"
                      >
                        Cancel
                      </button>
                      <button onClick={handleUpdateOrgAccess} disabled={loading} className="btn btn-primary">
                        {loading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Reseller Details Tab */}
          {activeTab === 'resellerDetails' && selectedReseller && (
            <>
              <div className="card" style={{ marginBottom: '20px' }}>
                <button onClick={() => { setActiveTab('resellers'); setSelectedReseller(null); }} className="btn btn-secondary" style={{ marginBottom: '16px' }}>
                  ← Back to Resellers
                </button>

                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-label">Reseller</div>
                    <div className="stat-value" style={{ fontSize: '20px' }}>{selectedReseller.username}</div>
                  </div>
                  <div className="stat-card" style={{ borderLeftColor: '#0b7da6' }}>
                    <div className="stat-label">User Group</div>
                    <div className="stat-value" style={{ fontSize: '20px' }}>{selectedReseller.userGroup}</div>
                  </div>
                  <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                    <div className="stat-label">License Usage</div>
                    <div className="stat-value">{resellerUsers.length} / {selectedReseller.maxLicenses}</div>
                  </div>
                </div>

                {/* License Duration Breakdown */}
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  <h4 style={{ marginBottom: '12px', color: '#6b7280' }}>License Duration Breakdown</h4>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ background: '#dbeafe', padding: '16px 24px', borderRadius: '8px', textAlign: 'center', flex: '1', minWidth: '120px' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1e40af' }}>{licenseBreakdown.monthly}</div>
                      <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px' }}>Monthly</div>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>(1-2 months)</div>
                    </div>
                    <div style={{ background: '#fef3c7', padding: '16px 24px', borderRadius: '8px', textAlign: 'center', flex: '1', minWidth: '120px' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#92400e' }}>{licenseBreakdown.quarterly}</div>
                      <div style={{ fontSize: '12px', color: '#d97706', marginTop: '4px' }}>Quarterly</div>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>(3-6 months)</div>
                    </div>
                    <div style={{ background: '#d1fae5', padding: '16px 24px', borderRadius: '8px', textAlign: 'center', flex: '1', minWidth: '120px' }}>
                      <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#065f46' }}>{licenseBreakdown.yearly}</div>
                      <div style={{ fontSize: '12px', color: '#059669', marginTop: '4px' }}>Yearly</div>
                      <div style={{ fontSize: '10px', color: '#6b7280' }}>(12+ months)</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">{selectedReseller.username}'s Customers</h3>
                  <button onClick={() => fetchResellerUsers(selectedReseller.id)} disabled={loadingResellerUsers} className="btn btn-secondary">
                    Refresh
                  </button>
                </div>

                {/* Quick Add User Form */}
                <div style={{
                  background: '#f0f9ff',
                  padding: '16px',
                  borderRadius: '8px',
                  marginBottom: '16px',
                  border: '1px solid #bae6fd'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                    <span style={{ fontSize: '16px', marginRight: '8px' }}><Icon name="plus" size={16} /></span>
                    <strong style={{ color: '#0369a1' }}>Quick Add User to {selectedReseller.username}</strong>
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: '12px',
                      color: resellerUsers.length >= selectedReseller.maxLicenses ? '#dc2626' : '#059669'
                    }}>
                      {selectedReseller.maxLicenses - resellerUsers.length} licenses available
                    </span>
                  </div>
                  <form onSubmit={handleAddUserToReseller} style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '2', minWidth: '200px' }}>
                      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Email Address *</label>
                      <input
                        type="email"
                        value={addUserToResellerForm.email}
                        onChange={(e) => setAddUserToResellerForm({ ...addUserToResellerForm, email: e.target.value })}
                        placeholder="user@example.com"
                        className="form-input"
                        style={{ padding: '8px' }}
                        required
                        disabled={resellerUsers.length >= selectedReseller.maxLicenses}
                      />
                    </div>
                    <div style={{ minWidth: '100px' }}>
                      <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Duration</label>
                      <select
                        value={addUserToResellerForm.durationMonths}
                        onChange={(e) => setAddUserToResellerForm({ ...addUserToResellerForm, durationMonths: parseInt(e.target.value) })}
                        className="form-input"
                        style={{ padding: '8px' }}
                        disabled={resellerUsers.length >= selectedReseller.maxLicenses}
                      >
                        <option value={1}>1 Month</option>
                        <option value={3}>3 Months</option>
                        <option value={6}>6 Months</option>
                        <option value={12}>12 Months</option>
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={addingUserToReseller || !addUserToResellerForm.email || resellerUsers.length >= selectedReseller.maxLicenses}
                      className="btn btn-primary"
                      style={{ padding: '8px 16px' }}
                    >
                      {addingUserToReseller ? 'Adding...' : 'Add User'}
                    </button>
                  </form>
                  {resellerUsers.length >= selectedReseller.maxLicenses && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#dc2626' }}>
                      License limit reached. Increase max licenses to add more users.
                    </div>
                  )}
                </div>

                {loadingResellerUsers && <div className="loading">Loading users...</div>}

                {!loadingResellerUsers && resellerUsers.length > 0 && (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Email Address</th>
                        <th>Duration</th>
                        <th>Expires</th>
                        <th>Products</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resellerUsers.map((user, index) => {
                        // Calculate progress
                        const totalDays = user.durationMonths ? user.durationMonths * 30 : 30;
                        const assignedAt = user.assignedAt ? new Date(user.assignedAt) : null;
                        const now = new Date();
                        const daysUsed = assignedAt ? Math.floor((now - assignedAt) / (1000 * 60 * 60 * 24)) : 0;
                        const progressPercent = totalDays > 0 ? Math.min(100, Math.max(0, ((totalDays - (user.daysLeft || 0)) / totalDays) * 100)) : 0;

                        let statusBadge = '●';
                        let statusColor = '#10b981';
                        let barColor = '#10b981';
                        if (user.status === 'expired') { statusBadge = '●'; statusColor = '#ef4444'; barColor = '#ef4444'; }
                        else if (user.status === 'expiring') { statusBadge = '●'; statusColor = '#f59e0b'; barColor = '#f59e0b'; }

                        return (
                          <tr key={index}>
                            <td>
                              <div>{user.email}</div>
                              <div style={{ fontSize: '11px', color: '#999' }}>{user.firstname} {user.lastname}</div>
                            </td>
                            <td>{user.durationMonths || 1} mo</td>
                            <td style={{ minWidth: '160px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: statusColor }}>{statusBadge}</span>
                                <div style={{ flex: 1 }}>
                                  {/* Progress Bar */}
                                  <div style={{
                                    width: '100%', height: '6px', background: '#e5e7eb',
                                    borderRadius: '3px', overflow: 'hidden', marginBottom: '4px'
                                  }}>
                                    <div style={{
                                      width: `${progressPercent}%`, height: '100%',
                                      background: barColor, borderRadius: '3px'
                                    }}></div>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                                    <span style={{ color: '#666' }}>{daysUsed}d used</span>
                                    <span style={{ color: statusColor, fontWeight: 600 }}>
                                      {user.daysLeft !== null ? (user.daysLeft <= 0 ? 'Expired' : `${user.daysLeft}d left`) : 'Active'}
                                    </span>
                                  </div>
                                  {user.expiresAt && (
                                    <div style={{ fontSize: '9px', color: '#999', marginTop: '2px' }}>
                                      Expires: {new Date(user.expiresAt).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {user.products && user.products.length > 0 ? user.products.join(', ') : '-'}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </table>
                )}

                {!loadingResellerUsers && resellerUsers.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="users" size={16} /></div>
                    <div className="empty-text">No customers assigned to this reseller yet</div>
                  </div>
                )}
              </div>

              {/* Activity Log Section */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Activity Log</h3>
                  <button onClick={() => fetchActivityLogs(selectedReseller.id)} disabled={loadingActivityLogs} className="btn btn-secondary">
                    Refresh
                  </button>
                </div>

                {loadingActivityLogs && <div className="loading">Loading activity logs...</div>}

                {!loadingActivityLogs && resellerActivityLogs.length > 0 && (
                  <div style={{ maxHeight: '400px', overflowY: 'auto', padding: '12px 0' }}>
                    {resellerActivityLogs.map((log) => {
                      const actionColors = {
                        'USER_ASSIGNED': { bg: '#d1fae5', text: '#065f46', icon: '✓' },
                        'USER_REMOVED': { bg: '#fee2e2', text: '#991b1b', icon: '✕' },
                        'USER_UPDATED': { bg: '#dbeafe', text: '#1e40af', icon: '✎' }
                      };
                      const style = actionColors[log.action] || { bg: '#f3f4f6', text: '#374151', icon: '✎' };
                      return (
                        <div key={log.id} style={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          padding: '12px 16px',
                          borderBottom: '1px solid #e5e7eb',
                          background: style.bg + '33'
                        }}>
                          <span style={{ fontSize: '18px', marginRight: '12px' }}>{style.icon}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', color: style.text, marginBottom: '4px' }}>
                              {log.action === 'USER_ASSIGNED' && `Assigned user: ${log.details?.email}`}
                              {log.action === 'USER_REMOVED' && `Removed user: ${log.details?.email}`}
                              {log.action === 'USER_UPDATED' && `Updated user: ${log.details?.email}`}
                            </div>
                            {log.details && (
                              <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                                {log.details.organizationName && <span>Org: {log.details.organizationName} | </span>}
                                {log.details.durationMonths && <span>Duration: {log.details.durationMonths} months | </span>}
                                {log.details.products && log.details.products.length > 0 && (
                                  <span>Products: {log.details.products.length}</span>
                                )}
                              </div>
                            )}
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>
                              {new Date(log.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {!loadingActivityLogs && resellerActivityLogs.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="clipboard" size={16} /></div>
                    <div className="empty-text">No activity recorded yet</div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Assign User Tab */}
          {activeTab === 'assign' && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Add User to Organization</h3>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Select Organization *</label>
                  <select
                    className="form-input"
                    value={selectedOrgForAssign?.id || ''}
                    onChange={e => {
                      const org = organizations.find(o => o.id === parseInt(e.target.value));
                      setSelectedOrgForAssign(org || null);
                      setSelectedProductsForAssign(org?.products || []);
                    }}
                    required
                  >
                    <option value="">-- Select Organization --</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="user@example.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </div>
                {selectedOrgForAssign && (
                  <div className="form-group">
                    <label className="form-label">Select Products *</label>
                    <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '4px' }}>
                      {selectedOrgForAssign.products && selectedOrgForAssign.products.length > 0
                        ? assignableProducts(selectedOrgForAssign.products).map((p, i) => (
                          <label key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selectedProductsForAssign.includes(p)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProductsForAssign([...selectedProductsForAssign, p]);
                                } else {
                                  setSelectedProductsForAssign(selectedProductsForAssign.filter(prod => prod !== p));
                                }
                              }}
                              style={{ marginRight: '10px', width: '18px', height: '18px' }}
                            />
                            <span style={{ fontSize: '13px' }}>{p}</span>
                          </label>
                        ))
                        : <span style={{ color: '#999' }}>No products configured</span>}
                    </div>
                    {selectedProductsForAssign.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#059669' }}>
                        {selectedProductsForAssign.length} product(s) selected
                      </div>
                    )}
                  </div>
                )}

                {/* Reseller Selection - Optional */}
                <div className="form-group">
                  <label className="form-label">Assign to Reseller (Optional)</label>
                  <select
                    className="form-input"
                    value={selectedResellerForAssign?.id || ''}
                    onChange={e => {
                      const reseller = resellers.find(r => r.id === parseInt(e.target.value));
                      setSelectedResellerForAssign(reseller || null);
                    }}
                  >
                    <option value="">-- No Reseller (Direct Assignment) --</option>
                    {resellers.map(r => (
                      <option key={r.id} value={r.id}>
                        {r.username} ({r.usedLicenses || 0}/{r.maxLicenses} licenses)
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                    If selected, the user will count towards this reseller's license quota
                  </div>
                </div>

                {/* Duration Selection */}
                <div className="form-group">
                  <label className="form-label">License Duration</label>
                  <select
                    className="form-input"
                    value={assignDurationMonths}
                    onChange={e => setAssignDurationMonths(parseInt(e.target.value))}
                    disabled={!!assignCustomExpiry}
                  >
                    <option value={1}>1 Month</option>
                    <option value={3}>3 Months</option>
                    <option value={6}>6 Months</option>
                    <option value={12}>12 Months</option>
                  </select>
                </div>

                {/* Custom Expiry Date (optional) */}
                <div className="form-group">
                  <label className="form-label">Custom Expiry Date (optional)</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="date"
                      className="form-input"
                      value={assignCustomExpiry}
                      min={new Date().toISOString().slice(0, 10)}
                      onChange={e => setAssignCustomExpiry(e.target.value)}
                      style={{ flex: 1 }}
                    />
                    {assignCustomExpiry && (
                      <button type="button" onClick={() => setAssignCustomExpiry('')} className="btn btn-secondary" style={{ whiteSpace: 'nowrap' }}>
                        Clear
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                    Pick an exact expiry date to override the duration above. Applies only when assigning through a reseller (shows in All Customers).
                  </div>
                </div>

                <button type="submit" disabled={loading || !selectedOrgForAssign} className="btn btn-primary" style={{ width: '100%' }}>
                  {loading ? 'Processing...' : 'Assign User'}
                </button>
              </form>
            </div>
          )}

          {/* Bulk Import Tab */}
          {activeTab === 'bulk' && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Bulk Import Users</h3>
              </div>
              <form onSubmit={handleBulkAssign}>
                <div className="form-group">
                  <label className="form-label">Select Organization *</label>
                  <select
                    className="form-input"
                    value={selectedOrgForAssign?.id || ''}
                    onChange={e => {
                      const org = organizations.find(o => o.id === parseInt(e.target.value));
                      setSelectedOrgForAssign(org || null);
                      setSelectedProductsForAssign(org?.products || []);
                    }}
                    required
                  >
                    <option value="">-- Select Organization --</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
                {selectedOrgForAssign && (
                  <div className="form-group">
                    <label className="form-label">Select Products *</label>
                    <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '4px' }}>
                      {selectedOrgForAssign.products && selectedOrgForAssign.products.length > 0
                        ? assignableProducts(selectedOrgForAssign.products).map((p, i) => (
                          <label key={i} style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={selectedProductsForAssign.includes(p)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProductsForAssign([...selectedProductsForAssign, p]);
                                } else {
                                  setSelectedProductsForAssign(selectedProductsForAssign.filter(prod => prod !== p));
                                }
                              }}
                              style={{ marginRight: '10px', width: '18px', height: '18px' }}
                            />
                            <span style={{ fontSize: '13px' }}>{p}</span>
                          </label>
                        ))
                        : <span style={{ color: '#999' }}>No products configured</span>}
                    </div>
                    {selectedProductsForAssign.length > 0 && (
                      <div style={{ marginTop: '8px', fontSize: '12px', color: '#059669' }}>
                        {selectedProductsForAssign.length} product(s) selected
                      </div>
                    )}
                  </div>
                )}

                {/* Reseller Selection - Optional */}
                <div className="form-group">
                  <label className="form-label">Assign to Reseller (Optional)</label>
                  <select
                    className="form-input"
                    value={selectedResellerForAssign?.id || ''}
                    onChange={e => {
                      const val = e.target.value;
                      if (val.startsWith('cp-')) {
                        const cpId = parseInt(val.replace('cp-', ''));
                        const cpReseller = cpResellers.find(r => r.id === cpId);
                        setSelectedResellerForAssign(cpReseller ? { ...cpReseller, id: val, type: 'credits-pool' } : null);
                      } else {
                        const reseller = resellers.find(r => r.id === parseInt(val));
                        setSelectedResellerForAssign(reseller ? { ...reseller, type: 'traditional' } : null);
                      }
                    }}
                  >
                    <option value="">-- No Reseller (Direct Assignment) --</option>
                    <optgroup label="Traditional Resellers">
                      {resellers.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.username} ({r.usedLicenses || 0}/{r.maxLicenses} licenses)
                        </option>
                      ))}
                    </optgroup>
                    <optgroup label="Credits Pool Resellers">
                      {cpResellers.map(r => (
                        <option key={`cp-${r.id}`} value={`cp-${r.id}`}>
                          [CP] {r.username} ({formatCredits(r.creditsBalance)} credits)
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '4px' }}>
                    If selected, all imported users will count towards this reseller's license quota
                  </div>
                </div>

                {/* Duration Selection */}
                <div className="form-group">
                  <label className="form-label">License Duration</label>
                  <select
                    className="form-input"
                    value={assignDurationMonths}
                    onChange={e => setAssignDurationMonths(parseInt(e.target.value))}
                  >
                    <option value={1}>1 Month</option>
                    <option value={3}>3 Months</option>
                    <option value={6}>6 Months</option>
                    <option value={12}>12 Months</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Email Addresses (comma or newline separated) *</label>
                  <textarea
                    className="form-input"
                    placeholder="user1@example.com, user2@example.com&#10;user3@example.com"
                    value={csvContent}
                    onChange={e => setCsvContent(e.target.value)}
                    rows={8}
                    style={{ fontFamily: 'monospace', resize: 'vertical' }}
                    required
                  />
                </div>
                <button type="submit" disabled={loading || !selectedOrgForAssign || selectedProductsForAssign.length === 0} className="btn btn-primary" style={{ width: '100%' }}>
                  {loading ? 'Processing...' : 'Import & Assign Users'}
                </button>
              </form>
            </div>
          )}

          {/* All Customers Tab - Shows all reseller-assigned users */}
          {activeTab === 'customers' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Total Customers</div>
                  <div className="stat-value">{customersSummary.total}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                  <div className="stat-label">Active</div>
                  <div className="stat-value">{customersSummary.active}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
                  <div className="stat-label">Expiring Soon</div>
                  <div className="stat-value">{customersSummary.expiring}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#ef4444' }}>
                  <div className="stat-label">Expired</div>
                  <div className="stat-value">{customersSummary.expired}</div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">All Reseller Customers</h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {selectedCustomers.length > 0 && (
                      <button
                        onClick={handleBulkDeleteCustomers}
                        disabled={loading}
                        className="btn btn-danger"
                      >
                        Delete Selected ({selectedCustomers.length})
                      </button>
                    )}
                    <button onClick={exportAllCustomers} disabled={loadingCustomers || allCustomers.length === 0} className="btn btn-primary">
                      Export CSV
                    </button>
                    <button onClick={fetchAllCustomers} disabled={loadingCustomers} className="btn btn-secondary">
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Search and Bulk Selection - Always show when not loading */}
                {!loadingCustomers && (
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedCustomers.length === filteredCustomers.length && filteredCustomers.length > 0}
                        onChange={toggleSelectAllCustomers}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151' }}>Select users for bulk actions</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '16px' }}><Icon name="search" size={16} /></span>
                      <input
                        type="text"
                        placeholder="Search users by email, reseller name, or group..."
                        value={customerSearchQuery}
                        onChange={(e) => setCustomerSearchQuery(e.target.value)}
                        className="form-input"
                        style={{ paddingLeft: '40px' }}
                      />
                    </div>

                    {/* Advanced Filters */}
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
                      {/* Organization Filter */}
                      <div style={{ minWidth: '180px' }}>
                        <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Organization</label>
                        <select
                          value={customerOrgFilter}
                          onChange={(e) => setCustomerOrgFilter(e.target.value)}
                          className="form-input"
                          style={{ fontSize: '13px', padding: '8px' }}
                        >
                          <option value="">All Organizations</option>
                          {organizations.map(org => (
                            <option key={org.id} value={org.name}>{org.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Duration Filter */}
                      <div style={{ minWidth: '140px' }}>
                        <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Duration</label>
                        <select
                          value={customerDurationFilter}
                          onChange={(e) => setCustomerDurationFilter(e.target.value)}
                          className="form-input"
                          style={{ fontSize: '13px', padding: '8px' }}
                        >
                          <option value="">All Durations</option>
                          <option value="1">1 Month</option>
                          <option value="3">3 Months</option>
                          <option value="6">6 Months</option>
                          <option value="12">12 Months</option>
                        </select>
                      </div>

                      {/* Status Filter */}
                      <div style={{ minWidth: '140px' }}>
                        <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Status</label>
                        <select
                          value={customerStatusFilter}
                          onChange={(e) => setCustomerStatusFilter(e.target.value)}
                          className="form-input"
                          style={{ fontSize: '13px', padding: '8px' }}
                        >
                          <option value="">All Status</option>
                          <option value="active">Active</option>
                          <option value="expiring">Expiring Soon</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>

                      {/* Days Remaining Filter (only for expired) */}
                      <div style={{ minWidth: '160px' }}>
                        <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>Expired Days ≥</label>
                        <input
                          type="number"
                          value={customerDaysFilter}
                          onChange={(e) => setCustomerDaysFilter(e.target.value)}
                          placeholder="e.g., 7"
                          className="form-input"
                          style={{ fontSize: '13px', padding: '8px' }}
                          min="0"
                        />
                      </div>

                      {/* Apply Filters */}
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                        <button
                          onClick={() => fetchAllCustomers(1)}
                          className="btn btn-primary"
                          style={{ padding: '8px 16px', fontSize: '12px' }}
                        >
                          Apply
                        </button>
                        {(customerSearchQuery || customerOrgFilter || customerDurationFilter || customerDaysFilter || customerStatusFilter) && (
                          <button
                            onClick={() => {
                              setCustomerSearchQuery('');
                              setCustomerOrgFilter('');
                              setCustomerDurationFilter('');
                              setCustomerDaysFilter('');
                              setCustomerStatusFilter('');
                              setCustomersPage(1);
                              // Trigger fetch after clearing
                              setTimeout(() => fetchAllCustomers(1), 0);
                            }}
                            className="btn btn-secondary"
                            style={{ padding: '8px 12px', fontSize: '12px' }}
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>

                    {(customerSearchQuery || customerOrgFilter || customerDurationFilter || customerDaysFilter) && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                        Showing {filteredCustomers.length} of {allCustomers.length} customers
                        {customerDaysFilter && ` (expiring in ≤ ${customerDaysFilter} days)`}
                      </div>
                    )}
                  </div>
                )}

                {/* Bulk Delete by Email Section */}
                {!loadingCustomers && allCustomers.length > 0 && (
                  <div style={{ marginBottom: '16px', padding: '16px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: bulkDeleteEmails ? '12px' : '0' }}>
                      <strong style={{ color: '#991b1b', fontSize: '14px' }}>Bulk Delete by Email</strong>
                      <button
                        onClick={() => setBulkDeleteEmails(bulkDeleteEmails ? '' : ' ')}
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        {bulkDeleteEmails ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {bulkDeleteEmails !== '' && (
                      <form onSubmit={handleBulkDeleteByEmail}>
                        <textarea
                          className="form-input"
                          placeholder="Paste email addresses here (comma, newline, or semicolon separated)&#10;user1@example.com, user2@example.com&#10;user3@example.com"
                          value={bulkDeleteEmails === ' ' ? '' : bulkDeleteEmails}
                          onChange={e => setBulkDeleteEmails(e.target.value)}
                          rows={5}
                          style={{ fontFamily: 'monospace', resize: 'vertical', marginBottom: '8px' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            {bulkDeleteEmails.trim() && bulkDeleteEmails !== ' ' ? (
                              <>{bulkDeleteEmails.split(/[\n,;]+/).filter(e => e.trim() && e.includes('@')).length} email(s) detected</>
                            ) : (
                              'Paste emails to delete multiple customers at once'
                            )}
                          </span>
                          <button
                            type="submit"
                            disabled={loading || !bulkDeleteEmails.trim() || bulkDeleteEmails === ' '}
                            className="btn btn-danger"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                          >
                            {loading ? 'Deleting...' : 'Delete Listed Emails'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* Bulk Extend by Email Section */}
                {!loadingCustomers && allCustomers.length > 0 && (
                  <div style={{ marginBottom: '16px', padding: '16px', background: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: bulkExtendEmails ? '12px' : '0' }}>
                      <strong style={{ color: '#065f46', fontSize: '14px' }}>Bulk Extend by Email</strong>
                      <button
                        onClick={() => setBulkExtendEmails(bulkExtendEmails ? '' : ' ')}
                        className="btn btn-secondary"
                        style={{ padding: '4px 8px', fontSize: '12px' }}
                      >
                        {bulkExtendEmails ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    {bulkExtendEmails !== '' && (
                      <form onSubmit={handleBulkExtendByEmail}>
                        <div style={{ marginBottom: '8px' }}>
                          <label style={{ fontSize: '12px', color: '#065f46' }}>Extension Duration</label>
                          <select
                            value={bulkExtendMonths}
                            onChange={e => setBulkExtendMonths(parseInt(e.target.value))}
                            className="form-input"
                            style={{ marginTop: '4px' }}
                          >
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                              <option key={m} value={m}>{m} {m === 1 ? 'Month' : 'Months'}</option>
                            ))}
                          </select>
                        </div>
                        <textarea
                          className="form-input"
                          placeholder="Paste email addresses here (comma, newline, or semicolon separated)"
                          value={bulkExtendEmails === ' ' ? '' : bulkExtendEmails}
                          onChange={e => setBulkExtendEmails(e.target.value)}
                          rows={5}
                          style={{ fontFamily: 'monospace', resize: 'vertical', marginBottom: '8px' }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: '#666' }}>
                            {bulkExtendEmails.trim() && bulkExtendEmails !== ' ' ? (
                              <>{bulkExtendEmails.split(/[\n,;]+/).filter(e => e.trim() && e.includes('@')).length} email(s) detected</>
                            ) : (
                              'Paste emails to extend multiple customers at once'
                            )}
                          </span>
                          <button
                            type="submit"
                            disabled={loading || !bulkExtendEmails.trim() || bulkExtendEmails === ' '}
                            className="btn btn-primary"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                          >
                            {loading ? 'Extending...' : 'Extend Listed Emails'}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {loadingCustomers && <div className="loading">Loading customers...</div>}

                {!loadingCustomers && filteredCustomers.length > 0 && (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }}></th>
                        <th>Email</th>
                        <th>Reseller</th>
                        <th>Organization</th>
                        <th>Duration</th>
                        <th>Expires</th>
                        <th>Products</th>
                        <th style={{ textAlign: 'center' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCustomers.map((user, index) => {
                        // Calculate progress
                        const totalDays = user.durationMonths ? user.durationMonths * 30 : 30;
                        const assignedAt = user.assignedAt ? new Date(user.assignedAt) : null;
                        const now = new Date();
                        const daysUsed = assignedAt ? Math.floor((now - assignedAt) / (1000 * 60 * 60 * 24)) : 0;
                        const progressPercent = totalDays > 0 ? Math.min(100, Math.max(0, ((totalDays - (user.daysLeft || 0)) / totalDays) * 100)) : 0;

                        let statusBadge = '●';
                        let statusColor = '#10b981';
                        let barColor = '#10b981';
                        if (user.status === 'expired') { statusBadge = '●'; statusColor = '#ef4444'; barColor = '#ef4444'; }
                        else if (user.status === 'expiring') { statusBadge = '●'; statusColor = '#f59e0b'; barColor = '#f59e0b'; }

                        const isSelected = selectedCustomers.some(c => `${c.resellerId}-${c.email}` === `${user.resellerId}-${user.email}`);

                        return (
                          <tr key={index} style={{ background: isSelected ? '#f0f9ff' : 'transparent' }}>
                            <td>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleCustomerSelection(user)}
                                style={{ width: '16px', height: '16px' }}
                              />
                            </td>
                            <td>
                              <div>{user.email}</div>
                              <div style={{ fontSize: '11px', color: '#999' }}>{user.firstname} {user.lastname}</div>
                            </td>
                            <td>
                              <div style={{ fontWeight: 500 }}>{user.resellerName}</div>
                              <div style={{ fontSize: '11px', color: '#666' }}>{user.resellerGroup}</div>
                            </td>
                            <td>
                              <span className="badge badge-info">{user.organizationName || 'Unknown'}</span>
                            </td>
                            <td>{user.durationMonths || 1} mo</td>
                            <td style={{ minWidth: '160px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ color: statusColor }}>{statusBadge}</span>
                                <div style={{ flex: 1 }}>
                                  {/* Progress Bar */}
                                  <div style={{
                                    width: '100%', height: '6px', background: '#e5e7eb',
                                    borderRadius: '3px', overflow: 'hidden', marginBottom: '4px'
                                  }}>
                                    <div style={{
                                      width: `${progressPercent}%`, height: '100%',
                                      background: barColor, borderRadius: '3px'
                                    }}></div>
                                  </div>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px' }}>
                                    <span style={{ color: '#666' }}>{daysUsed}d used</span>
                                    <span style={{ color: statusColor, fontWeight: 600 }}>
                                      {user.daysLeft !== null ? (user.daysLeft <= 0 ? 'Expired' : `${user.daysLeft}d left`) : 'Active'}
                                    </span>
                                  </div>
                                  {user.expiresAt && (
                                    <div style={{ fontSize: '9px', color: '#999', marginTop: '2px' }}>
                                      Expires: {new Date(user.expiresAt).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {user.products && user.products.length > 0 ? user.products.join(', ') : '-'}
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              <button
                                onClick={() => {
                                  setEditingCustomer(user);
                                  setEditCustomerForm({
                                    products: user.products || [],
                                    durationMonths: user.durationMonths || 1,
                                    customExpiryDate: '',
                                    organizationId: user.organizationId != null ? String(user.organizationId) : ''
                                  });
                                }}
                                className="btn btn-secondary"
                                style={{ marginRight: '4px', padding: '4px 8px', fontSize: '12px' }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteCustomer(user)}
                                className="btn btn-danger"
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                              ><Icon name="trash" size={16} /></button>
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </table>
                )}

                {/* Pagination Controls */}
                {!loadingCustomers && allCustomers.length > 0 && (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    background: '#f9fafb',
                    borderRadius: '8px',
                    marginTop: '16px'
                  }}>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      Showing {((customersPagination.page - 1) * customersPagination.limit) + 1} - {Math.min(customersPagination.page * customersPagination.limit, customersPagination.totalFiltered)} of {customersPagination.totalFiltered} customers
                      {customersPagination.totalFiltered !== customersSummary.total && (
                        <span style={{ marginLeft: '8px', color: '#999' }}>
                          (filtered from {customersSummary.total} total)
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => fetchAllCustomers(customersPage - 1)}
                        disabled={customersPage <= 1}
                        className="btn btn-secondary"
                        style={{ padding: '8px 16px', opacity: customersPage <= 1 ? 0.5 : 1 }}
                      >
                        ← Previous
                      </button>
                      <span style={{ padding: '0 16px', fontWeight: 500 }}>
                        Page {customersPagination.page} of {customersPagination.totalPages}
                      </span>
                      <button
                        onClick={() => fetchAllCustomers(customersPage + 1)}
                        disabled={!customersPagination.hasMore}
                        className="btn btn-secondary"
                        style={{ padding: '8px 16px', opacity: !customersPagination.hasMore ? 0.5 : 1 }}
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}

                {!loadingCustomers && customersSummary.total === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="users" size={16} /></div>
                    <div className="empty-text">No customers found. Resellers haven't assigned any users yet.</div>
                  </div>
                )}

                {!loadingCustomers && customersSummary.total > 0 && allCustomers.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="search" size={16} /></div>
                    <div className="empty-text">No customers match your filters</div>
                  </div>
                )}
              </div>

              {/* Pending Resume Modal — shown when server restart left migrations paused */}
              {showPendingResumeModal && pendingResumeMigrations.length > 0 && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.6)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', zIndex: 2000
                }}>
                  <div style={{
                    background: '#1e1e2e', border: '1px solid #f59e0b', borderRadius: 12,
                    padding: 32, width: '90vw', maxWidth: 640, maxHeight: '85vh', overflowY: 'auto',
                    boxShadow: '0 8px 40px rgba(0,0,0,0.6)', boxSizing: 'border-box'
                  }}>
                    <h3 style={{ margin: '0 0 8px 0', color: '#f59e0b', fontSize: 18 }}>
                      Migrations Paused by Server Restart
                    </h3>
                    <p style={{ margin: '0 0 20px 0', color: 'var(--app-muted)', fontSize: 14 }}>
                      The following migrations were interrupted when the server restarted. Do you want to continue them?
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                      {pendingResumeMigrations.map(m => (
                        <div key={m.id} style={{
                          background: 'var(--app-surface-alt)', border: '1px solid var(--app-border)', borderRadius: 8,
                          padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                        }}>
                          <div>
                            <div style={{ color: 'var(--app-text)', fontWeight: 600, fontSize: 14 }}>{m.orgName}</div>
                            <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>
                              {m.remainingCount.toLocaleString()} emails remaining
                              {m.totalUsers ? ` of ${m.totalUsers.toLocaleString()} total` : ''}
                            </div>
                          </div>
                          <button
                            style={{
                              background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 6,
                              padding: '6px 14px', fontSize: 13, cursor: 'pointer', fontWeight: 600
                            }}
                            onClick={async () => {
                              try {
                                const token = localStorage.getItem('adminToken');
                                await fetch(`/api/admin/migration/${m.id}/resume`, {
                                  method: 'POST',
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                setPendingResumeMigrations(prev => prev.filter(x => x.id !== m.id));
                                fetchMigrationLogs();
                              } catch (e) {
                                console.error('Resume failed:', e);
                              }
                            }}
                          >
                            Resume
                          </button>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                      <button
                        style={{
                          background: 'transparent', color: 'var(--app-muted)', border: '1px solid #475569',
                          borderRadius: 6, padding: '8px 18px', fontSize: 13, cursor: 'pointer'
                        }}
                        onClick={() => setShowPendingResumeModal(false)}
                      >
                        Dismiss — I'll resume later
                      </button>
                      <button
                        style={{
                          background: '#10b981', color: '#fff', border: 'none',
                          borderRadius: 6, padding: '8px 18px', fontSize: 13, cursor: 'pointer', fontWeight: 600
                        }}
                        onClick={async () => {
                          const token = localStorage.getItem('adminToken');
                          for (const m of pendingResumeMigrations) {
                            try {
                              await fetch(`/api/admin/migration/${m.id}/resume`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                            } catch (e) {
                              console.error(`Resume failed for ${m.id}:`, e);
                            }
                          }
                          setPendingResumeMigrations([]);
                          setShowPendingResumeModal(false);
                          fetchMigrationLogs();
                        }}
                      >
                        Resume All
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Product Mapping Modal for Migration */}
              {showProductMappingModal && migrationData && (
                <div style={{
                  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                  background: 'rgba(0,0,0,0.5)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', zIndex: 1000
                }}>
                  <div style={{
                    background: '#ffffff', padding: '24px', borderRadius: '12px',
                    width: '600px', maxWidth: '90%', boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                  }}>
                    <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '24px' }}><Icon name="refresh" size={16} /></span> Product Migration Mapping
                    </h3>

                    <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', padding: '12px', borderRadius: '8px', marginBottom: '20px' }}>
                      <p style={{ margin: 0, fontSize: '14px', color: '#92400e' }}><Icon name="alert" size={16} /><strong>Action Required:</strong> Product names differ between the old and new organizations.
                        Please map the products below to ensure users are migrated correctly.
                      </p>
                    </div>

                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
                        <thead>
                          <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                            <th style={{ padding: '10px', borderBottom: '2px solid #e5e7eb' }}>Old Product (Removed Org)</th>
                            <th style={{ padding: '10px', borderBottom: '2px solid #e5e7eb' }}>New Product (New Org)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {migrationData.oldProducts.map(oldProd => (
                            <tr key={oldProd} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: '12px' }}>
                                <div style={{ fontWeight: '500' }}>{oldProd}</div>
                              </td>
                              <td style={{ padding: '12px' }}>
                                <select
                                  className="form-input"
                                  style={{ width: '100%' }}
                                  value={productMapping[oldProd] || ''}
                                  onChange={(e) => {
                                    setProductMapping({
                                      ...productMapping,
                                      [oldProd]: e.target.value
                                    });
                                  }}
                                >
                                  <option value="">-- Select matching product --</option>
                                  {migrationData.newProducts.map(newProd => (
                                    <option key={newProd} value={newProd}>{newProd}</option>
                                  ))}
                                  <option value={oldProd}>Keep "{oldProd}" (Manual)</option>
                                </select>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' }}>
                      <button
                        onClick={() => {
                          setShowProductMappingModal(false);
                          setMigrationData(null);
                          setProductMapping({});
                        }}
                        className="btn btn-secondary"
                      >
                        Cancel Update
                      </button>
                      <button
                        onClick={() => handleUpdateCPOrgAccess(productMapping)}
                        className="btn btn-primary"
                        disabled={migrationData.oldProducts.length > 0 && Object.keys(productMapping).length === 0}
                      >
                        Confirm Migration & Update
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Customer Modal */}
              {editingCustomer && (
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}
                  onClick={() => setEditingCustomer(null)}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      maxWidth: '500px',
                      width: '90%',
                      background: 'white',
                      borderRadius: '12px',
                      padding: '24px',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                    }}
                  >
                    <div className="modal-header">
                      <h3>Edit Customer</h3>
                      <button className="modal-close" onClick={() => setEditingCustomer(null)}>×</button>
                    </div>

                    <div style={{ marginBottom: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px' }}>
                      <div style={{ fontWeight: 600 }}>{editingCustomer.email}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        Reseller: {editingCustomer.resellerName} ({editingCustomer.resellerGroup})
                      </div>
                    </div>

                    <form onSubmit={handleUpdateCustomer}>
                      <div className="form-group">
                        <label className="form-label">Organization</label>
                        <select
                          className="form-input"
                          value={editCustomerForm.organizationId || ''}
                          onChange={(e) => setEditCustomerForm({ ...editCustomerForm, organizationId: e.target.value })}
                        >
                          {!organizations.some(o => String(o.id) === String(editingCustomer.organizationId)) && (
                            <option value={editCustomerForm.organizationId || ''}>{editingCustomer.organizationName || 'Unknown'} (current)</option>
                          )}
                          {[...organizations].sort((a, b) => String(a.name).localeCompare(String(b.name))).map(o => (
                            <option key={o.id} value={String(o.id)}>
                              {o.name}{String(o.id) === String(editingCustomer.organizationId) ? ' (current)' : ''}
                            </option>
                          ))}
                        </select>
                        {editCustomerForm.organizationId && String(editCustomerForm.organizationId) !== String(editingCustomer.organizationId ?? '') ? (
                          <div style={{ fontSize: '12px', color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '6px', padding: '8px', marginTop: '6px' }}>
                            Records only — Adobe is not contacted. After saving, add <strong>{editingCustomer.email}</strong> in
                            the new organization's Adobe console yourself (and remove it from the old one if needed).
                            Expiry and products stay the same.
                          </div>
                        ) : (
                          <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            Move this customer to another organization in the records, without the Adobe API.
                          </div>
                        )}
                      </div>

                      <div className="form-group">
                        <label className="form-label">License Duration</label>
                        <select
                          className="form-input"
                          value={editCustomerForm.durationMonths}
                          onChange={(e) => setEditCustomerForm({ ...editCustomerForm, durationMonths: parseInt(e.target.value) })}
                          disabled={!!editCustomerForm.customExpiryDate}
                        >
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => (
                            <option key={m} value={m}>{m} {m === 1 ? 'Month' : 'Months'}</option>
                          ))}
                        </select>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          Changing duration will recalculate the expiration date from the original assignment date.
                        </div>
                      </div>

                      {/* Custom Expiry Date (overrides duration) */}
                      <div className="form-group">
                        <label className="form-label">Custom Expiry Date (optional)</label>
                        {editingCustomer.expiresAt && (
                          <div style={{ fontSize: '12px', color: '#475569', marginBottom: '6px' }}>
                            Current expiry: <strong>{new Date(editingCustomer.expiresAt).toLocaleDateString()}</strong>
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="date"
                            className="form-input"
                            value={editCustomerForm.customExpiryDate || ''}
                            min={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setEditCustomerForm({ ...editCustomerForm, customExpiryDate: e.target.value })}
                            style={{ flex: 1 }}
                          />
                          {editCustomerForm.customExpiryDate && (
                            <button type="button" onClick={() => setEditCustomerForm({ ...editCustomerForm, customExpiryDate: '' })} className="btn btn-secondary" style={{ whiteSpace: 'nowrap' }}>
                              Clear
                            </button>
                          )}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          Set an exact expiry date to override the duration above. Leave blank to keep duration-based expiry.
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">Current Products</label>
                        <div style={{ background: '#f8f9fa', padding: '12px', borderRadius: '6px', fontSize: '13px' }}>
                          {editCustomerForm.products && editCustomerForm.products.length > 0
                            ? editCustomerForm.products.map((p, i) => (
                              <div key={i} style={{ padding: '4px 0' }}>{p}</div>
                            ))
                            : <span style={{ color: '#999' }}>No products assigned</span>
                          }
                        </div>
                      </div>

                      <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn btn-primary"
                          style={{ flex: 1 }}
                        >
                          {loading ? 'Updating...' : 'Save Changes'}
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingCustomer(null)}
                          className="btn btn-secondary"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Credits Pool Resellers Tab */}
          {activeTab === 'cpResellers' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Total CP Resellers</div>
                  <div className="stat-value">{cpResellers.length}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                  <div className="stat-label">Total Credits Balance</div>
                  <div className="stat-value">{formatCredits(cpResellers.reduce((sum, r) => sum + (Number(r.creditsBalance) || 0), 0))}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#f59e0b' }}>
                  <div className="stat-label">Total Gold Coins</div>
                  <div className="stat-value">{formatCredits(cpResellers.reduce((sum, r) => sum + (Number(r.goldCoinsBalance) || 0), 0))}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#0b7da6' }}>
                  <div className="stat-label">Total Credits Used</div>
                  <div className="stat-value">{formatCredits(cpResellers.reduce((sum, r) => sum + (Number(r.creditsUsed ?? r.totalCreditsIssued ?? 0) || 0), 0))}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#f97316' }}>
                  <div className="stat-label">Total Refund Days</div>
                  <div className="stat-value">{cpResellers.reduce((sum, r) => sum + (r.refundDaysBalance || 0), 0)}</div>
                </div>
              </div>

              {/* Sub-tabs — List / Create New Reseller — so the create form doesn't
                  sit below however many resellers are in the table. */}
              <div className="shop-category-tabs" aria-label="CP Resellers sections" style={{ marginBottom: '16px' }}>
                <button type="button"
                  onClick={() => setCpResellersSubTab('list')}
                  className={`shop-category-tab ${cpResellersSubTab === 'list' ? 'is-active' : ''}`}
                >
                  <span>Resellers</span>
                  <span className="shop-category-count">{cpResellers.length}</span>
                </button>
                <button type="button"
                  onClick={() => setCpResellersSubTab('add')}
                  className={`shop-category-tab ${cpResellersSubTab === 'add' ? 'is-active' : ''}`}
                >
                  <span>+ Add New Reseller</span>
                </button>
              </div>

              {cpResellersSubTab === 'list' && (
              <>
              {/* CP Resellers Table */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Credits Pool Resellers</h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <input
                      type="search"
                      className="form-input"
                      value={cpResellerSearchQuery}
                      onChange={(e) => setCpResellerSearchQuery(e.target.value)}
                      placeholder="Search reseller, group, organization..."
                      style={{ width: '320px', maxWidth: '100%' }}
                    />
                    <button
                      onClick={() => { setBulkPricingForm({ creditsPerMonth: { ...DEFAULT_CP_CREDITS }, hiddenMonths: [], applyHidden: true }); setShowBulkPricing(true); }}
                      className="btn btn-primary"
                      title="Set the same credits-per-duration pricing for every CP reseller in one click"
                    >
                      <Icon name="coins" size={15} /> Set Pricing for All
                    </button>
                    <button
                      onClick={openRefundSettings}
                      className="btn btn-secondary"
                      title="Configure automatic pro-rata refund credits on user deletion"
                    >
                      <Icon name="refresh" size={15} /> Refund Settings
                    </button>
                    <button onClick={fetchCPResellers} disabled={loadingCPResellers} className="btn btn-secondary">
                      Refresh
                    </button>
                  </div>
                </div>

                {loadingCPResellers ? (
                  <div className="loading">Loading...</div>
                ) : cpResellers.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="card" size={16} /></div>
                    <div className="empty-text">No Credits Pool resellers yet</div>
                  </div>
                ) : filteredCPResellers.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="search" size={16} /></div>
                    <div className="empty-text">No Credits Pool resellers match your search</div>
                  </div>
                ) : (
                  <>
                    {cpResellerSearchQuery.trim() && (
                      <div style={{ color: '#64748b', fontSize: '13px', margin: '0 0 12px 0' }}>
                        Showing {filteredCPResellers.length} of {cpResellers.length} resellers
                      </div>
                    )}
                    <div className="data-table-scroll" style={{ overflowX: 'auto', width: '100%' }}>
                    <table className="data-table cp-resellers-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Username</th>
                          <th>User Group</th>
                          <th>Organization</th>
                          <th>Credits Balance</th>
                          <th>Gold Coins</th>
                          <th>Refund Days</th>
                          <th>Active Users</th>
                          <th title="Total credits this reseller has spent on activating/extending users">Credits Used</th>
                          <th title="Total credits admin has added to this reseller (minus any admin deductions)">Credits Added</th>
                          <th>Pool Expires</th>
                          <th style={{ minWidth: '180px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCPResellers.map(cpr => (
                        <tr key={cpr.id}>
                          <td data-label="ID" style={{ color: '#64748b', fontSize: '12px' }}>#{cpr.id}</td>
                          <td data-label="Username"><strong>{cpr.username}</strong>
                            {cpr.apiAccessEnabled && <span style={{ marginLeft: '6px', background: '#dbeafe', color: '#1e40af', padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600 }}>API</span>}
                            {/* Account security: verified email + own password (account-security.js) */}
                            {cpr.security && (
                              <div style={{ fontSize: '11px', marginTop: '3px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                {cpr.security.emailVerified
                                  ? <span title="Verified email" style={{ color: '#15803d' }}>✉️ {cpr.security.email}</span>
                                  : <span style={{ color: '#b45309' }}>✉️ no email yet</span>}
                                {cpr.security.mustResetPassword && <span style={{ color: '#b45309' }}>· must set password</span>}
                                {cpr.paused
                                  ? <span title={`Paused ${new Date(cpr.paused.at).toLocaleString()} by ${cpr.paused.by}${cpr.paused.reason ? ': ' + cpr.paused.reason : ''}`} style={{ background: '#fee2e2', color: '#b91c1c', padding: '0 6px', borderRadius: '4px', fontWeight: 700 }}>⏸ PAUSED{cpr.paused.reason ? ` – ${cpr.paused.reason}` : ''}</span>
                                  : null}
                                <button type="button" title={cpr.paused ? 'Give this reseller access again' : 'Block all access: login, open sessions and API keys'}
                                  onClick={async () => {
                                    let reason = '';
                                    if (!cpr.paused) {
                                      reason = window.prompt(`Pause "${cpr.username}"?\n\nThey will be logged out and cannot log in or use their API keys until you resume.\n\nReason (for your records):`, 'Suspected fraud – under review');
                                      if (reason === null) return;
                                    } else if (!window.confirm(`Resume "${cpr.username}"? They can log in and use the API again.`)) return;
                                    try {
                                      const res = await fetch(`${API_URL}/admin/credits-pool-resellers/${cpr.id}/pause`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken')}` }, body: JSON.stringify({ paused: !cpr.paused, reason }) });
                                      const d = await res.json().catch(() => ({}));
                                      if (!res.ok) setError(d.error || 'Failed'); else { setResult(d.message); fetchCPResellers(); }
                                    } catch (e) { setError('Network error'); }
                                  }}
                                  style={{ background: cpr.paused ? '#dcfce7' : '#fff1f2', border: `1px solid ${cpr.paused ? '#86efac' : '#fca5a5'}`, borderRadius: '4px', padding: '0 6px', fontSize: '10px', cursor: 'pointer', color: cpr.paused ? '#166534' : '#b91c1c', fontWeight: 600 }}>{cpr.paused ? '▶ resume' : '⏸ pause'}</button>
                                {cpr.security.weakPassword && <span title="Password is the account name or a very common one: login is locked until you set the owner's email" style={{ color: '#dc2626', fontWeight: 600 }}>· 🔒 weak password, locked</span>}
                                <button type="button" title="Set the owner's email (verified by you). They then set a new password with a code sent to it."
                                  onClick={async () => {
                                    const email = window.prompt(`Email for "${cpr.username}"\n\nOnly enter an address you have confirmed with the reseller. They will set a new password with a code sent to it.`, cpr.security.email || '');
                                    if (email === null || !email.trim()) return;
                                    try {
                                      const res = await fetch(`${API_URL}/admin/credits-pool-resellers/${cpr.id}/email`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('adminToken')}` }, body: JSON.stringify({ email: email.trim() }) });
                                      const d = await res.json().catch(() => ({}));
                                      if (!res.ok) setError(d.error || 'Failed'); else { setResult(d.message); fetchCPResellers(); }
                                    } catch (e) { setError('Network error'); }
                                  }}
                                  style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '0 5px', fontSize: '10px', cursor: 'pointer', color: '#2563eb' }}>{cpr.security.emailVerified ? 'change email' : 'set email'}</button>
                                <button type="button" title="Forget the email and make them verify a new one + set a new password at next login"
                                  onClick={async () => {
                                    if (!window.confirm(`Reset login security for "${cpr.username}"?\n\nTheir linked email is removed and at next login they must verify an email and set a new password.

WARNING: whoever knows their CURRENT password can then link their own email. If the account may have been taken over, use "change email" instead (or change the password first).`)) return;
                                    try {
                                      const res = await fetch(`${API_URL}/admin/credits-pool-resellers/${cpr.id}/reset-security`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });
                                      const d = await res.json().catch(() => ({}));
                                      if (!res.ok) setError(d.error || 'Failed'); else { setResult(d.message); fetchCPResellers(); }
                                    } catch (e) { setError('Network error'); }
                                  }}
                                  style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '0 5px', fontSize: '10px', cursor: 'pointer', color: '#64748b' }}>reset</button>
                              </div>
                            )}
                          </td>
                          <td data-label="User Group">{cpr.userGroup}</td>
                          <td data-label="Organization">
                            {(cpr.organizationAccess || []).length > 0 ? (
                              <span title={(cpr.organizationAccess || []).map(a => a.organizationName || 'Unknown').join(', ')}>
                                {(cpr.organizationAccess || []).map(a => a.organizationName || 'Unknown').join(', ')}
                              </span>
                            ) : (
                              <span style={{ color: 'var(--app-muted)' }}>—</span>
                            )}
                          </td>
                          <td data-label="Credits Balance" style={{ color: cpr.creditsBalance > 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                            {formatCredits(cpr.creditsBalance)}
                          </td>
                          <td data-label="Gold Coins" style={{ color: (Number(cpr.goldCoinsBalance) || 0) > 0 ? '#d97706' : '#64748b', fontWeight: 600 }}>
                            {formatCredits(cpr.goldCoinsBalance)}
                          </td>
                          <td data-label="Refund Days" style={{ color: (cpr.refundDaysBalance || 0) > 0 ? '#f97316' : '#64748b', fontWeight: 600 }}>
                            {cpr.refundDaysBalance || 0}
                            {(cpr.pendingRefundCredits || 0) > 0 && (
                              <div style={{ fontSize: '10px', color: '#0ea5e9', fontWeight: 700, marginTop: '2px', whiteSpace: 'nowrap' }} title="Pending pro-rata refund credits awaiting settlement">
                                +{cpr.pendingRefundCredits} cr
                              </div>
                            )}
                          </td>
                          <td data-label="Active Users">{cpr.activeUsers || 0}</td>
                          <td data-label="Credits Used" style={{ color: '#0b7da6', fontWeight: 600 }}>
                            {formatCredits(cpr.creditsUsed ?? cpr.totalCreditsIssued ?? 0)}
                          </td>
                          <td data-label="Credits Added" style={{ color: (cpr.creditsAddedByAdmin ?? 0) >= 0 ? '#0ea5e9' : '#dc2626', fontWeight: 600 }}>
                            {formatCredits(cpr.creditsAddedByAdmin ?? 0)}
                          </td>
                          <td data-label="Pool Expires" style={{ color: cpr.isPoolExpired ? '#dc2626' : 'inherit' }}>
                            {cpr.isPoolExpired ? 'Expired' : new Date(cpr.creditsPoolValidity).toLocaleDateString()}
                          </td>
                          <td data-label="Actions">
                            {editingCPReseller === cpr.id ? (
                              <div className="table-actions cp-reseller-edit-actions" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder="Credits"
                                  value={addCreditsForm.credits}
                                  onChange={e => setAddCreditsForm({ ...addCreditsForm, credits: e.target.value })}
                                  className="form-input"
                                  style={{ width: '80px', padding: '4px 8px' }}
                                />
                                <input
                                  type="text"
                                  placeholder="Reason"
                                  value={addCreditsForm.reason}
                                  onChange={e => setAddCreditsForm({ ...addCreditsForm, reason: e.target.value })}
                                  className="form-input"
                                  style={{ width: '120px', padding: '4px 8px' }}
                                />
                                <button onClick={() => handleAddCredits(cpr.id)} className="btn btn-primary" style={{ padding: '4px 8px' }} aria-label="Save"><Icon name="check" size={14} /></button>
                                <button onClick={() => { setEditingCPReseller(null); setAddCreditsForm({ credits: '', reason: '' }); }} className="btn btn-secondary" style={{ padding: '4px 8px' }} aria-label="Cancel"><Icon name="x" size={14} /></button>
                              </div>
                            ) : editingCPGoldCoins === cpr.id ? (
                              <div className="table-actions cp-reseller-edit-actions" style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  placeholder="Gold Coins"
                                  value={addGoldCoinsForm.goldCoins}
                                  onChange={e => setAddGoldCoinsForm({ ...addGoldCoinsForm, goldCoins: e.target.value })}
                                  className="form-input"
                                  style={{ width: '96px', padding: '4px 8px' }}
                                />
                                <input
                                  type="text"
                                  placeholder="Reason"
                                  value={addGoldCoinsForm.reason}
                                  onChange={e => setAddGoldCoinsForm({ ...addGoldCoinsForm, reason: e.target.value })}
                                  className="form-input"
                                  style={{ width: '120px', padding: '4px 8px' }}
                                />
                                <button onClick={() => handleAddGoldCoins(cpr.id)} className="btn btn-primary" style={{ padding: '4px 8px' }} aria-label="Save"><Icon name="check" size={14} /></button>
                                <button onClick={() => { setEditingCPGoldCoins(null); setAddGoldCoinsForm({ goldCoins: '', reason: '' }); }} className="btn btn-secondary" style={{ padding: '4px 8px' }} aria-label="Cancel"><Icon name="x" size={14} /></button>
                              </div>
                            ) : (
                              <div className="table-actions cp-reseller-actions" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '6px', minWidth: '150px' }}>
                                <button
                                  onClick={() => {
                                    setEditingCPOrgAccess(cpr.id);
                                    setEditCPOrgForm({ organizationAccess: cpr.organizationAccess || [] });
                                  }}
                                  className="btn btn-secondary action-icon-btn"
                                  style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                                  title="Edit Organizations"
                                ><Icon name="building" size={16} /></button>
                                <button
                                  onClick={() => {
                                    setEditCPSettingsId(cpr.id);
                                    setEditCPSettingsForm({
                                      password: '',
                                      extendPoolMonths: '',
                                      creditsPerMonth: { ...DEFAULT_CP_CREDITS, ...(cpr.creditsPerMonth || {}) },
                                      hiddenMonths: cpr.hiddenMonths || [],
                                      apiAccessEnabled: cpr.apiAccessEnabled || false,
                                      apiDefaultOrganizationId: cpr.apiDefaultOrganizationId || null
                                    });
                                  }}
                                  className="btn btn-secondary action-icon-btn"
                                  style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                                  title="Settings (Password/Expiry)"
                                ><Icon name="settings" size={16} /></button>
                                <button
                                  onClick={() => handleBulkLimit(cpr)}
                                  className="btn btn-secondary action-label-btn"
                                  style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                                  title={cpr.maxUsersPerBulk === null || cpr.maxUsersPerBulk === undefined
                                    ? 'Users per bulk operation: following the server default'
                                    : cpr.maxUsersPerBulk === 0
                                      ? 'Users per bulk operation: no limit'
                                      : `Users per bulk operation: ${cpr.maxUsersPerBulk}`}
                                >
                                  Limit{cpr.maxUsersPerBulk === null || cpr.maxUsersPerBulk === undefined
                                    ? '' : ` (${cpr.maxUsersPerBulk === 0 ? '∞' : cpr.maxUsersPerBulk})`}
                                </button>
                                <button
                                  onClick={() => handleRenameCPReseller(cpr)}
                                  className="btn btn-secondary action-label-btn"
                                  style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                                  title="Change the username they sign in with"
                                >
                                  Rename
                                </button>
                                <button
                                  onClick={() => { setEditingCPReseller(cpr.id); setEditingCPGoldCoins(null); }}
                                  className="btn btn-primary action-label-btn"
                                  style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap' }}
                                >
                                  Credits
                                </button>
                                <button
                                  onClick={() => { setEditingCPGoldCoins(cpr.id); setEditingCPReseller(null); }}
                                  className="btn btn-secondary action-label-btn action-gold-btn"
                                  style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', background: '#fffbeb', borderColor: '#f59e0b', color: '#92400e' }}
                                  title="Top up or deduct Marketplace Gold Coins"
                                >
                                  Gold
                                </button>
                                {cpr.refundDaysBalance > 0 && (
                                  <button
                                    onClick={() => handleClearRefundDays(cpr.id, cpr.username, cpr.refundDaysBalance)}
                                    className="btn btn-secondary action-label-btn action-warning-btn"
                                    style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', background: '#fff7ed', borderColor: '#fdba74', color: '#c2410c' }}
                                    title="Clear refund days after redemption"
                                  >
                                    Clear Days
                                  </button>
                                )}
                                {(cpr.pendingRefundCredits || 0) > 0 && (
                                  <button
                                    onClick={() => settleRefundCredits(cpr.id, cpr.username, cpr.pendingRefundCredits, 'credit')}
                                    className="btn btn-secondary action-label-btn"
                                    style={{ padding: '4px 8px', fontSize: '12px', whiteSpace: 'nowrap', background: '#ecfeff', borderColor: '#67e8f9', color: '#0e7490' }}
                                    title={`Settle ${cpr.pendingRefundCredits} pending refund credit(s) into ${cpr.username}'s balance`}
                                  >
                                    Settle {cpr.pendingRefundCredits}cr
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteCPReseller(cpr.id, cpr.username)}
                                  className="btn btn-danger action-icon-btn"
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                ><Icon name="trash" size={16} /></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  </>
                )}
              </div>
              </>
              )}

              {/* Create New CP Reseller */}
              {cpResellersSubTab === 'add' && (
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Create New Credits Pool Reseller</h3>
                </div>
                <form onSubmit={handleCreateCPReseller}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label className="form-label">Username *</label>
                      <input type="text" className="form-input" value={newCPReseller.username}
                        onChange={e => setNewCPReseller({ ...newCPReseller, username: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Password *</label>
                      <input type="password" className="form-input" name="cp-create-password" autoComplete="new-password" value={newCPReseller.password}
                        onChange={e => setNewCPReseller({ ...newCPReseller, password: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">User Group *</label>
                      <input type="text" className="form-input" value={newCPReseller.userGroup}
                        onChange={e => setNewCPReseller({ ...newCPReseller, userGroup: e.target.value })} required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Initial Credits</label>
                      <input type="number" min="0" step="any" className="form-input" value={newCPReseller.initialCredits}
                        onChange={e => setNewCPReseller({ ...newCPReseller, initialCredits: e.target.value })} placeholder="0" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Initial Gold Coins</label>
                      <input type="number" min="0" step="any" className="form-input" value={newCPReseller.initialGoldCoins}
                        onChange={e => setNewCPReseller({ ...newCPReseller, initialGoldCoins: e.target.value })} placeholder="0" />
                      <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Marketplace wallet only.</div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Pool Validity (Months)</label>
                      <select className="form-input" value={newCPReseller.poolValidityMonths}
                        onChange={e => setNewCPReseller({ ...newCPReseller, poolValidityMonths: e.target.value })}>
                        {[6, 12, 18, 24].map(m => <option key={m} value={m}>{m} Months</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Organization Access</label>
                      <select className="form-input"
                        onChange={e => {
                          if (e.target.value) {
                            const orgId = parseInt(e.target.value);
                            const org = organizations.find(o => o.id === orgId);
                            if (org && !newCPReseller.organizationAccess.some(a => a.organizationId === orgId)) {
                              setNewCPReseller({
                                ...newCPReseller,
                                organizationAccess: [...newCPReseller.organizationAccess, { organizationId: orgId, products: org.products || [] }]
                              });
                            }
                          }
                        }}>
                        <option value="">Select organization...</option>
                        {organizations.map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                      </select>
                      {newCPReseller.organizationAccess.length > 0 && (
                        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                          {newCPReseller.organizationAccess.map(a => (
                            <span key={a.organizationId} style={{ background: '#dbeafe', padding: '2px 8px', borderRadius: '4px', fontSize: '12px' }}>
                              {organizations.find(o => o.id === a.organizationId)?.name}
                              <button type="button" onClick={() => setNewCPReseller({ ...newCPReseller, organizationAccess: newCPReseller.organizationAccess.filter(x => x.organizationId !== a.organizationId) })} style={{ marginLeft: '4px', border: 'none', background: 'none', cursor: 'pointer' }}>×</button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Credits Per Duration Configuration */}
                  <div style={{ marginTop: '16px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                    <h4 style={{ marginBottom: '12px', fontSize: '14px', color: '#374151' }}>Credits Per Duration Configuration</h4>
                    <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>Set credits and visibility for each duration. Unchecked plans are hidden from reseller.</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                      {CREDIT_DURATION_OPTIONS.map(option => {
                        const key = option.key;
                        const isHidden = (newCPReseller.hiddenMonths || []).includes(key);
                        return (
                          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px', background: isHidden ? '#fee2e2' : '#ecfdf5', borderRadius: '6px' }}>
                            <input type="checkbox"
                              checked={!isHidden}
                              onChange={() => {
                                const hidden = newCPReseller.hiddenMonths || [];
                                if (isHidden) {
                                  setNewCPReseller({ ...newCPReseller, hiddenMonths: hidden.filter(x => x !== key) });
                                } else {
                                  setNewCPReseller({ ...newCPReseller, hiddenMonths: [...hidden, key] });
                                }
                              }}
                              title={isHidden ? 'Click to show' : 'Click to hide'}
                            />
                            <span style={{ fontSize: '12px', minWidth: '60px', color: isHidden ? '#991b1b' : '#065f46' }}>{option.label}:</span>
                            <input type="number" min="0.01" step="any"
                              style={{ width: '55px', padding: '4px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '12px', background: isHidden ? '#fecaca' : 'white' }}
                              value={newCPReseller.creditsPerMonth[key] ?? option.defaultCredits}
                              disabled={isHidden}
                              onChange={e => setNewCPReseller({
                                ...newCPReseller,
                                creditsPerMonth: { ...newCPReseller.creditsPerMonth, [key]: e.target.value }
                              })}
                            />
                            <span style={{ fontSize: '11px', color: '#6b7280' }}>cr</span>
                          </div>
                        );
                      })}
                    </div>
                    <p style={{ fontSize: '11px', color: '#9ca3af', marginTop: '8px' }}>= Visible to reseller, = Hidden from dropdown</p>
                  </div>

                  {/* API Access Toggle */}
                  <div style={{ marginTop: '16px', padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input type="checkbox"
                          checked={newCPReseller.apiAccessEnabled || false}
                          onChange={e => setNewCPReseller({ ...newCPReseller, apiAccessEnabled: e.target.checked })}
                        />
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#0369a1' }}>Enable API Access</span>
                      </label>
                    </div>
                    <p style={{ fontSize: '11px', color: '#0284c7', marginTop: '6px' }}>Allow this reseller to generate API keys and add users programmatically via the REST API.</p>
                  </div>

                  {/* Pre-Made Keys Access Toggle */}
                  <div style={{ marginTop: '16px', padding: '16px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={newCPReseller.allowBackupKeys === true}
                          onChange={e => setNewCPReseller({ ...newCPReseller, allowBackupKeys: e.target.checked })}
                        />
                        <span style={{ fontSize: '14px', fontWeight: 600, color: '#92400e' }}>Enable Pre-Made Keys</span>
                      </label>
                    </div>
                    <p style={{ fontSize: '11px', color: '#b45309', marginTop: '6px' }}>Only enabled resellers will see and redeem pre-made account keys.</p>
                  </div>

                  <button type="submit" disabled={loading || !newCPReseller.username || !newCPReseller.password || !newCPReseller.userGroup}
                    className="btn btn-primary" style={{ marginTop: '16px' }}>
                    {loading ? 'Creating...' : 'Create Credits Pool Reseller'}
                  </button>
                </form>
              </div>
              )}
            </>
          )}

          {/* CP Monitoring Tab */}
          {activeTab === 'cpMonitoring' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Total CP Resellers</div>
                  <div className="stat-value">{cpMonitoringResellers.length}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                  <div className="stat-label">Total Credits in Use</div>
                  <div className="stat-value">{formatCredits(cpMonitoringResellers.reduce((s, r) => s + (r.stats?.netCreditsUsed || 0), 0))}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
                  <div className="stat-label">Total Active Users</div>
                  <div className="stat-value">{cpMonitoringResellers.reduce((s, r) => s + (r.stats?.activeUsers || 0), 0).toLocaleString()}</div>
                </div>
              </div>

              {/* Resellers Overview Table */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Credits Pool Reseller Usage</h3>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <input
                      type="search"
                      className="form-input"
                      value={cpMonitoringSearchQuery}
                      onChange={(e) => setCpMonitoringSearchQuery(e.target.value)}
                      placeholder="Search reseller, group, status..."
                      style={{ width: '300px', maxWidth: '100%' }}
                    />
                    <button
                      onClick={() => handleBulkToggleTransfers(true)}
                      disabled={loadingCPMonitoring || cpMonitoringResellers.length === 0}
                      className="btn btn-success"
                      title="Enable Bulk Transfers for all CP resellers"
                    >
                      Enable All Transfers
                    </button>
                    <button
                      onClick={() => handleBulkToggleTransfers(false)}
                      disabled={loadingCPMonitoring || cpMonitoringResellers.length === 0}
                      className="btn btn-danger"
                      title="Disable Bulk Transfers for all CP resellers"
                    >
                      Disable All Transfers
                    </button>
                    <button
                      onClick={() => handleBulkToggleReinvite(true)}
                      disabled={loadingCPMonitoring || cpMonitoringResellers.length === 0}
                      className="btn btn-success"
                      title="Enable Reinvite for all CP resellers"
                    >
                      Enable All Reinvites
                    </button>
                    <button
                      onClick={() => handleBulkToggleReinvite(false)}
                      disabled={loadingCPMonitoring || cpMonitoringResellers.length === 0}
                      className="btn btn-danger"
                      title="Disable Reinvite for all CP resellers"
                    >
                      Disable All Reinvites
                    </button>
                    <button onClick={fetchCPMonitoring} disabled={loadingCPMonitoring} className="btn btn-secondary">
                      {loadingCPMonitoring ? 'Refreshing…' : 'Refresh'}
                    </button>
                  </div>
                </div>

                {loadingCPMonitoring ? (
                  <div className="loading">Loading monitoring data...</div>
                ) : cpMonitoringResellers.length === 0 ? (
                  <div className="empty-state">No credits pool resellers found</div>
                ) : filteredCPMonitoringResellers.length === 0 ? (
                  <div className="empty-state">No credits pool resellers match your search</div>
                ) : (
                  <>
                    {cpMonitoringSearchQuery.trim() && (
                      <div style={{ color: '#64748b', fontSize: '13px', margin: '0 0 12px 0' }}>
                        Showing {filteredCPMonitoringResellers.length} of {cpMonitoringResellers.length} resellers
                      </div>
                    )}
                    <div className="cp-monitor-wrap">
                    <table className="data-table cp-monitor-table">
                      <thead>
                        <tr>
                          <th>Reseller</th>
                          <th>Balance</th>
                          <th>Active</th>
                          <th>Used</th>
                          <th>Refunded</th>
                          <th>Pool / Activity</th>
                          <th title="Show Exp. = expiration info shown to customers · Keys = Pre-Made Account Keys · Transfer = organization transfers · Reinvite = Reinvite action · →GC = Credits to GC conversion">Permissions</th>
                          <th title="Choose whether a transfer keeps or removes Org A">Org A</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredCPMonitoringResellers.map(r => (
                        <tr key={r.id} style={{ background: selectedCPResellerForMonitor?.id === r.id ? '#f0f9ff' : 'inherit' }}>
                          <td className="cp-mon-reseller">
                            <strong title={r.username}>{r.username}</strong>
                            <div className="cp-mon-sub">
                              <span className="cp-mon-id">#{r.id}</span>
                              {r.userGroup && <span className="badge badge-info">{r.userGroup}</span>}
                            </div>
                          </td>
                          <td style={{ color: r.creditsBalance > 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                            {formatCredits(r.creditsBalance)}
                          </td>
                          <td>{r.stats?.activeUsers || 0}</td>
                          <td style={{ color: '#dc2626' }}>-{formatCredits(r.stats?.totalCreditsUsed)}</td>
                          <td style={{ color: '#059669' }}>+{formatCredits(r.stats?.totalCreditsRefunded)}</td>
                          <td>
                            <div style={{ color: r.poolDaysLeft <= 30 ? '#dc2626' : 'inherit' }}>
                              {r.isPoolExpired ? 'Expired' : `${r.poolDaysLeft} days`}
                            </div>
                            <div className="cp-mon-muted">
                              {r.stats?.lastTransaction ? new Date(r.stats.lastTransaction).toLocaleDateString() : 'no activity'}
                            </div>
                          </td>
                          <td>
                            <div className="cp-perm-grid">
                            <button
                              onClick={() => handleToggleShowExpiration('cp', r.id, r.showExpirationInfo)}
                              className={`btn ${r.showExpirationInfo ? 'btn-success' : 'btn-secondary'}`}
                              title={r.showExpirationInfo ? 'Enabled (click to disable)' : 'Disabled (click to enable)'}
                            >
                              Show Exp. <b>{r.showExpirationInfo ? 'ON' : 'OFF'}</b>
                            </button>
                            <button
                              onClick={async () => {
                                const newVal = !(r.allowBackupKeys === true);
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const res = await fetch(`/api/admin/credits-pool-resellers/${r.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ allowBackupKeys: newVal })
                                  });
                                  if (res.ok) {
                                    fetchCPMonitoring();
                                    fetchCPResellers();
                                  }
                                } catch (e) { console.error(e); }
                              }}
                              className={`btn ${r.allowBackupKeys === true ? 'btn-success' : 'btn-secondary'}`}
                              title={r.allowBackupKeys === true ? 'Pre-made keys enabled (click to disable)' : 'Pre-made keys hidden (click to enable)'}
                            >
                              Keys <b>{r.allowBackupKeys === true ? 'ON' : 'OFF'}</b>
                            </button>
                            <button
                              onClick={async () => {
                                const newVal = !(r.allowTransfer !== false);
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const res = await fetch(`/api/admin/credits-pool-resellers/${r.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ allowTransfer: newVal })
                                  });
                                  if (res.ok) fetchCPMonitoring();
                                } catch (e) { console.error(e); }
                              }}
                              className={`btn ${r.allowTransfer !== false ? 'btn-success' : 'btn-danger'}`}
                              title={r.allowTransfer !== false ? 'Transfers allowed (click to disable)' : 'Transfers disabled (click to enable)'}
                            >
                              Transfer <b>{r.allowTransfer !== false ? 'ON' : 'OFF'}</b>
                            </button>
                            <button
                              onClick={async () => {
                                const newVal = !(r.allowReinvite !== false);
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const res = await fetch(`/api/admin/credits-pool-resellers/${r.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ allowReinvite: newVal })
                                  });
                                  if (res.ok) fetchCPMonitoring();
                                } catch (e) { console.error(e); }
                              }}
                              className={`btn ${r.allowReinvite !== false ? 'btn-success' : 'btn-danger'}`}
                              title={r.allowReinvite !== false ? 'Reinvite shown (click to hide)' : 'Reinvite hidden (click to show)'}
                            >
                              Reinvite <b>{r.allowReinvite !== false ? 'ON' : 'OFF'}</b>
                            </button>
                            <button
                              onClick={async () => {
                                const newVal = !(r.allowCreditToGc !== false);
                                try {
                                  const token = localStorage.getItem('adminToken');
                                  const res = await fetch(`/api/admin/credits-pool-resellers/${r.id}`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                                    body: JSON.stringify({ allowCreditToGc: newVal })
                                  });
                                  if (res.ok) {
                                    setResult(`Credits → GC conversion ${newVal ? 'enabled' : 'disabled'} for ${r.username}`);
                                    fetchCPMonitoring();
                                  } else {
                                    const data = await res.json().catch(() => ({}));
                                    setError(data.error || 'Could not change the Credits → GC setting');
                                  }
                                } catch (e) { console.error(e); }
                              }}
                              className={`btn ${r.allowCreditToGc !== false ? 'btn-success' : 'btn-danger'}`}
                              title={r.allowCreditToGc !== false ? 'Can convert Credits to GC (click to disable for this reseller)' : 'Conversion disabled for this reseller (click to enable)'}
                            >
                              →GC <b>{r.allowCreditToGc !== false ? 'ON' : 'OFF'}</b>
                            </button>
                            </div>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <select
                              value={r.transferSourceOrgAction === 'keep' ? 'keep' : 'remove'}
                              onChange={(e) => handleUpdateCPTransferSourceOrgAction(r.id, e.target.value)}
                              className="form-input"
                              style={{ padding: '4px 6px', fontSize: '11px' }}
                              title="Choose whether transfers keep the source organization or remove it"
                            >
                              <option value="remove">Remove</option>
                              <option value="keep">Keep</option>
                            </select>
                          </td>
                          <td>
                            <button
                              onClick={() => {
                                setSelectedCPResellerForMonitor(r);
                                setCpTransactionPage(1); // Reset pagination
                                fetchCPResellerDetails(r.id);
                              }}
                              className="btn btn-secondary"
                              style={{ padding: '4px 8px', fontSize: '12px' }}
                            >
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  </>
                )}
              </div>

              {/* Selected Reseller Details - POPUP MODAL */}
              {selectedCPResellerForMonitor && (
                <div
                  id="cp-detail-overlay"
                  className={cpDetailFullscreen ? 'is-fullscreen' : ''}
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                  }}
                  onClick={() => setSelectedCPResellerForMonitor(null)}
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    id="cp-detail-modal"
                    className={cpDetailFullscreen ? 'is-fullscreen' : ''}
                    style={{
                      maxWidth: '1100px',
                      width: '95%',
                      maxHeight: '90vh',
                      background: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <div className="modal-header" style={{ padding: '16px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3 style={{ margin: 0 }}>{selectedCPResellerForMonitor.username} - Details</h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setCpDetailFullscreen(f => !f)}
                          title={cpDetailFullscreen ? 'Exit full screen' : 'Full screen'}
                          style={{ padding: '4px 10px', fontSize: '12px' }}
                        >
                          {cpDetailFullscreen ? '⤡ Exit full screen' : '⤢ Full screen'}
                        </button>
                        <button className="modal-close" onClick={() => setSelectedCPResellerForMonitor(null)} style={{ fontSize: '24px', background: 'none', border: 'none', cursor: 'pointer' }}>×</button>
                      </div>
                    </div>

                    <div style={{ padding: '10px 24px', borderBottom: '1px solid #f1f5f9' }}>
                      <input
                        type="text"
                        value={cpDetailSearch}
                        onChange={(e) => { setCpDetailSearch(e.target.value); setCpTransactionPage(1); }}
                        placeholder="Search transactions & users — email, type, reason, organization…"
                        className="form-input"
                        style={{ width: '100%', padding: '8px 12px', fontSize: '13px' }}
                      />
                    </div>

                    <div style={{ padding: '24px', overflowY: 'auto', flex: 1 }}>
                      {loadingCPResellerDetails ? (
                        <div className="loading">Loading details...</div>
                      ) : (() => {
                        const q = cpDetailSearch.trim().toLowerCase();
                        const txns = q ? cpResellerTransactions.filter(t =>
                          [t.type, cpTransactionLabel(t.type), t.reason, t.email, Array.isArray(t.emails) ? t.emails.join(' ') : '']
                            .some(v => String(v || '').toLowerCase().includes(q))) : cpResellerTransactions;
                        const activeUsers = cpResellerUsers.filter(u => !u.isDeleted);
                        const users = q ? activeUsers.filter(u =>
                          [u.email, u.organizationName].some(v => String(v || '').toLowerCase().includes(q))) : activeUsers;
                        return (
                        <>
                          {/* Transactions */}
                          <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ marginBottom: '12px', color: '#374151' }}>Transaction History ({txns.length}{q ? ` of ${cpResellerTransactions.length}` : ''} total)</h4>
                            {txns.length === 0 ? (
                              <p style={{ color: '#666' }}>{q ? 'No transactions match your search' : 'No transactions yet'}</p>
                            ) : (
                              <>
                                <div style={{ maxHeight: cpDetailFullscreen ? 'calc(100vh - 300px)' : '350px', overflow: 'auto' }}>
                                  <table className="data-table">
                                    <thead>
                                      <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Credits / Days</th>
                                        <th>Reason</th>
                                        <th>Balance</th>
                                        <th>Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {txns
                                        .slice((cpTransactionPage - 1) * CP_TRANSACTIONS_PER_PAGE, cpTransactionPage * CP_TRANSACTIONS_PER_PAGE)
                                        .map((t, i) => {
                                          const amountDisplay = getCPTransactionAmountDisplay(t);
                                          const txKey = t.id ?? `${t.timestamp}-${i}`;
                                          const txEmails = Array.isArray(t.emails) ? t.emails : [];
                                          // A search that hit one of this row's emails opens the list,
                                          // so the matching address is visible without the CSV.
                                          const emailHit = q && txEmails.some(e => String(e).toLowerCase().includes(q));
                                          const expanded = txEmails.length > 0 && (emailHit || cpExpandedTx.has(txKey));
                                          return (
                                          <React.Fragment key={txKey}>
                                          <tr>
                                            <td style={{ fontSize: '11px' }}>{new Date(t.timestamp).toLocaleString()}</td>
                                            <td>
                                              <span className={`badge ${getCPTransactionBadgeClass(t)}`}>
                                                {cpTransactionLabel(t.type)}
                                              </span>
                                            </td>
                                            <td style={{ color: amountDisplay.color, fontWeight: 600 }}>
                                              {amountDisplay.text}
                                            </td>
                                            <td style={{ fontSize: '12px', maxWidth: cpDetailFullscreen ? '520px' : '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.reason}>
                                              {t.email ? <span style={{ fontWeight: 600 }}>{t.email} · </span> : null}
                                              {t.reason}
                                            </td>
                                            <td>{displayBalanceBefore(t)} → {t.balanceAfter}</td>
                                            <td>
                                              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                              {txEmails.length > 0 && (
                                                <button
                                                  onClick={() => setCpExpandedTx(prev => {
                                                    const next = new Set(prev);
                                                    if (next.has(txKey)) next.delete(txKey); else next.add(txKey);
                                                    return next;
                                                  })}
                                                  className="btn btn-secondary"
                                                  style={{ padding: '2px 6px', fontSize: '10px' }}
                                                  title="Show the emails in this operation"
                                                >
                                                  {expanded ? '▾' : '▸'} {txEmails.length} email{txEmails.length === 1 ? '' : 's'}
                                                </button>
                                              )}
                                              {(['BULK_ASSIGN', 'BULK_DELETE', 'BULK_EXTEND', 'BULK_REFUND', 'BULK_REFUND_DAYS_ADD'].includes(t.type)) && t.operationId && (
                                                <button
                                                  onClick={() => {
                                                    window.open(`${API_URL}/credits-pool/bulk-operation/${t.operationId}/csv`, '_blank');
                                                  }}
                                                  className="btn btn-secondary"
                                                  style={{ padding: '2px 6px', fontSize: '10px' }}
                                                  title="Download email list as CSV"
                                                >
                                                  CSV
                                                </button>
                                              )}
                                              {(['BULK_DELETE', 'BULK_REFUND', 'BULK_REFUND_CREDITS_PENDING', 'BULK_REFUND_DAYS_ADD'].includes(t.type)) && t.operationId && (
                                                <button
                                                  onClick={() => restoreBulkDelete(parseInt(t.operationId, 10))}
                                                  className="btn btn-secondary"
                                                  style={{ padding: '2px 6px', fontSize: '10px' }}
                                                  title="Undo this bulk delete: re-add the users to Adobe and take back the refund"
                                                >
                                                  Restore
                                                </button>
                                              )}
                                              </div>
                                            </td>
                                          </tr>
                                          {expanded && (
                                            <tr className="cp-tx-emails-row">
                                              <td colSpan={6} style={{ whiteSpace: 'normal', paddingTop: 0 }}>
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                                  {txEmails.map((e, j) => {
                                                    const hit = q && String(e).toLowerCase().includes(q);
                                                    return (
                                                      <span key={j} className={`cp-tx-email${hit ? ' is-hit' : ''}`}>{e}</span>
                                                    );
                                                  })}
                                                </div>
                                              </td>
                                            </tr>
                                          )}
                                          </React.Fragment>
                                        )})}
                                    </tbody>
                                  </table>
                                </div>
                                {/* Pagination */}
                                {txns.length > CP_TRANSACTIONS_PER_PAGE && (
                                  <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: '6px' }}>
                                    <span style={{ fontSize: '12px', color: '#64748b' }}>
                                      Showing {((cpTransactionPage - 1) * CP_TRANSACTIONS_PER_PAGE) + 1} - {Math.min(cpTransactionPage * CP_TRANSACTIONS_PER_PAGE, txns.length)} of {txns.length}
                                    </span>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <button
                                        onClick={() => setCpTransactionPage(1)}
                                        disabled={cpTransactionPage === 1}
                                        className="btn btn-secondary"
                                        style={{ padding: '4px 8px', fontSize: '11px' }}
                                      >
                                        First
                                      </button>
                                      <button
                                        onClick={() => setCpTransactionPage(p => Math.max(1, p - 1))}
                                        disabled={cpTransactionPage === 1}
                                        className="btn btn-secondary"
                                        style={{ padding: '4px 8px', fontSize: '11px' }}
                                      >
                                        ‹ Prev
                                      </button>
                                      <span style={{ padding: '4px 12px', fontSize: '12px', fontWeight: 600 }}>
                                        Page {cpTransactionPage} / {Math.ceil(txns.length / CP_TRANSACTIONS_PER_PAGE)}
                                      </span>
                                      <button
                                        onClick={() => setCpTransactionPage(p => Math.min(Math.ceil(txns.length / CP_TRANSACTIONS_PER_PAGE), p + 1))}
                                        disabled={cpTransactionPage >= Math.ceil(txns.length / CP_TRANSACTIONS_PER_PAGE)}
                                        className="btn btn-secondary"
                                        style={{ padding: '4px 8px', fontSize: '11px' }}
                                      >
                                        Next ›
                                      </button>
                                      <button
                                        onClick={() => setCpTransactionPage(Math.ceil(txns.length / CP_TRANSACTIONS_PER_PAGE))}
                                        disabled={cpTransactionPage >= Math.ceil(txns.length / CP_TRANSACTIONS_PER_PAGE)}
                                        className="btn btn-secondary"
                                        style={{ padding: '4px 8px', fontSize: '11px' }}
                                      >
                                        Last </button>
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                          {/* Users */}
                          <div>
                            <h4 style={{ marginBottom: '12px', color: '#374151' }}>Assigned Users ({users.length}{q ? ` of ${activeUsers.length}` : ''})</h4>
                            {users.length === 0 ? (
                              <p style={{ color: '#666' }}>{q ? 'No users match your search' : 'No users assigned'}</p>
                            ) : (
                              <div style={{ maxHeight: cpDetailFullscreen ? '60vh' : '250px', overflowY: 'auto' }}>
                                <table className="data-table">
                                  <thead>
                                    <tr>
                                      <th>Email</th>
                                      <th>Organization</th>
                                      <th>Duration</th>
                                      <th>Expires</th>
                                      <th>Status</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {users.map((u, i) => (
                                      <tr key={i}>
                                        <td><strong>{u.email}</strong></td>
                                        <td><span className="badge badge-info">{u.organizationName}</span></td>
                                        <td>{u.durationMonths} months</td>
                                        <td style={{ color: u.isExpired ? '#dc2626' : u.daysLeft <= 7 ? '#f59e0b' : 'inherit' }}>
                                          {u.isExpired ? 'Expired' : `${u.daysLeft} days`}
                                        </td>
                                        <td>
                                          {u.isExpired ? (
                                            <span className="badge badge-danger">Expired</span>
                                          ) : (
                                            <span className="badge badge-success">Active</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                          </div>
                        </>
                        );
                      })()}
                    </div>

                    <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', textAlign: 'right' }}>
                      <button onClick={() => setSelectedCPResellerForMonitor(null)} className="btn btn-secondary">Close</button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {/* API Monitoring Tab */}
          {activeTab === 'apiMonitoring' && (
            <>
              {/* Stats Cards */}
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-label">Total API Keys</div>
                  <div className="stat-value">{apiMonitoringData.stats.totalKeys || 0}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#10b981' }}>
                  <div className="stat-label">Active Keys</div>
                  <div className="stat-value" style={{ color: '#10b981' }}>{apiMonitoringData.stats.activeKeys || 0}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#ef4444' }}>
                  <div className="stat-label">Revoked Keys</div>
                  <div className="stat-value" style={{ color: '#ef4444' }}>{apiMonitoringData.stats.revokedKeys || 0}</div>
                </div>
                <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
                  <div className="stat-label">Total API Requests</div>
                  <div className="stat-value">{apiMonitoringData.stats.totalRequests || 0}</div>
                </div>
              </div>

              {/* Kill Switch Card */}
              <div className="card" style={{ borderLeft: '4px solid #ef4444', marginBottom: '20px' }}>
                <div className="card-header">
                  <h3 className="card-title">Emergency Kill Switch</h3>
                  <button
                    onClick={handleKillSwitchAll}
                    disabled={loadingApiMonitoring || (apiMonitoringData.stats.activeKeys || 0) === 0}
                    className="btn btn-danger"
                    style={{ backgroundColor: '#ef4444', color: '#fff', fontWeight: 'bold' }}
                  >
                    Revoke ALL Active Keys ({apiMonitoringData.stats.activeKeys || 0})
                  </button>
                </div>
                <p style={{ color: '#9ca3af', fontSize: '13px', margin: '8px 0 0 0' }}>
                  Instantly revokes all active API keys across all resellers. Resellers will need to generate new keys after this action.
                </p>
              </div>

              {/* API Keys Table */}
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">All API Keys</h3>
                  <button onClick={fetchApiMonitoring} disabled={loadingApiMonitoring} className="btn btn-secondary">
                    Refresh
                  </button>
                </div>
                {loadingApiMonitoring ? (
                  <p style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>Loading...</p>
                ) : (apiMonitoringData.keys || []).length === 0 ? (
                  <p style={{ padding: '20px', textAlign: 'center', color: '#9ca3af' }}>No API keys have been generated yet.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Reseller</th>
                          <th>Key Prefix</th>
                          <th>Status</th>
                          <th>Created</th>
                          <th>Last Used</th>
                          <th>Total Requests</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {apiMonitoringData.keys.map((k) => (
                          <tr key={k.id}>
                            <td style={{ fontWeight: '600' }}>{k.resellerUsername}</td>
                            <td><code style={{ backgroundColor: '#374151', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>{k.keyPrefix}...</code></td>
                            <td>
                              <span style={{
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                backgroundColor: k.status === 'active' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                color: k.status === 'active' ? '#10b981' : '#ef4444'
                              }}>
                                {k.status === 'active' ? '● Active' : '● Revoked'}
                              </span>
                            </td>
                            <td style={{ fontSize: '13px', color: '#9ca3af' }}>{k.createdAt ? new Date(k.createdAt).toLocaleDateString() : '-'}</td>
                            <td style={{ fontSize: '13px', color: '#9ca3af' }}>{k.lastUsed ? new Date(k.lastUsed).toLocaleString() : 'Never'}</td>
                            <td style={{ fontWeight: '600', textAlign: 'center' }}>{k.totalRequests}</td>
                            <td>
                              {k.status === 'active' ? (
                                <button
                                  onClick={() => handleRevokeApiKey(k.id)}
                                  className="btn btn-danger"
                                  style={{ fontSize: '12px', padding: '4px 10px' }}
                                >
                                  Revoke
                                </button>
                              ) : (
                                <span style={{ fontSize: '12px', color: '#6b7280' }}>
                                  {k.revokedBy === 'admin-killswitch' ? 'Kill switch' : k.revokedBy === 'admin' ? 'By admin' : 'By reseller'}
                                  {k.revokedAt && ` · ${new Date(k.revokedAt).toLocaleDateString()}`}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Resellers API Access Overview */}
              <div className="card" style={{ marginTop: '20px' }}>
                <div className="card-header">
                  <h3 className="card-title">Reseller API Access</h3>
                </div>
                <p style={{ color: '#9ca3af', fontSize: '13px', margin: '0 0 12px 0' }}>
                  {apiMonitoringData.stats.resellersWithApiAccess || 0} reseller(s) have API access enabled
                </p>
                {(() => {
                  const resellersMap = {};
                  (apiMonitoringData.keys || []).forEach(k => {
                    if (!resellersMap[k.resellerId]) {
                      resellersMap[k.resellerId] = { username: k.resellerUsername, resellerId: k.resellerId, activeKeys: 0, totalRequests: 0 };
                    }
                    if (k.status === 'active') resellersMap[k.resellerId].activeKeys++;
                    resellersMap[k.resellerId].totalRequests += k.totalRequests;
                  });
                  const resellersList = Object.values(resellersMap);
                  if (resellersList.length === 0) {
                    return <p style={{ padding: '10px', color: '#6b7280', textAlign: 'center' }}>No resellers have generated API keys yet.</p>;
                  }
                  return (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Reseller</th>
                          <th>Active Keys</th>
                          <th>Total Requests</th>
                          <th>API Access</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resellersList.map(r => (
                          <tr key={r.resellerId}>
                            <td style={{ fontWeight: '600' }}>{r.username}</td>
                            <td style={{ textAlign: 'center' }}>{r.activeKeys}</td>
                            <td style={{ textAlign: 'center' }}>{r.totalRequests}</td>
                            <td>
                              {r.activeKeys > 0 ? (
                                <button
                                  onClick={() => handleToggleApiAccess(r.resellerId, false)}
                                  className="btn btn-danger"
                                  style={{ fontSize: '12px', padding: '4px 10px' }}
                                >
                                  Disable & Revoke
                                </button>
                              ) : (
                                <span style={{ color: '#6b7280', fontSize: '12px' }}>No active keys</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </>
          )}

          {/* Migration Logs Tab */}
          {activeTab === 'migrationLogs' && (
            <>
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Migration History</h3>
                  <button onClick={fetchMigrationLogs} disabled={loadingMigrationLogs} className="btn btn-secondary">
                    Refresh
                  </button>
                </div>

                {loadingMigrationLogs && <div className="loading">Loading migration logs...</div>}

                {/* Active Migrations */}
                {migrationLogs.active.length > 0 && (
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{ marginBottom: '12px', color: '#f59e0b' }}>Active Migrations</h4>
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Organization</th>
                          <th>Progress</th>
                          <th>Started</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {migrationLogs.active.map((m, i) => (
                          <tr key={i}>
                            <td><strong>{m.orgName}</strong></td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <div style={{ flex: 1, background: '#e5e7eb', borderRadius: '10px', height: '10px' }}>
                                  <div style={{
                                    width: `${Math.round((m.migratedCount / m.totalUsers) * 100)}%`,
                                    height: '100%',
                                    background: m.status === 'paused' ? '#8fa1ae' : '#ffa239',
                                    borderRadius: '10px'
                                  }}></div>
                                </div>
                                <span style={{ fontSize: '12px' }}>{m.migratedCount}/{m.totalUsers}</span>
                              </div>
                            </td>
                            <td style={{ fontSize: '12px' }}>{new Date(m.startedAt).toLocaleString()}</td>
                            <td>
                              {m.status === 'paused' ? (
                                <span className="badge" style={{ background: '#e5e7eb', color: '#374151' }}>Paused</span>
                              ) : (
                                <span className="badge" style={{ background: '#fef3c7', color: '#92400e' }}>In Progress</span>
                              )}
                            </td>
                            <td>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                {m.status === 'in_progress' && !m.pauseRequested && (
                                  <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '11px' }}
                                    onClick={() => handlePauseMigration(m.id)}>
                                    Pause
                                  </button>
                                )}
                                {m.status === 'in_progress' && m.pauseRequested && (
                                  <span style={{ fontSize: '11px', color: '#f59e0b' }}>Pausing...</span>
                                )}
                                {m.status === 'paused' && (
                                  <button className="btn btn-primary" style={{ padding: '4px 8px', fontSize: '11px' }}
                                    onClick={() => handleResumeMigration(m.id)}>
                                    Resume{getMigrationRemainingCount(m) > 0 ? ` (${getMigrationRemainingCount(m)})` : ''}
                                  </button>
                                )}
                                {/* Cancel button - always visible for in_progress or paused */}
                                {(m.status === 'in_progress' || m.status === 'paused') && (
                                  <button className="btn" style={{ padding: '4px 8px', fontSize: '11px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fecaca' }}
                                    onClick={() => handleCancelMigration(m.id, m.orgName)}>
                                    Cancel
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Completed Migrations */}
                {!loadingMigrationLogs && (
                  <>
                    <h4 style={{ marginBottom: '12px', color: '#374151' }}>Completed Migrations</h4>
                    {migrationLogs.history.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-icon"><Icon name="clipboard" size={16} /></div>
                        <div className="empty-text">No migration history yet</div>
                      </div>
                    ) : (
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Organization</th>
                            <th>Users</th>
                            <th>Duration</th>
                            <th>Speed</th>
                            <th>Started</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {migrationLogs.history.map((m, i) => (
                            <tr key={i}>
                              <td><strong>{m.orgName}</strong></td>
                              <td>
                                <span style={{ color: '#059669' }}>{m.migratedCount}</span>
                                {m.failedCount > 0 && (
                                  <span style={{ color: '#dc2626', marginLeft: '4px' }}>({m.failedCount} failed)</span>
                                )}
                                <span style={{ color: '#6b7280' }}> / {m.totalUsers}</span>
                              </td>
                              <td>
                                <span style={{ fontWeight: 600 }}>{m.durationFormatted}</span>
                              </td>
                              <td>
                                {m.usersPerMinute ? `${m.usersPerMinute} users/min` : '-'}
                              </td>
                              <td style={{ fontSize: '11px', color: '#6b7280' }}>
                                {new Date(m.startedAt).toLocaleString()}
                              </td>
                              <td>
                                <span className={`badge ${m.status === 'completed' ? 'badge-success' :
                                  m.status === 'cancelled' ? 'badge-danger' :
                                    'badge-warning'
                                  }`}>
                                  {m.status === 'completed' ? 'Completed' :
                                    m.status === 'cancelled' ? 'Cancelled' :
                                      m.status === 'auto-completed' ? 'Auto-completed' : m.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Email Whitelist Tab */}
          {activeTab === 'whitelist' && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Email Domain Whitelist</h3>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={emailWhitelist.enabled}
                      onChange={(e) => handleUpdateEmailWhitelist(e.target.checked, emailWhitelist.domains, emailWhitelist.mode, emailWhitelist.organizationRules)}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span style={{ fontSize: '14px', fontWeight: '500', color: emailWhitelist.enabled ? '#10b981' : '#6b7280' }}>
                    {emailWhitelist.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <button onClick={fetchEmailWhitelist} disabled={loading} className="btn btn-secondary" style={{ marginLeft: '12px' }}>
                    Refresh
                  </button>
                </div>
              </div>

              <div style={{ padding: '16px', background: '#f0f9ff', borderRadius: '8px', marginBottom: '20px', border: '1px solid #bae6fd' }}>
                <p style={{ margin: 0, color: '#0369a1', fontSize: '14px' }}>
                  When enabled, users can only be assigned if their email address belongs to one of the trusted domains.
                  Use <strong>Global</strong> mode to apply to all organizations, or <strong>Per-Organization</strong> to target specific ones.
                </p>
              </div>

              {/* Mode Selector */}
              {emailWhitelist.enabled && (
                <div style={{ marginBottom: '20px' }}>
                  <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>Whitelist Mode</label>
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      className={`btn ${(emailWhitelist.mode || 'global') === 'global' ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setEmailWhitelist({ ...emailWhitelist, mode: 'global' })}
                      style={{ padding: '8px 20px' }}
                    >
                      Global
                    </button>
                    <button
                      className={`btn ${emailWhitelist.mode === 'per-organization' ? 'btn-primary' : 'btn-secondary'}`}
                      onClick={() => setEmailWhitelist({ ...emailWhitelist, mode: 'per-organization' })}
                      style={{ padding: '8px 20px' }}
                    >
                      Per-Organization
                    </button>
                  </div>
                </div>
              )}

              {/* Global Domains - always visible for global mode */}
              {emailWhitelist.enabled && (emailWhitelist.mode || 'global') === 'global' && (
                <div className="form-group">
                  <label className="form-label">Trusted Domains (one per line or comma-separated)</label>
                  <textarea
                    className="form-input"
                    placeholder="@example.com, @school.edu"
                    value={whitelistInput}
                    onChange={(e) => setWhitelistInput(e.target.value)}
                    rows={8}
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
              )}

              {/* Per-Organization Rules */}
              {emailWhitelist.enabled && emailWhitelist.mode === 'per-organization' && (
                <div>
                  <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label className="form-label" style={{ margin: 0 }}>Organization Rules</label>
                    <select
                      className="form-input"
                      style={{ width: '280px' }}
                      value=""
                      onChange={(e) => {
                        if (e.target.value) {
                          const orgId = parseInt(e.target.value);
                          const org = organizations.find(o => o.id === orgId);
                          if (org && !(emailWhitelist.organizationRules || []).some(r => r.organizationId === orgId)) {
                            setEmailWhitelist({
                              ...emailWhitelist,
                              organizationRules: [
                                ...(emailWhitelist.organizationRules || []),
                                { organizationId: orgId, organizationName: org.name, enabled: true, domains: [] }
                              ]
                            });
                          }
                        }
                      }}
                    >
                      <option value="">Add Organization...</option>
                      {organizations
                        .filter(org => !(emailWhitelist.organizationRules || []).some(r => r.organizationId === org.id))
                        .map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                    </select>
                  </div>

                  {(!emailWhitelist.organizationRules || emailWhitelist.organizationRules.length === 0) ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: '8px', border: '1px dashed #d1d5db' }}>
                      No organization rules yet. Use the dropdown above to add one.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {(emailWhitelist.organizationRules || []).map((rule, idx) => (
                        <div key={rule.organizationId} style={{
                          padding: '16px',
                          background: rule.enabled ? '#f0fdf4' : '#f9fafb',
                          borderRadius: '8px',
                          border: `1px solid ${rule.enabled ? '#bbf7d0' : '#e5e7eb'}`
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                              <strong style={{ fontSize: '14px' }}>{rule.organizationName || `Org #${rule.organizationId}`}</strong>
                              <label className="toggle-switch" style={{ transform: 'scale(0.8)' }}>
                                <input
                                  type="checkbox"
                                  checked={rule.enabled}
                                  onChange={() => {
                                    const updated = [...emailWhitelist.organizationRules];
                                    updated[idx] = { ...updated[idx], enabled: !updated[idx].enabled };
                                    setEmailWhitelist({ ...emailWhitelist, organizationRules: updated });
                                  }}
                                />
                                <span className="slider round"></span>
                              </label>
                              <span style={{ fontSize: '12px', color: rule.enabled ? '#059669' : '#6b7280' }}>
                                {rule.enabled ? 'Active' : 'Inactive'}
                              </span>
                            </div>
                            <button
                              onClick={() => {
                                setEmailWhitelist({
                                  ...emailWhitelist,
                                  organizationRules: emailWhitelist.organizationRules.filter((_, i) => i !== idx)
                                });
                              }}
                              className="btn btn-danger"
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                            >
                              Remove
                            </button>
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label" style={{ fontSize: '12px' }}>Allowed Domains (one per line or comma-separated)</label>
                            <textarea
                              className="form-input"
                              placeholder="@gov.hk, @example.com"
                              value={rule.domainsText !== undefined ? rule.domainsText : (rule.domains || []).join('\n')}
                              onChange={(e) => {
                                const updated = [...emailWhitelist.organizationRules];
                                updated[idx] = {
                                  ...updated[idx],
                                  domainsText: e.target.value,
                                  domains: e.target.value.split(/[\n,]+/).map(d => d.trim()).filter(d => d.length > 0)
                                };
                                setEmailWhitelist({ ...emailWhitelist, organizationRules: updated });
                              }}
                              rows={3}
                              style={{ fontFamily: 'monospace', fontSize: '13px' }}
                              disabled={!rule.enabled}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ marginTop: '12px', padding: '12px', background: '#fffbeb', borderRadius: '8px', border: '1px solid #fde68a' }}>
                    <p style={{ margin: 0, color: '#92400e', fontSize: '12px' }}>
                      Organizations <strong>not listed</strong> above will allow <strong>all email domains</strong>. Only organizations with active rules will enforce domain restrictions.
                    </p>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
                <button
                  onClick={() => {
                    const domains = whitelistInput
                      .split(/[\n,]+/)
                      .map(d => d.trim())
                      .filter(d => d.length > 0);
                    handleUpdateEmailWhitelist(
                      emailWhitelist.enabled,
                      domains,
                      emailWhitelist.mode,
                      emailWhitelist.organizationRules
                    );
                  }}
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}

          {/* Embed Domains Tab */}
          {activeTab === 'embedDomains' && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Embed Domains</h3>
                <button onClick={fetchEmbedDomains} disabled={loadingEmbedDomains} className="btn btn-secondary">
                  Refresh
                </button>
              </div>

              <div style={{ padding: '16px', background: '#fff7ed', borderRadius: '8px', marginBottom: '20px', border: '1px solid #fed7aa' }}>
                <p style={{ margin: 0, color: '#9a3412', fontSize: '14px' }}>
                  The public status page (<code>/status</code>) blocks being embedded in an iframe on any other
                  site by default (<code>X-Frame-Options</code>) — this protects against clickjacking and against
                  the page being repackaged inside a phishing site. Only add a domain here if you've personally
                  verified who's asking and why. Each approved domain is added to the page's CSP
                  <code> frame-ancestors</code> allowlist; everything else stays blocked.
                </p>
              </div>

              <form onSubmit={handleAddEmbedDomain} style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: '1 1 240px', margin: 0 }}>
                  <label className="form-label">Domain (https:// origin)</label>
                  <input
                    className="form-input"
                    type="text"
                    value={embedDomainInput}
                    onChange={e => setEmbedDomainInput(e.target.value)}
                    placeholder="https://partner.com"
                    required
                  />
                </div>
                <div className="form-group" style={{ flex: '1 1 240px', margin: 0 }}>
                  <label className="form-label">Note (optional)</label>
                  <input
                    className="form-input"
                    type="text"
                    value={embedDomainNote}
                    onChange={e => setEmbedDomainNote(e.target.value)}
                    placeholder="Who is this and why"
                    maxLength={200}
                  />
                </div>
                <button type="submit" disabled={addingEmbedDomain || !embedDomainInput.trim()} className="btn btn-primary">
                  {addingEmbedDomain ? 'Adding...' : 'Approve Domain'}
                </button>
              </form>

              <h4 style={{ marginBottom: '12px' }}>Approved Domains ({embedDomains.length})</h4>
              {loadingEmbedDomains ? (
                <div className="loading">Loading...</div>
              ) : embedDomains.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-text">No domains approved — the status page can only be embedded on its own domain.</div>
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Note</th>
                      <th>Added By</th>
                      <th>Added</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {embedDomains.map(d => (
                      <tr key={d.id}>
                        <td><code>{d.origin}</code></td>
                        <td>{d.note || '—'}</td>
                        <td>{d.addedBy || '—'}</td>
                        <td>{d.createdAt ? new Date(d.createdAt).toLocaleString() : '—'}</td>
                        <td>
                          <button
                            onClick={() => handleRemoveEmbedDomain(d.id, d.origin)}
                            className="btn btn-danger"
                            style={{ padding: '4px 10px', fontSize: '12px' }}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Supplier API Tab (connection test only — no product sync yet) */}
          {activeTab === 'supplierApi' && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Supplier API Connection</h3>
                <button onClick={fetchSupplierSettings} disabled={loadingSupplierSettings} className="btn btn-secondary">
                  Refresh
                </button>
              </div>

              <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '8px', marginBottom: '20px', border: '1px solid #bfdbfe' }}>
                <p style={{ margin: 0, color: '#1d4ed8', fontSize: '14px' }}>
                  This connects to the supplier's API ({supplierSettings.baseUrl || 'https://x-panel.io/api/v2'}) to verify
                  the API key, check balance, and import supplier products into the Marketplace with a uniform markup.
                </p>
              </div>

              <div className="settings-status">
                <span className={`status-badge ${supplierSettings.configured ? 'configured' : 'not-configured'}`}>
                  {supplierSettings.configured ? 'API Key Configured' : 'API Key Not Configured'}
                </span>
                {supplierSettings.configured && (
                  <div className="settings-preview">
                    <div className="setting-row">
                      <span className="setting-label">Key Source</span>
                      <span className="setting-value">{supplierSettings.source === 'admin_panel' ? 'Admin Panel' : 'Environment Variable'}</span>
                    </div>
                    <div className="setting-row">
                      <span className="setting-label">Current Key</span>
                      <span className="setting-value">{supplierSettings.maskedKey}</span>
                    </div>
                  </div>
                )}
              </div>

              <form onSubmit={handleSaveSupplierApiKey} style={{ display: 'flex', gap: '8px', margin: '20px 0', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ flex: '1 1 280px', margin: 0 }}>
                  <label className="form-label">Supplier API URL</label>
                  <input
                    className="form-input"
                    type="url"
                    value={supplierBaseUrlInput}
                    onChange={e => setSupplierBaseUrlInput(e.target.value)}
                    placeholder="https://x-panel.io"
                  />
                </div>
                <div className="form-group" style={{ flex: '1 1 280px', margin: 0 }}>
                  <label className="form-label">{supplierSettings.configured ? 'Replace API Key' : 'API Key'}</label>
                  <input
                    className="form-input"
                    type="password"
                    autoComplete="off"
                    value={supplierApiKeyInput}
                    onChange={e => setSupplierApiKeyInput(e.target.value)}
                    placeholder={supplierSettings.configured ? 'Enter a new key to replace the current one' : 'Enter supplier API key'}
                  />
                </div>
                <button type="submit" disabled={savingSupplierKey || (!supplierApiKeyInput.trim() && !supplierBaseUrlInput.trim())} className="btn btn-primary">
                  {savingSupplierKey ? 'Saving...' : 'Save Settings'}
                </button>
              </form>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px' }}>
                <button
                  type="button"
                  onClick={handleTestSupplierConnection}
                  disabled={testingSupplierConnection || !supplierSettings.configured}
                  className="btn btn-primary"
                >
                  {testingSupplierConnection ? 'Testing...' : 'Test Supplier API Connection'}
                </button>
                {!supplierSettings.configured && (
                  <span style={{ marginLeft: '12px', fontSize: '13px', color: '#64748b' }}>Save an API key first.</span>
                )}

                {supplierTestResult && (
                  supplierTestResult.success ? (
                    <div className="alert alert-success" style={{ marginTop: '16px' }}>
                      <span style={{ display: 'inline-flex' }}><Icon name="check" size={17} /></span>
                      <div>
                        <strong>Connection successful</strong>
                        <div style={{ marginTop: '6px', fontSize: '14px' }}>
                          Balance: <strong>{supplierTestResult.balance} {supplierTestResult.currency}</strong>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-error" style={{ marginTop: '16px' }}>
                      <span style={{ fontSize: '18px' }}><Icon name="alert" size={16} /></span>
                      <div>
                        <strong>Connection failed</strong>
                        <div style={{ marginTop: '6px', fontSize: '14px' }}>{supplierTestResult.error}</div>
                      </div>
                    </div>
                  )
                )}
              </div>

              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '20px', marginTop: '20px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '12px' }}>Supplier Product Import</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(180px, 260px) auto auto', gap: '10px', alignItems: 'end', marginBottom: '12px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Uniform Markup (USD)</label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step="0.01"
                      value={supplierMarkup}
                      onChange={e => setSupplierMarkup(e.target.value)}
                      placeholder="0.10"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handlePreviewSupplierProducts}
                    disabled={loadingSupplierProducts || !supplierSettings.configured}
                    className="btn btn-secondary"
                  >
                    {loadingSupplierProducts ? 'Fetching...' : 'Preview Products'}
                  </button>
                  <button
                    type="button"
                    onClick={handleImportSupplierProducts}
                    disabled={importingSupplierProducts || !supplierSettings.configured}
                    className="btn btn-primary"
                  >
                    {importingSupplierProducts ? 'Importing...' : 'Import / Update Products'}
                  </button>
                </div>

                {supplierProductsPreview && (
                  supplierProductsPreview.success ? (
                    <div className="alert alert-success" style={{ marginTop: '12px', display: 'block' }}>
                      <strong>
                        {supplierProductsPreview.products
                          ? `Preview loaded: ${supplierProductsPreview.count} of ${supplierProductsPreview.total} products`
                          : `Import complete: ${supplierProductsPreview.created} created, ${supplierProductsPreview.updated} updated`}
                      </strong>
                      {supplierProductsPreview.products && (
                        <div style={{ marginTop: '10px', overflowX: 'auto' }}>
                          <table className="data-table" style={{ minWidth: '720px' }}>
                            <thead>
                              <tr>
                                <th>Service</th>
                                <th>Category</th>
                                <th>Cost</th>
                                <th>Sell Price</th>
                              </tr>
                            </thead>
                            <tbody>
                              {supplierProductsPreview.products.slice(0, 10).map(product => (
                                <tr key={product.supplierServiceId}>
                                  <td>{product.name}</td>
                                  <td>{product.category || '-'}</td>
                                  <td>${Number(product.supplierRate || 0).toFixed(4)}</td>
                                  <td>{Number(product.priceGoldCoins || 0).toFixed(4)} GC</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="alert alert-error" style={{ marginTop: '12px' }}>
                      <span style={{ fontSize: '18px' }}><Icon name="alert" size={16} /></span>
                      <div>
                        <strong>Product fetch failed</strong>
                        <div style={{ marginTop: '6px', fontSize: '14px' }}>{supplierProductsPreview.error}</div>
                        {supplierProductsPreview.code && <div style={{ marginTop: '4px', fontSize: '12px' }}>Code: {supplierProductsPreview.code}</div>}
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          )}

          {/* Key Redemption Tab */}
          {activeTab === 'redeem' && <KeyRedemptionAdmin />}
          {activeTab === 'manualOrg' && <ManualOrgAdmin onCountsChange={onManualOrgCounts} />}
          {activeTab === 'telegramShop' && <TelegramShopAdmin />}
          {activeTab === 'ipMonitor' && <IpMonitorAdmin />}
          {activeTab === 'dashboard' && <AdminDashboard />}

          {/* Announcements Tab */}
          {activeTab === 'announcements' && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Manage Announcements</h3>
                <button onClick={fetchAnnouncements} disabled={loadingAnnouncements} className="btn btn-secondary">
                  Refresh
                </button>
              </div>

              {/* Create Announcement Form */}
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px', marginBottom: '24px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, marginBottom: '16px' }}>Create New Announcement</h4>
                <form onSubmit={handleCreateAnnouncement}>
                  <div className="form-group">
                    <label className="form-label">Title</label>
                    <input
                      type="text"
                      className="form-input"
                      value={newAnnouncement.title}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                      placeholder="Announcement Title"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Message</label>
                    <textarea
                      className="form-input"
                      value={newAnnouncement.message}
                      onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                      placeholder="Announcement details..."
                      rows={3}
                      required
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                    <div className="form-group">
                      <label className="form-label">Type</label>
                      <select
                        className="form-input"
                        value={newAnnouncement.type}
                        onChange={(e) => setNewAnnouncement({ ...newAnnouncement, type: e.target.value })}
                      >
                        <option value="info">Info (Blue)</option>
                        <option value="warning">Warning (Orange)</option>
                        <option value="error">Critical (Red)</option>
                        <option value="success">Success (Green)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Target Audience</label>
                      <select
                        className="form-input"
                        value={newAnnouncement.target}
                        onChange={(e) => {
                          const target = e.target.value;
                          setNewAnnouncement({ ...newAnnouncement, target, specificResellerIds: [] });
                          if (target === 'credits-pool' && cpResellers.length === 0 && !loadingCPResellers) {
                            fetchCPResellers();
                          }
                        }}
                      >
                        <option value="all">All Resellers</option>
                        <option value="reseller">Traditional Resellers Only</option>
                        <option value="credits-pool">Credits Pool Resellers Only</option>
                      </select>
                    </div>
                  </div>

                  {/* Specific Reseller Selection */}
                  {newAnnouncement.target !== 'all' && (
                    <div className="form-group">
                      <label className="form-label">
                        Limit to Specific {newAnnouncement.target === 'credits-pool' ? 'Credits Pool' : 'Traditional'} Resellers
                        <span style={{ fontWeight: 'normal', color: '#64748b', fontSize: '12px', marginLeft: '8px' }}>(Optional - leave empty for all)</span>
                      </label>
                      <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        padding: '8px',
                        background: '#fff',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: '8px'
                      }}>
                        {(newAnnouncement.target === 'credits-pool' ? cpResellers : resellers).map(r => (
                          <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', borderRadius: '4px', cursor: 'pointer', border: '1px solid #e2e8f0', fontSize: '13px' }}>
                            <input
                              type="checkbox"
                              checked={(newAnnouncement.specificResellerIds || []).includes(r.id)}
                              onChange={(e) => {
                                const currentIds = newAnnouncement.specificResellerIds || [];
                                let newIds;
                                if (e.target.checked) {
                                  newIds = [...currentIds, r.id];
                                } else {
                                  newIds = currentIds.filter(id => id !== r.id);
                                }
                                setNewAnnouncement({ ...newAnnouncement, specificResellerIds: newIds });
                              }}
                            />
                            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.username}>
                              {r.username}
                            </div>
                          </label>
                        ))}
                        {(newAnnouncement.target === 'credits-pool' ? cpResellers : resellers).length === 0 && (
                          <div style={{ color: 'var(--app-muted)', padding: '8px', gridColumn: '1 / -1' }}>Number resellers found</div>
                        )}
                      </div>
                    </div>
                  )}

                  <div style={{ textAlign: 'right', marginTop: '16px' }}>
                    <button type="submit" disabled={loading} className="btn btn-primary">
                      {loading ? 'Creating...' : 'Post Announcement'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Active Announcements List */}
              <h4 style={{ marginBottom: '12px' }}>Active Announcements ({announcements.length})</h4>
              {loadingAnnouncements ? (
                <div className="loading">Loading announcements...</div>
              ) : announcements.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-text">No active announcements</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {announcements.map((ann) => (
                    <div key={ann.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      padding: '16px',
                      background: 'white',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      borderLeft: `4px solid ${ann.type === 'warning' ? '#f59e0b' : ann.type === 'error' ? '#ef4444' : ann.type === 'success' ? '#10b981' : '#3b82f6'}`
                    }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <strong style={{ fontSize: '16px' }}>{ann.title}</strong>
                          <span className="badge" style={{
                            background: '#f1f5f9', color: '#64748b', fontSize: '10px'
                          }}>
                            {ann.target === 'all' ? 'All Resellers' : ann.target === 'credits-pool' ? 'Credits Pool Only' : 'Traditional Only'}
                          </span>
                          <span style={{ fontSize: '11px', color: 'var(--app-muted)' }}>
                            {new Date(ann.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', color: '#475569', fontSize: '14px' }}>
                          {ann.message}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteAnnouncement(ann.id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 10px', fontSize: '12px' }}
                        title="Delete Announcement"
                      ><Icon name="trash" size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Reseller Feedback Tab */}
          {activeTab === 'feedback' && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Reseller Feedback</h3>
                <button onClick={() => fetchFeedback()} disabled={loadingFeedback} className="btn btn-secondary">
                  Refresh
                </button>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <select
                  className="form-input"
                  style={{ width: '180px' }}
                  value={feedbackStatusFilter}
                  onChange={e => { setFeedbackStatusFilter(e.target.value); fetchFeedback(e.target.value, feedbackTypeFilter); }}
                >
                  <option value="all">All Statuses</option>
                  <option value="new">New</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                </select>
                <select
                  className="form-input"
                  style={{ width: '180px' }}
                  value={feedbackTypeFilter}
                  onChange={e => { setFeedbackTypeFilter(e.target.value); fetchFeedback(feedbackStatusFilter, e.target.value); }}
                >
                  <option value="all">All Types</option>
                  <option value="bug">Bug Reports</option>
                  <option value="suggestion">Suggestions</option>
                </select>
              </div>

              {loadingFeedback ? (
                <div className="loading">Loading feedback...</div>
              ) : feedbackList.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-text">No feedback submitted yet</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {feedbackList.map((fb) => {
                    const statusColors = {
                      new: { bg: '#fef3c7', color: '#92400e' },
                      reviewed: { bg: '#dbeafe', color: '#1e40af' },
                      resolved: { bg: '#dcfce7', color: '#166534' },
                      dismissed: { bg: '#f1f5f9', color: '#64748b' }
                    };
                    const sc = statusColors[fb.status] || statusColors.new;
                    return (
                      <div key={fb.id} style={{
                        padding: '16px',
                        background: 'white',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        borderLeft: `4px solid ${fb.type === 'bug' ? '#ef4444' : '#3b82f6'}`
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <strong style={{ fontSize: '14px' }}>{fb.resellerUsername || `Reseller #${fb.resellerId}`}</strong>
                            <span className="badge" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '10px' }}>
                              {fb.resellerType === 'credits-pool' ? 'Credits Pool' : 'Traditional'}
                            </span>
                            <span className="badge" style={{ background: fb.type === 'bug' ? '#fee2e2' : '#dbeafe', color: fb.type === 'bug' ? '#991b1b' : '#1e40af', fontSize: '10px' }}>
                              {fb.type === 'bug' ? 'Bug' : 'Suggestion'}
                            </span>
                            <span className="badge" style={{ background: sc.bg, color: sc.color, fontSize: '10px', textTransform: 'capitalize' }}>
                              {fb.status}
                            </span>
                            <span style={{ fontSize: '11px', color: 'var(--app-muted)' }}>
                              {new Date(fb.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <select
                              className="form-input"
                              style={{ fontSize: '12px', padding: '4px 8px', width: 'auto' }}
                              value={fb.status}
                              onChange={e => handleUpdateFeedbackStatus(fb.id, e.target.value)}
                            >
                              <option value="new">New</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="resolved">Resolved</option>
                              <option value="dismissed">Dismissed</option>
                            </select>
                            <button
                              onClick={() => handleDeleteFeedback(fb.id)}
                              className="btn btn-danger"
                              style={{ padding: '4px 10px', fontSize: '12px' }}
                              title="Delete"
                            ><Icon name="trash" size={16} /></button>
                          </div>
                        </div>
                        <div style={{ whiteSpace: 'pre-wrap', color: '#475569', fontSize: '14px', marginTop: '10px' }}>
                          {fb.message}
                        </div>
                        {fb.adminNote && (
                          <div style={{ marginTop: '10px', padding: '8px 10px', background: '#f0f9ff', borderRadius: '6px', fontSize: '13px', color: '#0369a1' }}>
                            <strong>Your reply:</strong> {fb.adminNote}
                          </div>
                        )}
                        <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'flex-start' }}>
                          <textarea
                            className="form-input"
                            style={{ fontSize: '13px', flex: 1, minHeight: '36px' }}
                            rows={1}
                            placeholder={fb.adminNote ? 'Update your reply...' : 'Write a reply to this reseller...'}
                            value={feedbackReplyDrafts[fb.id] ?? ''}
                            onChange={e => setFeedbackReplyDrafts(prev => ({ ...prev, [fb.id]: e.target.value }))}
                          />
                          <button
                            onClick={() => handleSubmitFeedbackReply(fb.id)}
                            disabled={submittingReplyId === fb.id || !(feedbackReplyDrafts[fb.id] || '').trim()}
                            className="btn btn-primary"
                            style={{ fontSize: '12px', padding: '6px 12px', whiteSpace: 'nowrap' }}
                          >
                            {submittingReplyId === fb.id ? 'Sending…' : 'Reply'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* CP Org Edit Modal */}
          {editingCPOrgAccess && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => setEditingCPOrgAccess(null)}>
              <div onClick={e => e.stopPropagation()}
                style={{ maxWidth: '600px', width: '90%', background: 'white', borderRadius: '12px', padding: '24px', maxHeight: '80vh', overflow: 'auto' }}>
                <h3 style={{ marginBottom: '16px' }}>Edit Organization Access</h3>

                <div className="form-group">
                  <label className="form-label">Add Organization</label>
                  <select className="form-input"
                    onChange={e => {
                      if (e.target.value) {
                        const orgId = parseInt(e.target.value);
                        const org = organizations.find(o => o.id === orgId);
                        if (org && !editCPOrgForm.organizationAccess.some(a => a.organizationId === orgId)) {
                          setEditCPOrgForm({
                            ...editCPOrgForm,
                            organizationAccess: [...editCPOrgForm.organizationAccess, { organizationId: orgId, products: org.products || [] }]
                          });
                        }
                        e.target.value = '';
                      }
                    }}>
                    <option value="">Select organization to add...</option>
                    {organizations.filter(o => !editCPOrgForm.organizationAccess.some(a => a.organizationId === o.id))
                      .map(org => <option key={org.id} value={org.id}>{org.name}</option>)}
                  </select>
                </div>

                {editCPOrgForm.organizationAccess.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <label className="form-label">Selected Organizations</label>
                    {editCPOrgForm.organizationAccess.map(access => {
                      const org = organizations.find(o => o.id === access.organizationId);
                      return (
                        <div key={access.organizationId} style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', marginBottom: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <strong>{org?.name || 'Unknown'}</strong>
                            <button type="button" onClick={() => setEditCPOrgForm({
                              ...editCPOrgForm,
                              organizationAccess: editCPOrgForm.organizationAccess.filter(a => a.organizationId !== access.organizationId)
                            })} className="btn btn-danger" style={{ padding: '2px 8px', fontSize: '12px' }}>Remove</button>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {(org?.products || [])
                              .map((product, idx) => (
                                <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', background: access.products?.includes(product) ? '#dbeafe' : '#fff', borderRadius: '4px', fontSize: '12px', cursor: 'pointer', border: '1px solid #e2e8f0' }}>
                                  <input type="checkbox" checked={access.products?.includes(product) || false}
                                    onChange={e => {
                                      const newAccess = editCPOrgForm.organizationAccess.map(a => {
                                        if (a.organizationId !== access.organizationId) return a;
                                        const newProducts = e.target.checked
                                          ? [...(a.products || []), product]
                                          : (a.products || []).filter(p => p !== product);
                                        return { ...a, products: newProducts };
                                      });
                                      setEditCPOrgForm({ ...editCPOrgForm, organizationAccess: newAccess });
                                    }} />
                                  {product}
                                </label>
                              ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
                  <button type="button" onClick={handleUpdateCPOrgAccess} disabled={loading} className="btn btn-primary">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button onClick={() => setEditingCPOrgAccess(null)} className="btn btn-secondary">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* CP Settings Edit Modal (Password/Expiry) */}
          {editCPSettingsId && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => setEditCPSettingsId(null)}>
              <div onClick={e => e.stopPropagation()}
                style={{ maxWidth: '600px', width: '95%', background: 'white', borderRadius: '12px', padding: '24px', maxHeight: '90vh', overflow: 'auto' }}>
                <h3 style={{ marginBottom: '16px' }}>Edit Reseller Settings</h3>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                  Editing: <strong>{cpResellers.find(r => r.id === editCPSettingsId)?.username}</strong>
                </p>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="form-label">New Password (leave blank to keep current)</label>
                  <input
                    type="password"
                    className="form-input"
                    name="cp-edit-new-password"
                    autoComplete="new-password"
                    value={editCPSettingsForm.password}
                    onChange={e => setEditCPSettingsForm({ ...editCPSettingsForm, password: e.target.value })}
                    placeholder="Enter new password"
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="form-label">Extend Pool Expiry (months)</label>
                  <select
                    className="form-input"
                    value={editCPSettingsForm.extendPoolMonths}
                    onChange={e => setEditCPSettingsForm({ ...editCPSettingsForm, extendPoolMonths: e.target.value })}
                  >
                    <option value="">Don't extend</option>
                    {[1, 2, 3, 6, 12, 18, 24].map(m => <option key={m} value={m}>+{m} month{m > 1 ? 's' : ''}</option>)}
                  </select>
                  <small style={{ color: '#666' }}>
                    Current expiry: {new Date(cpResellers.find(r => r.id === editCPSettingsId)?.creditsPoolValidity).toLocaleDateString()}
                  </small>
                </div>

                {/* Credits Per Duration Configuration */}
                <div style={{ marginTop: '16px', padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '8px', fontSize: '14px', color: '#374151' }}>Credits Per Duration</h4>
                  <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '12px' }}>Set credits and visibility for each duration</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {CREDIT_DURATION_OPTIONS.map(option => {
                      const key = option.key;
                      const isHidden = (editCPSettingsForm.hiddenMonths || []).includes(key);
                      return (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 6px', background: isHidden ? '#fee2e2' : '#ecfdf5', borderRadius: '4px' }}>
                          <input type="checkbox"
                            checked={!isHidden}
                            onChange={() => {
                              const hidden = editCPSettingsForm.hiddenMonths || [];
                              if (isHidden) {
                                setEditCPSettingsForm({ ...editCPSettingsForm, hiddenMonths: hidden.filter(x => x !== key) });
                              } else {
                                setEditCPSettingsForm({ ...editCPSettingsForm, hiddenMonths: [...hidden, key] });
                              }
                            }}
                          />
                          <span style={{ fontSize: '11px', minWidth: '48px', color: isHidden ? '#991b1b' : '#065f46' }}>{typeof key === 'string' ? option.label : `${key}mo`}:</span>
                          <input type="number" min="0.01" step="any"
                            style={{ width: '45px', padding: '2px 4px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '11px', background: isHidden ? '#fecaca' : 'white' }}
                            value={editCPSettingsForm.creditsPerMonth?.[key] ?? option.defaultCredits}
                            disabled={isHidden}
                            onChange={e => setEditCPSettingsForm({
                              ...editCPSettingsForm,
                              creditsPerMonth: { ...editCPSettingsForm.creditsPerMonth, [key]: e.target.value }
                            })}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* API Access Toggle */}
                <div style={{ marginTop: '16px', padding: '16px', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="checkbox"
                        checked={editCPSettingsForm.apiAccessEnabled || false}
                        onChange={e => setEditCPSettingsForm({ ...editCPSettingsForm, apiAccessEnabled: e.target.checked })}
                      />
                      <span style={{ fontSize: '14px', fontWeight: 600, color: '#0369a1' }}>Enable API Access</span>
                    </label>
                  </div>
                  <p style={{ fontSize: '11px', color: '#0284c7', marginTop: '6px' }}>
                    Allow this reseller to generate API keys and add users via REST API.
                    {editCPSettingsForm.apiAccessEnabled && <span style={{ color: '#dc2626', marginLeft: '4px' }}>Disabling will revoke all active API keys.</span>}
                  </p>

                  {/* Default Organization for API */}
                  {editCPSettingsForm.apiAccessEnabled && (() => {
                    const resellerBeingEdited = cpResellers.find(r => r.id === editCPSettingsId);
                    const resellerOrgs = resellerBeingEdited?.organizationAccess || [];
                    return (
                      <div style={{ marginTop: '12px', padding: '12px', background: '#e0f2fe', borderRadius: '6px', border: '1px solid #7dd3fc' }}>
                        <label style={{ fontSize: '13px', fontWeight: 600, color: '#0369a1', display: 'block', marginBottom: '6px' }}>Default Organization for API</label>
                        <select
                          className="form-input"
                          style={{ fontSize: '13px' }}
                          value={editCPSettingsForm.apiDefaultOrganizationId || ''}
                          onChange={e => setEditCPSettingsForm({ ...editCPSettingsForm, apiDefaultOrganizationId: e.target.value ? parseInt(e.target.value) : null })}
                        >
                          <option value="">— Auto (use current access when possible) —</option>
                          {resellerOrgs.map(oa => {
                            const org = organizations.find(o => o.id === oa.organizationId);
                            return <option key={oa.organizationId} value={oa.organizationId}>{org?.name || `Organization #${oa.organizationId}`}</option>;
                          })}
                        </select>
                        <p style={{ fontSize: '11px', color: '#0284c7', marginTop: '4px', marginBottom: 0 }}>
                          Best practice: API users should omit organizationId. If the selected/default org is later removed, the server will move API calls to the reseller's current default or only accessible organization.
                        </p>
                      </div>
                    );
                  })()}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
                  <button onClick={handleUpdateCPSettings} disabled={loading} className="btn btn-primary">
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button onClick={() => setEditCPSettingsId(null)} className="btn btn-secondary">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Bulk pricing: apply one credits-per-duration config to ALL CP resellers */}
          {showBulkPricing && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => setShowBulkPricing(false)}>
              <div onClick={e => e.stopPropagation()}
                style={{ maxWidth: '600px', width: '95%', background: 'white', borderRadius: '12px', padding: '24px', maxHeight: '90vh', overflow: 'auto' }}>
                <h3 style={{ marginBottom: '8px' }}>Set Pricing for All Resellers</h3>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                  Applies the same credits-per-duration pricing to <strong>all {cpResellers.length} Credits Pool reseller{cpResellers.length === 1 ? '' : 's'}</strong> in one click. This overwrites each reseller's current prices.
                </p>

                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '8px', fontSize: '14px', color: '#374151' }}>Credits Per Duration</h4>
                  <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '12px' }}>Set the credit price (and, optionally, visibility) for each duration</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
                    {CREDIT_DURATION_OPTIONS.map(option => {
                      const key = option.key;
                      const isHidden = (bulkPricingForm.hiddenMonths || []).includes(key);
                      const dimHidden = bulkPricingForm.applyHidden && isHidden;
                      return (
                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 6px', background: dimHidden ? '#fee2e2' : '#ecfdf5', borderRadius: '4px' }}>
                          <input type="checkbox"
                            checked={!isHidden}
                            disabled={!bulkPricingForm.applyHidden}
                            title={bulkPricingForm.applyHidden ? 'Show/hide this duration' : 'Enable "also apply visibility" to change this'}
                            onChange={() => {
                              const hidden = bulkPricingForm.hiddenMonths || [];
                              setBulkPricingForm({
                                ...bulkPricingForm,
                                hiddenMonths: isHidden ? hidden.filter(x => x !== key) : [...hidden, key],
                              });
                            }}
                          />
                          <span style={{ fontSize: '11px', minWidth: '48px', color: dimHidden ? '#991b1b' : '#065f46' }}>{typeof key === 'string' ? option.label : `${key}mo`}:</span>
                          <input type="number" min="0.01" step="any"
                            style={{ width: '45px', padding: '2px 4px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '11px', background: dimHidden ? '#fecaca' : 'white' }}
                            value={bulkPricingForm.creditsPerMonth?.[key] ?? option.defaultCredits}
                            onChange={e => setBulkPricingForm({
                              ...bulkPricingForm,
                              creditsPerMonth: { ...bulkPricingForm.creditsPerMonth, [key]: e.target.value }
                            })}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', fontSize: '13px', color: '#374151', cursor: 'pointer' }}>
                    <input type="checkbox"
                      checked={bulkPricingForm.applyHidden}
                      onChange={e => setBulkPricingForm({ ...bulkPricingForm, applyHidden: e.target.checked })}
                    />
                    Also apply the show/hide visibility above (otherwise only prices change)
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
                  <button onClick={handleBulkPricing} disabled={loading} className="btn btn-primary">
                    {loading ? 'Applying...' : `Apply to All ${cpResellers.length} Reseller${cpResellers.length === 1 ? '' : 's'}`}
                  </button>
                  <button onClick={() => setShowBulkPricing(false)} className="btn btn-secondary">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Ship a manual-fulfilment order */}
          {shippingOrder && (
            <div className="modal-overlay"
              style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }}
              onClick={() => { if (!shipBusy) { setShippingOrder(null); setShipInfo(''); } }}>
              <div onClick={e => e.stopPropagation()} className="mp-ship-modal">
                <h3 style={{ margin: '0 0 4px' }}>
                  {shippingOrder.fulfillmentStatus === 'pending' ? 'Ship order' : 'Edit shipped details'} #{shippingOrder.id}
                </h3>
                <p className="mp-ship-meta">
                  <strong>{shippingOrder.productName}</strong>
                  {' · '}{shippingOrder.retentionMonths === 0 ? 'Lifetime' : shippingOrder.retentionMonths < 0 ? `${-shippingOrder.retentionMonths}d` : `${shippingOrder.retentionMonths}mo`}
                  {' · '}buyer: {shippingOrder.resellerUsername || `#${shippingOrder.resellerId}`}
                  {shippingOrder.buyerEmail ? ` · ${shippingOrder.buyerEmail}` : ''}
                </p>

                <label className="form-label" style={{ marginTop: '12px' }}>Information to send the reseller</label>
                <textarea
                  className="form-input mp-ship-textarea"
                  rows={8}
                  maxLength={5000}
                  value={shipInfo}
                  onChange={e => setShipInfo(e.target.value)}
                  placeholder={'login: account@example.com\npassword: ...\nnotes: do not change the email'}
                />
                <small className="mp-ship-hint">
                  This appears in the reseller's order details as soon as you save. {shipInfo.length}/5000
                </small>

                <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                  <button className="btn btn-primary" disabled={shipBusy || !shipInfo.trim()} onClick={handleShipMarketplaceOrder}>
                    {shipBusy ? 'Saving…' : shippingOrder.fulfillmentStatus === 'pending' ? 'Mark Completed' : 'Save changes'}
                  </button>
                  <button className="btn btn-secondary" disabled={shipBusy} onClick={() => { setShippingOrder(null); setShipInfo(''); }}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Pro-rata Refund Settings modal */}
          {showRefundSettings && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => setShowRefundSettings(false)}>
              <div onClick={e => e.stopPropagation()}
                style={{ maxWidth: '620px', width: '95%', background: 'white', borderRadius: '12px', padding: '24px', maxHeight: '90vh', overflow: 'auto' }}>
                <h3 style={{ marginBottom: '8px' }}>Refund Settings</h3>
                <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
                  Deleting a user inside the <strong>grace period</strong> refunds everything they paid. After it, the reseller gets
                  either pro-rata credits or refund-days, depending on section 2 below.
                </p>

                {/* 1. Grace period — governs the full refund */}
                <div className="refund-grace-section">
                  <h4 className="refund-grace-title">1. Grace period — full refund</h4>
                  <p className="refund-grace-help">
                    Deleting a user within this many hours of their <strong>original activation</strong> refunds the full amount paid
                    (activation plus any extensions). Set the default to <strong>0</strong> to switch the full refund off — those deletions then fall
                    through to section 2 instead. A <strong>per-reseller 0</strong>: that reseller gets no full refund at all; every deletion is refunded
                    <strong>by usage, in whole days</strong> (each started day counts as used, rounded down to 0.1), so even an
                    instant delete keeps day 1.
                  </p>

                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label className="form-label">Default for all resellers (hours)</label>
                    <input type="number" min="0" step="1" className="form-input" style={{ maxWidth: '140px' }}
                      value={refundSettings.graceHours}
                      onChange={e => setRefundSettings(s => ({ ...s, graceHours: e.target.value }))} />
                  </div>

                  <div className="refund-grace-overrides">
                    <div className="refund-grace-subtitle">Per-reseller overrides</div>
                    {Object.keys(refundSettings.graceOverrides || {}).length === 0 ? (
                      <p className="refund-grace-empty">None — every reseller uses the default above.</p>
                    ) : (
                      <div className="refund-grace-list">
                        {Object.entries(refundSettings.graceOverrides).map(([oid, hours]) => {
                          const ovr = cpResellers.find(x => String(x.id) === String(oid));
                          const isZero = String(hours).trim() !== '' && Number(hours) === 0;
                          return (
                            <div key={oid} className="refund-grace-row">
                              <span className="refund-grace-name">
                                {ovr ? ovr.username : 'Reseller #' + oid} <span className="refund-grace-id">(#{oid})</span>
                              </span>
                              <input type="number" min="0" step="1" className="refund-grace-hours"
                                value={hours}
                                onChange={e => setRefundSettings(s => ({ ...s, graceOverrides: { ...s.graceOverrides, [oid]: e.target.value } }))} />
                              <span className="refund-grace-unit">h</span>
                              {isZero && (
                                <span className="refund-grace-badge">NO GRACE · PRO-RATA BY DAY</span>
                              )}
                              <button type="button" className="btn btn-secondary refund-grace-remove"
                                onClick={() => setRefundSettings(s => {
                                  const next = { ...s.graceOverrides };
                                  delete next[oid];
                                  return { ...s, graceOverrides: next };
                                })}>
                                Remove
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    <div className="refund-grace-add">
                      <select className="form-input refund-grace-select"
                        value={refundOverrideDraft.resellerId}
                        onChange={e => setRefundOverrideDraft(d => ({ ...d, resellerId: e.target.value }))}>
                        <option value="">Select a reseller…</option>
                        {cpResellers
                          .filter(r => !(String(r.id) in (refundSettings.graceOverrides || {})))
                          .map(r => <option key={r.id} value={r.id}>{r.username} (#{r.id})</option>)}
                      </select>
                      <input type="number" min="0" step="1" placeholder="hours" className="refund-grace-hours"
                        value={refundOverrideDraft.hours}
                        onChange={e => setRefundOverrideDraft(d => ({ ...d, hours: e.target.value }))} />
                      <button type="button" className="btn btn-primary refund-grace-addbtn"
                        disabled={!refundOverrideDraft.resellerId}
                        onClick={() => {
                          const oid = String(refundOverrideDraft.resellerId);
                          if (!oid) return;
                          const hrs = refundOverrideDraft.hours === '' ? '0' : refundOverrideDraft.hours;
                          setRefundSettings(s => ({ ...s, graceOverrides: { ...s.graceOverrides, [oid]: hrs } }));
                          setRefundOverrideDraft({ resellerId: '', hours: '0' });
                        }}>
                        Add
                      </button>
                    </div>
                    <small className="refund-grace-note">
                      An override wins over the default, including an explicit 0. Changes take effect immediately on save.
                    </small>
                  </div>
                </div>

                {/* 2. After the grace period */}
                <h4 className="refund-grace-title">2. After the grace period</h4>
                <p className="refund-grace-help">
                  When on, deleting a user past the grace period returns the <strong>unused</strong> portion of what they paid, pro-rata,
                  to the reseller's <strong>Pending Refund Credits</strong> balance for you to settle. Formula:
                  <code style={{ margin: '0 4px' }}>price × (totalHours − hoursUsed) / totalHours</code>, rounded to 1 decimal (a 1-month plan is 720 hours).
                  When off, deletions accrue refund-days as before.
                </p>

                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: refundSettings.enabled ? '#ecfdf5' : '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '16px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={refundSettings.enabled} onChange={e => setRefundSettings(s => ({ ...s, enabled: e.target.checked }))} />
                  <span style={{ fontSize: '14px', fontWeight: 600, color: refundSettings.enabled ? '#065f46' : '#374151' }}>
                    {refundSettings.enabled ? 'Automatic pro-rata refunds are ON' : 'Automatic pro-rata refunds are OFF (deletions behave as before)'}
                  </span>
                </label>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="form-label">Cutoff date (old vs new users)</label>
                  <input type="date" className="form-input" style={{ maxWidth: '220px' }}
                    value={refundSettings.cutoffDate}
                    onChange={e => setRefundSettings(s => ({ ...s, cutoffDate: e.target.value }))} />
                  <small style={{ color: '#666', display: 'block', marginTop: '4px' }}>
                    Users activated <strong>on/after</strong> this date use each user's recorded price. Users <strong>before</strong> it use the old-price table below.
                    Leave blank to price everyone by their recorded price.
                  </small>
                </div>

                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '8px' }}>
                  <h4 style={{ marginBottom: '4px', fontSize: '14px', color: '#374151' }}>Old prices (credits per duration)</h4>
                  <p style={{ fontSize: '11px', color: '#6b7280', marginBottom: '12px' }}>Used only for pre-cutoff users whose paid amount wasn't recorded.</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
                    {REFUND_DURATION_KEYS.map(k => (
                      <div key={k} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 6px', background: 'white', borderRadius: '4px', border: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '11px', minWidth: '42px', color: '#475569' }}>{refundDurationLabel(k)}:</span>
                        <input type="number" min="0" step="any"
                          style={{ width: '56px', padding: '2px 4px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '11px' }}
                          value={refundSettings.oldPrices?.[k] ?? ''}
                          placeholder="—"
                          onChange={e => setRefundSettings(s => ({ ...s, oldPrices: { ...s.oldPrices, [k]: e.target.value } }))} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* 3. Self-service redemption of pending refund credits */}
                <h4 className="refund-grace-title" style={{ marginTop: '20px' }}>3. Self-service redemption</h4>
                <p className="refund-grace-help">
                  A reseller can redeem their own <strong>Pending Refund Credits</strong> into their balance without you,
                  as long as the amount is <strong>at or below</strong> this limit. Above it, pressing “exchange” raises a
                  request for you to approve below. Set <strong>0</strong> to require your approval for every redemption.
                </p>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label className="form-label">Instant self-redeem limit (credits)</label>
                  <input type="number" min="0" step="any" className="form-input" style={{ maxWidth: '160px' }}
                    value={refundSettings.selfRedeemThreshold}
                    onChange={e => setRefundSettings(s => ({ ...s, selfRedeemThreshold: e.target.value }))} />
                </div>

                <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', marginBottom: '4px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', color: '#374151' }}>
                      Requests awaiting approval
                      {redemptionRequests.length > 0 && (
                        <span style={{ marginLeft: '8px', background: '#ef4444', color: 'white', borderRadius: '999px', padding: '1px 8px', fontSize: '12px' }}>{redemptionRequests.length}</span>
                      )}
                    </h4>
                    <button type="button" className="btn btn-secondary" style={{ padding: '3px 9px', fontSize: '12px' }} onClick={fetchRedemptionRequests}>Refresh</button>
                  </div>
                  {redemptionRequests.length === 0 ? (
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>No pending requests.</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {redemptionRequests.map(r => (
                        <div key={r.resellerId} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '8px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                          <span style={{ flex: 1, minWidth: '170px', fontSize: '13px' }}>
                            <strong>{r.username}</strong> <span style={{ color: '#94a3b8' }}>(#{r.resellerId})</span>
                            <span style={{ display: 'block', color: '#475569', fontSize: '12px' }}>
                              Pending now: <strong>{r.pendingRefundCredits}</strong> credit(s)
                              {r.createdAt ? ` · asked ${new Date(r.createdAt).toLocaleString()}` : ''}
                            </span>
                          </span>
                          <button type="button" className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => resolveRedemptionRequest(r.resellerId, 'approve')}>Approve</button>
                          <button type="button" className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => resolveRedemptionRequest(r.resellerId, 'reject')}>Reject</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '24px' }}>
                  <button onClick={saveRefundSettings} disabled={loading} className="btn btn-primary">
                    {loading ? 'Saving...' : 'Save Settings'}
                  </button>
                  <button onClick={() => setShowRefundSettings(false)} className="btn btn-secondary">Cancel</button>
                </div>
              </div>
            </div>
          )}

          {/* Redemption Requests — opened from the sidebar badge */}
          {showRedemptionModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
              onClick={() => setShowRedemptionModal(false)}>
              <div onClick={e => e.stopPropagation()}
                style={{ maxWidth: '640px', width: '95%', background: 'white', borderRadius: '12px', padding: '24px', maxHeight: '90vh', overflow: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <h3 style={{ margin: 0 }}>Redemption Requests{redemptionRequests.length > 0 ? ` (${redemptionRequests.length})` : ''}</h3>
                  <button className="btn btn-secondary" style={{ padding: '4px 10px' }} onClick={() => setShowRedemptionModal(false)}>×</button>
                </div>
                <p style={{ fontSize: '13px', color: '#666', margin: '4px 0 12px' }}>
                  Resellers can redeem their pending refund credits themselves up to the limit below. Above it, it comes here for
                  your approval — approving adds their <strong>current</strong> pending credits to their balance; rejecting leaves the pending untouched.
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '10px 12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', marginBottom: '12px' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#1e3a8a' }}>Instant self-redeem limit</span>
                  <input type="number" min="0" step="any" className="form-input" style={{ maxWidth: '110px' }}
                    value={redeemThresholdInput}
                    onChange={e => setRedeemThresholdInput(e.target.value)} />
                  <span style={{ fontSize: '13px', color: '#475569' }}>credits</span>
                  <button type="button" className="btn btn-primary" style={{ padding: '5px 12px', fontSize: '13px' }} onClick={saveSelfRedeemThreshold}>Save limit</button>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>
                    Currently <strong>{redeemThreshold}</strong>. Set <strong>0</strong> to require approval for every redemption.
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                  <button type="button" className="btn btn-secondary" style={{ padding: '3px 9px', fontSize: '12px' }} onClick={fetchRedemptionRequests}>Refresh</button>
                </div>
                {redemptionRequests.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#6b7280' }}>No pending requests.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {redemptionRequests.map(r => (
                      <div key={r.resellerId} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                        <span style={{ flex: 1, minWidth: '180px', fontSize: '14px' }}>
                          <strong>{r.username}</strong> <span style={{ color: '#94a3b8' }}>(#{r.resellerId})</span>
                          <span style={{ display: 'block', color: '#475569', fontSize: '13px' }}>
                            Pending now: <strong>{r.pendingRefundCredits}</strong> credit(s)
                            {r.createdAt ? ` · asked ${new Date(r.createdAt).toLocaleString()}` : ''}
                          </span>
                        </span>
                        <button type="button" className="btn btn-primary" style={{ padding: '5px 12px', fontSize: '13px' }} onClick={() => resolveRedemptionRequest(r.resellerId, 'approve')}>Approve</button>
                        <button type="button" className="btn btn-secondary" style={{ padding: '5px 12px', fontSize: '13px' }} onClick={() => resolveRedemptionRequest(r.resellerId, 'reject')}>Reject</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Console Migration Product Mapping Modal */}
        {showConsoleMigrationMapping && consoleMigrationData && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflow: 'auto' }}>
              <h3 style={{ marginBottom: '8px' }}>Product Mapping Required</h3>
              <p style={{ color: '#666', marginBottom: '16px', fontSize: '14px' }}>
                The new console ({consoleMigrationData.orgName}) has different product names.
                Please map each old product to its equivalent in the new console.
                <br /><strong>{consoleMigrationData.userCount} users</strong> will be migrated with these products.
              </p>

              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
                {consoleMigrationData.oldProducts.map((oldProduct, idx) => (
                  <div key={idx} style={{ marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1', minWidth: '200px', padding: '8px', background: consoleMigrationMapping[oldProduct] === '__NONE__' ? '#fef3c7' : '#fee2e2', borderRadius: '4px', fontSize: '13px' }}>
                      <strong>Old:</strong> {oldProduct}
                      {consoleMigrationMapping[oldProduct] === '__NONE__' && <span style={{ marginLeft: '8px', color: '#b45309', fontWeight: 600 }}>(Will skip)</span>}
                    </div>
                    <span>→</span>
                    <select
                      style={{ flex: '1', minWidth: '200px', padding: '8px', borderRadius: '4px', border: '1px solid #e2e8f0', background: consoleMigrationMapping[oldProduct] === '__NONE__' ? '#fef3c7' : '#fff' }}
                      value={consoleMigrationMapping[oldProduct] || ''}
                      onChange={e => setConsoleMigrationMapping({ ...consoleMigrationMapping, [oldProduct]: e.target.value })}
                    >
                      <option value="">-- Select New Product --</option>
                      <option value="__NONE__" style={{ background: '#fef3c7', color: '#b45309' }}>Do not migrate (skip users)</option>
                      {consoleMigrationData.newProducts.map((newP, i) => (
                        <option key={i} value={newP}>{newP}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              {/* Warning when some products not mapped */}
              {Object.keys(consoleMigrationMapping).some(k => !consoleMigrationMapping[k]) && (
                <div style={{ color: '#dc2626', fontSize: '13px', marginBottom: '16px' }}>
                  Please map all products before proceeding
                </div>
              )}

              {/* Warning when some products set to skip */}
              {Object.values(consoleMigrationMapping).some(v => v === '__NONE__') && (
                <div style={{ color: '#b45309', fontSize: '13px', marginBottom: '16px', background: '#fef3c7', padding: '8px', borderRadius: '6px' }}>
                  Users with products marked "Do not migrate" will be skipped and not added to the new console.
                </div>
              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => {
                    setShowConsoleMigrationMapping(false);
                    setConsoleMigrationData(null);
                    setConsoleMigrationMapping({});
                  }}
                  className="btn btn-secondary"
                >Cancel</button>
                <button
                  onClick={() => {
                    setShowConsoleMigrationMapping(false);
                    handleUpdateOrg(null, consoleMigrationMapping);
                  }}
                  disabled={Object.keys(consoleMigrationMapping).length !== consoleMigrationData.oldProducts.length || Object.values(consoleMigrationMapping).some(v => !v)}
                  className="btn btn-primary"
                >Confirm & Migrate</button>
              </div>
            </div>
          </div>
        )}

      {/* Bulk Organization Access Tab */}
      {activeTab === 'bulkOrg' && (
        <>
          {/* ── Local-Only Reassignment (no Adobe API) ── */}
          <div className="card" style={{ borderLeft: '4px solid #0b7da6' }}>
            <div className="card-header">
              <h3 className="card-title">Local Org Reassignment <span style={{ fontSize: '12px', fontWeight: 400, color: '#0b7da6', marginLeft: '8px' }}>No Adobe API — instant, works with expired credentials</span></h3>
            </div>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '16px' }}>
              Moves all active users from one organization to another <strong>in the local database only</strong>.
              Nothing is sent to Adobe. Use this to merge orgs, rename groupings, or reorganize when API credentials are invalid.
            </p>
            <LocalOrgReassign organizations={organizations} onDone={(msg) => { setResult(msg); fetchOrganizations(); }} onError={setError} />
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Organization User Transfer</h3>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div className="form-group">
                <label className="form-label">Source Organization</label>
                <OrgSearchSelect
                  organizations={organizations}
                  value={orgTransferSourceOrg}
                  onChange={v => {
                    setOrgTransferSourceOrg(v);
                    setOrgTransferPreview(null);
                    setOrgTransferStatus(null);
                  }}
                  placeholder="Select source..."
                />
              </div>

              <div className="form-group">
                <label className="form-label">Target Organization</label>
                <OrgSearchSelect
                  organizations={organizations}
                  value={orgTransferTargetOrg}
                  onChange={v => {
                    setOrgTransferTargetOrg(v);
                    setOrgTransferPreview(null);
                    setOrgTransferStatus(null);
                    const org = organizations.find(o => o.id === parseInt(v));
                    setOrgTransferTargetProducts(org?.products || []);
                  }}
                  placeholder="Select target..."
                  excludeId={orgTransferSourceOrg ? parseInt(orgTransferSourceOrg) : null}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <div className="form-group">
                <label className="form-label">Users to Transfer</label>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                  <button
                    className={`btn ${orgTransferSelectionMode === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setOrgTransferSelectionMode('all'); setOrgTransferPreview(null); }}
                    style={{ flex: 1 }}
                    type="button"
                  >All Users</button>
                  <button
                    className={`btn ${orgTransferSelectionMode === 'range' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setOrgTransferSelectionMode('range'); setOrgTransferPreview(null); }}
                    style={{ flex: 1 }}
                    type="button"
                  >Number Range</button>
                </div>

                {orgTransferSelectionMode === 'range' && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label className="form-label" style={{ fontSize: '12px' }}>Start User #</label>
                      <input
                        className="form-input"
                        type="number"
                        min="1"
                        value={orgTransferStartNumber}
                        onChange={e => { setOrgTransferStartNumber(e.target.value); setOrgTransferPreview(null); }}
                      />
                    </div>
                    <div>
                      <label className="form-label" style={{ fontSize: '12px' }}>User Count</label>
                      <input
                        className="form-input"
                        type="number"
                        min="1"
                        value={orgTransferCount}
                        onChange={e => { setOrgTransferCount(e.target.value); setOrgTransferPreview(null); }}
                      />
                    </div>
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px', color: 'var(--app-text)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={orgTransferKeepSource}
                    onChange={e => setOrgTransferKeepSource(e.target.checked)}
                  />
                  Keep users in source organization
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">Target Products</label>
                <div style={{ maxHeight: '150px', overflowY: 'auto', border: '1px solid #334155', borderRadius: '6px', padding: '8px' }}>
                  {(() => {
                    const targetOrg = organizations.find(o => o.id === parseInt(orgTransferTargetOrg));
                    const products = targetOrg?.products || [];
                    if (products.length === 0) {
                      return <div style={{ color: 'var(--app-muted)', fontSize: '13px' }}>No products configured.</div>;
                    }
                    return products.map((product, i) => (
                      <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--app-text)', padding: '4px 0', fontSize: '13px' }}>
                        <input
                          type="checkbox"
                          checked={orgTransferTargetProducts.includes(product)}
                          onChange={e => {
                            setOrgTransferTargetProducts(prev =>
                              e.target.checked ? [...prev, product] : prev.filter(p => p !== product)
                            );
                            setOrgTransferPreview(null);
                          }}
                        />
                        {getProductDisplayName(product)}
                      </label>
                    ));
                  })()}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: orgTransferPreview || orgTransferStatus ? '20px' : 0 }}>
              <button
                className="btn btn-primary"
                disabled={!orgTransferSourceOrg || !orgTransferTargetOrg || orgTransferSourceOrg === orgTransferTargetOrg || orgTransferLoadingPreview}
                onClick={async () => {
                  setOrgTransferLoadingPreview(true);
                  setError(null);
                  setResult(null);
                  setOrgTransferPreview(null);
                  try {
                    const token = localStorage.getItem('adminToken');
                    const params = new URLSearchParams({
                      sourceOrganizationId: orgTransferSourceOrg,
                      targetOrganizationId: orgTransferTargetOrg,
                      selectionMode: orgTransferSelectionMode,
                      startNumber: String(orgTransferStartNumber || 1),
                      count: String(orgTransferCount || 0),
                      targetProducts: JSON.stringify(orgTransferTargetProducts)
                    });
                    const res = await fetch(`/api/admin/org-transfer/preview?${params.toString()}`, {
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const data = await res.json();
                    if (data.success) {
                      setOrgTransferPreview(data);
                    } else {
                      setError(data.error || 'Failed to preview organization transfer');
                    }
                  } catch (err) {
                    setError('Transfer preview failed: ' + err.message);
                  }
                  setOrgTransferLoadingPreview(false);
                }}
              >
                {orgTransferLoadingPreview ? 'Loading Preview...' : 'Preview Transfer'}
              </button>

              {orgTransferSourceOrg && orgTransferTargetOrg && orgTransferSourceOrg === orgTransferTargetOrg && (
                <span style={{ color: '#f87171', fontSize: '13px' }}>Choose two different organizations.</span>
              )}
            </div>

            {orgTransferPreview && (
              <div style={{ border: '1px solid var(--app-border)', borderRadius: '8px', padding: '16px', background: 'var(--app-surface-alt)', marginBottom: '20px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <div style={{ color: 'var(--app-muted)', fontSize: '12px' }}>Source Users</div>
                    <div style={{ color: 'var(--app-text)', fontSize: '22px', fontWeight: 700 }}>{orgTransferPreview.totalSourceUsers}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--app-muted)', fontSize: '12px' }}>Selected</div>
                    <div style={{ color: 'var(--app-text)', fontSize: '22px', fontWeight: 700 }}>{orgTransferPreview.selectedCount}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--app-muted)', fontSize: '12px' }}>Target Now</div>
                    <div style={{ color: 'var(--app-text)', fontSize: '22px', fontWeight: 700 }}>{orgTransferPreview.capacity.targetCount}</div>
                  </div>
                  <div>
                    <div style={{ color: 'var(--app-muted)', fontSize: '12px' }}>Remaining Capacity</div>
                    <div style={{ color: orgTransferPreview.capacity.willExceedCapacity ? '#f87171' : '#22c55e', fontSize: '22px', fontWeight: 700 }}>
                      {orgTransferPreview.capacity.remainingCapacity}
                    </div>
                  </div>
                </div>

                {orgTransferPreview.capacity.willExceedCapacity && (
                  <div style={{ padding: '10px 12px', borderRadius: '6px', background: '#7f1d1d', color: '#fecaca', marginBottom: '12px' }}>
                    Target organization must stay below {orgTransferPreview.capacity.limit} users.
                  </div>
                )}

                {orgTransferPreview.missingProductCount > 0 && (
                  <div style={{ padding: '10px 12px', borderRadius: '6px', background: '#78350f', color: '#fed7aa', marginBottom: '12px' }}>
                    {orgTransferPreview.missingProductCount} selected user(s) have no matching target product.
                  </div>
                )}

                <div style={{ maxHeight: '340px', overflowY: 'auto', marginBottom: '16px' }}>
                  <table className="data-table" style={{ fontSize: '13px' }}>
                    <thead>
                      <tr>
                        <th style={{ width: '58px' }}>#</th>
                        <th>Email</th>
                        <th>Type</th>
                        <th>Reseller</th>
                        <th>Current Products</th>
                        <th>Target Products</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgTransferPreview.users.map(user => (
                        <tr key={`${user.sourceType}_${user.rowid}`}>
                          <td style={{ color: 'var(--app-muted)' }}>{user.transferNumber}</td>
                          <td style={{ color: 'var(--app-text)', fontWeight: 500 }}>{user.email}</td>
                          <td>
                            <span style={{ background: user.sourceType === 'traditional' ? '#0b7da6' : '#1b8a5a', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>
                              {user.sourceType === 'traditional' ? 'Traditional' : 'Credits Pool'}
                            </span>
                          </td>
                          <td style={{ color: 'var(--app-muted)' }}>{user.resellerName || 'Unknown'}</td>
                          <td style={{ color: 'var(--app-muted)' }}>{(user.products || []).map(getProductDisplayName).join(', ') || '-'}</td>
                          <td style={{ color: user.hasTargetProducts ? '#94a3b8' : '#f87171' }}>
                            {(user.resolvedProducts || []).map(getProductDisplayName).join(', ') || 'No match'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                  <span style={{ color: 'var(--app-muted)', fontSize: '13px' }}>
                    Showing {orgTransferPreview.users.length} of {orgTransferPreview.selectedCount} selected user(s).
                  </span>
                  <button
                    className="btn btn-primary"
                    disabled={orgTransferExecuting || orgTransferPreview.selectedCount === 0 || orgTransferPreview.capacity.willExceedCapacity || orgTransferPreview.missingProductCount > 0}
                    onClick={async () => {
                      const sourceName = orgTransferPreview.sourceOrganization.name;
                      const targetName = orgTransferPreview.targetOrganization.name;
                      const actionText = orgTransferKeepSource ? 'keep users in the source organization' : 'remove users from the source organization';
                      if (!window.confirm(`Transfer ${orgTransferPreview.selectedCount} user(s) from "${sourceName}" to "${targetName}" and ${actionText}?`)) return;

                      setOrgTransferExecuting(true);
                      setError(null);
                      setResult(null);
                      try {
                        const token = localStorage.getItem('adminToken');
                        const res = await fetch('/api/admin/org-transfer', {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            sourceOrganizationId: parseInt(orgTransferSourceOrg),
                            targetOrganizationId: parseInt(orgTransferTargetOrg),
                            selectionMode: orgTransferSelectionMode,
                            startNumber: parseInt(orgTransferStartNumber) || 1,
                            count: parseInt(orgTransferCount) || 0,
                            keepSourceOrg: orgTransferKeepSource,
                            targetProducts: orgTransferTargetProducts
                          })
                        });
                        const data = await res.json();
                        if (data.success) {
                          setOrgTransferOperation({ id: data.operationId, totalUsers: data.totalUsers });
                          setOrgTransferStatus({ status: 'pending', totalUsers: data.totalUsers, successCount: 0, failCount: 0, remainingCount: data.totalUsers });
                          setResult(data.message || 'Organization transfer started.');
                        } else {
                          setError(data.error || 'Failed to start organization transfer');
                        }
                      } catch (err) {
                        setError('Transfer failed: ' + err.message);
                      }
                      setOrgTransferExecuting(false);
                    }}
                    style={{ padding: '10px 24px' }}
                  >
                    {orgTransferExecuting ? 'Starting...' : `Start Transfer (${orgTransferPreview.selectedCount})`}
                  </button>
                </div>
              </div>
            )}

            {(orgTransferOperation || orgTransferStatus) && (
              <div style={{ border: '1px solid var(--app-border)', borderRadius: '8px', padding: '16px', background: 'var(--app-surface-alt)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                  <div>
                    <div style={{ color: 'var(--app-muted)', fontSize: '12px' }}>Transfer Status</div>
                    <div style={{ color: 'var(--app-text)', fontSize: '18px', fontWeight: 700 }}>
                      Operation #{orgTransferOperation?.id || orgTransferStatus?.id || '-'} - {orgTransferStatus?.status || 'pending'}
                    </div>
                  </div>
                  <button
                    className="btn btn-secondary"
                    disabled={!orgTransferOperation?.id || orgTransferRefreshingStatus}
                    onClick={async () => {
                      setOrgTransferRefreshingStatus(true);
                      setError(null);
                      try {
                        const token = localStorage.getItem('adminToken');
                        const res = await fetch(`/api/admin/org-transfer/status/${orgTransferOperation.id}`, {
                          headers: { 'Authorization': `Bearer ${token}` }
                        });
                        const data = await res.json();
                        if (data.success) {
                          setOrgTransferStatus(data.operation);
                        } else {
                          setError(data.error || 'Failed to refresh transfer status');
                        }
                      } catch (err) {
                        setError('Status refresh failed: ' + err.message);
                      }
                      setOrgTransferRefreshingStatus(false);
                    }}
                  >
                    {orgTransferRefreshingStatus ? 'Refreshing...' : 'Refresh Status'}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '12px' }}>
                  <div style={{ color: 'var(--app-muted)' }}>Total: <strong style={{ color: 'var(--app-text)' }}>{orgTransferStatus?.totalUsers ?? orgTransferOperation?.totalUsers ?? 0}</strong></div>
                  <div style={{ color: 'var(--app-muted)' }}>Success: <strong style={{ color: 'var(--app-success-text)' }}>{orgTransferStatus?.successCount ?? 0}</strong></div>
                  <div style={{ color: 'var(--app-muted)' }}>Failed: <strong style={{ color: 'var(--app-danger-text)' }}>{orgTransferStatus?.failCount ?? 0}</strong></div>
                  <div style={{ color: 'var(--app-muted)' }}>Remaining: <strong style={{ color: 'var(--app-text)' }}>{orgTransferStatus?.remainingCount ?? 0}</strong></div>
                </div>
                {orgTransferStatus?.failures?.length > 0 && (
                  <div style={{ marginTop: '12px', color: 'var(--app-danger-text)', fontSize: '13px' }}>
                    {orgTransferStatus.failures.slice(0, 5).join(' | ')}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Bulk Organization Access Management</h3>
            </div>
            <p style={{ color: 'var(--app-muted)', fontSize: '14px', marginBottom: '20px' }}>
              Add or remove an organization from multiple resellers at once.
            </p>

            {/* Configuration Row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              {/* Organization Selector */}
              <div className="form-group">
                <label className="form-label">Organization</label>
                <OrgSearchSelect
                  organizations={organizations}
                  value={bulkOrgSelectedOrg}
                  onChange={v => {
                    setBulkOrgSelectedOrg(v);
                    setBulkOrgPreview(null);
                    const org = organizations.find(o => o.id === parseInt(v));
                    if (org) setBulkOrgSelectedProducts(org.products || []);
                  }}
                  placeholder="Select an organization..."
                />
              </div>

              {/* Action Toggle */}
              <div className="form-group">
                <label className="form-label">Action</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className={`btn ${bulkOrgAction === 'add' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setBulkOrgAction('add'); setBulkOrgPreview(null); }}
                    style={bulkOrgAction === 'add' ? { background: '#10b981', borderColor: '#10b981', flex: 1 } : { flex: 1 }}
                  >Add to Resellers</button>
                  <button
                    className={`btn ${bulkOrgAction === 'remove' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => { setBulkOrgAction('remove'); setBulkOrgPreview(null); }}
                    style={bulkOrgAction === 'remove' ? { background: '#ef4444', borderColor: '#ef4444', flex: 1 } : { flex: 1 }}
                  >Remove from Resellers</button>
                </div>
              </div>
            </div>

            {/* Type Checkboxes + Product Selection */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              {/* Target Types */}
              <div className="form-group">
                <label className="form-label">Target Reseller Types</label>
                <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--app-text)' }}>
                    <input
                      type="checkbox"
                      checked={bulkOrgTargetTypes.traditional}
                      onChange={e => { setBulkOrgTargetTypes(prev => ({ ...prev, traditional: e.target.checked })); setBulkOrgPreview(null); }}
                    />
                    Traditional
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--app-text)' }}>
                    <input
                      type="checkbox"
                      checked={bulkOrgTargetTypes.cp}
                      onChange={e => { setBulkOrgTargetTypes(prev => ({ ...prev, cp: e.target.checked })); setBulkOrgPreview(null); }}
                    />
                    Credits Pool
                  </label>
                </div>
              </div>

              {/* Product Selection (only for Add) */}
              {bulkOrgAction === 'add' && bulkOrgSelectedOrg && (
                <div className="form-group">
                  <label className="form-label">Products to Grant</label>
                  <div style={{ maxHeight: '120px', overflowY: 'auto', border: '1px solid #334155', borderRadius: '6px', padding: '8px' }}>
                    {(() => {
                      const org = organizations.find(o => o.id === parseInt(bulkOrgSelectedOrg));
                      return (org?.products || []).map((product, i) => (
                        <label key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: 'var(--app-text)', padding: '4px 0', fontSize: '13px' }}>
                          <input
                            type="checkbox"
                            checked={bulkOrgSelectedProducts.includes(product)}
                            onChange={e => {
                              setBulkOrgSelectedProducts(prev =>
                                e.target.checked ? [...prev, product] : prev.filter(p => p !== product)
                              );
                            }}
                          />
                          {getProductDisplayName(product)}
                        </label>
                      ));
                    })()}
                  </div>
                </div>
              )}
            </div>

            {/* Preview Button */}
            <button
              className="btn btn-primary"
              disabled={!bulkOrgSelectedOrg || (!bulkOrgTargetTypes.traditional && !bulkOrgTargetTypes.cp) || bulkOrgLoadingPreview}
              onClick={async () => {
                setBulkOrgLoadingPreview(true);
                setError(null);
                try {
                  const types = [];
                  if (bulkOrgTargetTypes.traditional) types.push('traditional');
                  if (bulkOrgTargetTypes.cp) types.push('cp');
                  const token = localStorage.getItem('adminToken');
                  const res = await fetch(`/api/admin/bulk-org-access/preview?organizationId=${bulkOrgSelectedOrg}&action=${bulkOrgAction}&targetTypes=${types.join(',')}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  const data = await res.json();
                  if (data.success) {
                    setBulkOrgPreview(data);
                    // Select all by default
                    const allIds = new Set([...data.traditional.map(r => `t_${r.id}`), ...data.cp.map(r => `c_${r.id}`)]);
                    setBulkOrgSelectedResellers(allIds);
                  } else {
                    setError(data.error || 'Failed to preview');
                  }
                } catch (err) {
                  setError('Preview failed: ' + err.message);
                }
                setBulkOrgLoadingPreview(false);
              }}
              style={{ marginBottom: '20px' }}
            >
              {bulkOrgLoadingPreview ? 'Loading Preview...' : 'Preview Affected Resellers'}
            </button>

            {/* Preview Results */}
            {bulkOrgPreview && (
              <div style={{ border: '1px solid var(--app-border)', borderRadius: '8px', padding: '16px', background: 'var(--app-surface-alt)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ color: 'var(--app-text)', margin: 0 }}>
                    Preview: {bulkOrgAction === 'add' ? 'Add' : 'Remove'} "{bulkOrgPreview.organization.name}"
                  </h4>
                  <span style={{ color: 'var(--app-muted)', fontSize: '13px' }}>
                    {bulkOrgPreview.traditional.length + bulkOrgPreview.cp.length} reseller(s) will be affected
                  </span>
                </div>

                {(bulkOrgPreview.traditional.length + bulkOrgPreview.cp.length) === 0 ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--app-muted)' }}>
                    No resellers {bulkOrgAction === 'add' ? 'without' : 'with'} this organization found.
                  </div>
                ) : (
                  <>
                    {/* Select All */}
                    <div style={{ marginBottom: '12px', display: 'flex', gap: '12px' }}>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '4px 12px' }}
                        onClick={() => {
                          const allIds = new Set([...bulkOrgPreview.traditional.map(r => `t_${r.id}`), ...bulkOrgPreview.cp.map(r => `c_${r.id}`)]);
                          setBulkOrgSelectedResellers(allIds);
                        }}
                      >Select All</button>
                      <button
                        className="btn btn-secondary"
                        style={{ fontSize: '12px', padding: '4px 12px' }}
                        onClick={() => setBulkOrgSelectedResellers(new Set())}
                      >Deselect All</button>
                      <span style={{ color: 'var(--app-muted)', fontSize: '13px', alignSelf: 'center' }}>
                        {bulkOrgSelectedResellers.size} selected
                      </span>
                    </div>

                    {/* Table */}
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="data-table" style={{ fontSize: '13px' }}>
                        <thead>
                          <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>Type</th>
                            <th>Username</th>
                            <th>Group</th>
                            <th>Current Organizations</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bulkOrgPreview.traditional.map(r => (
                            <tr key={`t_${r.id}`}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={bulkOrgSelectedResellers.has(`t_${r.id}`)}
                                  onChange={e => {
                                    setBulkOrgSelectedResellers(prev => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(`t_${r.id}`); else next.delete(`t_${r.id}`);
                                      return next;
                                    });
                                  }}
                                />
                              </td>
                              <td><span style={{ background: '#3b82f6', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>Traditional</span></td>
                              <td style={{ color: 'var(--app-text)', fontWeight: 500 }}>{r.username}</td>
                              <td style={{ color: 'var(--app-muted)' }}>{r.userGroup}</td>
                              <td style={{ color: 'var(--app-muted)' }}>{r.currentOrgs.map(o => o.organizationName).join(', ') || 'None'}</td>
                            </tr>
                          ))}
                          {bulkOrgPreview.cp.map(r => (
                            <tr key={`c_${r.id}`}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={bulkOrgSelectedResellers.has(`c_${r.id}`)}
                                  onChange={e => {
                                    setBulkOrgSelectedResellers(prev => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(`c_${r.id}`); else next.delete(`c_${r.id}`);
                                      return next;
                                    });
                                  }}
                                />
                              </td>
                              <td><span style={{ background: '#0b7da6', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>Credits Pool</span></td>
                              <td style={{ color: 'var(--app-text)', fontWeight: 500 }}>{r.username}</td>
                              <td style={{ color: 'var(--app-muted)' }}>{r.userGroup}</td>
                              <td style={{ color: 'var(--app-muted)' }}>{r.currentOrgs.map(o => o.organizationName).join(', ') || 'None'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Execute Button */}
                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        className="btn btn-primary"
                        disabled={bulkOrgSelectedResellers.size === 0 || bulkOrgExecuting}
                        onClick={async () => {
                          const selectedCount = bulkOrgSelectedResellers.size;
                          const orgName = bulkOrgPreview.organization.name;
                          if (!window.confirm(
                            `Are you sure you want to ${bulkOrgAction === 'add' ? 'ADD' : 'REMOVE'} "${orgName}" ${bulkOrgAction === 'add' ? 'to' : 'from'} ${selectedCount} reseller(s)?`
                          )) return;

                          setBulkOrgExecuting(true);
                          setError(null);
                          setResult(null);
                          try {
                            const types = [];
                            const resellerIds = [];
                            bulkOrgSelectedResellers.forEach(key => {
                              const [type, id] = key.split('_');
                              if (type === 't' && !types.includes('traditional')) types.push('traditional');
                              if (type === 'c' && !types.includes('cp')) types.push('cp');
                              resellerIds.push(parseInt(id));
                            });

                            const token = localStorage.getItem('adminToken');
                            const res = await fetch('/api/admin/bulk-org-access', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                              },
                              body: JSON.stringify({
                                organizationId: parseInt(bulkOrgSelectedOrg),
                                action: bulkOrgAction,
                                targetTypes: types,
                                selectedProducts: bulkOrgSelectedProducts,
                                resellerIds
                              })
                            });
                            const data = await res.json();
                            if (data.success) {
                              setResult(`${data.message} (Traditional: ${data.affectedTraditional}, CP: ${data.affectedCP}, Skipped: ${data.skipped})`);
                              setBulkOrgPreview(null);
                              fetchResellers();
                              fetchCPResellers();
                            } else {
                              setError(data.error || 'Failed to execute bulk operation');
                            }
                          } catch (err) {
                            setError('Bulk operation failed: ' + err.message);
                          }
                          setBulkOrgExecuting(false);
                        }}
                        style={{
                          background: bulkOrgAction === 'add' ? '#10b981' : '#ef4444',
                          borderColor: bulkOrgAction === 'add' ? '#10b981' : '#ef4444',
                          padding: '10px 24px',
                          fontSize: '14px'
                        }}
                      >
                        {bulkOrgExecuting
                          ? 'Executing...'
                          : `${bulkOrgAction === 'add' ? 'Add' : 'Remove'} Organization ${bulkOrgAction === 'add' ? 'to' : 'from'} ${bulkOrgSelectedResellers.size} Reseller(s)`
                        }
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Migration Settings Tab */}
      {activeTab === 'settings' && (
        <>
        <div className="card" style={{ borderLeft: `4px solid ${manualOrgInfo.enabled ? '#2563eb' : '#cbd5e1'}` }}>
          <div className="card-header">
            <h3 className="card-title">Manual Organizations (no Adobe API)</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: '1.6', flex: 1, minWidth: '240px' }}>
              <b>Off (default):</b> nothing changes anywhere. <b>On:</b> organizations you mark as manual (e.g. "org-01") work without the Adobe API —
              resellers add customers as usual, the customers are saved as <i>pending</i>, and you get them in <b>Manual Activations</b> (plus a Telegram ping to the shop bot's admin chats)
              to add in the Adobe console by hand. Resellers see them turn Active when you mark them done. Saves immediately.
            </p>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: manualOrgInfo.loaded ? 'pointer' : 'wait', fontWeight: 700, color: manualOrgInfo.enabled ? '#1d4ed8' : '#334155' }}>
              <input
                type="checkbox"
                checked={manualOrgInfo.enabled}
                disabled={!manualOrgInfo.loaded || savingManualOrg}
                onChange={(e) => saveManualOrgEnabled(e.target.checked)}
                style={{ width: '20px', height: '20px' }}
              />
              {!manualOrgInfo.loaded ? 'Loading…' : manualOrgInfo.enabled ? 'On' : 'Off'}
            </label>
          </div>
        </div>
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Activation Duration Control</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={fetchActivationSettings} disabled={loadingActivationSettings || savingActivationSettings} className="btn btn-secondary" type="button">
                {loadingActivationSettings ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div style={{ padding: '16px', background: '#fff7ed', borderRadius: '12px', marginBottom: '20px', border: '1px solid #fed7aa' }}>
            <strong style={{ display: 'block', marginBottom: '6px', color: '#c2410c' }}>Pause activation lengths for all sellers</strong>
            <p style={{ margin: 0, color: '#7c2d12', fontSize: '14px', lineHeight: '1.6' }}>
              Turn on a month to remove that activation duration from seller dashboards and block it on the backend. This applies to Traditional and Credits Pool sellers, including bulk actions and API activations.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '10px', marginBottom: '18px' }}>
            {(activationSettings.monthOptions || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]).map((month) => {
              const isPaused = (activationSettings.pausedMonths || []).includes(month);
              return (
                <button
                  key={month}
                  type="button"
                  onClick={() => handleTogglePausedActivationMonth(month)}
                  className={isPaused ? 'btn btn-danger' : 'btn btn-secondary'}
                  disabled={savingActivationSettings || loadingActivationSettings}
                  style={{ justifyContent: 'center', padding: '12px 10px' }}
                >
                  {month} mo {isPaused ? 'Paused' : 'Active'}
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', color: '#64748b' }}>
              {(activationSettings.pausedMonths || []).length > 0
                ? `Paused now: ${(activationSettings.pausedMonths || []).join(', ')} month option(s)`
                : 'All activation durations are currently enabled.'}
            </div>
            <button
              type="button"
              onClick={handleSaveActivationSettings}
              className="btn btn-primary"
              disabled={savingActivationSettings || loadingActivationSettings}
            >
              {savingActivationSettings ? 'Saving...' : 'Save Activation Settings'}
            </button>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Migration Runtime Settings</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={fetchMigrationSettings} disabled={loadingMigrationSettings || savingMigrationSettings} className="btn btn-secondary" type="button">
                {loadingMigrationSettings ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '12px', marginBottom: '20px', border: '1px solid #bfdbfe' }}>
            <strong style={{ display: 'block', marginBottom: '6px', color: '#1d4ed8' }}>Control migration load from the admin panel</strong>
            <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: '1.6' }}>
              These values are used for newly started migrations and resumed migrations. Presets give you a quick starting point, and manual values let you fine-tune how aggressive or conservative the worker should be.
            </p>
          </div>

          {loadingMigrationSettings && migrationPresets.length === 0 ? (
            <p>Loading migration settings...</p>
          ) : (
            <form onSubmit={handleSaveMigrationSettings}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Active Profile</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', textTransform: 'capitalize' }}>
                    {migrationSettingsPresetKey}
                  </div>
                </div>
                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Users Per Round</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a' }}>{migrationUsersPerRound || 0}</div>
                </div>
                <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Estimated Throughput</div>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a' }}>
                    {migrationEstimatedUsersPerMinute || 0}/min
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label className="form-label" style={{ margin: 0 }}>Presets</label>
                  <span style={{ fontSize: '12px', color: '#64748b' }}>Load a preset, review the values, then save.</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                  {migrationPresets.map((preset) => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => handleLoadMigrationPreset(preset)}
                      className={migrationSettingsPresetKey === preset.key ? 'btn btn-primary' : 'btn btn-secondary'}
                      style={{
                        textAlign: 'left',
                        padding: '14px 16px',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px'
                      }}
                    >
                      <span style={{ fontWeight: '700', fontSize: '15px' }}>{preset.name}</span>
                      <span style={{ fontSize: '12px', lineHeight: '1.5', opacity: 0.9 }}>{preset.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Batch Size</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    className="form-input"
                    value={migrationSettings.batchSize}
                    onChange={(e) => setMigrationSettings({ ...migrationSettings, batchSize: e.target.value })}
                  />
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>Users sent in each Adobe API request. Keep at 10 unless you need to be extra gentle.</div>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Parallel Batches</label>
                  <input
                    type="number"
                    min="1"
                    max="5"
                    className="form-input"
                    value={migrationSettings.parallelBatches}
                    onChange={(e) => setMigrationSettings({ ...migrationSettings, parallelBatches: e.target.value })}
                  />
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>How many request batches run at the same time in each round.</div>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Base Delay (ms)</label>
                  <input
                    type="number"
                    min="1000"
                    max="30000"
                    step="100"
                    className="form-input"
                    value={migrationSettings.baseDelayMs}
                    onChange={(e) => setMigrationSettings({ ...migrationSettings, baseDelayMs: e.target.value })}
                  />
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>Wait time between migration rounds. Higher values reduce API pressure.</div>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Progress Flush Interval (ms)</label>
                  <input
                    type="number"
                    min="1000"
                    max="60000"
                    step="500"
                    className="form-input"
                    value={migrationSettings.progressFlushMs}
                    onChange={(e) => setMigrationSettings({ ...migrationSettings, progressFlushMs: e.target.value })}
                  />
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>How often migration progress is written back to status storage.</div>
                </div>

                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Max Retries</label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    className="form-input"
                    value={migrationSettings.maxRetries}
                    onChange={(e) => setMigrationSettings({ ...migrationSettings, maxRetries: e.target.value })}
                  />
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>Retry attempts for transient Adobe/API failures before a user is marked failed.</div>
                </div>
              </div>

              <div style={{ padding: '16px', background: '#fffbeb', borderRadius: '12px', border: '1px solid #fde68a', marginBottom: '24px' }}>
                <strong style={{ display: 'block', marginBottom: '6px', color: '#92400e' }}>Reset target</strong>
                <div style={{ fontSize: '13px', color: '#78350f', lineHeight: '1.6' }}>
                  Batch size {migrationSettingsDefaults.batchSize}, parallel {migrationSettingsDefaults.parallelBatches}, delay {migrationSettingsDefaults.baseDelayMs}ms, flush {migrationSettingsDefaults.progressFlushMs}ms, retries {migrationSettingsDefaults.maxRetries}.
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleResetMigrationSettings}
                  className="btn btn-secondary"
                  disabled={savingMigrationSettings}
                >
                  Reset to Defaults
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={savingMigrationSettings || loadingMigrationSettings}
                >
                  {savingMigrationSettings ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Bulk Operation Limit</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={fetchBulkSettings} disabled={loadingBulkSettings || savingBulkSettings} className="btn btn-secondary" type="button">
                {loadingBulkSettings ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '12px', marginBottom: '20px', border: '1px solid #bbf7d0' }}>
            <strong style={{ display: 'block', marginBottom: '6px', color: '#15803d' }}>Limit concurrent bulk operations server-wide</strong>
            <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: '1.6' }}>
              When the limit is reached, new bulk requests are rejected with a 429 error until a running operation finishes. Applies to all bulk assign, delete, and extend operations across all sellers.
            </p>
          </div>

          <form onSubmit={handleSaveBulkSettings}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
              padding: '16px',
              background: bulkSettings.bulkOperationsPaused ? '#fffbeb' : '#eff6ff',
              borderRadius: '12px',
              border: `1px solid ${bulkSettings.bulkOperationsPaused ? '#fde68a' : '#bfdbfe'}`,
              marginBottom: '20px'
            }}>
              <div style={{ minWidth: '220px', flex: 1 }}>
                <strong style={{ display: 'block', marginBottom: '6px', color: bulkSettings.bulkOperationsPaused ? '#92400e' : '#1d4ed8' }}>
                  Pause New Bulk Operations
                </strong>
                <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: '1.6' }}>
                  Use this only as a manual emergency stop. When allowed, sellers can continue new bulk assign, delete, extend, transfer, and modify operations up to the server-wide concurrency limit.
                </p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 700, color: bulkSettings.bulkOperationsPaused ? '#92400e' : '#1d4ed8' }}>
                <input
                  type="checkbox"
                  checked={bulkSettings.bulkOperationsPaused === true}
                  onChange={(e) => setBulkSettings({ ...bulkSettings, bulkOperationsPaused: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                {bulkSettings.bulkOperationsPaused ? 'Paused' : 'Allowed'}
              </label>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
              padding: '16px',
              background: bulkSettings.selfSwitchPaused ? '#fffbeb' : '#eff6ff',
              borderRadius: '12px',
              border: `1px solid ${bulkSettings.selfSwitchPaused ? '#fde68a' : '#bfdbfe'}`,
              marginBottom: '20px'
            }}>
              <div style={{ minWidth: '220px', flex: 1 }}>
                <strong style={{ display: 'block', marginBottom: '6px', color: bulkSettings.selfSwitchPaused ? '#92400e' : '#1d4ed8' }}>
                  Pause Self-Switch (status page)
                </strong>
                <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: '1.6' }}>
                  Emergency stop for the public status page's "move me to a backup org" feature — both the preview check and the actual switch, plus the auto/manual evacuation triggers. Use when backup capacity is too thin to serve current demand and the volume of attempts is straining the server. Basic status-checking keeps working.
                </p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 700, color: bulkSettings.selfSwitchPaused ? '#92400e' : '#1d4ed8' }}>
                <input
                  type="checkbox"
                  checked={bulkSettings.selfSwitchPaused === true}
                  onChange={(e) => setBulkSettings({ ...bulkSettings, selfSwitchPaused: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                {bulkSettings.selfSwitchPaused ? 'Paused' : 'Allowed'}
              </label>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
              padding: '16px',
              background: bulkSettings.cpBulkTransferEnabled ? '#ecfdf5' : '#fef2f2',
              borderRadius: '12px',
              border: `1px solid ${bulkSettings.cpBulkTransferEnabled ? '#bbf7d0' : '#fecaca'}`,
              marginBottom: '20px'
            }}>
              <div style={{ minWidth: '220px', flex: 1 }}>
                <strong style={{ display: 'block', marginBottom: '6px', color: bulkSettings.cpBulkTransferEnabled ? '#15803d' : '#991b1b' }}>
                  Credits Pool Bulk Transfer
                </strong>
                <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: '1.6' }}>
                  When disabled, Credits Pool sellers cannot start the Bulk Transfer Users operation and direct API calls are rejected.
                </p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 700, color: bulkSettings.cpBulkTransferEnabled ? '#15803d' : '#991b1b' }}>
                <input
                  type="checkbox"
                  checked={bulkSettings.cpBulkTransferEnabled === true}
                  onChange={(e) => setBulkSettings({ ...bulkSettings, cpBulkTransferEnabled: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                {bulkSettings.cpBulkTransferEnabled ? 'Enabled' : 'Disabled'}
              </label>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
              padding: '16px',
              background: bulkSettings.creditToGcEnabled !== false ? '#ecfdf5' : '#fef2f2',
              borderRadius: '12px',
              border: `1px solid ${bulkSettings.creditToGcEnabled !== false ? '#bbf7d0' : '#fecaca'}`,
              marginBottom: '20px'
            }}>
              <div style={{ minWidth: '220px', flex: 1 }}>
                <strong style={{ display: 'block', marginBottom: '6px', color: bulkSettings.creditToGcEnabled !== false ? '#15803d' : '#991b1b' }}>
                  Credits → GC Conversion (all resellers)
                </strong>
                <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: '1.6' }}>
                  When disabled, the "Convert Credits to GC" button disappears from every reseller's dashboard and direct API calls are rejected. Balances already converted are not touched. Click Save Settings to apply.
                </p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 700, color: bulkSettings.creditToGcEnabled !== false ? '#15803d' : '#991b1b' }}>
                <input
                  type="checkbox"
                  checked={bulkSettings.creditToGcEnabled !== false}
                  onChange={(e) => setBulkSettings({ ...bulkSettings, creditToGcEnabled: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                {bulkSettings.creditToGcEnabled !== false ? 'Enabled' : 'Disabled'}
              </label>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '16px',
              flexWrap: 'wrap',
              padding: '16px',
              background: bulkSettings.emailChangeWithoutApi === true ? '#fffbeb' : '#f8fafc',
              borderRadius: '12px',
              border: `1px solid ${bulkSettings.emailChangeWithoutApi === true ? '#fde68a' : '#e2e8f0'}`,
              marginBottom: '20px'
            }}>
              <div style={{ minWidth: '220px', flex: 1 }}>
                <strong style={{ display: 'block', marginBottom: '6px', color: bulkSettings.emailChangeWithoutApi === true ? '#92400e' : '#334155' }}>
                  Change Email Without Adobe API
                </strong>
                <p style={{ margin: 0, color: '#334155', fontSize: '14px', lineHeight: '1.6' }}>
                  <b>Off (normal):</b> the Transfer button changes a customer's email by adding the new address to Adobe and removing the old one — it needs Adobe's API to be working.
                  {' '}<b>On:</b> Transfer only changes the email in your records (same expiry, no credits) and never contacts Adobe. You must then add the new email in the Adobe Admin Console yourself — until you do, that customer has no working license. Moving a customer to another organization still needs Adobe. Click Save Settings to apply.
                </p>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', fontWeight: 700, color: bulkSettings.emailChangeWithoutApi === true ? '#92400e' : '#334155' }}>
                <input
                  type="checkbox"
                  checked={bulkSettings.emailChangeWithoutApi === true}
                  onChange={(e) => setBulkSettings({ ...bulkSettings, emailChangeWithoutApi: e.target.checked })}
                  style={{ width: '20px', height: '20px' }}
                />
                {bulkSettings.emailChangeWithoutApi === true ? 'On — without Adobe' : 'Off — uses Adobe'}
              </label>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '24px' }}>
              <div style={{ padding: '16px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Currently Running</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: bulkRunning > 0 ? '#dc2626' : '#0f172a' }}>
                  {bulkRunning} / {bulkSettings.maxConcurrent}
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Max Concurrent Bulk Operations</label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  className="form-input"
                  value={bulkSettings.maxConcurrent}
                  onChange={(e) => setBulkSettings({ ...bulkSettings, maxConcurrent: e.target.value })}
                />
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>How many bulk operations can run simultaneously across all sellers. Default: 10.</div>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Max Users Per Bulk Operation</label>
                <input
                  type="number"
                  min="0"
                  max="10000"
                  className="form-input"
                  value={bulkSettings.maxUsersPerOp}
                  onChange={(e) => setBulkSettings({ ...bulkSettings, maxUsersPerOp: e.target.value })}
                />
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>Maximum emails a seller can submit in a single bulk request. Set to 0 to disable (unlimited).</div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
              {bulkJustSaved && <span style={{ color: '#15803d', fontWeight: 700 }}>✓ Saved</span>}
              {!bulkSettingsLoaded && !loadingBulkSettings && <span style={{ color: '#b45309', fontSize: '13px' }}>Settings not loaded — click Refresh above</span>}
              <button type="submit" className="btn btn-primary" disabled={savingBulkSettings || loadingBulkSettings || !bulkSettingsLoaded}>
                {savingBulkSettings ? 'Saving...' : loadingBulkSettings && !bulkSettingsLoaded ? 'Loading settings...' : 'Save Settings'}
              </button>
            </div>
          </form>
        </div>
        </>
      )}

      {/* Backup Keys Management Tab */}
      {activeTab === 'backupKeys' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Pre-Made Keys Management</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={fetchBackupKeys} disabled={loadingBackupKeys} className="btn btn-secondary">
                {loadingBackupKeys ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>

          {loadingBackupKeys && !backupKeysData ? (
            <p>Loading...</p>
          ) : backupKeysData ? (
            <div>
              {/* Feature Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: backupKeysData.enabled ? '#f0fdf4' : '#fef2f2', borderRadius: '12px', marginBottom: '20px', border: `1px solid ${backupKeysData.enabled ? '#bbf7d0' : '#fecaca'}` }}>
                <div>
                  <strong style={{ fontSize: '16px' }}>{backupKeysData.enabled ? 'Feature Enabled' : 'Feature Disabled'}</strong>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
                    When enabled, resellers can purchase pre-made account keys using their credits. Keys are handled separately from email-based user activations.
                  </p>
                </div>
                <button
                  onClick={() => handleToggleBackupKeys(!backupKeysData.enabled)}
                  className={`btn ${backupKeysData.enabled ? 'btn-danger' : 'btn-primary'}`}
                >
                  {backupKeysData.enabled ? 'Disable' : 'Enable'}
                </button>
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#1d4ed8' }}>{backupKeysData.totalAvailable}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Available Keys</div>
                </div>
                <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#15803d' }}>{backupKeysData.totalAssigned}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Assigned Keys</div>
                </div>
                <div style={{ padding: '16px', background: '#fefce8', borderRadius: '12px', textAlign: 'center' }}>
                  <input
                    type="text"
                    value={backupKeysData.redeemUrl || ''}
                    onChange={(e) => setBackupKeysData({ ...backupKeysData, redeemUrl: e.target.value })}
                    className="form-input"
                    placeholder="https://redeem.adobe.com"
                    style={{ fontSize: '13px', textAlign: 'center', marginBottom: '4px' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '4px' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>Redeem URL</span>
                    <button
                      onClick={async () => {
                        const token = localStorage.getItem('adminToken');
                        const res = await fetch(`${API_URL}/admin/backup-keys/settings`, {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                          body: JSON.stringify({ redeemUrl: backupKeysData.redeemUrl })
                        });
                        const data = await res.json();
                        if (res.ok) setResult('Redeem URL saved');
                        else setError(data.error || 'Failed to save');
                      }}
                      className="btn btn-primary"
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                    >Save</button>
                  </div>
                </div>
              </div>

              {/* Inventory by Duration */}
              {Object.keys(backupKeysData.inventory || {}).length > 0 && (
                <div style={{ marginBottom: '24px' }}>
                  <h4 style={{ marginBottom: '12px' }}>Inventory by Duration</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
                    {Object.entries(backupKeysData.inventory).map(([months, counts]) => (
                      <div key={months} style={{ padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong>{months} {parseInt(months) === 1 ? 'Month' : 'Months'}</strong>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {counts.available} available · {counts.assigned} assigned
                          </div>
                        </div>
                        <button onClick={() => handleDeleteBackupKeys(parseInt(months))} className="btn btn-danger" style={{ padding: '4px 8px', fontSize: '11px' }}><Icon name="trash" size={16} /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Global Key Pricing */}
              <div style={{ marginBottom: '24px', padding: '20px', background: '#fdf4ff', borderRadius: '12px', border: '1px solid #e9d5ff' }}>
                <h4 style={{ marginBottom: '6px', color: '#0b7da6' }}>Global Key Pricing (Credits)</h4>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '14px' }}>
                  Set a fixed credit price for each duration that applies to ALL resellers. Leave blank to use each reseller's own per-month config.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px', marginBottom: '12px' }}>
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                    <div key={m} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151' }}>{m} Month{m > 1 ? 's' : ''}</label>
                      <input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="e.g. 5"
                        value={(backupKeysData.keyPricing || {})[m] ?? ''}
                        onChange={(e) => setBackupKeysData({
                          ...backupKeysData,
                          keyPricing: { ...(backupKeysData.keyPricing || {}), [m]: e.target.value === '' ? null : parseFloat(e.target.value) }
                        })}
                        className="form-input"
                        style={{ fontSize: '13px', padding: '6px 8px' }}
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={async () => {
                    const token = localStorage.getItem('adminToken');
                    const res = await fetch(`${API_URL}/admin/backup-keys/settings`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                      body: JSON.stringify({ keyPricing: backupKeysData.keyPricing || {} })
                    });
                    const data = await res.json();
                    if (res.ok && data.success) {
                      setBackupKeysData({ ...backupKeysData, keyPricing: data.keyPricing });
                      setResult('Key pricing saved successfully');
                    } else {
                      setError(data.error || 'Failed to save pricing');
                    }
                  }}
                  className="btn btn-primary"
                >
                  Save Pricing
                </button>
              </div>

              {/* Description Editor */}
              <div style={{ marginBottom: '24px', padding: '20px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                <h4 style={{ marginBottom: '12px', color: '#0369a1' }}>Reseller-Facing Description</h4>
                <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '12px' }}>This message is shown to resellers when they select "Get Pre-Made Key" in the Add User tab.</p>
                <textarea
                  className="form-input"
                  value={backupKeysData.description || ''}
                  onChange={(e) => setBackupKeysData({ ...backupKeysData, description: e.target.value })}
                  rows={4}
                  style={{ fontSize: '14px', lineHeight: '1.6' }}
                  placeholder="Enter a description to show to resellers..."
                />
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('adminToken');
                      const res = await fetch(`${API_URL}/admin/backup-keys/settings`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ description: backupKeysData.description })
                      });
                      const data = await res.json();
                      if (res.ok && data.success) {
                        setResult('Description updated successfully!');
                      } else {
                        setError(data.error || 'Failed to save description');
                      }
                    } catch (err) {
                      setError('Network error');
                    }
                  }}
                  className="btn btn-primary"
                  style={{ marginTop: '10px' }}
                >
                  Save Description
                </button>
              </div>

              {/* Import Keys */}
              <div style={{ marginBottom: '24px', padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ marginBottom: '12px' }}>Import Pre-Made Keys</h4>
                <form onSubmit={handleImportBackupKeys}>
                  <div className="form-group">
                    <label className="form-label">Duration</label>
                    <select className="form-input" value={backupKeyImportDuration} onChange={(e) => setBackupKeyImportDuration(parseInt(e.target.value))}>
                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => (
                        <option key={m} value={m}>{m} {m === 1 ? 'Month' : 'Months'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Activation Codes (one per line, or comma-separated)</label>
                    <textarea
                      className="form-input"
                      value={backupKeyImportText}
                      onChange={(e) => setBackupKeyImportText(e.target.value)}
                      placeholder={"CODE-XXXX-XXXX-XXXX\nCODE-YYYY-YYYY-YYYY\nCODE-ZZZZ-ZZZZ-ZZZZ"}
                      rows={8}
                      style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    />
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                      {backupKeyImportText.trim() ? `${backupKeyImportText.split(/[,\n\r]+/).filter(k => k.trim()).length} key(s) detected` : 'Paste your activation codes here'}
                    </div>
                  </div>
                  <button type="submit" disabled={loading || !backupKeyImportText.trim()} className="btn btn-primary">
                    {loading ? 'Importing...' : `Import Keys (${backupKeyImportDuration} month${backupKeyImportDuration > 1 ? 's' : ''})`}
                  </button>
                </form>
              </div>

              {/* Recently Assigned Keys */}
              {backupKeysData.assignedKeys && backupKeysData.assignedKeys.length > 0 && (
                <div>
                  <h4 style={{ marginBottom: '12px' }}>Recently Assigned Keys</h4>
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Code</th>
                          <th>Duration</th>
                          <th>Assigned To</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backupKeysData.assignedKeys.map((key, idx) => (
                          <tr key={idx}>
                            <td><code style={{ fontSize: '12px' }}>{key.code}</code></td>
                            <td>{key.durationMonths} month{key.durationMonths > 1 ? 's' : ''}</td>
                            <td style={{ fontSize: '13px' }}>{key.assignedTo}</td>
                            <td style={{ fontSize: '12px', color: '#64748b' }}>{new Date(key.assignedAt).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p style={{ color: 'var(--app-muted)' }}>Click Refresh to load backup keys data.</p>
          )}
        </div>
      )}

      {/* Marketplace Management Tab */}
      {/* New-order notification popup — shows on any admin tab */}
      {adminAuth.isLoggedIn && newOrderNotice && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', left: '20px', zIndex: 2000, background: 'white', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', border: '1px solid #fde68a', padding: '16px 18px', maxWidth: '360px', marginLeft: 'auto', boxSizing: 'border-box' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{ fontWeight: 700, color: '#92400e' }}>
              {newOrderNotice.count} new order{newOrderNotice.count > 1 ? 's' : ''}
            </div>
            <button onClick={() => setNewOrderNotice(null)} className="btn btn-secondary" style={{ padding: '0 8px', lineHeight: '1.6' }}>×</button>
          </div>
          {newOrderNotice.manual > 0 && (
            <div style={{ marginTop: '6px', fontSize: '13px', color: '#b91c1c', fontWeight: 600 }}>
              {newOrderNotice.manual} need manual processing
            </div>
          )}
          <div style={{ marginTop: '8px', fontSize: '12px', color: '#475569' }}>
            {newOrderNotice.orders.map(o => (
              <div key={o.id} style={{ padding: '2px 0' }}>
                #{o.id} — {o.productName}{o.buyerEmail ? '' : ''}
              </div>
            ))}
          </div>
          <button
            className="btn btn-primary"
            style={{ marginTop: '10px', width: '100%' }}
            onClick={() => { setNewOrderNotice(null); setActiveTab('marketplace'); fetchMarketplace(); fetchMarketplaceOrders(1, ''); }}
          >View Orders</button>
        </div>
      )}

      {activeTab === 'marketplace' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Marketplace</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button onClick={() => fetchMarketplaceOrders(1)} className="btn btn-secondary">View All Orders</button>
              <button onClick={fetchMarketplace} disabled={loadingMarketplace} className="btn btn-secondary">
                {loadingMarketplace ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>

          {loadingMarketplace && !marketplaceData ? (
            <p>Loading...</p>
          ) : marketplaceData ? (
            <div>
              {/* Feature Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: marketplaceData.enabled ? '#f0fdf4' : '#fef2f2', borderRadius: '12px', marginBottom: '20px', border: `1px solid ${marketplaceData.enabled ? '#bbf7d0' : '#fecaca'}` }}>
                <div>
                  <strong style={{ fontSize: '16px' }}>{marketplaceData.enabled ? 'Marketplace Enabled' : 'Marketplace Disabled'}</strong>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>
                    When enabled, Credits Pool resellers can browse and buy products with Gold Coins. Marketplace Gold Coins are separate from activation credits.
                  </p>
                </div>
                <button
                  onClick={() => handleToggleMarketplace(!marketplaceData.enabled)}
                  className={`btn ${marketplaceData.enabled ? 'btn-danger' : 'btn-primary'}`}
                >
                  {marketplaceData.enabled ? 'Disable' : 'Enable'}
                </button>
              </div>

              {/* Stats */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                <div style={{ padding: '16px', background: '#eff6ff', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#1d4ed8' }}>{marketplaceData.products.length}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Products</div>
                </div>
                <div style={{ padding: '16px', background: '#f0fdf4', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#15803d' }}>{marketplaceData.totalAvailable}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Available Codes</div>
                </div>
                <div style={{ padding: '16px', background: '#fefce8', borderRadius: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#a16207' }}>{marketplaceData.totalSold}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>Sold</div>
                </div>
              </div>

              {/* Add New Product */}
              <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
                <h4 style={{ marginTop: 0, marginBottom: '12px' }}>Add New Product</h4>
                <form onSubmit={handleCreateProduct}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Name *</label>
                      <input type="text" className="form-input" value={newProduct.name} onChange={e => setNewProduct({ ...newProduct, name: e.target.value })} placeholder="e.g. Envato Elements Subscription" required />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Image URL</label>
                      <input type="text" className="form-input" value={newProduct.imageUrl} onChange={e => setNewProduct({ ...newProduct, imageUrl: e.target.value })} placeholder="https://example.com/image.jpg" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Category</label>
                      <input type="text" className="form-input" value={newProduct.category} onChange={e => setNewProduct({ ...newProduct, category: e.target.value })} placeholder="e.g. Adobe, Design Tools, Video..." />
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingTop: '28px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>
                        <input type="checkbox" checked={newProduct.requiresEmail} onChange={e => setNewProduct({ ...newProduct, requiresEmail: e.target.checked })} style={{ width: '18px', height: '18px' }} />
                        Requires buyer's email for activation
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
                        title="Pinned products are shown first in the reseller shop, above best sellers.">
                        <input type="checkbox" checked={newProduct.pinned} onChange={e => setNewProduct({ ...newProduct, pinned: e.target.checked })} style={{ width: '18px', height: '18px' }} />
                        Pin to top of shop
                      </label>
                      <small style={{ flexBasis: '100%', color: 'var(--app-muted)', fontSize: '11px' }}>
                        Email-activation products are shipped by hand automatically: their orders arrive as Pending Processing with a Ship button.
                      </small>
                    </div>
                  </div>
                  <div className="form-group">
                    <WarrantyFields form={newProduct} onChange={setNewProduct} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea className="form-input" value={newProduct.description} onChange={e => setNewProduct({ ...newProduct, description: e.target.value })} rows={4} placeholder="Plain-text description visible to resellers..." />
                  </div>
                  <div className="marketplace-month-panel">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'center', marginBottom: '10px' }}>
                      <label className="form-label" style={{ margin: 0 }}>Marketplace Gold Coin Pricing</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>Checked months are visible to resellers.</span>
                        <button type="button" className="btn btn-secondary" style={{ padding: '3px 9px', fontSize: '12px' }}
                          onClick={addMarketplaceDuration}
                          title="Add a duration - 24 months, 2 years, 30 days, anything">
                          + Duration
                        </button>
                      </div>
                    </div>
                    {marketplaceDurations.some(d => !d.builtIn) && (
                      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                        Added by you:{' '}
                        {marketplaceDurations.filter(d => !d.builtIn).map(d => (
                          <button key={d.value} type="button" className="btn btn-secondary"
                            style={{ padding: '2px 7px', fontSize: '11px', marginRight: '6px' }}
                            onClick={() => removeMarketplaceDuration(d)}
                            title="Remove this duration">
                            {d.label} &times;
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="marketplace-month-grid">
                      {marketplaceDurations.map(d => d.value).map(month => {
                        const isEnabled = (newProduct.enabledMonths || []).includes(month);
                        return (
                          <div key={month} className={`marketplace-month-control ${isEnabled ? '' : 'is-disabled'}`}>
                            <label>
                              <input
                                type="checkbox"
                                checked={isEnabled}
                                onChange={() => {
                                  const enabled = newProduct.enabledMonths || [];
                                  setNewProduct({
                                    ...newProduct,
                                    enabledMonths: isEnabled ? enabled.filter(m => m !== month) : [...enabled, month].sort(sortMarketplaceMonths)
                                  });
                                }}
                              />
                              <span>{formatMarketplaceMonth(month)}</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={newProduct.priceByMonth?.[month] ?? (month > 0 ? month : 0)}
                              disabled={!isEnabled}
                              onChange={e => setNewProduct({
                                ...newProduct,
                                priceByMonth: { ...newProduct.priceByMonth, [month]: e.target.value }
                              })}
                            />
                            <span>GC</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <MarketplaceVisibilityControl form={newProduct} onChange={setNewProduct} resellers={cpResellers} />
                  <button type="submit" disabled={loading} className="btn btn-primary" style={{ marginTop: '12px' }}>
                    {loading ? 'Saving...' : 'Add Product'}
                  </button>
                </form>
              </div>

              {/* Sub-tabs — Products / Sales & Promotions / Dashboard & Banners each get
                  their own view so the product list (which can be long) doesn't bury
                  the other panels at the bottom of an endless scroll. */}
              <div className="shop-category-tabs" aria-label="Marketplace sections" style={{ marginBottom: '16px' }}>
                <button type="button"
                  onClick={() => setMarketplaceSubTab('products')}
                  className={`shop-category-tab ${marketplaceSubTab === 'products' ? 'is-active' : ''}`}
                >
                  <span>Products</span>
                  <span className="shop-category-count">{marketplaceData.products.length}</span>
                </button>
                <button type="button"
                  onClick={() => setMarketplaceSubTab('promotions')}
                  className={`shop-category-tab ${marketplaceSubTab === 'promotions' ? 'is-active' : ''}`}
                >
                  <span>Sales &amp; Promotions</span>
                  <span className="shop-category-count">{marketplacePromotions.length}</span>
                </button>
                <button type="button"
                  onClick={() => setMarketplaceSubTab('banners')}
                  className={`shop-category-tab ${marketplaceSubTab === 'banners' ? 'is-active' : ''}`}
                >
                  <span>Dashboard &amp; Banners</span>
                </button>
                <button type="button"
                  onClick={() => setMarketplaceSubTab('suppliers')}
                  className={`shop-category-tab ${marketplaceSubTab === 'suppliers' ? 'is-active' : ''}`}
                >
                  <span>From suppliers</span>
                </button>
              </div>

              {marketplaceSubTab === 'suppliers' && <MarketplaceSupplierProducts />}

              {supplierSourceFor && (
                <SupplierSourceEditor product={supplierSourceFor}
                  ownStock={Object.fromEntries((supplierSourceFor.enabledMonths || []).map(m => [m, getMarketplaceMonthStock(supplierSourceFor, m)]))}
                  onClose={() => setSupplierSourceFor(null)}
                  onSaved={(n) => { if (n) setResult(`Supplier stock saved for "${supplierSourceFor.name}".`); loadMarketplaceLinks(); }} />
              )}

              {marketplaceSubTab === 'products' && (() => {
                const all = marketplaceData.products || [];
                const categories = Array.from(new Set(all.map(p => (p.category || '').trim()).filter(Boolean))).sort();
                const counts = {
                  all: all.length,
                  active: all.filter(p => p.active).length,
                  disabled: all.filter(p => !p.active).length,
                  out: all.filter(p => !(p.availableCount > 0)).length,
                  restricted: all.filter(p => p.visibility === 'restricted').length,
                };
                const q = productSearch.trim().toLowerCase();
                let list = all.filter(p => {
                  if (q) {
                    const hay = `${p.name || ''} ${p.category || ''} ${p.description || ''} #${p.id}`.toLowerCase();
                    if (!hay.includes(q)) return false;
                  }
                  if (productCategoryFilter !== 'all' && (p.category || '').trim() !== productCategoryFilter) return false;
                  if (productStatusFilter === 'active' && !p.active) return false;
                  if (productStatusFilter === 'disabled' && p.active) return false;
                  if (productStatusFilter === 'restricted' && p.visibility !== 'restricted') return false;
                  if (productStatusFilter === 'out' && p.availableCount > 0) return false;
                  return true;
                });
                list = list.slice().sort((a, b) => {
                  if (productSort === 'name') return (a.name || '').localeCompare(b.name || '');
                  if (productSort === 'sold') return (b.soldCount || 0) - (a.soldCount || 0);
                  if (productSort === 'stock') return (b.availableCount || 0) - (a.availableCount || 0);
                  if (productSort === 'lowstock') return (a.availableCount || 0) - (b.availableCount || 0);
                  if (productSort === 'newest') return b.id - a.id;
                  // default: pinned first (newest pin on top), then the original id order.
                  const pin = (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0);
                  if (pin !== 0) return pin;
                  if (a.pinned && b.pinned) return new Date(b.pinnedAt || 0) - new Date(a.pinnedAt || 0);
                  return a.id - b.id;
                });
                const total = list.length;
                const perPage = productsPerPage;
                const totalPages = Math.max(1, Math.ceil(total / perPage));
                const page = Math.min(Math.max(1, productsPage), totalPages);
                const start = (page - 1) * perPage;
                const pageItems = list.slice(start, start + perPage);
                return (
                <>
                {/* Products List — toolbar */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0 }}>Products ({all.length})</h4>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        className="form-input"
                        placeholder="Search by name, category, #ID…"
                        value={productSearch}
                        onChange={e => { setProductSearch(e.target.value); setProductsPage(1); }}
                        style={{ width: '260px', paddingRight: productSearch ? '26px' : undefined }}
                      />
                      {productSearch && (
                        <button type="button" onClick={() => { setProductSearch(''); setProductsPage(1); }}
                          title="Clear search"
                          style={{ position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '16px', lineHeight: 1 }}>×</button>
                      )}
                    </div>
                    <select className="form-input" value={productSort} onChange={e => { setProductSort(e.target.value); setProductsPage(1); }} style={{ width: 'auto' }}>
                      <option value="default">Sort: Pinned first</option>
                      <option value="newest">Newest first</option>
                      <option value="name">Name A–Z</option>
                      <option value="sold">Most sold</option>
                      <option value="stock">Most stock</option>
                      <option value="lowstock">Lowest stock</option>
                    </select>
                  </div>
                </div>

                {/* status + category filters */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
                  {[
                    { key: 'all', label: 'All', n: counts.all },
                    { key: 'active', label: 'Active', n: counts.active },
                    { key: 'disabled', label: 'Disabled', n: counts.disabled },
                    { key: 'out', label: 'Out of stock', n: counts.out },
                    { key: 'restricted', label: 'Restricted', n: counts.restricted },
                  ].map(t => (
                    <button key={t.key} type="button"
                      className={`shop-category-tab ${productStatusFilter === t.key ? 'is-active' : ''}`}
                      onClick={() => { setProductStatusFilter(t.key); setProductsPage(1); }}>
                      <span>{t.label}</span>
                      <span className="shop-category-count">{t.n}</span>
                    </button>
                  ))}
                  {categories.length > 0 && (
                    <select className="form-input" value={productCategoryFilter} onChange={e => { setProductCategoryFilter(e.target.value); setProductsPage(1); }} style={{ width: 'auto', marginLeft: 'auto' }}>
                      <option value="all">All categories</option>
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  )}
                </div>

                {total === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon"><Icon name="bag" size={16} /></div>
                    <div className="empty-text">{all.length === 0 ? 'No products yet. Add one above to get started.' : 'No products match your search or filters.'}</div>
                  </div>
                ) : (
                  <>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                    Showing {start + 1}–{Math.min(start + perPage, total)} of {total}{total !== all.length ? ` (filtered from ${all.length})` : ''}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {pageItems.map(p => (
                    <div key={p.id} style={{ padding: '16px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                      {editingProductId === p.id ? (
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <input className="form-input" value={editProductForm.name} onChange={e => setEditProductForm({ ...editProductForm, name: e.target.value })} placeholder="Name" />
                            <input className="form-input" value={editProductForm.imageUrl} onChange={e => setEditProductForm({ ...editProductForm, imageUrl: e.target.value })} placeholder="Image URL" />
                            <input className="form-input" value={editProductForm.category} onChange={e => setEditProductForm({ ...editProductForm, category: e.target.value })} placeholder="Category (e.g. Adobe, Design Tools)" />
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, paddingTop: '10px' }}>
                              <input type="checkbox" checked={editProductForm.requiresEmail || false} onChange={e => setEditProductForm({ ...editProductForm, requiresEmail: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                              Requires buyer email
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                              title="Pinned products are shown first in the reseller shop, above best sellers.">
                              <input type="checkbox" checked={editProductForm.pinned || false} onChange={e => setEditProductForm({ ...editProductForm, pinned: e.target.checked })} style={{ width: '16px', height: '16px' }} />
                              Pin to top of shop
                            </label>
                          </div>
                          <div style={{ marginTop: '8px' }}><WarrantyFields form={editProductForm} onChange={setEditProductForm} /></div>
                          <textarea className="form-input" rows={4} value={editProductForm.description} onChange={e => setEditProductForm({ ...editProductForm, description: e.target.value })} placeholder="Description" style={{ marginTop: '8px' }} />
                          <div className="marketplace-month-panel" style={{ marginTop: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                              <span style={{ fontSize: '12px', color: '#64748b' }}>Checked durations are visible to resellers.</span>
                              <button type="button" className="btn btn-secondary" style={{ padding: '3px 9px', fontSize: '12px' }}
                                onClick={addMarketplaceDuration}
                                title="Add a duration - 24 months, 2 years, 30 days, anything">
                                + Duration
                              </button>
                            </div>
                            {marketplaceDurations.some(d => !d.builtIn) && (
                              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>
                                Added by you:{' '}
                                {marketplaceDurations.filter(d => !d.builtIn).map(d => (
                                  <button key={d.value} type="button" className="btn btn-secondary"
                                    style={{ padding: '2px 7px', fontSize: '11px', marginRight: '6px' }}
                                    onClick={() => removeMarketplaceDuration(d)}
                                    title="Remove this duration">
                                    {d.label} &times;
                                  </button>
                                ))}
                              </div>
                            )}
                            <div className="marketplace-month-grid">
                              {marketplaceDurations.map(d => d.value).map(month => {
                                const isEnabled = (editProductForm.enabledMonths || []).includes(month);
                                return (
                                  <div key={month} className={`marketplace-month-control ${isEnabled ? '' : 'is-disabled'}`}>
                                    <label>
                                      <input
                                        type="checkbox"
                                        checked={isEnabled}
                                        onChange={() => {
                                          const enabled = editProductForm.enabledMonths || [];
                                          setEditProductForm({
                                            ...editProductForm,
                                            enabledMonths: isEnabled ? enabled.filter(m => m !== month) : [...enabled, month].sort(sortMarketplaceMonths)
                                          });
                                        }}
                                      />
                                      <span>{formatMarketplaceMonth(month)}</span>
                                    </label>
                                    <input
                                      type="number"
                                      min="0"
                                      step="0.1"
                                      value={editProductForm.priceByMonth?.[month] ?? (month > 0 ? month : 0)}
                                      disabled={!isEnabled}
                                      onChange={e => setEditProductForm({
                                        ...editProductForm,
                                        priceByMonth: { ...editProductForm.priceByMonth, [month]: e.target.value }
                                      })}
                                    />
                                    <span>GC</span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginTop: '10px', alignItems: 'center' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <input type="checkbox" checked={editProductForm.active} onChange={e => setEditProductForm({ ...editProductForm, active: e.target.checked })} />
                              Active (visible to resellers)
                            </label>
                          </div>
                          <MarketplaceVisibilityControl form={editProductForm} onChange={setEditProductForm} resellers={cpResellers} />
                          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                            <button onClick={() => handleUpdateProduct(p.id)} disabled={loading} className="btn btn-primary">Save</button>
                            <button onClick={() => setEditingProductId(null)} className="btn btn-secondary">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                          {p.imageUrl ? (
                            <img src={p.imageUrl} alt={p.name} style={{ width: '120px', height: '120px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0, background: '#f1f5f9' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          ) : (
                            <div style={{ width: '120px', height: '120px', borderRadius: '8px', flexShrink: 0, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '40px' }}><Icon name="bag" size={16} /></div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', flexWrap: 'wrap', gap: '8px' }}>
                              <div>
                                <strong style={{ fontSize: '16px' }}>{p.name}</strong>
                                {!p.active && <span style={{ marginLeft: '8px', background: '#fee2e2', color: '#991b1b', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>Disabled</span>}
                                {p.visibility === 'restricted' && <span style={{ marginLeft: '8px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }}>Restricted{Array.isArray(p.allowedResellerIds) ? ` (${p.allowedResellerIds.length})` : ''}</span>}
                              </div>
                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                <button onClick={() => { setEditingProductId(p.id); setEditProductForm(createMarketplaceProductForm(p)); }} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}>Edit</button>
                                <button onClick={() => {
                                  const enabledMonths = Array.isArray(p.enabledMonths) && p.enabledMonths.length > 0 ? p.enabledMonths : [1];
                                  setImportCodesForProductId(p.id);
                                  setImportCodesText('');
                                  setImportCodesDuration(enabledMonths[0]);
                                }} className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '12px' }}>Import Codes</button>
                                <button onClick={() => openProductCodes(p)} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}>Codes</button>
                                {!p.manualFulfill && !p.requiresEmail && (() => {
                                  const n = marketplaceLinks.filter(l => l.productId === p.id).length;
                                  return (
                                    <button onClick={() => setSupplierSourceFor(p)} className="btn btn-secondary"
                                      title="Restock this product from a linked supplier when your own codes run out"
                                      style={{ padding: '4px 10px', fontSize: '12px', ...(n ? { borderColor: '#2563eb', color: '#2563eb' } : {}) }}>
                                      🔗 Supplier stock{n ? ` (${n})` : ''}
                                    </button>
                                  );
                                })()}
                                <button onClick={() => handleDeleteProduct(p.id, p.name)} className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '12px' }}>Product</button>
                              </div>
                            </div>
                            {p.description && <p style={{ margin: '4px 0 8px', fontSize: '13px', color: '#475569', whiteSpace: 'pre-wrap' }}>{p.description}</p>}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', fontSize: '12px', color: '#475569' }}>
                              {(p.enabledMonths || [1, 2]).map(month => (
                                <span key={month} className="marketplace-admin-pill">
                                  {formatMarketplaceMonth(month)}: <strong>{p.priceByMonth?.[month] ?? p.priceByMonth?.[String(month)] ?? (month === 1 ? p.price1Month : p.price2Month)} GC</strong>
                                  <small>{getMarketplaceMonthStock(p, month)} stock</small>
                                  {(() => {
                                    const l = marketplaceLinks.find(x => x.productId === p.id && Number(x.durationMonths) === Number(month));
                                    if (!l) return null;
                                    return <small title={`Restocked from ${l.supplierProduct} (${l.supplierName}), cost ${Number(l.costUsd || 0).toFixed(2)}`}
                                      style={{ color: l.belowCost || !l.supplierStock ? '#dc2626' : '#2563eb' }}>
                                      🔗 +{l.belowCost ? 0 : l.supplierStock} supplier{l.belowCost ? ' (price ≤ cost)' : ''}{l.autoPrice ? ` · auto +${l.marginPct ?? 30}%` : ''}
                                    </small>;
                                  })()}
                                </span>
                              ))}
                              <span>Total: <strong style={{ color: p.availableCount > 0 ? '#15803d' : '#b91c1c' }}>{p.availableCount}</strong></span>
                              <span>Sold: <strong>{p.soldCount}</strong></span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Inline codes viewer */}
                      {viewCodesProductId === p.id && (
                        <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                            <h5 style={{ margin: 0 }}>Codes in stock for "{p.name}"</h5>
                            <button type="button" className="btn btn-secondary" style={{ padding: '3px 9px', fontSize: '12px' }} onClick={() => setViewCodesProductId(null)}>Close</button>
                          </div>
                          {viewCodesLoading ? (
                            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Loading…</p>
                          ) : !viewCodesData ? null : viewCodesData.codes.length === 0 ? (
                            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                              No codes in stock.{viewCodesData.manualFulfill
                                ? ' This is a manual-fulfilment product — buyers are sent their details by hand, so it holds no codes.'
                                : ' Use “Import Codes” to add some.'}
                            </p>
                          ) : (
                            <>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#64748b' }}>
                                  {viewCodesData.total} in stock · {viewCodesData.sold} sold
                                  {viewCodesData.total > viewCodesData.codes.length ? ` (showing first ${viewCodesData.codes.length})` : ''}
                                </span>
                                <button type="button" className="btn btn-secondary" style={{ padding: '3px 9px', fontSize: '12px' }}
                                  onClick={() => copyProductCodes(viewCodesData.codes.map(c => c.code).join('\n'), `Copied ${viewCodesData.codes.length} code(s)`)}>Copy all</button>
                                <button type="button" className="btn btn-danger" style={{ padding: '3px 9px', fontSize: '12px' }}
                                  onClick={() => handleDeleteProductCodes(p.id, p.name, false)}>Delete all</button>
                              </div>
                              <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px', background: 'white' }}>
                                {viewCodesData.codes.map(c => (
                                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderBottom: '1px solid #f1f5f9', fontFamily: 'monospace', fontSize: '13px' }}>
                                    <span style={{ color: '#94a3b8', fontSize: '11px', minWidth: '68px', flexShrink: 0 }}>{formatMarketplaceMonth(c.durationMonths)}</span>
                                    <span style={{ flex: 1, wordBreak: 'break-all' }}>{c.code}</span>
                                    <button type="button" title="Copy this code" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '11px', flexShrink: 0 }} onClick={() => copyProductCodes(c.code, 'Code copied')}>Copy</button>
                                    <button type="button" title="Delete this code" className="btn btn-secondary" style={{ padding: '2px 8px', fontSize: '11px', flexShrink: 0 }} onClick={() => deleteOneProductCode(p.id, c.id)}>×</button>
                                  </div>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Inline Import Codes */}
                      {importCodesForProductId === p.id && (
                        <div style={{ marginTop: '16px', padding: '12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                          <h5 style={{ marginTop: 0 }}>Import codes for "{p.name}"</h5>
                          <form onSubmit={handleImportProductCodes}>
                            <div className="form-group" style={{ marginBottom: '10px' }}>
                              <label className="form-label">Code Duration</label>
                              <select
                                className="form-input"
                                value={importCodesDuration}
                                onChange={e => setImportCodesDuration(parseInt(e.target.value, 10))}
                              >
                                {(p.enabledMonths || MARKETPLACE_MONTHS).map(month => (
                                  <option key={month} value={month}>
                                    {formatMarketplaceMonth(month)} - {p.priceByMonth?.[month] ?? p.priceByMonth?.[String(month)] ?? (month === 1 ? p.price1Month : p.price2Month)} Gold Coins
                                  </option>
                                ))}
                              </select>
                            </div>
                            <textarea className="form-input" rows={6} value={importCodesText} onChange={e => setImportCodesText(e.target.value)} placeholder={"CODE-XXXX-XXXX-XXXX\nCODE-YYYY-YYYY-YYYY"} style={{ fontFamily: 'monospace', fontSize: '13px' }} />
                            <div style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 8px' }}>
                              {importCodesText.trim() ? `${importCodesText.split(/[,\n\r]+/).filter(c => c.trim()).length} code(s) detected` : 'Paste codes (newline, comma, or semicolon separated)'}
                            </div>
                            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', margin: '0 0 10px', fontSize: '13px', cursor: 'pointer' }}>
                              <input type="checkbox" checked={importAllowDuplicates} style={{ marginTop: '3px' }}
                                onChange={e => setImportAllowDuplicates(e.target.checked)} />
                              <span>
                                <b>Allow duplicate codes</b>
                                <span style={{ display: 'block', color: '#64748b', fontSize: '12px', marginTop: '2px' }}>
                                  Normally identical codes are skipped so one key cannot be sold twice. Tick this when
                                  every unit is meant to be the same text — a manual-fulfilment message, a shared
                                  invite link — and all lines will be kept as separate stock.
                                </span>
                              </span>
                            </label>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button type="submit" disabled={loading || !importCodesText.trim()} className="btn btn-primary">
                                {loading ? 'Importing…' : 'Import'}
                              </button>
                              <button type="button" onClick={() => { setImportCodesForProductId(null); setImportCodesText(''); setImportCodesDuration(1); }} className="btn btn-secondary">Cancel</button>
                            </div>
                          </form>
                        </div>
                      )}
                    </div>
                  ))}
                  </div>
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '16px', flexWrap: 'wrap' }}>
                      <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setProductsPage(1)}>« First</button>
                      <button className="btn btn-secondary" disabled={page <= 1} onClick={() => setProductsPage(page - 1)}>‹ Prev</button>
                      <span style={{ fontSize: '13px', color: '#64748b' }}>Page {page} of {totalPages}</span>
                      <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setProductsPage(page + 1)}>Next ›</button>
                      <button className="btn btn-secondary" disabled={page >= totalPages} onClick={() => setProductsPage(totalPages)}>Last »</button>
                      <select className="form-input" value={perPage} onChange={e => { setProductsPerPage(Number(e.target.value)); setProductsPage(1); }} style={{ width: 'auto', marginLeft: '8px' }}>
                        {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n} / page</option>)}
                      </select>
                    </div>
                  )}
                  </>
                )}
                </>
                );
              })()}

              {/* ===== Sales & Promotions ===== */}
              {marketplaceSubTab === 'promotions' && (
              <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                  <div>
                    <h4 style={{ margin: 0 }}>Sales &amp; Promotions</h4>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0' }}>
                      Put selected products on sale. Discounts apply automatically in the reseller Shop — restricted products stay hidden from non-allowed resellers, sale or not.
                    </p>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      setEditingPromoId(null);
                      setPromoForm(createPromotionForm());
                      setShowPromoForm(!showPromoForm);
                    }}
                  >
                    {showPromoForm && !editingPromoId ? 'Close' : '+ New Promotion'}
                  </button>
                </div>

                {showPromoForm && (
                  <form onSubmit={handleSavePromotion} style={{ padding: '16px', background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                    <label className="form-label" style={{ display: 'block', marginBottom: '6px' }}>Sale Type</label>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      {PROMO_TYPE_PRESETS.map(preset => (
                        <button
                          type="button"
                          key={preset.type}
                          onClick={() => setPromoForm({
                            ...promoForm,
                            promoType: preset.type,
                            badgeText: preset.badge,
                            badgeColor: preset.color,
                            name: promoForm.name || preset.label
                          })}
                          className={`btn ${promoForm.promoType === preset.type ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ padding: '6px 14px', fontSize: '13px' }}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '12px' }}>
                      <div className="form-group">
                        <label className="form-label">Promotion Name *</label>
                        <input type="text" className="form-input" value={promoForm.name} onChange={e => setPromoForm({ ...promoForm, name: e.target.value })} placeholder="e.g. Summer Holiday Sale" required />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Discount % *</label>
                        <input type="number" min="1" max="90" className="form-input" value={promoForm.discountPercent} onChange={e => setPromoForm({ ...promoForm, discountPercent: e.target.value })} required />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Badge Color</label>
                        <input type="color" className="form-input" value={promoForm.badgeColor || '#dc2626'} onChange={e => setPromoForm({ ...promoForm, badgeColor: e.target.value })} style={{ height: '40px', padding: '4px' }} />
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Badge Text (shown on product cards)</label>
                      <input type="text" className="form-input" maxLength={40} value={promoForm.badgeText} onChange={e => setPromoForm({ ...promoForm, badgeText: e.target.value })} placeholder="e.g. Stock Clearance -30%" />
                    </div>

                    <div className="form-group">
                      <label className="form-label">How long does the sale last?</label>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {PROMO_DURATION_PRESETS.map(preset => (
                          <button
                            type="button"
                            key={preset.key}
                            onClick={() => setPromoForm({ ...promoForm, durationPreset: preset.key })}
                            className={`btn ${promoForm.durationPreset === preset.key ? 'btn-primary' : 'btn-secondary'}`}
                            style={{ padding: '6px 12px', fontSize: '13px' }}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                      {promoForm.durationPreset === 'custom' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '10px' }}>
                          <div>
                            <label className="form-label">Starts (optional — empty = now)</label>
                            <input type="datetime-local" className="form-input" value={promoForm.startsAt} onChange={e => setPromoForm({ ...promoForm, startsAt: e.target.value })} />
                          </div>
                          <div>
                            <label className="form-label">Ends</label>
                            <input type="datetime-local" className="form-input" value={promoForm.endsAt} onChange={e => setPromoForm({ ...promoForm, endsAt: e.target.value })} />
                          </div>
                          <div style={{ gridColumn: '1 / -1', fontSize: '11px', color: '#94a3b8' }}>Times are in your local timezone.</div>
                        </div>
                      )}
                      {promoForm.durationPreset !== 'custom' && promoForm.durationPreset !== 'noend' && (
                        <div style={{ fontSize: '12px', color: '#64748b', marginTop: '6px' }}>
                          Sale starts immediately when saved and ends automatically after {PROMO_DURATION_PRESETS.find(p => p.key === promoForm.durationPreset)?.label?.toLowerCase()}.
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Products on sale * ({promoForm.productIds.length} selected)</label>
                      <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '8px', maxHeight: '200px', overflowY: 'auto', background: '#fafafa' }}>
                        {marketplaceData.products.filter(p => p.active).length === 0 ? (
                          <div style={{ fontSize: '13px', color: '#94a3b8', padding: '6px' }}>No active products.</div>
                        ) : marketplaceData.products.filter(p => p.active).map(p => {
                          const checked = promoForm.productIds.includes(p.id);
                          return (
                            <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 6px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => setPromoForm({
                                  ...promoForm,
                                  productIds: checked ? promoForm.productIds.filter(id => id !== p.id) : [...promoForm.productIds, p.id]
                                })}
                              />
                              <span>{p.name}</span>
                              {p.visibility === 'restricted' && (
                                <span style={{ background: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: '4px', fontSize: '10px' }}>Restricted</span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', marginBottom: '12px' }}>
                      <input type="checkbox" checked={promoForm.active} onChange={e => setPromoForm({ ...promoForm, active: e.target.checked })} />
                      Active
                    </label>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button type="submit" disabled={loading} className="btn btn-primary">
                        {loading ? 'Saving…' : editingPromoId ? 'Save Changes' : 'Create Promotion'}
                      </button>
                      <button type="button" className="btn btn-secondary" onClick={() => { setShowPromoForm(false); setEditingPromoId(null); setPromoForm(createPromotionForm()); }}>Cancel</button>
                    </div>
                  </form>
                )}

                {loadingPromotions ? (
                  <p style={{ fontSize: '13px', color: '#64748b' }}>Loading promotions…</p>
                ) : marketplacePromotions.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>No promotions yet. Create one to put products on sale.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {marketplacePromotions.map(promo => {
                      const statusPill = promo.isLive
                        ? { text: 'LIVE', bg: '#dcfce7', color: '#15803d' }
                        : promo.isScheduled
                          ? { text: 'SCHEDULED', bg: '#dbeafe', color: '#1d4ed8' }
                          : promo.isExpired
                            ? { text: 'EXPIRED', bg: '#f1f5f9', color: '#64748b' }
                            : { text: 'OFF', bg: '#fee2e2', color: '#991b1b' };
                      return (
                        <div key={promo.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '12px 16px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <span style={{ background: statusPill.bg, color: statusPill.color, padding: '2px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700 }}>{statusPill.text}</span>
                            <strong>{promo.name}</strong>
                            <span style={{ background: promo.badgeColor || '#dc2626', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600 }}>
                              -{promo.discountPercent}%
                            </span>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>{promo.productIds.length} product(s)</span>
                            {promo.endsAt && <span style={{ fontSize: '12px', color: promo.isExpired ? '#94a3b8' : '#b45309' }}>{formatPromoTimeRemaining(promo.endsAt)}</span>}
                            {!promo.endsAt && promo.active && <span style={{ fontSize: '12px', color: '#64748b' }}>No end date</span>}
                          </div>
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                              className="btn btn-secondary"
                              style={{ padding: '4px 10px', fontSize: '12px' }}
                              onClick={() => {
                                setEditingPromoId(promo.id);
                                setPromoForm(createPromotionForm(promo));
                                setShowPromoForm(true);
                              }}
                            >Edit</button>
                            <button
                              className={`btn ${promo.active ? 'btn-secondary' : 'btn-primary'}`}
                              style={{ padding: '4px 10px', fontSize: '12px' }}
                              onClick={() => handleTogglePromotionActive(promo)}
                            >{promo.active ? 'Turn Off' : 'Turn On'}</button>
                            <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => handleDeletePromotion(promo)}>Delete</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              )}

              {/* ===== Reseller Dashboard & Banners ===== */}
              {marketplaceSubTab === 'banners' && (
              <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                <h4 style={{ marginTop: 0, marginBottom: '4px' }}>Reseller Dashboard &amp; Banners</h4>
                <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 16px' }}>
                  Control the Credits Pool reseller dashboard look. When the modern dashboard is off, resellers see the classic dashboard unchanged.
                </p>

                {!cpDashboardConfig ? (
                  <p style={{ fontSize: '13px', color: '#64748b' }}>Loading…</p>
                ) : (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: cpDashboardConfig.dashboardV2Enabled ? '#f0fdf4' : 'white', borderRadius: '10px', border: `1px solid ${cpDashboardConfig.dashboardV2Enabled ? '#bbf7d0' : '#e2e8f0'}` }}>
                        <div>
                          <strong style={{ fontSize: '14px' }}>Modern Dashboard</strong>
                          <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>New look with sale highlights &amp; animations</p>
                        </div>
                        <button
                          className={`btn ${cpDashboardConfig.dashboardV2Enabled ? 'btn-danger' : 'btn-primary'}`}
                          style={{ padding: '6px 14px', fontSize: '13px' }}
                          onClick={() => handleSaveCpDashboardToggles({ dashboardV2Enabled: !cpDashboardConfig.dashboardV2Enabled })}
                        >{cpDashboardConfig.dashboardV2Enabled ? 'Disable' : 'Enable'}</button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', background: cpDashboardConfig.bannerEnabled ? '#f0fdf4' : 'white', borderRadius: '10px', border: `1px solid ${cpDashboardConfig.bannerEnabled ? '#bbf7d0' : '#e2e8f0'}` }}>
                        <div>
                          <strong style={{ fontSize: '14px' }}>Banner Slider</strong>
                          <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>Show uploaded banners on the dashboard</p>
                        </div>
                        <button
                          className={`btn ${cpDashboardConfig.bannerEnabled ? 'btn-danger' : 'btn-primary'}`}
                          style={{ padding: '6px 14px', fontSize: '13px' }}
                          onClick={() => handleSaveCpDashboardToggles({ bannerEnabled: !cpDashboardConfig.bannerEnabled })}
                        >{cpDashboardConfig.bannerEnabled ? 'Disable' : 'Enable'}</button>
                      </div>
                    </div>

                    <form onSubmit={handleUploadBanner} style={{ padding: '16px', background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                      <h5 style={{ marginTop: 0, marginBottom: '10px' }}>Upload Banner</h5>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div className="form-group">
                          <label className="form-label">Image * (PNG/JPEG/WEBP, max 5MB — must be 4:1, e.g. 1200×300 or 1600×400)</label>
                          <input type="file" accept="image/png,image/jpeg,image/webp" className="form-input" onChange={e => setBannerForm({ ...bannerForm, file: e.target.files?.[0] || null })} />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Title (overlay text)</label>
                          <input type="text" className="form-input" maxLength={80} value={bannerForm.title} onChange={e => setBannerForm({ ...bannerForm, title: e.target.value })} placeholder="e.g. Holiday Sale — up to 30% off" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Subtitle</label>
                          <input type="text" className="form-input" maxLength={160} value={bannerForm.subtitle} onChange={e => setBannerForm({ ...bannerForm, subtitle: e.target.value })} placeholder="Optional smaller text" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">Click action</label>
                          <select
                            className="form-input"
                            value={bannerForm.linkProductId ? `product:${bannerForm.linkProductId}` : bannerForm.linkTab || ''}
                            onChange={e => {
                              const v = e.target.value;
                              if (v.startsWith('product:')) setBannerForm({ ...bannerForm, linkProductId: v.slice(8), linkTab: '' });
                              else setBannerForm({ ...bannerForm, linkTab: v, linkProductId: '' });
                            }}
                          >
                            <option value="">None</option>
                            <option value="shop">Open Shop</option>
                            {marketplaceData.products.filter(p => p.active).map(p => (
                              <option key={p.id} value={`product:${p.id}`}>Open product: {p.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <button type="submit" disabled={uploadingBanner || !bannerForm.file} className="btn btn-primary">
                        {uploadingBanner ? 'Uploading…' : 'Upload Banner'}
                      </button>
                    </form>

                    {cpDashboardConfig.banners.length === 0 ? (
                      <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>No banners uploaded yet.</p>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {[...cpDashboardConfig.banners].sort((a, b) => a.sortOrder - b.sortOrder).map((banner, idx, arr) => (
                          <div key={banner.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                            <img src={`${API_URL}${banner.imageUrl}`} alt={banner.title || 'banner'} style={{ width: '160px', height: '52px', objectFit: 'cover', borderRadius: '6px', background: '#f1f5f9', opacity: banner.active ? 1 : 0.4 }} />
                            <div style={{ flex: 1, minWidth: '160px' }}>
                              <strong style={{ fontSize: '13px' }}>{banner.title || <em style={{ color: '#94a3b8' }}>No title</em>}</strong>
                              {banner.subtitle && <div style={{ fontSize: '12px', color: '#64748b' }}>{banner.subtitle}</div>}
                              {!banner.active && <span style={{ fontSize: '11px', color: '#991b1b' }}>Hidden</span>}
                            </div>
                            <div style={{ display: 'flex', gap: '6px' }}>
                              <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} disabled={idx === 0}
                                onClick={async () => {
                                  const prev = arr[idx - 1];
                                  await handleBannerPatch(banner.id, { sortOrder: prev.sortOrder });
                                  await handleBannerPatch(prev.id, { sortOrder: banner.sortOrder });
                                }}>↑</button>
                              <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: '12px' }} disabled={idx === arr.length - 1}
                                onClick={async () => {
                                  const next = arr[idx + 1];
                                  await handleBannerPatch(banner.id, { sortOrder: next.sortOrder });
                                  await handleBannerPatch(next.id, { sortOrder: banner.sortOrder });
                                }}>↓</button>
                              <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}
                                onClick={() => handleBannerPatch(banner.id, { active: !banner.active })}>{banner.active ? 'Hide' : 'Show'}</button>
                              <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: '12px' }} onClick={() => handleDeleteBanner(banner.id)}>Delete</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
              )}
            </div>
          ) : (
            <p style={{ color: 'var(--app-muted)' }}>Click Refresh to load marketplace data.</p>
          )}

          {/* Orders Modal */}
          {showMarketplaceOrders && (
            <div className="mp-orders-overlay" onClick={() => setShowMarketplaceOrders(false)}>
              <div onClick={e => e.stopPropagation()} className="mp-orders-modal">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0 }}>All Marketplace Orders ({marketplaceOrdersTotal})</h3>
                  <button onClick={() => setShowMarketplaceOrders(false)} className="btn btn-secondary">×</button>
                </div>

                <form
                  onSubmit={e => { e.preventDefault(); fetchMarketplaceOrders(1, marketplaceOrdersSearch); }}
                  style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}
                >
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Search by order # (or code / product / reseller)…"
                    value={marketplaceOrdersSearch}
                    onChange={e => setMarketplaceOrdersSearch(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button type="submit" className="btn btn-primary" disabled={loadingMarketplaceOrders}>Search</button>
                  {[
                    { key: 'all', label: 'All' },
                    { key: 'active', label: 'Active' },
                    { key: 'refunded', label: 'Refunded' }
                  ].map(tab => (
                    <button key={tab.key} type="button"
                      className={`shop-category-tab ${marketplaceOrdersStatus === tab.key ? 'is-active' : ''}`}
                      onClick={() => {
                        setMarketplaceOrdersStatus(tab.key);
                        fetchMarketplaceOrders(1, marketplaceOrdersSearch, tab.key);
                      }}>
                      <span>{tab.label}</span>
                      <span className="shop-category-count">{marketplaceOrdersCounts[tab.key] ?? 0}</span>
                    </button>
                  ))}
                  {marketplaceOrdersSearch && (
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => { setMarketplaceOrdersSearch(''); fetchMarketplaceOrders(1, ''); }}
                    >Clear</button>
                  )}
                </form>

                {loadingMarketplaceOrders ? (
                  <p>Loading orders…</p>
                ) : marketplaceOrders.length === 0 ? (
                  <p>{marketplaceOrdersSearch ? 'No orders match your search.' : 'No orders yet.'}</p>
                ) : (
                  <div className="mp-orders-wrap">
                  <table className="data-table mp-orders-table">
                    <thead>
                      <tr>
                        <th className="mp-c-id">#</th>
                        <th className="mp-c-product">Product</th>
                        <th className="mp-c-code">Code</th>
                        <th className="mp-c-reseller">Reseller</th>
                        <th className="mp-c-email">Activation / Receive Email</th>
                        <th className="mp-c-duration">Duration</th>
                        <th className="mp-c-gc">GC</th>
                        <th className="mp-c-date">Purchased</th>
                        <th className="mp-c-status">Status</th>
                        <th className="mp-c-actions">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marketplaceOrders.map(o => (
                        <tr key={o.id}
                          className={o.refundedAt ? 'mp-order-refunded' : undefined}
                          style={{ background: !o.refundedAt && o.buyerEmail ? '#fffbeb' : 'transparent' }}>
                          <td className="mp-c-id" data-label="Order">#{o.id}</td>
                          <td className="mp-c-product" data-label="Product">{o.productName}</td>
                          <td className="mp-c-code" data-label="Code">
                            {o.code
                              ? <code className="mp-order-code" title={o.code}>{o.code}</code>
                              : (o.fulfillmentStatus === 'completed' && o.deliveredInfo)
                                // Shipped / supplier-delivered: what the reseller received.
                                ? <code className="mp-order-code" title={o.deliveredInfo}>{o.deliveredInfo}</code>
                                : <span style={{ color: 'var(--app-muted)' }}>—</span>}
                          </td>
                          <td className="mp-c-reseller" data-label="Reseller">{o.resellerUsername || `#${o.resellerId}`}</td>
                          <td className="mp-c-email" data-label="Email">
                            {o.buyerEmail
                              ? <span style={{ color: '#92400e', fontWeight: 600, fontSize: '12px' }}>{o.buyerEmail}</span>
                              : <span style={{ color: 'var(--app-muted)', fontSize: '12px' }}>—</span>}
                          </td>
                          <td className="mp-c-duration" data-label="Duration">{o.retentionMonths === 0 ? 'Lifetime' : o.retentionMonths < 0 ? `${-o.retentionMonths}d` : `${o.retentionMonths}mo`}</td>
                          <td className="mp-c-gc" data-label="GC">{o.priceGoldCoins ?? o.priceCredits}</td>
                          <td className="mp-c-date" data-label="Purchased">{new Date(o.purchasedAt).toLocaleString()}</td>
                          <td className="mp-c-status" data-label="Status">
                            {o.refundedAt ? (
                              <span className="mp-status mp-status-refunded"
                                title={`Refunded ${new Date(o.refundedAt).toLocaleString()}${o.refundedGoldCoins ? ` · ${o.refundedGoldCoins} GC returned` : ''}`}>
                                Refunded
                              </span>
                            ) : o.fulfillmentStatus === 'pending' ? (
                              <span className="mp-status mp-status-pending">Pending Processing</span>
                            ) : o.fulfillmentStatus === 'completed' ? (
                              <span className="mp-status mp-status-done" title={o.deliveredAt ? `Shipped ${new Date(o.deliveredAt).toLocaleString()}${o.deliveredBy ? ' by ' + o.deliveredBy : ''}` : 'Shipped'}>
                                Completed
                              </span>
                            ) : (
                              <span className="mp-status mp-status-instant">Instant</span>
                            )}
                          </td>
                          <td className="mp-c-actions" data-label="Actions">
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                              {/* A refunded order is a record, not a pending action: the
                                  server rejects a second refund, so do not offer one. */}
                              {o.refundedAt ? (
                                <span style={{ fontSize: '12px', color: 'var(--app-muted)', whiteSpace: 'nowrap' }}>
                                  {o.refundedGoldCoins ? `${o.refundedGoldCoins} GC returned` : 'Refunded'}
                                </span>
                              ) : (
                                <>
                              {isManualOrder(o) && (
                                <button
                                  className={o.fulfillmentStatus === 'pending' ? 'btn btn-primary' : 'btn btn-secondary'}
                                  style={{ padding: '4px 10px', fontSize: '12px' }}
                                  onClick={() => openShipOrder(o)}
                                >
                                  {o.fulfillmentStatus === 'pending' ? 'Ship' : 'View / Edit'}
                                </button>
                              )}
                              <button
                                className="btn btn-danger"
                                style={{ padding: '4px 10px', fontSize: '12px' }}
                                disabled={refundingOrderId === o.id}
                                onClick={() => handleRefundMarketplaceOrder(o)}
                              >
                                {refundingOrderId === o.id ? 'Refunding…' : 'Refund'}
                              </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}

                {marketplaceOrdersTotalPages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
                    <button
                      className="btn btn-secondary"
                      disabled={loadingMarketplaceOrders || marketplaceOrdersPage <= 1}
                      onClick={() => fetchMarketplaceOrders(marketplaceOrdersPage - 1, marketplaceOrdersSearch)}
                    >← Prev</button>
                    <span style={{ fontSize: '13px', color: '#64748b' }}>
                      Page {marketplaceOrdersPage} of {marketplaceOrdersTotalPages}
                    </span>
                    <button
                      className="btn btn-secondary"
                      disabled={loadingMarketplaceOrders || marketplaceOrdersPage >= marketplaceOrdersTotalPages}
                      onClick={() => fetchMarketplaceOrders(marketplaceOrdersPage + 1, marketplaceOrdersSearch)}
                    >Next →</button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Admin Activity Logs Tab */}
      {activeTab === 'adminLogs' && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Admin Activity Logs</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '13px', color: '#64748b' }}>
                {adminLogsPagination.totalFiltered || adminLogs.length} total entries
              </span>
              <button onClick={() => fetchAdminLogs(1)} disabled={loadingAdminLogs} className="btn btn-secondary">
                Refresh
              </button>
            </div>
          </div>

          {loadingAdminLogs ? (
            <div className="loading">Loading admin logs...</div>
          ) : adminLogs.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><Icon name="shield" size={16} /></div>
              <div className="empty-text">No admin activity logs found.</div>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {adminLogs.map((log, idx) => {
                  const d = log.details || {};
                  const actionColors = {
                    migrate_reseller_users: { bg: '#eff6ff', border: '#bfdbfe', badge: '#1d4ed8', badgeBg: '#dbeafe' },
                    assign_user: { bg: '#f0fdf4', border: '#bbf7d0', badge: '#15803d', badgeBg: '#dcfce7' },
                    delete_user: { bg: '#fef2f2', border: '#fecaca', badge: '#b91c1c', badgeBg: '#fee2e2' },
                    remove_user: { bg: '#fef2f2', border: '#fecaca', badge: '#b91c1c', badgeBg: '#fee2e2' },
                    remove_reseller_user: { bg: '#fef2f2', border: '#fecaca', badge: '#b91c1c', badgeBg: '#fee2e2' },
                    bulk_assign_users: { bg: '#fefce8', border: '#fef08a', badge: '#a16207', badgeBg: '#fef9c3' },
                    bulk_delete_users: { bg: '#fef2f2', border: '#fecaca', badge: '#b91c1c', badgeBg: '#fee2e2' },
                    bulk_extend_users: { bg: '#f0fafe', border: '#bfe7f4', badge: '#0b7da6', badgeBg: '#dff4fd' },
                    create_reseller: { bg: '#f0fdf4', border: '#bbf7d0', badge: '#15803d', badgeBg: '#dcfce7' },
                    create_cp_reseller: { bg: '#f0fdf4', border: '#bbf7d0', badge: '#15803d', badgeBg: '#dcfce7' },
                    delete_reseller: { bg: '#fef2f2', border: '#fecaca', badge: '#b91c1c', badgeBg: '#fee2e2' },
                    delete_cp_reseller: { bg: '#fef2f2', border: '#fecaca', badge: '#b91c1c', badgeBg: '#fee2e2' },
                    update_reseller: { bg: '#eff6ff', border: '#bfdbfe', badge: '#1d4ed8', badgeBg: '#dbeafe' },
                    update_cp_reseller: { bg: '#eff6ff', border: '#bfdbfe', badge: '#1d4ed8', badgeBg: '#dbeafe' },
                    delete_organization: { bg: '#fef2f2', border: '#fecaca', badge: '#b91c1c', badgeBg: '#fee2e2' },
                    add_credits: { bg: '#fefce8', border: '#fef08a', badge: '#a16207', badgeBg: '#fef9c3' },
                    bulk_org_access: { bg: '#f0fafe', border: '#bfe7f4', badge: '#0b7da6', badgeBg: '#dff4fd' },
                  };
                  const colors = actionColors[log.action] || { bg: '#f8fafc', border: '#e2e8f0', badge: '#475569', badgeBg: '#f1f5f9' };

                  // Render human-readable details
                  const renderDetails = () => {
                    if (!d || Object.keys(d).length === 0) return null;

                    // Migration action
                    if (log.action === 'migrate_reseller_users') {
                      return (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                            <span style={{ background: '#fee2e2', color: '#991b1b', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                              {d.sourceType === 'credits-pool' ? '[CP] ' : ''}{d.sourceResellerName}
                            </span>
                            <span style={{ fontSize: '16px', color: '#9ca3af' }}>→</span>
                            <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                              {d.targetType === 'credits-pool' ? '[CP] ' : ''}{d.targetResellerName}
                            </span>
                            <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>
                              {d.migratedCount} migrated{d.skippedDuplicates > 0 ? `, ${d.skippedDuplicates} skipped` : ''}
                            </span>
                          </div>
                          {d.emails && d.emails.length > 0 && (
                            <details style={{ fontSize: '12px' }}>
                              <summary style={{ cursor: 'pointer', color: '#3b82f6', userSelect: 'none' }}>
                                {d.emails.length} email(s)
                              </summary>
                              <div style={{ marginTop: '6px', padding: '8px 12px', background: 'rgba(0,0,0,0.03)', borderRadius: '6px', maxHeight: '150px', overflow: 'auto', lineHeight: '1.8' }}>
                                {d.emails.map((email, i) => (
                                  <span key={i} style={{ display: 'inline-block', background: '#e2e8f0', padding: '2px 8px', borderRadius: '4px', marginRight: '6px', marginBottom: '4px', fontSize: '11px', fontFamily: 'monospace' }}>
                                    {email}
                                  </span>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      );
                    }

                    // Add credits
                    if (log.action === 'add_credits') {
                      const isDeduct = d.credits < 0;
                      return (
                        <div style={{ marginTop: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <span style={{ background: isDeduct ? '#fee2e2' : '#dcfce7', color: isDeduct ? '#991b1b' : '#166534', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>
                              {isDeduct ? '−' : '+'}{Math.abs(d.credits)} credits
                            </span>
                            <span style={{ fontSize: '12px', color: '#6b7280' }}>→</span>
                            <span style={{ fontWeight: '600', fontSize: '12px', color: '#334155' }}>{d.resellerUsername}</span>
                            <span style={{ fontSize: '11px', color: 'var(--app-muted)' }}>({d.balanceBefore} → {d.balanceAfter})</span>
                          </div>
                          {d.reason && <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Reason: {d.reason}</div>}
                        </div>
                      );
                    }

                    // Delete/Create reseller
                    if (log.action === 'delete_reseller' || log.action === 'delete_cp_reseller' || log.action === 'create_reseller' || log.action === 'create_cp_reseller') {
                      return (
                        <div style={{ marginTop: '10px', fontSize: '12px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <span><span style={{ color: 'var(--app-muted)' }}>Username: </span><strong>{d.username}</strong></span>
                          <span><span style={{ color: 'var(--app-muted)' }}>Group: </span>{d.userGroup}</span>
                          {d.maxLicenses && <span><span style={{ color: 'var(--app-muted)' }}>Licenses: </span>{d.maxLicenses}</span>}
                          {d.initialCredits !== undefined && <span><span style={{ color: 'var(--app-muted)' }}>Credits: </span>{d.initialCredits}</span>}
                        </div>
                      );
                    }

                    // Delete organization
                    if (log.action === 'delete_organization') {
                      return (
                        <div style={{ marginTop: '10px', fontSize: '12px' }}>
                          <span style={{ fontWeight: '600', color: '#dc2626' }}>{d.organizationName}</span>
                          {d.affectedResellers > 0 && <span style={{ color: 'var(--app-muted)', marginLeft: '8px' }}>{d.affectedResellers} reseller(s) cleaned up</span>}
                        </div>
                      );
                    }

                    // Bulk delete/extend
                    if (log.action === 'bulk_delete_users' || log.action === 'bulk_extend_users') {
                      const count = d.deletedCount || d.extended || 0;
                      return (
                        <div style={{ marginTop: '10px' }}>
                          <span style={{ fontSize: '12px', color: '#334155', fontWeight: '500' }}>
                            {count} user(s) {log.action === 'bulk_delete_users' ? 'deleted' : `extended by ${d.extensionMonths}mo`}
                          </span>
                          {d.emails && d.emails.length > 0 && (
                            <details style={{ fontSize: '12px', marginTop: '6px' }}>
                              <summary style={{ cursor: 'pointer', color: '#3b82f6', userSelect: 'none' }}>
                                {d.emails.length} email(s)
                              </summary>
                              <div style={{ marginTop: '6px', padding: '8px 12px', background: 'rgba(0,0,0,0.03)', borderRadius: '6px', maxHeight: '150px', overflow: 'auto', lineHeight: '1.8' }}>
                                {d.emails.map((email, i) => (
                                  <span key={i} style={{ display: 'inline-block', background: '#e2e8f0', padding: '2px 8px', borderRadius: '4px', marginRight: '6px', marginBottom: '4px', fontSize: '11px', fontFamily: 'monospace' }}>
                                    {email}
                                  </span>
                                ))}
                              </div>
                            </details>
                          )}
                        </div>
                      );
                    }

                    // Generic: render key-value pairs nicely
                    const skipKeys = ['timestamp', 'action'];
                    const entries = Object.entries(d).filter(([k]) => !skipKeys.includes(k));
                    if (entries.length === 0) return null;

                    // Check if there's an emails array
                    const emailsEntry = entries.find(([k]) => k === 'emails' || k === 'email');
                    const otherEntries = entries.filter(([k]) => k !== 'emails');

                    return (
                      <div style={{ marginTop: '10px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
                          {otherEntries.map(([key, val]) => {
                            if (typeof val === 'object' && val !== null) return null;
                            return (
                              <div key={key} style={{ fontSize: '12px' }}>
                                <span style={{ color: 'var(--app-muted)' }}>{key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}: </span>
                                <span style={{ color: '#334155', fontWeight: '500' }}>
                                  {typeof val === 'boolean' ? (val ? 'Yes' : 'No') : String(val)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {emailsEntry && Array.isArray(emailsEntry[1]) && emailsEntry[1].length > 0 && (
                          <details style={{ fontSize: '12px', marginTop: '6px' }}>
                            <summary style={{ cursor: 'pointer', color: '#3b82f6', userSelect: 'none' }}>
                              {emailsEntry[1].length} email(s)
                            </summary>
                            <div style={{ marginTop: '6px', padding: '8px 12px', background: 'rgba(0,0,0,0.03)', borderRadius: '6px', maxHeight: '150px', overflow: 'auto', lineHeight: '1.8' }}>
                              {emailsEntry[1].map((email, i) => (
                                <span key={i} style={{ display: 'inline-block', background: '#e2e8f0', padding: '2px 8px', borderRadius: '4px', marginRight: '6px', marginBottom: '4px', fontSize: '11px', fontFamily: 'monospace' }}>
                                  {typeof email === 'string' ? email : JSON.stringify(email)}
                                </span>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    );
                  };

                  return (
                    <div
                      key={idx}
                      style={{
                        background: colors.bg,
                        border: `1px solid ${colors.border}`,
                        borderRadius: '10px',
                        padding: '14px 18px',
                        transition: 'box-shadow 0.15s',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{
                            background: colors.badgeBg,
                            color: colors.badge,
                            padding: '4px 12px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                          }}>
                            {log.action.replace(/_/g, ' ')}
                          </span>
                          <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>
                            {log.adminUsername || log.adminName || 'admin'}
                          </span>
                        </div>
                        <span style={{ fontSize: '12px', color: 'var(--app-muted)' }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                      </div>
                      {renderDetails()}
                    </div>
                  );
                })}
              </div>

              {/* Pagination Controls */}
              {adminLogsPagination && adminLogsPagination.totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px', marginTop: '20px' }}>
                  <button
                    className="btn btn-secondary"
                    disabled={adminLogsPage <= 1}
                    onClick={() => fetchAdminLogs(adminLogsPage - 1)}
                  >
                    ‹ Previous
                  </button>
                  <span style={{ fontSize: '14px', color: '#64748b' }}>
                    Page {adminLogsPage} of {adminLogsPagination.totalPages}
                  </span>
                  <button
                    className="btn btn-secondary"
                    disabled={adminLogsPage >= adminLogsPagination.totalPages}
                    onClick={() => fetchAdminLogs(adminLogsPage + 1)}
                  >
                    Next ›
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
      </div>
    </div >
  );
}

export default App;
